import React, { useEffect, useRef, useState, useMemo } from "react";
import { useFeatures } from "../contexts/FeaturesContext";
import "../css/Appointment.css";
import "../css/AppointmentLegend.css";
import CustomSelect from "../components/CustomSelect";
import CustomButton from "../components/CustomButton";
import CustomTextField from "../components/CustomTextField";
import GridCalendar from "../components/GridCalendar";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popper,
  Paper,
  ClickAwayListener,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  DatePicker,
  LocalizationProvider,
  DateCalendar,
  PickersDay,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import InfoIcon from "@mui/icons-material/Info";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import InvoiceItemsEditor, {
  InvoiceItem,
  isInvoiceProductMissingSelection,
} from "../components/invoices/InvoiceItemsEditor";
import {
  createAppointment,
  updateAppointment,
  deleteAppointment,
  getAppointmentsByUnit,
  getAppointmentsByBusiness,
  getUnavailableSlots,
} from "../services/AppointmentService";
import { getAllBranches } from "../services/BranchesService";
import { getClientById, getClientsSelect } from "../services/ClientsService";
import { manageSlots, getSlots } from "../services/SlotService";
import {
  getAllServices,
  getAllServicesByBusiness,
} from "../services/ServicesService";
import { getAllBusinesses } from "../services/VenueService";
import { fetchProducts } from "../services/ProductsNewService";
import { fetchStockSummary } from "../services/StocksNewService";
import {
  updateQuotation,
  getInvoiceById,
  payInvoice,
  refundInvoice,
} from "../services/newInvoicesService";
import { getPaymentsByInvoice } from "../services/newPaymentsService";
import { getAllInvoicesForUser } from "../services/InvoicesService";
import { getUserId } from "../services/authService";
import { getUnitSettings } from "../services/AdvancedSettings";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import AddClientDialog from "../components/AddClientDialog";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import CustomDialog from "../components/CustomDialog";
import CustomTable from "../components/table/CustomTable";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";
import { formatTableDate } from "../utils/tableDateFormat";
import { createCreditNote, createDebitNote } from "../services/newNotesService";
import { fetchPromos } from "../services/PromoCodeService";
import {
  ADMIN_APPOINTMENT_SOCKET_EVENT,
  AdminAppointmentSocketPayload,
} from "../services/adminAppointmentSocket";
import {
  getAppointmentPointShares,
  getClientLoyaltyBalance,
  getLoyaltyPreview,
  getLoyaltySettings,
  getLoyaltyTransactions,
  getRewards,
  LoyaltyReward,
  LoyaltySettingsResponse,
  isServiceAllowedForReward,
  ResolvedServiceReward,
  resolveServiceReward,
  resolveShareSettingForBusiness,
} from "../services/LoyaltyService";
import SessionRosterDialog from "../components/classes/SessionRosterDialog";
import {
  CLASS_EVENT_COLORS,
  VIRTUAL_CLASS_EVENT_COLORS,
  buildClassEventProps,
  getClassEventTitle,
  isClassAppointment,
  isClassUnavailableSlot,
  isClassEvent,
  isVirtualClassAppointment,
} from "../components/classes/calendarClassEvent";
import {
  createClass,
  isScheduleConflictError,
  readScheduleConflicts,
} from "../services/ClassesService";
import ScheduleConflictsDialog from "../components/classes/ScheduleConflictsDialog";
import { ScheduleConflict } from "../components/classes/types";
import {
  buildClassFormFields,
  buildClassFormInitialValues,
  buildCreateClassPayloadFromValues,
  readClassFormControlState,
} from "../components/classes/classFormFields";
import {
  useCategoryOptions,
  useCoachOptions,
} from "../components/classes/useClassOptions";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const normalizeArrayResponse = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.services)) return response.services;
  if (Array.isArray(response?.products)) return response.products;
  if (Array.isArray(response?.venues)) return response.venues;
  return [];
};

const getStoredOrganizationId = () => {
  const userRaw = localStorage.getItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;
  return (
    user?.organizationId ||
    user?.organization_id ||
    user?.orgId ||
    user?.organization?.id ||
    ""
  );
};

const getCachedSharePointsEnabled = () => {
  const organizationId = getStoredOrganizationId();
  if (!organizationId) return false;
  return localStorage.getItem(`loyalty_share_points_${organizationId}`) ===
    "true";
};

const parseBooleanSetting = (value: any, fallback: boolean) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true") return true;
    if (normalized === "false") return false;
  }
  if (typeof value === "number") return value !== 0;
  return fallback;
};

const isTruthyFlag = (value: any) => parseBooleanSetting(value, false);

const normalizeRedeemDiscountType = (...values: any[]): "PERCENT" | "FIXED" =>
  values.some((value) => String(value || "").toUpperCase() === "FIXED")
    ? "FIXED"
    : "PERCENT";

const calculateFixedCashbackDiscountPercent = (
  fixedCashbackAmount: number,
  subtotalBeforeFullInvoiceDiscount: number,
) => {
  const cashback = Math.max(0, Number(fixedCashbackAmount) || 0);
  const subtotal = Math.max(
    0,
    Number(subtotalBeforeFullInvoiceDiscount) || 0,
  );

  if (cashback <= 0 || subtotal <= 0) return 0;

  return Math.round(Math.min(100, (cashback / subtotal) * 100) * 100) / 100;
};

const calculateFixedCashbackAmountForPoints = (
  redeemPoints: number,
  rulePoints: number,
  ruleCashback: number,
) => {
  const points = Math.max(0, Number(redeemPoints) || 0);
  const requiredPoints = Math.max(0, Number(rulePoints) || 0);
  const cashback = Math.max(0, Number(ruleCashback) || 0);

  if (points <= 0 || requiredPoints <= 0 || cashback <= 0) return 0;

  return Math.round(((points / requiredPoints) * cashback) * 100) / 100;
};

const appointmentSelectInputSx = {
  height: 40,
  minHeight: 39,
  "& .MuiSelect-select": {
    height: "100% !important",
    minHeight: "0 !important",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
  },
};

const appointmentHelperSlotSx = {
  height: 34,
  minHeight: 34,
  mt: 0.35,
  lineHeight: 1.2,
  fontSize: "0.68rem",
  maxWidth: "100%",
  whiteSpace: "normal",
  overflow: "hidden",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  display: "block",
};

const calculateLiveInvoiceItemTotal = (item: any) => {
  const payableTotal = Number(
    item?.totalPrice ??
      item?.net_amount ??
      item?.total_price ??
      item?.total_amount ??
      item?.total,
  );

  if (Number.isFinite(payableTotal)) {
    return Math.round(Math.max(0, payableTotal) * 100) / 100;
  }

  const quantity = Number(item?.quantity ?? item?.qty) || 0;
  const unitCost = Number(
    item?.unitCost ?? item?.amount ?? item?.unitPrice ?? item?.unit_price,
  );
  const discountPercent = Math.min(
    100,
    Math.max(
      0,
      Number(
        item?.discount ??
          item?.discountPercent ??
          item?.discount_percent ??
          0,
      ) || 0,
    ),
  );

  if (quantity > 0 && Number.isFinite(unitCost) && unitCost >= 0) {
    const subtotal = quantity * unitCost;
    return Math.round((subtotal - (subtotal * discountPercent) / 100) * 100) / 100;
  }

  return 0;
};

const calculateOriginalInvoiceItemSubtotal = (item: any) => {
  const quantity = Number(item?.quantity ?? item?.qty) || 0;
  const unitCost = Number(
    item?.unitCost ??
      item?.amount ??
      item?.unitPrice ??
      item?.unit_price ??
      item?.price,
  );

  if (quantity > 0 && Number.isFinite(unitCost) && unitCost >= 0) {
    return Math.round(quantity * unitCost * 100) / 100;
  }

  return 0;
};

const getCachedFullInvoiceEnabled = () => {
  const organizationId = getStoredOrganizationId();
  if (!organizationId) return true;
  const value = localStorage.getItem(`loyalty_full_invoice_${organizationId}`);
  return parseBooleanSetting(value, true);
};

// Sharing without a fixed divisor still splits between whoever is on the
// booking, and that list has always been capped at 3 guests.
const DEFAULT_MAX_SHARED_CLIENTS = 3;

// The divisor varies per business inside one org — a value cached from a padel
// screen is wrong on a football one — so every cache entry is keyed by business
// and only falls back to the org-level entry when that business has no row.
const getCachedFixedShareEnabled = (businessId?: string | null) => {
  const organizationId = getStoredOrganizationId();
  if (!organizationId) return false;
  const businessValue = businessId
    ? localStorage.getItem(
        `loyalty_fixed_share_${organizationId}_${businessId}`,
      )
    : null;
  return parseBooleanSetting(
    businessValue ??
      localStorage.getItem(`loyalty_fixed_share_${organizationId}`),
    false,
  );
};

const getCachedShareDivisor = (businessId?: string | null) => {
  const organizationId = getStoredOrganizationId();
  if (!organizationId) return null;
  const readDivisor = (key: string) => {
    const value = Number(localStorage.getItem(key));
    return Number.isInteger(value) && value >= 2 ? value : null;
  };
  return (
    (businessId
      ? readDivisor(`loyalty_share_divisor_${organizationId}_${businessId}`)
      : null) ?? readDivisor(`loyalty_share_divisor_${organizationId}`)
  );
};

const setCachedShareSetting = (
  businessId: string | null | undefined,
  isFixedShare: boolean,
  shareDivisor: number | null,
) => {
  const organizationId = getStoredOrganizationId();
  if (!organizationId) return;
  const suffix = businessId ? `_${businessId}` : "";
  localStorage.setItem(
    `loyalty_fixed_share_${organizationId}${suffix}`,
    String(isFixedShare),
  );
  if (shareDivisor) {
    localStorage.setItem(
      `loyalty_share_divisor_${organizationId}${suffix}`,
      String(shareDivisor),
    );
  }
};

/**
 * How many clients can be added to a booking alongside the payer. Players per
 * match is a roster cap first: a football pitch holds 12 whether or not points
 * are being fixed-divided, so this reads the number alone. An org that never
 * set one keeps the old 4 players / 3 guests.
 */
const resolveMaxSharedClients = (shareDivisor: number | null) =>
  shareDivisor && shareDivisor >= 2
    ? shareDivisor - 1
    : DEFAULT_MAX_SHARED_CLIENTS;

const getRewardTypeValue = (reward: any) =>
  String(reward?.reward_type ?? reward?.rewardType ?? "").toUpperCase();

const getRewardItemIdValue = (reward: any) =>
  String(reward?.reward_item_id ?? reward?.rewardItemId ?? "");

const getRewardRedeemPointsValue = (reward: any) =>
  Number(reward?.redeem_points ?? reward?.redeemPoints ?? 0) || 0;

const getRewardQuantityValue = (reward: any) =>
  Math.max(1, Math.floor(Number(reward?.quantity) || 1));

// A SERVICE reward's free time is the duration of the service it points at.
const getRewardFreeMinutes = (reward: any) =>
  Number(reward?.duration ?? 0) || 0;

const getServiceDurationMinutes = (service: any) =>
  Number(service?.duration ?? service?.minutes ?? service?.length ?? 0) || 0;

/**
 * A service reward buys its own service's worth of time: the discount is that
 * share of the booked service's length, capped at 100%. Same sum the backend
 * runs — for a preview only, the invoice path asks the API.
 */
const calculateServiceRewardDiscountPercent = (
  reward: any,
  serviceDurationMinutes: number,
) => {
  const rewardMinutes = getRewardFreeMinutes(reward);
  if (rewardMinutes <= 0 || serviceDurationMinutes <= 0) return 0;
  return (
    Math.round(
      Math.min(100, (rewardMinutes / serviceDurationMinutes) * 100) * 100,
    ) / 100
  );
};

const getRewardTypeLabel = (rewardType: string) =>
  rewardType === "PRODUCT" ? "Product" : "Service";

/**
 * Only a reward whose free time covers the whole booking still blocks promo
 * codes and redeemed points. Anything shorter leaves a remainder those may
 * discount, capped together at 100%.
 */
const serviceRewardCoversBooking = (
  reward: any,
  bookedServiceDurationMinutes: number,
) =>
  calculateServiceRewardDiscountPercent(
    reward,
    bookedServiceDurationMinutes,
  ) >= 100;

const getProductRewardAvailableStock = (
  stockItems: any[],
  productId: string,
  businessId?: string | null,
) => {
  if (!productId) return 0;
  const item = (Array.isArray(stockItems) ? stockItems : []).find(
    (stockItem: any) =>
      String(
        stockItem?.productId ??
          stockItem?.product_id ??
          stockItem?.product?.id ??
          "",
      ) === productId,
  );

  if (!item) return 0;

  if (businessId) {
    const businessStock = Array.isArray(item?.businesses)
      ? item.businesses.find(
          (business: any) =>
            String(business?.businessId ?? business?.business_id ?? "") ===
            String(businessId),
        )
      : null;
    return Math.max(0, Math.floor(Number(businessStock?.qty) || 0));
  }

  return Math.max(0, Math.floor(Number(item?.totalQty ?? item?.qty) || 0));
};

const getProductRewardStockError = (
  reward: any,
  stockItems: any[],
  businessId?: string | null,
) => {
  if (!reward || getRewardTypeValue(reward) !== "PRODUCT") return "";

  const rewardItemId = getRewardItemIdValue(reward);
  const rewardQuantity = getRewardQuantityValue(reward);
  const availableStock = getProductRewardAvailableStock(
    stockItems,
    rewardItemId,
    businessId,
  );

  return availableStock < rewardQuantity
    ? "Not enough stock for this reward product."
    : "";
};

const getInvoiceLineMetadata = (item: any) =>
  item?.metadata && typeof item.metadata === "object" ? item.metadata : {};

const getInvoiceLineType = (item: any) => {
  const metadata = getInvoiceLineMetadata(item);
  return String(
    item?.itemType ?? item?.type ?? metadata?.type ?? "SERVICE",
  ).toUpperCase();
};

const getInvoiceLineItemId = (item: any) => {
  const metadata = getInvoiceLineMetadata(item);
  return String(
    item?.serviceId ??
      item?.service ??
      metadata?.serviceId ??
      metadata?.service_id ??
      item?.productId ??
      item?.product_id ??
      metadata?.productId ??
      metadata?.product_id ??
      "",
  );
};

export const hasInvoiceSuccessfulPayment = (invoice: any) => {
  const payments = [
    ...(Array.isArray(invoice?.payments) ? invoice.payments : []),
    ...(Array.isArray(invoice?.invoice?.payments) ? invoice.invoice.payments : []),
  ];
  if (
    payments.some((payment: any) =>
      ["SUCCESS", "PAID", "COMPLETED"].includes(
        String(payment?.status ?? "").toUpperCase(),
      ),
    )
  ) {
    return true;
  }
  const paidAmount = Number(
    invoice?.invoice?.paid_amount ??
      invoice?.invoice?.paidAmount ??
      invoice?.invoice?.total_paid ??
      invoice?.paid_amount ??
      invoice?.paid ??
      invoice?.total_paid ??
      0,
  );
  return Number.isFinite(paidAmount) && paidAmount > 0;
};

const isLoyaltyServiceItem = (item: any) => {
  const metadata = item?.metadata ?? {};
  const itemType = String(item?.itemType ?? item?.item_type ?? "").toUpperCase();
  if (itemType === "PRODUCT") return false;
  const type = String(metadata?.type ?? item?.type ?? "").toUpperCase();
  if (type === "PRODUCT") return false;
  return (
    type === "SERVICE" ||
    metadata?.serviceId !== undefined ||
    metadata?.service_id !== undefined ||
    item?.serviceId !== undefined ||
    item?.service_id !== undefined
  );
};

const calculateInvoiceAmountForLoyaltyPreview = (
  values: Record<string, any>,
  options: { serviceOnly?: boolean } = {},
) => {
  const itemSource = getInvoiceObjectSources(values).find(
    (source: any) => Array.isArray(source?.items) && source.items.length > 0,
  );
  const items = Array.isArray(itemSource?.items) ? itemSource.items : [];
  const loyaltyItems = options.serviceOnly
    ? items.filter(isLoyaltyServiceItem)
    : items;
  const itemTotal = loyaltyItems.reduce((sum: number, item: any) => {
    const total = Number(
      item?.totalPrice ??
        item?.net_amount ??
        item?.total_price ??
        item?.total_amount ??
        item?.total,
    );
    return sum + (Number.isFinite(total) ? total : 0);
  }, 0);

  if (items.length > 0) {
    if (options.serviceOnly) {
      return Math.round(Math.max(0, itemTotal) * 100) / 100;
    }

    const discountPercent = Math.min(
      100,
      Math.max(0, Number(values?.discount) || 0),
    );
    const amountAfterDiscount = Math.max(
      0,
      itemTotal - (itemTotal * discountPercent) / 100,
    );
    const vatPercent = Math.min(100, Math.max(0, Number(values?.vat) || 0));
    return (
      Math.round(
        (amountAfterDiscount + (amountAfterDiscount * vatPercent) / 100) * 100,
      ) / 100
    );
  }

  const invoiceTotal = Number(
    values?.net_amount ??
      values?.total_amount ??
      values?.amount ??
      values?.balance ??
      values?.subtotal ??
      values?.total,
  );
  return Number.isFinite(invoiceTotal) && invoiceTotal > 0
    ? Math.round(invoiceTotal * 100) / 100
    : 0;
};

const buildInvoiceItemsSignatureForLoyaltyPreview = (
  values: Record<string, any>,
  options: { serviceOnly?: boolean } = {},
) => {
  const itemSource = getInvoiceObjectSources(values).find(
    (source: any) => Array.isArray(source?.items) && source.items.length > 0,
  );
  const items = Array.isArray(itemSource?.items) ? itemSource.items : [];
  const loyaltyItems = options.serviceOnly
    ? items.filter(isLoyaltyServiceItem)
    : items;

  return JSON.stringify(
    loyaltyItems.map((item: any) => {
      const metadata = item?.metadata ?? {};
      const itemType = String(
        item?.itemType ?? item?.item_type ?? metadata?.type ?? item?.type ?? "",
      ).toUpperCase();
      const itemId = String(
        item?.serviceId ??
          item?.service_id ??
          item?.productId ??
          item?.product_id ??
          metadata?.serviceId ??
          metadata?.service_id ??
          metadata?.productId ??
          metadata?.product_id ??
          item?.id ??
          "",
      );
      const quantity = Number(item?.quantity ?? item?.qty ?? 1) || 1;
      const unitPrice = Number(
        item?.unitCost ?? item?.unitPrice ?? item?.unit_price ?? item?.amount ?? 0,
      ) || 0;
      const discount = Number(
        item?.discount ?? item?.discountPercent ?? metadata?.discountPercent ?? 0,
      ) || 0;
      const total = Number(
        item?.totalPrice ??
          item?.net_amount ??
          item?.total_price ??
          item?.total_amount ??
          item?.total ??
          0,
      ) || 0;

      return {
        itemType,
        itemId,
        quantity: Math.round(quantity * 100) / 100,
        unitPrice: Math.round(unitPrice * 100) / 100,
        discount: Math.round(discount * 100) / 100,
        total: Math.round(total * 100) / 100,
      };
    }),
  );
};

const getInvoiceObjectSources = (invoice: any): any[] =>
  [
    invoice,
    invoice?.sourceInvoice,
    invoice?.invoice,
    invoice?.sourceInvoice?.invoice,
    invoice?.data,
    invoice?.data?.invoice,
    invoice?.sourceInvoice?.data,
    invoice?.sourceInvoice?.data?.invoice,
  ].filter(Boolean);

const getInvoiceMetadataSources = (invoice: any): any[] =>
  getInvoiceObjectSources(invoice)
    .map((source) => source?.metadata)
    .filter(Boolean);

const getInvoiceItemSources = (invoice: any): any[] =>
  getInvoiceObjectSources(invoice).flatMap((source) => [
    ...(Array.isArray(source?.items) ? source.items : []),
    ...(Array.isArray(source?.details) ? source.details : []),
  ]);

const getInvoiceServiceSources = (invoice: any): any[] =>
  getInvoiceMetadataSources(invoice).flatMap((metadata) =>
    Array.isArray(metadata?.services) ? metadata.services : [],
  );

const collectValuesFromSources = (
  sources: any[],
  keys: string[],
  includeMetadata = false,
) => {
  const values: any[] = [];
  sources.forEach((source) => {
    keys.forEach((key) => values.push(source?.[key]));
    if (includeMetadata) {
      keys.forEach((key) => values.push(source?.metadata?.[key]));
    }
  });
  return values;
};

const extractInvoiceLoyaltyRedeemPoints = (invoice: any): number => {
  const candidates: number[] = [];
  const addCandidate = (value: any) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      candidates.push(Math.floor(numeric));
    }
  };

  const keys = [
    "loyaltyRedeemPoints",
    "loyalty_redeem_points",
  ];
  [
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceServiceSources(invoice), keys, true),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), keys, true),
  ].forEach(addCandidate);

  return candidates.length > 0 ? Math.max(...candidates) : 0;
};

const extractInvoiceLoyaltyDiscountPercent = (invoice: any): number => {
  const candidates: number[] = [];
  const addCandidate = (value: any) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      candidates.push(Math.min(100, Math.max(0, numeric)));
    }
  };

  const keys = ["loyaltyDiscountPercent", "loyalty_discount_percent"];
  const hasRewardMarker = (source: any) =>
    Boolean(
      source?.loyaltyReward ||
        source?.loyaltyRewardId ||
        source?.loyalty_reward_id ||
        source?.rewardType ||
        source?.reward_type ||
        source?.metadata?.loyaltyReward ||
        source?.metadata?.loyaltyRewardId ||
        source?.metadata?.loyalty_reward_id ||
        source?.metadata?.rewardType ||
        source?.metadata?.reward_type,
    );
  const addFromSource = (source: any, includeMetadata = false) => {
    if (!source || hasRewardMarker(source)) return;
    keys.forEach((key) => addCandidate(source?.[key]));
    if (includeMetadata && !hasRewardMarker(source?.metadata)) {
      keys.forEach((key) => addCandidate(source?.metadata?.[key]));
    }
  };

  getInvoiceObjectSources(invoice).forEach((source) => addFromSource(source));
  getInvoiceMetadataSources(invoice).forEach((source) => addFromSource(source));
  getInvoiceServiceSources(invoice).forEach((source) =>
    addFromSource(source, true),
  );
  getInvoiceItemSources(invoice).forEach((source) =>
    addFromSource(source, true),
  );

  return candidates.length > 0 ? Math.max(...candidates) : 0;
};

const extractInvoiceLoyaltyRedeemDiscountType = (
  invoice: any,
): "PERCENT" | "FIXED" | null => {
  const keys = [
    "loyaltyRedeemDiscountType",
    "loyalty_redeem_discount_type",
    "redeemDiscountType",
    "redeem_discount_type",
  ];

  const value = [
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceServiceSources(invoice), keys, true),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), keys, true),
  ].find((candidate) => candidate !== undefined && candidate !== null && candidate !== "");

  const normalized = String(value || "").toUpperCase();
  if (normalized === "FIXED") return "FIXED";
  if (normalized === "PERCENT") return "PERCENT";
  return null;
};

const extractInvoiceLoyaltyRedeemDiscountAmount = (invoice: any): number => {
  const candidates: number[] = [];
  const addCandidate = (value: any) => {
    const numeric = Number(value);
    if (Number.isFinite(numeric) && numeric > 0) {
      candidates.push(Math.round(numeric * 100) / 100);
    }
  };

  const keys = [
    "loyaltyRedeemDiscountAmount",
    "loyalty_redeem_discount_amount",
    "loyaltyFixedCashbackAmount",
    "loyalty_fixed_cashback_amount",
    "redeemDiscountAmount",
    "redeem_discount_amount",
  ];

  [
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceServiceSources(invoice), keys, true),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), keys, true),
  ].forEach(addCandidate);

  return candidates.length > 0 ? Math.max(...candidates) : 0;
};

const extractInvoiceBusinessId = (source: any): string => {
  if (!source) return "";
  const candidates = [
    source?.business?.id,
    source?.business_id,
    source?.businessId,
    source?.invoice?.business_id,
    source?.invoice?.businessId,
    source?.invoice?.metadata?.businessId,
    source?.invoice?.metadata?.business_id,
    source?.metadata?.businessId,
    source?.metadata?.business_id,
    source?.sourceInvoice ? extractInvoiceBusinessId(source.sourceInvoice) : "",
  ];
  const found = candidates.find((value) => Boolean(String(value || "").trim()));
  return found ? String(found).trim() : "";
};

const extractInvoiceSharedClientIds = (invoice: any): string[] => {
  const ids: string[] = [];
  const addIds = (value: any) => {
    if (!Array.isArray(value)) return;
    value.forEach((id) => {
      const normalizedId = String(id || "").trim();
      if (normalizedId) ids.push(normalizedId);
    });
  };

  const keys = ["sharedClientIds", "shared_client_ids"];
  // The roster can also live on the appointment when players joined after
  // booking rather than being picked in the dialog.
  const appointmentSources = [
    invoice?.appointment,
    invoice?.sourceInvoice?.appointment,
    invoice?.invoice?.appointment,
    invoice?.raw,
    invoice?.raw?.appointment,
  ].filter(Boolean);
  [
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceServiceSources(invoice), keys, true),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), keys, true),
    ...collectValuesFromSources(appointmentSources, keys, true),
  ].forEach(addIds);

  // Never truncate what was saved. Players per match caps the picker when
  // staff add people to a booking; a roster that is already on the invoice —
  // a 12-a-side football match — must be rendered in full, whatever this
  // browser happens to have cached for that business.
  return Array.from(new Set(ids));
};

const extractInvoiceLoyaltyRewardDetails = (invoice: any) => {
  const sources = [
    ...getInvoiceObjectSources(invoice),
    ...getInvoiceMetadataSources(invoice),
    ...getInvoiceItemSources(invoice),
    ...getInvoiceItemSources(invoice).map((item) => item?.metadata),
  ].filter(Boolean);

  const firstValue = (keys: string[]) => {
    for (const source of sources) {
      for (const key of keys) {
        const value = source?.[key];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
    }
    return undefined;
  };

  const rewardId = firstValue(["loyaltyRewardId", "loyalty_reward_id"]);
  const rewardType = firstValue([
    "loyaltyRewardType",
    "loyalty_reward_type",
    "rewardType",
    "reward_type",
  ]);
  const rewardItemId = firstValue([
    "loyaltyRewardItemId",
    "loyalty_reward_item_id",
    "rewardItemId",
    "reward_item_id",
  ]);
  const redeemPoints = firstValue([
    "loyaltyRewardRedeemPoints",
    "loyalty_reward_redeem_points",
    "redeemPoints",
    "redeem_points",
  ]);

  if (!rewardId && !rewardType && !rewardItemId && !redeemPoints) {
    return null;
  }

  const rewardItem = getInvoiceItemSources(invoice).find((item) => {
    const metadata = item?.metadata || {};
    return (
      metadata?.loyaltyReward ||
      metadata?.loyaltyRewardId ||
      metadata?.rewardType ||
      metadata?.reward_type ||
      item?.loyaltyRewardId ||
      item?.rewardType ||
      item?.reward_type
    );
  });
  const itemName =
    rewardItem?.description ||
    rewardItem?.title ||
    rewardItem?.serviceLabel ||
    rewardItem?.service_label ||
    rewardItem?.name ||
    rewardItemId;
  const typeLabel = rewardType
    ? String(rewardType).toLowerCase() === "product"
      ? "Product"
      : "Service"
    : "Reward";
  const normalizedRewardType = String(rewardType || typeLabel).toUpperCase();
  const rewardItemType = String(
    rewardItem?.itemType ||
      rewardItem?.type ||
      rewardItem?.metadata?.type ||
      normalizedRewardType,
  ).toUpperCase();
  const pointsLabel =
    redeemPoints !== undefined && redeemPoints !== null && redeemPoints !== ""
      ? ` - ${redeemPoints} pts`
      : "";

  return {
    label: `${itemName || typeLabel} (${typeLabel})${pointsLabel}`,
    rewardId,
    rewardType: normalizedRewardType,
    rewardItemId,
    redeemPoints,
    rewardAppliedToService:
      normalizedRewardType === "SERVICE" && rewardItemType !== "PRODUCT",
  };
};

const extractInvoiceLoyaltyRewardDetailsList = (invoice: any) => {
  const map = new Map<string, any>();

  const addReward = (source: any, fallbackItem?: any) => {
    if (!source) return;
    const rewardId =
      source?.loyaltyRewardId ??
      source?.loyalty_reward_id ??
      source?.metadata?.loyaltyRewardId ??
      source?.metadata?.loyalty_reward_id;
    const rewardType =
      source?.loyaltyRewardType ??
      source?.loyalty_reward_type ??
      source?.rewardType ??
      source?.reward_type ??
      source?.metadata?.loyaltyRewardType ??
      source?.metadata?.loyalty_reward_type ??
      source?.metadata?.rewardType ??
      source?.metadata?.reward_type;
    const rewardItemId =
      source?.loyaltyRewardItemId ??
      source?.loyalty_reward_item_id ??
      source?.rewardItemId ??
      source?.reward_item_id ??
      source?.metadata?.loyaltyRewardItemId ??
      source?.metadata?.loyalty_reward_item_id ??
      source?.metadata?.rewardItemId ??
      source?.metadata?.reward_item_id;
    const redeemPoints =
      source?.loyaltyRewardRedeemPoints ??
      source?.loyalty_reward_redeem_points ??
      source?.redeemPoints ??
      source?.redeem_points ??
      source?.metadata?.loyaltyRewardRedeemPoints ??
      source?.metadata?.loyalty_reward_redeem_points ??
      source?.metadata?.redeemPoints ??
      source?.metadata?.redeem_points;

    if (!rewardId && !rewardType && !rewardItemId && !redeemPoints) return;

    const normalizedType = String(rewardType || "").toUpperCase();
    const key =
      rewardId !== undefined && rewardId !== null && rewardId !== ""
        ? String(rewardId)
        : `${normalizedType}-${rewardItemId || map.size}`;
    if (map.has(key)) return;

    const item = fallbackItem || source;
    const typeLabel =
      normalizedType === "PRODUCT"
        ? "Product"
        : normalizedType === "SERVICE"
          ? "Service"
          : "Reward";
    const itemName =
      item?.description ||
      item?.title ||
      item?.serviceLabel ||
      item?.service_label ||
      item?.name ||
      rewardItemId ||
      typeLabel;
    const pointsLabel =
      redeemPoints !== undefined && redeemPoints !== null && redeemPoints !== ""
        ? ` - ${redeemPoints} pts`
        : "";

    map.set(key, {
      label: `${itemName} (${typeLabel})${pointsLabel}`,
      rewardId,
      rewardType: normalizedType || typeLabel.toUpperCase(),
      rewardItemId,
      redeemPoints,
      rewardAppliedToService: normalizedType === "SERVICE",
    });
  };

  getInvoiceItemSources(invoice).forEach((item) => {
    addReward(item, item);
    addReward(item?.metadata, item);
  });

  if (map.size === 0) {
    [
      ...getInvoiceObjectSources(invoice),
      ...getInvoiceMetadataSources(invoice),
    ].forEach((source) => addReward(source));
  }

  return Array.from(map.values());
};

const extractInvoiceLoyaltyRewardInfo = (invoice: any) => {
  return extractInvoiceLoyaltyRewardDetails(invoice)?.label ?? null;
};

const firstBooleanCandidate = (values: any[]) => {
  for (const candidate of values) {
    if (candidate === undefined || candidate === null || candidate === "") {
      continue;
    }
    return parseBooleanSetting(candidate, true);
  }

  return null;
};

const extractInvoiceLoyaltyOnFullInvoice = (invoice: any): boolean | null => {
  const rootKeys = [
    "loyaltyOnFullInvoice",
    "loyalty_on_full_invoice",
    "onFullInvoice",
    "on_full_invoice",
  ];
  const itemKeys = ["loyaltyOnFullInvoice", "loyalty_on_full_invoice"];

  return firstBooleanCandidate([
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), rootKeys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), rootKeys),
    ...collectValuesFromSources(
      getInvoiceServiceSources(invoice),
      itemKeys,
      true,
    ),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), itemKeys, true),
  ]);
};

const extractInvoiceLoyaltyEnabledAtCreation = (
  invoice: any,
): boolean | null => {
  const keys = ["loyaltyEnabledAtCreation", "loyalty_enabled_at_creation"];

  return firstBooleanCandidate([
    ...collectValuesFromSources(getInvoiceObjectSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceMetadataSources(invoice), keys),
    ...collectValuesFromSources(getInvoiceServiceSources(invoice), keys, true),
    ...collectValuesFromSources(getInvoiceItemSources(invoice), keys, true),
  ]);
};

const calculatePromoDiscountPercentForItem = (
  item: any,
  promoType: any,
  promoValue: any,
) => {
  const normalizedPromoType = String(promoType || "")
    .trim()
    .toUpperCase();
  const value = Number(promoValue);
  if (!Number.isFinite(value) || value <= 0) return 0;

  if (
    normalizedPromoType === "PERCENT" ||
    normalizedPromoType === "PERCENTAGE" ||
    normalizedPromoType === "DISCOUNT"
  ) {
    return Math.min(100, Math.max(0, value));
  }

  if (normalizedPromoType === "FLAT" || normalizedPromoType === "FIXED") {
    const quantity = Number(item?.quantity ?? item?.qty ?? 1) || 0;
    const unitCost =
      Number(
        item?.unitCost ??
          item?.unitPrice ??
          item?.unit_price ??
          item?.amount ??
          item?.price,
      ) || 0;
    const subtotal = quantity * unitCost;
    if (subtotal <= 0) return 0;

    return Math.min(
      100,
      Math.max(0, Math.round((value / subtotal) * 100 * 100) / 100),
    );
  }

  return 0;
};

const calculateCombinedServiceDiscount = ({
  item,
  promoType,
  promoValue,
  loyaltyDiscountPercent,
  onFullInvoice,
}: {
  item: any;
  promoType: any;
  promoValue: any;
  loyaltyDiscountPercent: number;
  onFullInvoice: boolean;
}) => {
  const promoDiscountPercent = calculatePromoDiscountPercentForItem(
    item,
    promoType,
    promoValue,
  );
  const maxLoyaltyDiscountPercent = Math.max(0, 100 - promoDiscountPercent);
  const nextLoyaltyDiscountPercent = onFullInvoice
    ? 0
    : Math.min(
        maxLoyaltyDiscountPercent,
        Math.max(0, Number(loyaltyDiscountPercent) || 0),
      );
  const currentDiscountPercent = Math.max(
    0,
    Number(item?.discountPercent ?? item?.discount) || 0,
  );
  const storedLoyaltyDiscountPercent = Math.max(
    0,
    Number(item?.loyaltyDiscountPercent) || 0,
  );
  const previousLoyaltyDiscountPercent =
    storedLoyaltyDiscountPercent > 0
      ? storedLoyaltyDiscountPercent
      : !onFullInvoice && nextLoyaltyDiscountPercent > 0
        ? Math.min(
            nextLoyaltyDiscountPercent,
            Math.max(0, currentDiscountPercent - promoDiscountPercent),
          )
        : 0;
  const baseDiscountPercent = Math.max(
    promoDiscountPercent,
    currentDiscountPercent - previousLoyaltyDiscountPercent,
  );
  const discountPercent = Math.min(
    100,
    baseDiscountPercent + nextLoyaltyDiscountPercent,
  );
  const quantity = Number(item?.quantity ?? item?.qty) || 0;
  const unitCost =
    Number(
      item?.unitCost ?? item?.amount ?? item?.unitPrice ?? item?.unit_price,
    ) || 0;
  const subtotal = quantity * unitCost;
  const discountAmount =
    Math.round(((subtotal * discountPercent) / 100) * 100) / 100;

  return {
    discountPercent,
    discountAmount,
    loyaltyDiscountPercent: nextLoyaltyDiscountPercent,
    promoDiscountPercent,
    totalPrice: Math.max(
      0,
      Math.round((subtotal - discountAmount) * 100) / 100,
    ),
  };
};

export const LoyaltyInvoicePreview = ({
  value,
  values,
  clients,
  onDiscountChange,
}: {
  value: any;
  values: Record<string, any>;
  clients: Client[];
  onDiscountChange?: (value: number) => void;
}) => {
  const [preview, setPreview] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [previewError, setPreviewError] = React.useState("");
  const [pointShares, setPointShares] = React.useState<any[]>([]);
  const [savedEarnedPoints, setSavedEarnedPoints] = React.useState<
    number | null
  >(null);
  const [savedEarnedPointsLoading, setSavedEarnedPointsLoading] =
    React.useState(false);
  const [onFullInvoice, setOnFullInvoice] = React.useState(true);
  const [loyaltyEnabled, setLoyaltyEnabled] = React.useState<boolean | null>(
    null,
  );
  const [sharePointsEnabled, setSharePointsEnabled] = React.useState<
    boolean | null
  >(null);
  const [loyaltySettings, setLoyaltySettings] =
    React.useState<LoyaltySettingsResponse | null>(null);
  const [redeemDiscountType, setRedeemDiscountType] = React.useState<
    "PERCENT" | "FIXED"
  >("PERCENT");
  const [redeemSettingPoints, setRedeemSettingPoints] = React.useState(0);
  const [redeemSettingDiscount, setRedeemSettingDiscount] = React.useState(0);
  const lastFixedRedeemDiscountBaseRef = React.useRef(0);
  const clientId = String(values?.clientName || "");
  const invoiceId = String(
    values?.invoice_id ||
      values?.invoiceId ||
      values?.id ||
      values?.invoice?.id ||
      values?.sourceInvoice?.invoice?.id ||
      values?.sourceInvoice?.id ||
      "",
  );
  const redeemPoints = Math.max(0, Math.floor(Number(value) || 0));
  const balance = Number(preview?.balance ?? 0);
  const invoiceStatuses = [
    values?.status,
    values?.invoiceStatus,
    values?.invoice_status,
    values?.paymentStatus,
    values?.payment_status,
  ].map((status) => String(status || "").toUpperCase());
  const isPaid = invoiceStatuses.includes("PAID");
  // isPaid follows the live dropdown, so it flips the moment staff pick Paid —
  // before anything is saved. Reading stored shares/transactions off that shows
  // settled numbers for a settlement that hasn't happened, so anything sourcing
  // saved data keys off the persisted invoice instead.
  const savedInvoiceIsPaid = [
    values?.sourceInvoice?.status,
    values?.sourceInvoice?.invoiceStatus,
    values?.sourceInvoice?.invoice_status,
    values?.sourceInvoice?.paymentStatus,
    values?.sourceInvoice?.payment_status,
    values?.sourceInvoice?.invoice?.status,
  ]
    .map((status) => String(status || "").toUpperCase())
    .includes("PAID");
  const isRefunded = invoiceStatuses.some(
    (status) => status.includes("REFUNDED") || status === "CANCELLED",
  );
  const savedRedeemDiscountPercent = Number(
    (Array.isArray(values?.items)
      ? values.items.find((item: any) => item?.itemType !== "PRODUCT")
      : null)?.loyaltyDiscountPercent ?? 0,
  );
  const savedInvoiceLoyaltyDiscountPercent =
    extractInvoiceLoyaltyDiscountPercent(values);
  const savedLoyaltyDiscountPercent =
    savedInvoiceLoyaltyDiscountPercent || savedRedeemDiscountPercent;
  const savedRedeemPoints = extractInvoiceLoyaltyRedeemPoints(values);
  const manualRedeemRemoved = values?.loyaltyRedeemRemoved === true;
  const effectiveSavedRedeemPoints = manualRedeemRemoved
    ? 0
    : savedRedeemPoints;
  const effectiveSavedLoyaltyDiscountPercent = manualRedeemRemoved
    ? 0
    : savedLoyaltyDiscountPercent;
  const hasRedeemUsage = redeemPoints > 0 || effectiveSavedRedeemPoints > 0;
  const savedLoyaltyEnabledAtCreation =
    extractInvoiceLoyaltyEnabledAtCreation(values);
  const hasSavedLoyaltyData =
    savedLoyaltyEnabledAtCreation === true ||
    redeemPoints > 0 ||
    effectiveSavedLoyaltyDiscountPercent > 0 ||
    effectiveSavedRedeemPoints > 0 ||
    extractInvoiceSharedClientIds(values).length > 0 ||
    extractInvoiceLoyaltyOnFullInvoice(values) !== null;
  const shouldShowLoyalty = loyaltyEnabled === true;
  const allowPreviewWhenDisabled =
    shouldShowLoyalty && savedLoyaltyEnabledAtCreation !== false;
  const savedOnFullInvoice = extractInvoiceLoyaltyOnFullInvoice(values);
  const effectiveOnFullInvoice = savedOnFullInvoice ?? onFullInvoice;
  const amount = calculateInvoiceAmountForLoyaltyPreview(values, {
    serviceOnly: !effectiveOnFullInvoice,
  });
  const sourceInvoiceAmount = calculateInvoiceAmountForLoyaltyPreview(
    values?.sourceInvoice || {},
    { serviceOnly: !effectiveOnFullInvoice },
  );
  const currentItemsSignature = buildInvoiceItemsSignatureForLoyaltyPreview(
    values,
    { serviceOnly: !effectiveOnFullInvoice },
  );
  const sourceItemsSignature = buildInvoiceItemsSignatureForLoyaltyPreview(
    values?.sourceInvoice || {},
    { serviceOnly: !effectiveOnFullInvoice },
  );
  const currentEarnPreviewPoints =
    amount > 0 ? Math.max(1, Math.floor(amount)) : 0;
  const currentAmountMatchesSavedInvoice =
    sourceInvoiceAmount > 0 && Math.abs(amount - sourceInvoiceAmount) < 0.01;
  const currentItemsMatchSavedInvoice =
    sourceItemsSignature !== "[]" &&
    currentItemsSignature === sourceItemsSignature;
  const previewDiscountType = String(preview?.discountType || "").toUpperCase();
  const savedRedeemDiscountType = extractInvoiceLoyaltyRedeemDiscountType(values);
  const savedRedeemDiscountAmount =
    extractInvoiceLoyaltyRedeemDiscountAmount(values);
  const currentFullInvoiceDiscountPercent = Math.min(
    100,
    Math.max(0, Number(values?.discount) || 0),
  );
  const effectiveRedeemDiscountType =
    savedRedeemDiscountType ||
    (previewDiscountType === "FIXED" || redeemDiscountType === "FIXED"
      ? "FIXED"
      : "PERCENT");
  const previewDiscountPercent = Math.max(
    0,
    Number(
      preview?.discountPercent ?? preview?.redeemRuleDiscountPercent ?? 0,
    ) || 0,
  );
  const displayRedeemPoints = redeemPoints > 0
    ? redeemPoints
    : effectiveSavedRedeemPoints;
  const rawRedeemDiscountPercent =
    effectiveSavedLoyaltyDiscountPercent > 0 &&
    displayRedeemPoints === effectiveSavedRedeemPoints
      ? effectiveSavedLoyaltyDiscountPercent
      : loyaltyEnabled === null || loading
        ? 0
        : previewDiscountPercent;
  const redeemDiscountPercent = rawRedeemDiscountPercent;
  const selectedRewardDetails = extractInvoiceLoyaltyRewardDetails(values);
  const serviceRewardSelected =
    selectedRewardDetails?.rewardType === "SERVICE" ||
    selectedRewardDetails?.rewardAppliedToService === true;
  const isLoyaltyRewardInvoiceItem = (item: any) => {
    const itemType = String(item?.itemType || item?.item_type || "").toUpperCase();
    const isProduct = itemType === "PRODUCT";
    const itemServiceId = String(
      item?.serviceId ??
        item?.service_id ??
        item?.metadata?.serviceId ??
        item?.metadata?.service_id ??
        item?.service ??
        "",
    );
    const rewardItemId = String(selectedRewardDetails?.rewardItemId || "");
    const discountPercent = Math.max(
      Number(item?.discount) || 0,
      Number(item?.discountPercent) || 0,
      Number(item?.loyaltyDiscountPercent) || 0,
    );
    const serviceRewardFreeLine =
      serviceRewardSelected &&
      !isProduct &&
      (discountPercent >= 100 ||
        (rewardItemId && itemServiceId && rewardItemId === itemServiceId));

    return Boolean(
      item?.loyaltyRewardId ||
        item?.loyalty_reward_id ||
        item?.rewardType ||
        item?.reward_type ||
        item?.metadata?.loyaltyRewardId ||
        item?.metadata?.loyalty_reward_id ||
        item?.metadata?.rewardType ||
        item?.metadata?.reward_type ||
        serviceRewardFreeLine,
    );
  };
  const fixedRedeemDiscountBase = (() => {
    const itemSource = getInvoiceObjectSources(values).find(
      (source: any) => Array.isArray(source?.items) && source.items.length > 0,
    );
    const items = Array.isArray(itemSource?.items) ? itemSource.items : [];
    const applicableItems = items.filter(
      (item: any) => !isLoyaltyRewardInvoiceItem(item),
    );

    if (items.length === 0) return lastFixedRedeemDiscountBaseRef.current || 0;

    if (effectiveOnFullInvoice) {
      const itemTotal = applicableItems.reduce((sum: number, item: any) => {
        const total = calculateLiveInvoiceItemTotal(item);
        return sum + (Number.isFinite(total) ? total : 0);
      }, 0);
      return itemTotal > 0
        ? Math.round(itemTotal * 100) / 100
        : lastFixedRedeemDiscountBaseRef.current || 0;
    }

    const serviceItemTotal = applicableItems
      .filter((item: any) => String(item?.itemType || "").toUpperCase() !== "PRODUCT")
      .reduce((sum: number, item: any) => {
        const total =
          effectiveRedeemDiscountType === "FIXED"
            ? calculateOriginalInvoiceItemSubtotal(item)
            : calculateLiveInvoiceItemTotal(item);
        return sum + (Number.isFinite(total) ? total : 0);
      }, 0);

    return serviceItemTotal > 0
      ? Math.round(serviceItemTotal * 100) / 100
      : lastFixedRedeemDiscountBaseRef.current || 0;
  })();
  const effectiveFixedRedeemDiscountBase = fixedRedeemDiscountBase;
  if (effectiveFixedRedeemDiscountBase > 0) {
    lastFixedRedeemDiscountBaseRef.current = effectiveFixedRedeemDiscountBase;
  }
  const fixedRedeemCashAmount = Math.min(
    Math.max(0, effectiveFixedRedeemDiscountBase),
    calculateFixedCashbackAmountForPoints(
      displayRedeemPoints,
      redeemSettingPoints,
      savedRedeemDiscountAmount || redeemSettingDiscount,
    ),
  );
  const fixedRedeemDiscountAmount =
    Math.round(fixedRedeemCashAmount * 100) / 100;
  const redeemDiscountLabel =
    effectiveRedeemDiscountType === "FIXED"
      ? `$${fixedRedeemDiscountAmount} off`
      : `${redeemDiscountPercent}% discount`;
  const useFixedFullInvoicePreviewBase =
    effectiveOnFullInvoice &&
    effectiveRedeemDiscountType === "FIXED" &&
    displayRedeemPoints > 0 &&
    effectiveFixedRedeemDiscountBase > 0;
  const loyaltyPreviewAmount = useFixedFullInvoicePreviewBase
    ? effectiveFixedRedeemDiscountBase
    : amount;
  const selectedRewardLabel = selectedRewardDetails?.label ?? null;
  const appointmentId = String(values?.appointment_id || values?.appointmentId || "");
  const sharedClientIds = Array.isArray(values?.sharedClientIds)
    ? values.sharedClientIds.map((id: any) => String(id)).filter(Boolean)
    : [];
  const pointShareIds = pointShares
    .map((share) => String(share?.user_id || share?.userId || "").trim())
    .filter(Boolean);
  const shouldSharePoints = sharePointsEnabled === true;
  const participantIds = Array.from(
    new Set(
      [
        clientId,
        ...(shouldSharePoints ? sharedClientIds : []),
        ...(shouldSharePoints ? pointShareIds : []),
      ].filter(Boolean),
    ),
  );
  const participantIdsKey = participantIds.join(",");
  const savedShareEarnedPoints = pointShares.reduce(
    (sum, share) => sum + Math.max(0, Math.floor(Number(share?.points) || 0)),
    0,
  );
  const shouldUseSavedEarnedPoints =
    savedInvoiceIsPaid &&
    currentAmountMatchesSavedInvoice &&
    currentItemsMatchSavedInvoice;
  const shouldUseSavedPointShares =
    savedInvoiceIsPaid &&
    currentAmountMatchesSavedInvoice &&
    currentItemsMatchSavedInvoice;
  const hasSavedEarnedDisplay =
    shouldUseSavedEarnedPoints &&
    (savedShareEarnedPoints > 0 ||
      (savedEarnedPoints !== null && savedEarnedPoints > 0));
  const displayEarnedPoints =
    shouldUseSavedEarnedPoints && savedShareEarnedPoints > 0
      ? savedShareEarnedPoints
      : shouldUseSavedEarnedPoints &&
          savedEarnedPoints !== null &&
          savedEarnedPoints > 0
        ? savedEarnedPoints
        : currentEarnPreviewPoints;
  const showEarnedPointsLoading =
    (amount <= 0 && !isPaid && loading) ||
    (shouldUseSavedEarnedPoints &&
      savedEarnedPointsLoading &&
      !hasSavedEarnedDisplay &&
      currentEarnPreviewPoints <= 0);
  const pointsToEarn = displayEarnedPoints;
  // The divisor belongs to the invoice's business, not the organization.
  const invoiceBusinessId =
    extractInvoiceBusinessId(values?.sourceInvoice) ||
    extractInvoiceBusinessId(values);
  const { isFixedShare: fixedShareEnabled, shareDivisor } = loyaltySettings
    ? resolveShareSettingForBusiness(loyaltySettings, invoiceBusinessId)
    : {
        isFixedShare: getCachedFixedShareEnabled(invoiceBusinessId),
        shareDivisor: getCachedShareDivisor(invoiceBusinessId),
      };
  // splitBetween is how many ways the API divided the quote, correct whether or
  // not the business fixed-divides. Saved invoices keep their stored shares, so
  // only estimate this way while unpaid.
  const previewSplitBetween = Number(preview?.splitBetween);
  const estimatedSplitBetween =
    Number.isInteger(previewSplitBetween) && previewSplitBetween > 0
      ? previewSplitBetween
      : fixedShareEnabled && shareDivisor
        ? shareDivisor
        : participantIds.length;
  const useSplitEstimate =
    shouldSharePoints &&
    estimatedSplitBetween > 1 &&
    !shouldUseSavedPointShares;
  // Local last-resort estimate only — the API now returns a computed share
  // per participantId we send (previewSharesByUserId), which takes priority
  // whenever it's present.
  const pointsEachClientEarns =
    shouldSharePoints && estimatedSplitBetween > 1
      ? Math.floor(pointsToEarn / estimatedSplitBetween)
      : pointsToEarn;
  const previewSharesByUserId = new Map<string, number>();
  (Array.isArray(preview?.shares) ? preview.shares : []).forEach((share: any) => {
    const id = String(share?.userId ?? share?.user_id ?? "").trim();
    const points = Number(share?.points);
    if (id && Number.isFinite(points)) {
      previewSharesByUserId.set(id, Math.max(0, Math.floor(points)));
    }
  });
  // What the booking is worth in full, before anyone's slot goes unclaimed.
  const previewTotalBeforeShare = Math.max(
    0,
    Number(preview?.totalPointsBeforeShare ?? 0),
  );
  const maxEarnablePoints =
    previewTotalBeforeShare > 0
      ? previewTotalBeforeShare
      : currentEarnPreviewPoints;
  // What the players actually in the booking bank between them. With a fixed
  // divisor the two differ whenever the roster is short — a 4-way split with
  // 2 players in awards half the pool and leaves the rest unclaimed.
  const previewAwardedPoints =
    previewSharesByUserId.size > 0
      ? Array.from(previewSharesByUserId.values()).reduce(
          (sum, points) => sum + points,
          0,
        )
      : Math.min(
          maxEarnablePoints,
          pointsEachClientEarns * Math.max(1, participantIds.length),
        );
  const displayHeadlineEarnedPoints = shouldUseSavedEarnedPoints
    ? displayEarnedPoints
    : previewAwardedPoints;
  // Only a genuinely unclaimed slot is worth showing as a shortfall. With
  // fixed dividing off the pool always splits across whoever is in, so the
  // few points a floor() leaves behind there are rounding, not an unclaimed
  // share, and must not read as one.
  const unclaimedSlotCount = Math.max(
    0,
    estimatedSplitBetween -
      Math.max(participantIds.length, previewSharesByUserId.size),
  );
  const showEarnedPointsOutOfMax =
    !shouldUseSavedEarnedPoints &&
    unclaimedSlotCount > 0 &&
    maxEarnablePoints > displayHeadlineEarnedPoints;
  const participantEarnings = participantIds.map((participantId) => {
    const client = Array.isArray(clients)
      ? clients.find(
          (item) => String(item.id || item._id || "") === participantId,
        )
      : null;
    const storedShare = pointShares.find(
      (share) =>
        String(share?.user_id || share?.userId || "") === participantId,
    );
    const storedPoints = Number(storedShare?.points);
    const previewPoints = previewSharesByUserId.get(participantId);
    return {
      id: participantId,
      name: client ? formatClientLabel(client) : participantId,
      email: String((client as any)?.email || (client as any)?.user?.email || "").trim(),
      points:
        shouldUseSavedPointShares &&
        Number.isFinite(storedPoints) &&
        storedPoints > 0
          ? Math.floor(storedPoints)
          : previewPoints !== undefined
            ? previewPoints
            : pointsEachClientEarns,
    };
  });

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();
    if (!organizationId) {
      setLoyaltyEnabled(false);
      return;
    }

    let cancelled = false;

    getLoyaltySettings(organizationId)
      .then((settings) => {
        if (cancelled) return;
        const value: any = settings?.is_loyalty ?? settings?.isLoyalty;
        const onFullInvoiceValue: any =
          settings?.on_full_invoice ?? settings?.onFullInvoice;
        const sharePointsValue: any =
          settings?.is_share ??
          settings?.isShare ??
          (settings as any)?.sharePoints;
        const discountType = normalizeRedeemDiscountType(
          settings?.redeem?.type,
          (settings?.redeem as any)?.discountType,
          (settings?.redeem as any)?.discount_type,
          settings?.type,
          (settings as any)?.discountType,
          (settings as any)?.discount_type,
          (settings as any)?.redeemDiscountType,
          (settings as any)?.redeem_discount_type,
        );
        const settingsRedeemDiscount = Number(
          settings?.redeem?.discount ??
            settings?.redeem_discount ??
            settings?.redeemDiscount,
        );
        const settingsRedeemPoints = Number(
          settings?.redeem?.points ??
            settings?.redeem_points ??
            settings?.redeemPoints,
        );
        setLoyaltyEnabled(
          parseBooleanSetting(value, false),
        );
        setSharePointsEnabled(parseBooleanSetting(sharePointsValue, false));
        setLoyaltySettings(settings);
        setOnFullInvoice(parseBooleanSetting(onFullInvoiceValue, true));
        setRedeemDiscountType(discountType);
        setRedeemSettingPoints(
          Number.isFinite(settingsRedeemPoints) && settingsRedeemPoints > 0
            ? settingsRedeemPoints
            : 0,
        );
        setRedeemSettingDiscount(
          Number.isFinite(settingsRedeemDiscount) && settingsRedeemDiscount > 0
            ? settingsRedeemDiscount
            : 0,
        );
      })
      .catch(() => {
        if (!cancelled) {
          setLoyaltyEnabled(false);
          setSharePointsEnabled(false);
          setRedeemDiscountType("PERCENT");
          setRedeemSettingPoints(0);
          setRedeemSettingDiscount(0);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();

    if (
      !shouldShowLoyalty ||
      !organizationId ||
      !clientId
    ) {
      setPreview(null);
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      try {
        setLoading(true);
        const response = await getLoyaltyPreview({
          organizationId,
          userId: clientId,
          amount: loyaltyPreviewAmount,
          redeemPoints,
          allowWhenDisabled: allowPreviewWhenDisabled,
          businessId: invoiceBusinessId,
          participants: Math.max(1, participantIds.length),
          participantIds,
        });
        if (!cancelled) {
          setPreviewError("");
          setPreview(response);
          const responseDiscountType = normalizeRedeemDiscountType(
            response?.discountType,
            (response as any)?.discountType,
            (response as any)?.discount_type,
            (response as any)?.redeemDiscountType,
            (response as any)?.redeem_discount_type,
          );
          const isFixedRedeemDiscount =
            responseDiscountType === "FIXED" || redeemDiscountType === "FIXED";
          const appliedRedeemPoints =
            effectiveOnFullInvoice && isFixedRedeemDiscount
              ? displayRedeemPoints
              : Math.max(
                  0,
                  Math.floor(
                    Number(response?.pointsToRedeem ?? displayRedeemPoints) || 0,
                  ),
                );
          const fixedCashback = Math.min(
            Math.max(0, effectiveFixedRedeemDiscountBase),
            calculateFixedCashbackAmountForPoints(
              appliedRedeemPoints,
              redeemSettingPoints,
              savedRedeemDiscountAmount || redeemSettingDiscount,
            ),
          );

          if (
            effectiveOnFullInvoice &&
            isFixedRedeemDiscount &&
            displayRedeemPoints > 0 &&
            effectiveFixedRedeemDiscountBase > 0
          ) {
            const nextDiscountPercent = calculateFixedCashbackDiscountPercent(
              fixedCashback,
              effectiveFixedRedeemDiscountBase,
            );

            if (
              Math.abs(
                currentFullInvoiceDiscountPercent - nextDiscountPercent,
              ) > 0.01
            ) {
              onDiscountChange?.(nextDiscountPercent);
            }
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          setPreviewError(
            error?.response?.data?.message ||
              error?.message ||
              "Failed to load loyalty preview.",
          );
          setPreview(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [
    clientId,
    hasSavedLoyaltyData,
    invoiceBusinessId,
    loyaltyEnabled,
    loyaltyPreviewAmount,
    onFullInvoice,
    participantIdsKey,
    redeemPoints,
    allowPreviewWhenDisabled,
    effectiveOnFullInvoice,
    effectiveFixedRedeemDiscountBase,
    fixedRedeemDiscountBase,
    hasRedeemUsage,
    savedLoyaltyEnabledAtCreation,
    redeemDiscountType,
    shouldShowLoyalty,
    savedRedeemDiscountAmount,
    redeemSettingPoints,
    redeemSettingDiscount,
  ]);

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();

    if (
      !shouldShowLoyalty ||
      !organizationId ||
      !appointmentId
    ) {
      setPointShares([]);
      return;
    }

    let cancelled = false;

    const loadPointShares = async () => {
      try {
        const response = await getAppointmentPointShares(
          organizationId,
          appointmentId,
        );
        if (!cancelled) {
          setPointShares(Array.isArray(response) ? response : []);
        }
      } catch {
        if (!cancelled) setPointShares([]);
      }
    };

    loadPointShares();

    return () => {
      cancelled = true;
    };
  }, [appointmentId, hasSavedLoyaltyData, loyaltyEnabled, shouldShowLoyalty]);

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();

    if (
      !savedInvoiceIsPaid ||
      !shouldShowLoyalty ||
      !organizationId ||
      !clientId ||
      !invoiceId
    ) {
      setSavedEarnedPoints(null);
      setSavedEarnedPointsLoading(false);
      return;
    }

    let cancelled = false;

    const loadSavedEarnedPoints = async () => {
      try {
        setSavedEarnedPointsLoading(true);
        const paymentIds = new Set<string>([`invoice-${invoiceId}`]);
        const numericInvoiceId = Number(invoiceId);
        getInvoiceObjectSources(values).forEach((source: any) => {
          [
            source?.paymentId,
            source?.payment_id,
            source?.payment?.id,
            source?.payment?.paymentId,
            source?.payment?.payment_id,
            source?.latestPayment?.id,
            source?.latestPayment?.paymentId,
            source?.latestPayment?.payment_id,
          ].forEach((paymentId) => {
            if (paymentId !== undefined && paymentId !== null) {
              paymentIds.add(String(paymentId));
            }
          });
        });

        if (Number.isFinite(numericInvoiceId) && numericInvoiceId > 0) {
          try {
            const payments = await getPaymentsByInvoice(numericInvoiceId);
            (Array.isArray(payments) ? payments : []).forEach((payment: any) => {
              [
                payment?.id,
                payment?.paymentId,
                payment?.payment_id,
                payment?.providerTransactionId,
                payment?.provider_transaction_id,
              ].forEach((paymentId) => {
                if (paymentId !== undefined && paymentId !== null) {
                  paymentIds.add(String(paymentId));
                }
              });
            });
          } catch {
            // Fall back to invoice-{id}; some invoice payloads may not expose payments.
          }
        }

        const transactions = await getLoyaltyTransactions(
          organizationId,
          clientId,
        );
        if (cancelled) return;

        const earnedPoints = (Array.isArray(transactions) ? transactions : [])
          .filter((transaction: any) => {
            const transactionPaymentId = String(
              transaction?.payment_id ?? transaction?.paymentId ?? "",
            );
            const transactionType = String(transaction?.type || "").toUpperCase();
            return (
              paymentIds.has(transactionPaymentId) &&
              (transactionType === "EARN" || transactionType === "SHARE_EARN")
            );
          })
          .reduce(
            (sum: number, transaction: any) =>
              sum + Math.max(0, Number(transaction?.points) || 0),
            0,
          );

        setSavedEarnedPoints(Math.floor(earnedPoints));
      } catch {
        if (!cancelled) setSavedEarnedPoints(null);
      } finally {
        if (!cancelled) setSavedEarnedPointsLoading(false);
      }
    };

    loadSavedEarnedPoints();

    return () => {
      cancelled = true;
    };
  }, [clientId, invoiceId, savedInvoiceIsPaid, shouldShowLoyalty]);

  if (!shouldShowLoyalty || !clientId) {
    return null;
  }

  const loyaltyPreviewPending =
    loyaltyEnabled === null || loading || (!preview && !previewError);

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        p: 1.5,
      }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(3, 1fr)",
          },
          gap: 1.5,
          alignItems: "end",
        }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Balance
          </Typography>
          <Typography fontWeight={600}>
            {loyaltyPreviewPending ? "..." : balance} pts
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {isPaid ? "Earned" : "Earn"}
            {showEarnedPointsOutOfMax ? " / max" : ""}
          </Typography>
          <Typography
            fontWeight={600}
            color={isPaid ? "success.main" : "text.primary"}>
            {loyaltyPreviewPending || showEarnedPointsLoading
              ? "..."
              : showEarnedPointsOutOfMax
                ? `${displayHeadlineEarnedPoints} / ${maxEarnablePoints}`
                : `${isPaid ? "+" : ""}${displayHeadlineEarnedPoints}`} pts
          </Typography>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary">
            {isRefunded ? "Redeem points" : "Redeemed points"}
          </Typography>
          <Typography
            fontWeight={600}
            color="text.primary"
            noWrap>
            <Box
              component="span"
              sx={{
                color:
                  displayRedeemPoints > 0 && !isRefunded
                    ? "error.main"
                    : "text.primary",
              }}>
              {displayRedeemPoints > 0 && !isRefunded ? "-" : ""}{displayRedeemPoints} pts
            </Box>
            {" / "}
            {loyaltyPreviewPending ? "..." : redeemDiscountLabel} (
            {effectiveOnFullInvoice ? "Full invoice" : "Service"})
          </Typography>
        </Box>
      </Box>
      {previewError ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 1 }}>
          {previewError}
        </Typography>
      ) : null}
      {shouldSharePoints && participantEarnings.length > 1 && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            width: "100%",
          }}>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mb: 0.75 }}>
            Client earnings
            {useSplitEstimate
              ? ` — ${maxEarnablePoints} split ${estimatedSplitBetween} ways`
              : ""}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(auto-fit, minmax(150px, 1fr))",
              },
              gap: 0.75,
            }}>
            {participantEarnings.map((participant) => (
              <Box
                key={participant.id}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                  minWidth: 0,
                }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                  noWrap>
                  {participant.name}
                </Typography>
                {participant.email ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                    noWrap>
                    {participant.email}
                  </Typography>
                ) : null}
                <Typography
                  fontWeight={600}
                  color={isPaid ? "success.main" : "text.primary"}
                  sx={{ display: "block", fontSize: 13 }}
                  noWrap>
                  {loyaltyPreviewPending
                    ? "..."
                    : `${isPaid ? "+" : ""}${participant.points}`} pts
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const InvoiceRewardPackageField = ({
  onChange,
  helpers,
  invoiceEditingRow,
  loyaltyRewards,
  products,
  services,
  rewardServices,
  stockItems,
  venueId,
  loadingLoyaltyRewards,
  rewardEditingLocked,
}: {
  value?: any;
  onChange: (value: string) => void;
  helpers: any;
  invoiceEditingRow: any;
  loyaltyRewards: LoyaltyReward[];
  products: any;
  services: any[];
  rewardServices: any[];
  stockItems: any[];
  venueId?: string | null;
  loadingLoyaltyRewards?: boolean;
  rewardEditingLocked?: boolean;
}) => {
  const [serviceRewardConflict, setServiceRewardConflict] = React.useState<{
    productRewardIds: string[];
    serviceRewardId: string;
    hasPromoDiscount: boolean;
    hasServiceRedeemDiscount: boolean;
  } | null>(null);
  const [rewardLoyaltyEnabled, setRewardLoyaltyEnabled] = React.useState<
    boolean | null
  >(null);
  const existingRewards = extractInvoiceLoyaltyRewardDetailsList(
    invoiceEditingRow,
  );
  const existingReward = existingRewards[0] || null;
  const hasSavedServiceReward = Boolean(
    existingRewards.some(
      (reward) =>
        reward?.rewardId &&
        String(reward?.rewardType || "").toUpperCase() === "SERVICE",
    ),
  );
  const currentItemsForOptions: any[] = Array.isArray(helpers?.values?.items)
    ? (helpers?.values?.items as any[])
    : [];
  const allKnownServices = [
    ...(Array.isArray(services) ? services : []),
    ...(Array.isArray(rewardServices) ? rewardServices : []),
  ];
  const productList = Array.isArray(products)
    ? products
    : Array.isArray((products as any)?.data)
      ? (products as any).data
      : [];
  const getServiceId = (service: any) =>
    String(service?.id || service?._id || service?.service_id || "");
  const findKnownServiceById = (serviceId: string) =>
    allKnownServices.find(
      (service: any) => getServiceId(service) === serviceId,
    ) || null;
  const getRewardById = (rewardId: any) =>
    loyaltyRewards.find(
      (item: any) => String(item.id) === String(rewardId || ""),
    );
  const getRewardItemLabel = (reward: any) => {
    const rewardType = getRewardTypeValue(reward);
    const rewardItemId = getRewardItemIdValue(reward);
    if (rewardType === "SERVICE") {
      const service = findKnownServiceById(rewardItemId);
      return (
        service?.name ||
        service?.title ||
        service?.service_name ||
        service?.label ||
        reward?.label ||
        rewardItemId
      );
    }
    if (rewardType === "PRODUCT") {
      const product = productList.find(
        (item: any) =>
          String(item?.id || item?._id || item?.product_id || "") ===
          rewardItemId,
      );
      return (
        product?.name ||
        product?.title ||
        product?.product_name ||
        product?.label ||
        reward?.label ||
        rewardItemId
      );
    }
    return reward?.label || rewardItemId;
  };
  const rewardOptions = [
    { label: "None", value: "" },
    ...loyaltyRewards.map((reward) => {
      const rewardType = getRewardTypeValue(reward);
      const redeemPoints = getRewardRedeemPointsValue(reward);
      const quantity = getRewardQuantityValue(reward);
      return {
        label: `${getRewardItemLabel(reward)} (${getRewardTypeLabel(
          rewardType,
        )}) - ${redeemPoints} pts - qty ${quantity}`,
        value: String((reward as any).id),
      };
    }),
  ];
  const productRewardOptions = rewardOptions.filter((option) => {
    if (!option.value) return true;
    const reward = getRewardById(option.value);
    if (!reward) return false;
    return getRewardTypeValue(reward) === "PRODUCT";
  });
  /**
   * A service reward fits a line when the line is at least as long as the
   * reward's own service and the reward is allowed on that service.
   */
  const serviceRewardFitsLine = (reward: any, item: any) => {
    if (getInvoiceLineType(item) === "PRODUCT") return false;
    const lineRewardId =
      getInvoiceLineMetadata(item)?.loyaltyRewardId || item?.loyaltyRewardId;
    // A line already carrying another reward is spoken for.
    if (lineRewardId && String(lineRewardId) !== String(reward?.id)) {
      return false;
    }
    const lineServiceId = getInvoiceLineItemId(item);
    if (!isServiceAllowedForReward(reward, lineServiceId)) return false;

    const rewardMinutes = getRewardFreeMinutes(reward);
    if (rewardMinutes <= 0) return false;
    const service = findKnownServiceById(lineServiceId);
    // Overflow is judged on the line's total minutes: a 90-minute reward does
    // not fit one 60-minute slot but does fit two.
    const lineMinutes =
      getServiceDurationMinutes(service) * (Number(item?.quantity || 1) || 1);
    return lineMinutes >= rewardMinutes;
  };

  const serviceRewardOptions = rewardOptions.filter((option) => {
    if (!option.value) return true;
    const reward = getRewardById(option.value);
    if (!reward) return false;
    if (hasSavedServiceReward) return false;
    if (getRewardTypeValue(reward) !== "SERVICE") return false;
    // Offer it whenever some line on the invoice is long enough and allowed —
    // it no longer has to be the reward's own service.
    return currentItemsForOptions.some((item: any) =>
      serviceRewardFitsLine(reward, item),
    );
  });

  const editInvoiceRewardOptions = [
    ...productRewardOptions,
    ...serviceRewardOptions.filter((option) => option.value),
  ];
  const lockedMessage =
    "Rewards cannot be changed after a payment has been recorded. Refund the payment or mark the invoice unpaid first.";
  const currentProductRewardIds = Array.isArray(
    helpers?.values?.loyaltyProductRewardIds,
  )
    ? (helpers?.values?.loyaltyProductRewardIds as any[]).map(String)
    : currentItemsForOptions
        .filter(
          (item: any) =>
            getInvoiceLineType(item) === "PRODUCT" &&
            (getInvoiceLineMetadata(item)?.loyaltyRewardId ||
              item?.loyaltyRewardId),
        )
        .map((item: any) =>
          String(
            getInvoiceLineMetadata(item)?.loyaltyRewardId ||
              item?.loyaltyRewardId,
          ),
        );
  const currentServiceRewardId =
    hasSavedServiceReward
      ? ""
      : String(helpers?.values?.loyaltyServiceRewardId || "");
  const buildRewardMetadata = (reward: any) => {
    const rewardType = getRewardTypeValue(reward);
    const rewardItemId = getRewardItemIdValue(reward);
    const rewardPoints = getRewardRedeemPointsValue(reward);
    return {
      loyaltyRewardId: Number(reward.id),
      loyaltyRewardType: rewardType,
      loyaltyRewardItemId: rewardItemId,
      loyaltyRewardRedeemPoints: rewardPoints,
      rewardType,
      rewardItemId,
      redeemPoints: rewardPoints,
    };
  };

  const updateRewardMetadata = (rewards: any[]) => {
    const firstReward = rewards[0] || null;
    const rewardIds = rewards
      .map((reward) => Number(reward?.id))
      .filter((id) => Number.isFinite(id) && id > 0);
    helpers?.setFieldValue("loyaltyRewardIds", rewardIds);
    helpers?.setFieldValue(
      "loyaltyRewardId",
      firstReward ? String(firstReward.id) : "",
    );
    helpers?.setFieldValue(
      "loyaltyRewardRedeemPoints",
      rewards.reduce(
        (sum, reward) => sum + getRewardRedeemPointsValue(reward),
        0,
      ),
    );
    helpers?.setFieldValue(
      "loyaltyRewardType",
      firstReward ? getRewardTypeValue(firstReward) : "",
    );
    helpers?.setFieldValue(
      "loyaltyRewardItemId",
      firstReward ? getRewardItemIdValue(firstReward) : "",
    );
    helpers?.setFieldValue(
      "loyaltyRewardQuantity",
      firstReward ? getRewardQuantityValue(firstReward) : 0,
    );
  };

  const applyRewardsToItems = async (
    productRewardIds: string[],
    serviceRewardId: string,
    options: {
      ignorePromoRedeemConflict?: boolean;
    } = {},
  ) => {
    if (rewardEditingLocked) {
      toast.info(lockedMessage);
      return;
    }

    const currentItems: any[] = Array.isArray(helpers?.values?.items)
      ? (helpers?.values?.items as any[])
      : [];
    const selectedProductIds = Array.from(
      new Set(productRewardIds.map(String).filter(Boolean)),
    );
    const selectedProductRewards = selectedProductIds
      .map(getRewardById)
      .filter((reward): reward is LoyaltyReward => Boolean(reward));
    const selectedServiceReward = serviceRewardId
      ? getRewardById(serviceRewardId)
      : null;
    const selectedRewards = [
      ...selectedProductRewards,
      ...(selectedServiceReward ? [selectedServiceReward] : []),
    ];
    const clearRewardItems = (
      items: any[],
      preserveServiceRewards = false,
    ) =>
      items
        .filter((item: any) => {
          const metadata = getInvoiceLineMetadata(item);
          return !(
            (metadata?.loyaltyRewardId || item?.loyaltyRewardId) &&
            getInvoiceLineType(item) === "PRODUCT"
          );
        })
        .map((item: any) => {
          const metadata = getInvoiceLineMetadata(item);
          if (!metadata?.loyaltyRewardId && !item?.loyaltyRewardId) {
            return item;
          }
          if (preserveServiceRewards && getInvoiceLineType(item) !== "PRODUCT") {
            return item;
          }
          const quantity = Number(item.quantity || 1) || 1;
          const unitCost = Number(item.unitCost || 0) || 0;
          const cleanedMetadata = { ...metadata };
          delete cleanedMetadata.loyaltyRewardId;
          delete cleanedMetadata.loyaltyRewardType;
          delete cleanedMetadata.loyaltyRewardItemId;
          delete cleanedMetadata.loyaltyRewardRedeemPoints;
          delete cleanedMetadata.rewardType;
          delete cleanedMetadata.rewardItemId;
          delete cleanedMetadata.redeemPoints;
          return {
            ...item,
            loyaltyRewardId: null,
            loyaltyRewardType: null,
            discount: 0,
            totalPrice: Math.round(quantity * unitCost * 100) / 100,
            metadata: cleanedMetadata,
          };
        });

    if (helpers?.values?.clientName) {
      try {
        const balance = await getClientLoyaltyBalance(
          String(helpers.values.clientName),
        );
        const available = Number((balance as any)?.balance || 0);
        const manualRedeem = Math.max(
          0,
          Math.floor(Number(helpers?.values?.loyaltyRedeemPoints) || 0),
        );
        const rewardPointsTotal = selectedRewards.reduce(
          (sum, reward) => sum + getRewardRedeemPointsValue(reward),
          0,
        );
        if (available < manualRedeem + rewardPointsTotal) {
          toast.error(
            "Client does not have enough loyalty points for this reward.",
          );
          return;
        }
      } catch (error) {
        console.error("Could not validate loyalty balance", error);
      }
    }

    let rewardBaseItems = clearRewardItems(
      currentItems,
      hasSavedServiceReward,
    );
    const newProductItems: any[] = [];
    for (const reward of selectedProductRewards) {
      const rewardItemId = getRewardItemIdValue(reward);
      const rewardQuantity = getRewardQuantityValue(reward);
      const rewardMetadata = buildRewardMetadata(reward);
      const product = productList.find(
        (item: any) =>
          String(item?.id || item?._id || item?.product_id || "") ===
          rewardItemId,
      );
      const businessId =
        invoiceEditingRow?.business?.id ||
        invoiceEditingRow?.invoice?.metadata?.businessId ||
        invoiceEditingRow?.metadata?.businessId ||
        invoiceEditingRow?.businessId ||
        venueId;
      const stockError = getProductRewardStockError(
        reward,
        stockItems,
        businessId ? String(businessId) : null,
      );
      if (stockError) {
        toast.error(stockError);
        return;
      }

      const productName =
        product?.name ||
        product?.title ||
        product?.product_name ||
        getRewardItemLabel(reward);
      const unitCost =
        Number(
          product?.salePrice ??
            product?.sale_price ??
            product?.price ??
            product?.unitPrice ??
            0,
        ) || 0;
      newProductItems.push({
        id: `reward-product-${reward.id}`,
        service: rewardItemId,
        serviceId: rewardItemId,
        serviceLabel: productName,
        quantity: rewardQuantity,
        unitCost,
        discount: 100,
        totalPrice: 0,
        itemType: "PRODUCT",
        loyaltyRewardId: Number(reward.id),
        loyaltyRewardType: "PRODUCT",
        metadata: rewardMetadata,
      });
    }

    const serviceReward =
      selectedServiceReward &&
      getRewardTypeValue(selectedServiceReward) === "SERVICE"
        ? selectedServiceReward
        : null;
    if (serviceReward) {
      // The reward's free time is its own service's duration, so it fits any
      // line at least that long that passes the include/exclude list.
      const serviceIndex = rewardBaseItems.findIndex((item: any) =>
        serviceRewardFitsLine(serviceReward, item),
      );

      if (serviceIndex < 0) {
        toast.error(
          "No service on this invoice can take this reward. It needs a booking at least as long, that the reward is allowed on.",
        );
        return;
      }

      const serviceItem = rewardBaseItems[serviceIndex];
      const serviceMetadata = getInvoiceLineMetadata(serviceItem);
      const lineServiceId = getInvoiceLineItemId(serviceItem);
      const serviceQuantity = Number(serviceItem?.quantity || 1) || 1;
      const serviceUnitCost = Number(serviceItem?.unitCost || 0) || 0;
      const serviceSubtotal = serviceQuantity * serviceUnitCost;
      let resolved: ResolvedServiceReward | null = null;

      try {
        // Never work the percentage out here, and price against this line
        // rather than the catalogue — a repriced line overshoots otherwise.
        resolved = await resolveServiceReward(serviceReward.id, {
          serviceId: lineServiceId,
          price: serviceUnitCost,
          qty: serviceQuantity,
        });
      } catch (error: any) {
        toast.error(getErrorMessage(error, "Could not price this reward."));
        return;
      }

      // discountAmount always covers the whole line, never one unit.
      const rewardDiscountAmount = Math.max(
        0,
        Number(resolved?.discountAmount) || 0,
      );
      const rewardDiscountPercent = Math.min(
        100,
        Math.max(0, Number(resolved?.discountPercent) || 0),
      );
      // Only an all-or-nothing reward still blocks a promo and redeemed
      // points. A 60-minute reward on a 90-minute court leaves 30 minutes to
      // pay for, and those may discount the remainder.
      const coversWholeBooking = rewardDiscountPercent >= 100;

      const existingServiceRewardId =
        serviceMetadata?.loyaltyRewardId || serviceItem?.loyaltyRewardId;
      const hasDifferentServiceReward = Boolean(
        existingServiceRewardId &&
          String(existingServiceRewardId) !== String(serviceReward.id),
      );
      if (hasDifferentServiceReward) {
        toast.error("This service already has a reward.");
        return;
      }

      const hasPromoDiscount =
        Number(helpers?.values?.promoValue || 0) > 0 ||
        Number(helpers?.values?.promoDiscount || 0) > 0;
      const onFullInvoice =
        helpers?.values?.loyaltyOnFullInvoice ??
        invoiceEditingRow?.metadata?.loyaltyOnFullInvoice ??
        invoiceEditingRow?.invoice?.metadata?.loyaltyOnFullInvoice;
      const manualRedeemPoints = Math.max(
        0,
        Math.floor(Number(helpers?.values?.loyaltyRedeemPoints) || 0),
      );
      const hasServiceRedeemDiscount =
        manualRedeemPoints > 0 &&
        parseBooleanSetting(onFullInvoice, true) === false;

      if (
        coversWholeBooking &&
        !options.ignorePromoRedeemConflict &&
        (hasPromoDiscount || hasServiceRedeemDiscount)
      ) {
        setServiceRewardConflict({
          productRewardIds: selectedProductIds,
          serviceRewardId: String(serviceReward.id),
          hasPromoDiscount,
          hasServiceRedeemDiscount,
        });
        toast.error("This reward already covers the whole booking.");
        return;
      }

      // The backend checks a floor, not an equality: the line has to be worth
      // at least the reward and at most itself. So a promo already on the line
      // stacks on top rather than being overwritten.
      const existingTotalPrice = Number(serviceItem?.totalPrice);
      const discountOnLine = Number.isFinite(existingTotalPrice)
        ? Math.max(0, serviceSubtotal - existingTotalPrice)
        : (serviceSubtotal *
            Math.min(100, Math.max(0, Number(serviceItem?.discount) || 0))) /
          100;
      // Clearing the old reward zeroes the line's discount but leaves these
      // two percentages behind, so they are the promo and redeem portions to
      // put back before the reward is added on top.
      //
      // NEVER recombine from discountPercent here. Booking stamps that as the
      // combined total, service reward included, so adding the reward on top
      // of it double-counts. The backend guard is a floor, not an equality —
      // a double-count sits between the floor and the line value and passes
      // silently, giving the client money. This is the only defence.
      const trackedDiscountPercent = Math.min(
        100,
        Math.max(
          0,
          (Number(serviceItem?.promoDiscountPercent) || 0) +
            (Number(serviceItem?.loyaltyDiscountPercent) || 0),
        ),
      );
      const existingDiscountAmount = Math.max(
        discountOnLine,
        (serviceSubtotal * trackedDiscountPercent) / 100,
      );
      const combinedDiscountAmount = Math.min(
        serviceSubtotal,
        Math.round((existingDiscountAmount + rewardDiscountAmount) * 100) / 100,
      );
      const combinedDiscountPercent =
        serviceSubtotal > 0
          ? Math.min(
              100,
              Math.round((combinedDiscountAmount / serviceSubtotal) * 10000) /
                100,
            )
          : 0;

      rewardBaseItems = rewardBaseItems.map((item: any, index: number) =>
        index === serviceIndex
          ? {
              ...item,
              discount: combinedDiscountPercent,
              // The payload sends subtotal - totalPrice as the line discount,
              // so this is what puts the figure on the wire.
              totalPrice:
                Math.round(
                  Math.max(0, serviceSubtotal - combinedDiscountAmount) * 100,
                ) / 100,
              loyaltyRewardId: Number(serviceReward.id),
              loyaltyRewardType: "SERVICE",
              metadata: {
                ...serviceMetadata,
                ...buildRewardMetadata(serviceReward),
                // The reward id must sit on the line it discounts.
                serviceId: lineServiceId,
              },
            }
          : item,
      );
    }

    const allSelectedRewards = [
      ...selectedProductIds.map(getRewardById).filter(Boolean),
      ...(serviceReward ? [serviceReward] : []),
    ];
    helpers?.setFieldValue("items", [...rewardBaseItems, ...newProductItems]);
    helpers?.setFieldValue("loyaltyProductRewardIds", selectedProductIds);
    helpers?.setFieldValue(
      "loyaltyServiceRewardId",
      serviceReward ? String(serviceReward.id) : "",
    );
    updateRewardMetadata(allSelectedRewards);
    setServiceRewardConflict(null);
    onChange(allSelectedRewards[0] ? String(allSelectedRewards[0].id) : "");
  };

  const removePromoAndRedeemForServiceReward = () => {
    if (!serviceRewardConflict) return;

    if (serviceRewardConflict.hasPromoDiscount) {
      helpers?.setFieldValue("promoCode", "");
      helpers?.setFieldValue("promoValue", 0);
      helpers?.setFieldValue("promoDiscount", 0);
      helpers?.setFieldValue("promoType", "");
    }

    if (serviceRewardConflict.hasServiceRedeemDiscount) {
      helpers?.setFieldValue("loyaltyRedeemPoints", 0);
      helpers?.setFieldValue("loyaltyRedeemRemoved", true);
    }

    setTimeout(() => {
      void applyRewardsToItems(
        serviceRewardConflict.productRewardIds,
        serviceRewardConflict.serviceRewardId,
        { ignorePromoRedeemConflict: true },
      );
    }, 0);
  };

  const existingRewardIds = new Set(
    existingRewards
      .map((reward) => String(reward?.rewardId || ""))
      .filter(Boolean),
  );
  const savedRewardsForList = existingRewards
    .map((reward) =>
      getRewardById(reward.rewardId) || {
        id: reward.rewardId,
        reward_type: reward.rewardType,
        reward_item_id: reward.rewardItemId,
        redeem_points: reward.redeemPoints,
        quantity: reward.quantity || 1,
        label: reward.label,
      },
    )
    .filter(Boolean);
  const newRewardsForList = [
    ...currentProductRewardIds.map(getRewardById).filter(Boolean),
    ...(currentServiceRewardId ? [getRewardById(currentServiceRewardId)].filter(Boolean) : []),
  ].filter((reward: any) => {
    const id = String(reward?.id || reward?.rewardId || "");
    return id && !existingRewardIds.has(id);
  });
  const selectedRewardIds = [
    ...currentProductRewardIds,
    ...(currentServiceRewardId ? [currentServiceRewardId] : []),
  ];
  const renderRewardList = (
    rewards: any[],
    label: string,
    removable: boolean,
  ) => (
    <Box
      sx={{
        mt: 1,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        width: { xs: "100%", sm: "375px" },
      }}>
      <CustomLabel
        text={label}
        fontSize={12}
        color={COLORS.typographyColor}
      />
      {rewards.map((reward: any) => {
        const id = String(reward?.id || reward?.rewardId || "");
        const rewardType = getRewardTypeValue(reward);
        return (
          <Box
            key={`${label}-${rewardType}-${id}`}
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              px: 1.25,
              py: 0.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}>
            <CustomLabel
              text={
                reward?.label ||
                `${getRewardItemLabel(reward)} (${
                  rewardType === "PRODUCT" ? "Product" : "Service"
                }) - ${getRewardRedeemPointsValue(
                  reward,
                )} pts - qty ${getRewardQuantityValue(reward)}`
              }
              fontSize={13}
              color={COLORS.typographyColor}
            />
            {!rewardEditingLocked && removable && (
              <IconButton
                size="small"
                onClick={() => {
                  if (rewardType === "PRODUCT") {
                    void applyRewardsToItems(
                      currentProductRewardIds.filter(
                        (rewardId) => rewardId !== id,
                      ),
                      currentServiceRewardId,
                    );
                    return;
                  }
                  void applyRewardsToItems(currentProductRewardIds, "");
                }}>
                <CloseRounded fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();
    if (!organizationId) {
      setRewardLoyaltyEnabled(false);
      return;
    }

    let cancelled = false;

    getLoyaltySettings(organizationId)
      .then((settings) => {
        if (cancelled) return;
        const value: any = settings?.is_loyalty ?? settings?.isLoyalty;
        setRewardLoyaltyEnabled(parseBooleanSetting(value, false));
      })
      .catch(() => {
        if (!cancelled) setRewardLoyaltyEnabled(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (rewardLoyaltyEnabled !== true) {
    return null;
  }

  return (
    <>
      <CustomLabel text="Reward Package" fontWeight={500} fontSize={14} />
      <CustomSelect
        label="Reward Package"
        value={selectedRewardIds}
        options={editInvoiceRewardOptions}
        disabled={rewardEditingLocked || loadingLoyaltyRewards}
        onChange={(nextValue: any) => {
          const rewardIds = Array.isArray(nextValue)
            ? nextValue.map(String).filter(Boolean)
            : [];
          const productIds: string[] = [];
          const serviceIds: string[] = [];

          rewardIds.forEach((rewardId) => {
            const reward = getRewardById(rewardId);
            const rewardType = getRewardTypeValue(reward);
            if (rewardType === "PRODUCT") {
              productIds.push(rewardId);
              return;
            }
            if (rewardType === "SERVICE") {
              serviceIds.push(rewardId);
            }
          });

          if (serviceIds.length > 1) {
            toast.error("Only one service reward can be selected.", {
              toastId: "edit-invoice-one-service-reward",
            });
            return;
          }

          void applyRewardsToItems(productIds, serviceIds[0] || "");
        }}
        multiSelect
        sx={{ width: { xs: "100%", sm: "375px" } }}
        selectedSummary={
          selectedRewardIds.length
            ? `${selectedRewardIds.length} rewards selected`
            : undefined
        }
        placeholder={
          hasSavedServiceReward
            ? "Select product rewards"
            : loadingLoyaltyRewards
              ? "Loading rewards..."
              : "Select rewards"
        }
      />
      {serviceRewardConflict ? (
        <Box
          sx={{
            mt: 1,
            p: 1,
            border: "1px solid #ffcdd2",
            borderRadius: "8px",
            backgroundColor: "#fff8f8",
            width: { xs: "100%", sm: "375px" },
          }}>
          <CustomLabel
            text="This service already has a discount."
            fontSize={12}
            color="#d32f2f"
          />
          <Button
            size="small"
            variant="text"
            sx={{ mt: 0.5, px: 0, textTransform: "none" }}
            onClick={removePromoAndRedeemForServiceReward}>
            Remove promo/redeem and apply reward
          </Button>
        </Box>
      ) : null}
      {savedRewardsForList.length > 0 || newRewardsForList.length > 0 ? (
        <>
          {savedRewardsForList.length > 0
            ? renderRewardList(savedRewardsForList, "Reward added", false)
            : null}
          {newRewardsForList.length > 0
            ? renderRewardList(newRewardsForList, "Rewards to add", true)
            : null}
        </>
      ) : existingReward?.rewardId ? (
        <CustomLabel
          text="This invoice already has a reward applied."
          fontSize={12}
          color={COLORS.typographyColor}
          sx={{ mt: 0.5 }}
        />
      ) : null}
      {rewardEditingLocked && (
        <CustomLabel
          text={lockedMessage}
          fontSize={12}
          color="#d32f2f"
          sx={{ mt: 0.5 }}
        />
      )}
    </>
  );
};

const AppointmentLoyaltyPreview = ({
  value,
  onChange,
  values,
  services,
  rewardServices,
  promos,
  clients,
  onClientSearch,
  sharedClientIds,
  onSharedClientsChange,
  onDiscountChange,
  onFullInvoiceChange,
  rewardOptions,
  loyaltyRewards,
  stockItems,
  rewardBusinessId,
  loadingLoyaltyRewards,
  onRewardChange,
  onRedeemRuleChange,
}: {
  value: any;
  onChange: (value: any) => void;
  values: Record<string, any>;
  services: Service[];
  rewardServices: Service[];
  promos: { id: string; code: string; type?: string; value?: number }[];
  clients: Client[];
  /** Search text typed in the guests picker, to fetch more clients. */
  onClientSearch?: (term: string) => void;
  sharedClientIds: (string | number)[];
  onSharedClientsChange: (value: (string | number)[]) => void;
  onDiscountChange?: (value: any) => void;
  onFullInvoiceChange?: (value: boolean) => void;
  rewardOptions: { label: string; value: string | number }[];
  loyaltyRewards: LoyaltyReward[];
  stockItems: any[];
  rewardBusinessId?: string | null;
  loadingLoyaltyRewards: boolean;
  onRewardChange: (value: string | number) => void;
  onRedeemRuleChange?: (value: {
    type: "PERCENT" | "FIXED";
    discount: number;
  }) => void;
}) => {
  const [preview, setPreview] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [redeemError, setRedeemError] = React.useState("");
  const [loyaltyEnabled, setLoyaltyEnabled] = React.useState<boolean | null>(
    null,
  );
  const [redeemRule, setRedeemRule] = React.useState<{
    points: number;
    discount: number;
    type: "PERCENT" | "FIXED";
  } | null>(null);
  const [sharePointsEnabled, setSharePointsEnabled] = React.useState<
    boolean | null
  >(true);
  const [loyaltySettings, setLoyaltySettings] =
    React.useState<LoyaltySettingsResponse | null>(null);
  const clientId = String(values?.client_id || "");
  const serviceId = String(values?.service_id || "");
  const redeemPoints = Math.max(0, Math.floor(Number(value) || 0));
  const balance = Number(preview?.balance ?? 0);
  const selectedRewardId = String(values?.loyaltyRewardId || "");
  const selectedReward = Array.isArray(loyaltyRewards)
    ? loyaltyRewards.find((reward) => String(reward.id) === selectedRewardId)
    : null;
  const normalizeComparableServiceName = React.useCallback((value: any) => {
    return String(value || "").trim().toLowerCase();
  }, []);
  const normalizeComparableServicePrice = React.useCallback((value: any) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? Math.round(numeric * 100) / 100 : null;
  }, []);
  const getRewardType = React.useCallback((reward: any) => {
    return String(reward?.reward_type ?? reward?.rewardType ?? "").toUpperCase();
  }, []);
  const getRewardItemId = React.useCallback((reward: any) => {
    return String(reward?.reward_item_id ?? reward?.rewardItemId ?? "");
  }, []);
  const getServiceId = React.useCallback((service: any) => {
    return String(service?.id || service?._id || service?.service_id || "");
  }, []);
  const getServiceName = React.useCallback((service: any) => {
    return String(
      service?.name || service?.title || service?.service_name || service?.label || "",
    );
  }, []);
  const getServicePrice = React.useCallback((service: any) => {
    return service?.price ?? service?.cost ?? service?.unit_price;
  }, []);
  const findServiceById = React.useCallback(
    (id: string) => {
      const allServices = [
        ...(Array.isArray(services) ? services : []),
        ...(Array.isArray(rewardServices) ? rewardServices : []),
      ];
      return allServices.find((service: any) => getServiceId(service) === id) || null;
    },
    [getServiceId, rewardServices, services],
  );
  // The business of the court being booked decides the divisor, so resolve
  // against it rather than the organization default.
  const businessId = rewardBusinessId ? String(rewardBusinessId) : "";
  const resolvedShareSetting = React.useMemo(() => {
    if (loyaltySettings) {
      return resolveShareSettingForBusiness(loyaltySettings, businessId);
    }
    return {
      isFixedShare: getCachedFixedShareEnabled(businessId),
      shareDivisor: getCachedShareDivisor(businessId),
    };
  }, [businessId, loyaltySettings]);
  const fixedShareEnabled = resolvedShareSetting.isFixedShare;
  const shareDivisor = resolvedShareSetting.shareDivisor;

  React.useEffect(() => {
    setCachedShareSetting(businessId, fixedShareEnabled, shareDivisor);
  }, [businessId, fixedShareEnabled, shareDivisor]);

  const isRewardForSelectedService = React.useCallback(
    (reward: any) => {
      const rewardServiceId = getRewardItemId(reward);
      if (!serviceId || !rewardServiceId) return false;
      if (rewardServiceId === serviceId) return true;

      const selected = findServiceById(serviceId);
      const rewardService = findServiceById(rewardServiceId);
      if (!selected || !rewardService) return false;

      return (
        normalizeComparableServiceName(getServiceName(selected)) ===
          normalizeComparableServiceName(getServiceName(rewardService)) &&
        normalizeComparableServicePrice(getServicePrice(selected)) ===
          normalizeComparableServicePrice(getServicePrice(rewardService))
      );
    },
    [
      findServiceById,
      getRewardItemId,
      getServiceName,
      getServicePrice,
      normalizeComparableServiceName,
      normalizeComparableServicePrice,
      serviceId,
    ],
  );
  const selectedServiceDurationMinutes = React.useMemo(
    () => getServiceDurationMinutes(findServiceById(serviceId)),
    [findServiceById, serviceId],
  );
  /**
   * A service reward fits the booking when its own service's duration is no
   * longer than the booked service, and the booked service passes the
   * reward's include/exclude list. Offering one that doesn't only earns a 400
   * at confirm time.
   */
  const serviceRewardFitsBooking = React.useCallback(
    (reward: any) => {
      if (selectedServiceDurationMinutes <= 0) return false;
      if (!isServiceAllowedForReward(reward, serviceId)) return false;
      const rewardMinutes = getRewardFreeMinutes(reward);
      return (
        rewardMinutes > 0 && rewardMinutes <= selectedServiceDurationMinutes
      );
    },
    [selectedServiceDurationMinutes, serviceId],
  );
  const visibleRewardOptions = React.useMemo(() => {
    return rewardOptions.filter((option) => {
      if (!option.value) return true;

      const reward = Array.isArray(loyaltyRewards)
        ? loyaltyRewards.find(
            (item) => String(item.id) === String(option.value),
          )
        : null;

      if (!reward) return false;

      const rewardType = getRewardType(reward);
      if (rewardType !== "SERVICE") return true;

      return serviceRewardFitsBooking(reward);
    });
  }, [getRewardType, loyaltyRewards, rewardOptions, serviceRewardFitsBooking]);
  const selectedRewardRedeemPoints = selectedReward
    ? Math.max(
        0,
        Math.floor(
          Number(
            (selectedReward as any).redeem_points ??
              (selectedReward as any).redeemPoints ??
              0,
          ) || 0,
        ),
      )
    : 0;
  const rewardStockError = getProductRewardStockError(
    selectedReward,
    stockItems,
    rewardBusinessId,
  );
  const rewardBalanceError =
    selectedReward && balance < selectedRewardRedeemPoints
      ? "Client does not have enough loyalty points for this reward."
      : "";
  const rewardError = rewardBalanceError || rewardStockError;
  const pointsToRedeem = redeemPoints;
  const previewBalance = Math.max(
    0,
    balance - pointsToRedeem - selectedRewardRedeemPoints,
  );
  // pointsToEarn is what the payer banks, already divided by the API in both
  // modes now that participants is sent — never divide it again here.
  const pointsToEarn = Math.max(0, Number(preview?.pointsToEarn ?? 0));
  const previewIsFixedShare = parseBooleanSetting(
    preview?.isFixedShare,
    fixedShareEnabled,
  );
  // Players per match caps the roster whether or not points are fixed-divided.
  const rosterDivisorValue = Number(preview?.shareDivisor ?? shareDivisor);
  const rosterDivisor =
    Number.isInteger(rosterDivisorValue) && rosterDivisorValue >= 2
      ? rosterDivisorValue
      : null;
  const maxSharedClients = resolveMaxSharedClients(rosterDivisor);
  const participantCount = 1 + sharedClientIds.length;
  // How many ways the API actually divided the quote — right in both modes.
  const splitBetweenValue = Number(preview?.splitBetween);
  const splitBetween =
    Number.isInteger(splitBetweenValue) && splitBetweenValue > 0
      ? splitBetweenValue
      : previewIsFixedShare && rosterDivisor
        ? rosterDivisor
        : participantCount;
  const previewTotalBeforeShareValue = Math.max(
    0,
    Number(preview?.totalPointsBeforeShare ?? 0),
  );
  const totalPointsBeforeShare =
    previewTotalBeforeShareValue > 0
      ? previewTotalBeforeShareValue
      : pointsToEarn * splitBetween;
  const pointsEachClientEarns = pointsToEarn;
  // Shares nobody has claimed sit in the pool until they accept or it expires.
  const unclaimedShareCount =
    previewIsFixedShare && splitBetween > 0
      ? Math.max(0, splitBetween - participantCount)
      : 0;
  const shareSummary =
    splitBetween > 1 && pointsToEarn > 0
      ? `${totalPointsBeforeShare} split ${splitBetween} ways`
      : "";
  const earningParticipants =
    sharedClientIds.length > 0
      ? [clientId, ...sharedClientIds]
          .filter(Boolean)
          .map((participantId) => {
            const client = (Array.isArray(clients) ? clients : []).find(
              (item) =>
                String(item.id || item._id || "") === String(participantId),
            );

            return {
              id: String(participantId),
              label: client ? formatClientLabel(client) : String(participantId),
              email: String((client as any)?.email || (client as any)?.user?.email || "").trim(),
            };
          })
      : [];
  const selectedService = Array.isArray(services)
    ? services.find(
        (service: any) =>
          String(service?.id || service?._id || service?.service_id || "") ===
          serviceId,
      )
    : null;
  const serviceAmount = Number(
    (selectedService as any)?.price ??
      (selectedService as any)?.cost ??
      (selectedService as any)?.unit_price ??
      0,
  );
  const selectedPromo = Array.isArray(promos)
    ? promos.find((promo) => String(promo.id) === String(values?.promo_id || ""))
    : null;
  const sharedClientOptions = (Array.isArray(clients) ? clients : [])
    .filter((client) => String(client.id || client._id || "") !== clientId)
    .map((client) => ({
      label: formatClientLabel(client),
      value: String(client.id || client._id || ""),
    }))
    .filter((option) => option.value);
  const promoType = String(selectedPromo?.type || "").toUpperCase();
  const promoValue = Math.max(0, Number(selectedPromo?.value) || 0);
  const promoDiscountPercent =
    promoType === "PERCENT" || promoType === "PERCENTAGE"
      ? Math.min(100, promoValue)
      : (promoType === "FIXED" || promoType === "FLAT") && serviceAmount > 0
        ? Math.min(100, Math.round((promoValue / serviceAmount) * 100 * 100) / 100)
        : 0;
  const promoDiscountAmount =
    promoType === "PERCENT"
      ? (serviceAmount * Math.min(100, promoValue)) / 100
      : promoType === "FIXED" || promoType === "FLAT"
        ? Math.min(serviceAmount, promoValue)
        : 0;
  const selectedRewardType = selectedReward ? getRewardType(selectedReward) : "";
  const selectedServiceRewardApplies =
    Boolean(selectedReward) &&
    selectedRewardType === "SERVICE" &&
    serviceRewardFitsBooking(selectedReward);
  const onFullInvoiceSelected = parseBooleanSetting(
    values?.loyaltyOnFullInvoice,
    getCachedFullInvoiceEnabled(),
  );
  // Only a reward covering the whole booking still blocks a promo and points.
  const serviceRewardCoversWholeBooking =
    selectedServiceRewardApplies &&
    serviceRewardCoversBooking(selectedReward, selectedServiceDurationMinutes);
  const showServiceRewardRedeemNotice =
    selectedServiceRewardApplies &&
    serviceRewardCoversWholeBooking &&
    onFullInvoiceSelected === false &&
    redeemPoints > 0;
  const redeemHelperMessage =
    redeemError ||
    (showServiceRewardRedeemNotice
      ? "This reward already covers the whole booking."
      : "");
  const redeemHasError = Boolean(redeemError || showServiceRewardRedeemNotice);
  const rewardSelectSx = selectedRewardId
    ? {
        ...appointmentSelectInputSx,
        "& .MuiSelect-select": {
          ...appointmentSelectInputSx["& .MuiSelect-select"],
          paddingRight: "76px !important",
          maxWidth: "calc(100% - 76px)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
        "& .MuiInputBase-input": {
          paddingRight: "76px !important",
        },
      }
    : appointmentSelectInputSx;
  // The reward buys its own service's worth of time, so on a longer booking it
  // is a proportional discount rather than the old all-or-nothing 100%.
  const serviceRewardDiscountPercent = selectedServiceRewardApplies
    ? calculateServiceRewardDiscountPercent(
        selectedReward,
        selectedServiceDurationMinutes,
      )
    : 0;
  const serviceRewardDiscountAmount =
    serviceRewardDiscountPercent > 0
      ? Math.round(
          Math.min(
            serviceAmount,
            (serviceAmount * serviceRewardDiscountPercent) / 100,
          ) * 100,
        ) / 100
      : 0;
  const amount =
    Math.round(
      Math.max(
        0,
        serviceAmount - promoDiscountAmount - serviceRewardDiscountAmount,
      ) * 100,
    ) / 100;
  const fixedRedeemPercentBase =
    redeemRule?.type === "FIXED" && onFullInvoiceSelected === false
      ? Math.max(0, Number(serviceAmount) || 0)
      : amount;
  const fallbackRedeemRuleDiscountPercent =
    redeemRule?.type === "FIXED"
      ? fixedRedeemPercentBase > 0
        ? Math.min(
            100,
            Math.max(
              0,
              ((redeemRule.discount || 0) / fixedRedeemPercentBase) * 100,
            ),
          )
        : 0
      : Math.max(0, Number(redeemRule?.discount) || 0);
  const previewRedeemRuleDiscountPercent = Number(
    preview?.redeemRuleDiscountPercent,
  );
  const redeemRuleDiscountPercent = Math.max(
    0,
    Number.isFinite(previewRedeemRuleDiscountPercent) &&
      previewRedeemRuleDiscountPercent > 0
      ? previewRedeemRuleDiscountPercent
      : fallbackRedeemRuleDiscountPercent,
  );
  const currentFixedCashbackAmount = calculateFixedCashbackAmountForPoints(
    pointsToRedeem,
    Number(redeemRule?.points) || 0,
    Number(redeemRule?.discount) || 0,
  );
  const currentRedeemDiscountLabel =
    redeemRule?.type === "FIXED"
      ? `$${currentFixedCashbackAmount} off`
      : `${preview?.discountPercent ?? 0}%`;
  const redeemRuleDiscountLabel =
    redeemRule?.type === "FIXED"
      ? `$${Math.round(Math.max(0, Number(redeemRule?.discount) || 0) * 100) / 100} off`
      : `${redeemRuleDiscountPercent}% off`;
  const handleRedeemChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = Math.max(0, Math.floor(Number(event.target.value) || 0));

    if (preview && nextValue > balance) {
      setRedeemError(`Cannot exceed balance (${balance} pts).`);
      onChange(String(Math.floor(balance)));
      return;
    }

    setRedeemError("");
    onChange(preview ? String(nextValue) : event.target.value);
  };

  React.useEffect(() => {
    setRedeemError("");
  }, [clientId]);

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();
    if (!organizationId) {
      setLoyaltyEnabled(false);
      setSharePointsEnabled(true);
      setRedeemRule({ points: 0, discount: 0, type: "PERCENT" });
      return;
    }

    let cancelled = false;

    const loadRedeemRule = async () => {
      try {
        const settings = await getLoyaltySettings(organizationId);
        const loyaltyValue: any = settings?.is_loyalty ?? settings?.isLoyalty;
        const shareValue: any = settings?.is_share ?? settings?.isShare;
        const onFullInvoiceValue: any =
          settings?.on_full_invoice ?? settings?.onFullInvoice;
        const settingsRedeemPoints = Number(
          settings?.redeem?.points ??
            settings?.redeem_points ??
            settings?.redeemPoints,
        );
        const settingsRedeemDiscount = Number(
          settings?.redeem?.discount ??
            settings?.redeem_discount ??
            settings?.redeemDiscount,
        );
        const settingsRedeemType = normalizeRedeemDiscountType(
          settings?.redeem?.type,
          (settings?.redeem as any)?.discountType,
          (settings?.redeem as any)?.discount_type,
          settings?.type,
          (settings as any)?.discountType,
          (settings as any)?.discount_type,
          (settings as any)?.redeemDiscountType,
          (settings as any)?.redeem_discount_type,
        );
        if (!cancelled) {
          const nextSharePointsEnabled =
            parseBooleanSetting(shareValue, false);
          const nextOnFullInvoice =
            parseBooleanSetting(onFullInvoiceValue, true);
          localStorage.setItem(
            `loyalty_share_points_${organizationId}`,
            String(nextSharePointsEnabled),
          );
          localStorage.setItem(
            `loyalty_full_invoice_${organizationId}`,
            String(nextOnFullInvoice),
          );
          setLoyaltySettings(settings);
          setLoyaltyEnabled(
            parseBooleanSetting(loyaltyValue, false),
          );
          setSharePointsEnabled(true);
          onFullInvoiceChange?.(nextOnFullInvoice);
          const nextRedeemRule = {
            points:
              Number.isFinite(settingsRedeemPoints) && settingsRedeemPoints > 0
                ? settingsRedeemPoints
                : 0,
            discount:
              Number.isFinite(settingsRedeemDiscount) &&
              settingsRedeemDiscount > 0
                ? settingsRedeemDiscount
                : 0,
            type: settingsRedeemType,
          };
          setRedeemRule(nextRedeemRule);
          onRedeemRuleChange?.({
            type: nextRedeemRule.type,
            discount: nextRedeemRule.discount,
          });
        }
      } catch {
        if (!cancelled) {
          setLoyaltyEnabled(false);
          setSharePointsEnabled(true);
          setRedeemRule({ points: 0, discount: 0, type: "PERCENT" });
        }
      }
    };

    loadRedeemRule();

    const handleSharePointsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ value?: boolean }>).detail;
      if (typeof detail?.value === "boolean") {
        setSharePointsEnabled(true);
      }
    };

    window.addEventListener(
      "loyalty-share-points-changed",
      handleSharePointsChanged,
    );

    return () => {
      cancelled = true;
      window.removeEventListener(
        "loyalty-share-points-changed",
        handleSharePointsChanged,
      );
    };
  }, []);

  React.useEffect(() => {
    if (!selectedReward) return;

    const rewardType = getRewardType(selectedReward);
    // Clear it when the booked service changes to one it no longer fits.
    if (rewardType === "SERVICE" && !serviceRewardFitsBooking(selectedReward)) {
      onRewardChange("");
    }
  }, [getRewardType, onRewardChange, selectedReward, serviceRewardFitsBooking]);

  React.useEffect(() => {
    const organizationId = getStoredOrganizationId();

    if (loyaltyEnabled !== true || !organizationId || !clientId || !serviceId) {
      setPreview(null);
      onDiscountChange?.(0);
      return;
    }

    let cancelled = false;

    const loadPreview = async () => {
      try {
        setLoading(true);
        const response = await getLoyaltyPreview({
          organizationId,
          userId: clientId,
          amount,
          redeemPoints,
          // Without this the preview answers with the org divisor, so a
          // football booking would quote the padel number.
          businessId,
          // With sharing on but fixed dividing off the API cannot know the
          // headcount, and would quote the undivided total.
          participants: participantCount,
        });
        if (!cancelled) {
          setPreview({
            ...response,
            requestedRedeemPoints: redeemPoints,
          });
          const responseDiscountType = normalizeRedeemDiscountType(
            response?.discountType,
            (response as any)?.discountType,
            (response as any)?.discount_type,
            (response as any)?.redeemDiscountType,
            (response as any)?.redeem_discount_type,
          );
          const settingsDiscountType = normalizeRedeemDiscountType(
            redeemRule?.type,
          );
          const isFixedRedeemDiscount =
            responseDiscountType === "FIXED" || settingsDiscountType === "FIXED";
          const responseDiscountValue = Number(response?.discountPercent) || 0;
          const appliedRedeemPoints =
            onFullInvoiceSelected && isFixedRedeemDiscount
              ? redeemPoints
              : Math.max(
                  0,
                  Math.floor(Number(response?.pointsToRedeem ?? redeemPoints) || 0),
                );
          const fixedCashbackAmount = Math.min(
            fixedRedeemPercentBase,
            calculateFixedCashbackAmountForPoints(
              appliedRedeemPoints,
              Number(redeemRule?.points) || 0,
              Number(redeemRule?.discount) || 0,
            ),
          );
          const invoiceDiscountPercent =
            redeemPoints <= 0
              ? 0
              : onFullInvoiceSelected && isFixedRedeemDiscount
                ? amount > 0
                  ? calculateFixedCashbackDiscountPercent(
                      fixedCashbackAmount,
                      amount,
                    )
                  : 0
                : !onFullInvoiceSelected && isFixedRedeemDiscount
                  ? calculateFixedCashbackDiscountPercent(
                      fixedCashbackAmount,
                      fixedRedeemPercentBase,
                    )
                : responseDiscountValue;
          onDiscountChange?.(
            Math.round(Math.max(0, invoiceDiscountPercent) * 100) / 100,
          );
        }
      } catch {
        if (!cancelled) {
          setPreview(null);
          onDiscountChange?.(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
    };
  }, [
    amount,
    businessId,
    clientId,
    loyaltyEnabled,
    onFullInvoiceSelected,
    participantCount,
    redeemPoints,
    redeemRule?.discount,
    redeemRule?.type,
    serviceId,
  ]);

  if (loyaltyEnabled !== true) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        px: 1,
        py: 0.75,
      }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          display: "block",
          mb: 0.75,
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
        Balance:{" "}
        <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
          {loyaltyEnabled === null || loading ? "..." : previewBalance} pts
        </Box>
        {" • "}
        Earn:{" "}
        <Box component="span" sx={{ fontWeight: 600, color: "success.main" }}>
          {loyaltyEnabled === null || loading ? "..." : `+${pointsToEarn}`} pts
        </Box>
      </Typography>
      {!loading && shareSummary ? (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: -0.5, mb: 0.75, lineHeight: 1.3 }}>
          {previewIsFixedShare
            ? `${pointsToEarn} pts for the payer — ${shareSummary}, the rest held for the other players.`
            : `${pointsToEarn} pts each — ${shareSummary}.`}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
          },
          gap: 1,
          alignItems: "start",
        }}>
        <Box>
          <CustomTextField
            placeholder="Redeem Points"
            type="number"
            value={String(value || "")}
            onChange={handleRedeemChange}
            error={redeemHasError}
            sx={
              redeemHasError
                ? {
                    "& .MuiOutlinedInput-root fieldset": {
                      borderColor: "error.main",
                    },
                    "& .MuiOutlinedInput-root:hover fieldset": {
                      borderColor: "error.main",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: "error.main",
                    },
                  }
                : undefined
            }
            fullWidth
          />
          <Typography
            variant="caption"
            color={redeemHasError ? "error" : "warning.main"}
            sx={{
              ...appointmentHelperSlotSx,
              height: redeemHelperMessage ? 34 : 12,
              minHeight: redeemHelperMessage ? 34 : 12,
              mt: 0.2,
            }}
            display="block">
            {redeemHelperMessage}
          </Typography>
        </Box>
        <Box>
          <Box sx={{ position: "relative" }}>
            <CustomSelect
              label="Reward Package"
              value={selectedRewardId}
              options={visibleRewardOptions}
              searchable
              disabled={loadingLoyaltyRewards}
              placeholder={
                loadingLoyaltyRewards ? "Loading rewards..." : "Select reward"
              }
              error={Boolean(rewardError)}
              onChange={(nextValue) =>
                onRewardChange(
                  Array.isArray(nextValue) ? "" : String(nextValue || ""),
                )
              }
              fullWidth
              sx={rewardSelectSx}
            />
            {selectedRewardId ? (
              <IconButton
                size="small"
                aria-label="Clear reward package"
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onRewardChange("");
                }}
                sx={{
                  position: "absolute",
                  right: 40,
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 2,
                  width: 24,
                  height: 24,
                  p: 0.25,
                  color: "text.secondary",
                }}>
                <CloseRounded fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
          <Typography
            variant="caption"
            color="error"
            sx={{
              ...appointmentHelperSlotSx,
              height: rewardError ? 34 : 12,
              minHeight: rewardError ? 34 : 12,
              mt: 0.2,
            }}
            display="block">
            {rewardError}
          </Typography>
        </Box>
      </Box>
      {!(selectedRewardType === "SERVICE" && onFullInvoiceSelected === false) && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 0.1 }}>
          Discount: {loyaltyEnabled === null || loading ? "..." : currentRedeemDiscountLabel}{" | "}
          Every {redeemRule?.points ?? 0} points gives{" "}
          {loyaltyEnabled === null || loading ? "..." : redeemRuleDiscountLabel}.
        </Typography>
      )}
      {serviceRewardDiscountPercent > 0 ? (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ display: "block", mt: 0.1 }}>
          Reward: {getRewardFreeMinutes(selectedReward)} of{" "}
          {selectedServiceDurationMinutes} min — {serviceRewardDiscountPercent}% off
          the service
          {serviceAmount > 0
            ? ` ($${serviceRewardDiscountAmount} off $${serviceAmount})`
            : ""}
          .
          {serviceRewardCoversWholeBooking
            ? " Covers the whole booking, so a promo code and redeemed points cannot be added."
            : " The rest can still take a promo code and redeemed points."}
        </Typography>
      ) : null}
      <Box sx={{ mt: 0.55 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.4 }}>
          Invite clients
          {rosterDivisor
            ? ` — up to ${maxSharedClients} (${rosterDivisor} players per match)`
            : ` — up to ${maxSharedClients}`}
        </Typography>
        <Box sx={{ width: { xs: "100%", sm: "calc((100% - 8px) / 2)" } }}>
          <CustomSelect
            label="Clients"
            value={sharedClientIds}
            options={sharedClientOptions}
            multiSelect
            searchable
            onSearchInput={onClientSearch}
            selectedSummary={
              sharedClientIds.length === 1
                ? "1 client selected"
                : `${sharedClientIds.length} clients selected`
            }
            placeholder="Select clients"
            onChange={(nextValue) => {
              const selected = Array.isArray(nextValue)
                ? nextValue
                : [nextValue];
              const uniqueSelected = Array.from(
                new Set(selected.map((item) => String(item))),
              ).slice(0, maxSharedClients);
              if (selected.length > maxSharedClients) {
                toast.warning(
                  `You can select up to ${maxSharedClients} client${maxSharedClients === 1 ? "" : "s"}.`,
                  {
                    toastId: "share-points-max-clients",
                  },
                );
              }
              onSharedClientsChange(uniqueSelected);
            }}
            fullWidth
            sx={appointmentSelectInputSx}
          />
        </Box>
        {sharePointsEnabled === true && earningParticipants.length > 1 ? (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mb: 0.5 }}>
                Client earnings
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 0.75,
                }}>
                {earningParticipants.map((participant) => (
                  <Box
                    key={participant.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      px: 1,
                      py: 0.75,
                      minWidth: 0,
                    }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                      {participant.label}
                    </Typography>
                    {participant.email ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {participant.email}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="caption"
                      color="success.main"
                      sx={{ display: "block", fontWeight: 700 }}>
                      +{loading ? "..." : pointsEachClientEarns} pts
                    </Typography>
                  </Box>
                ))}
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}>
                {`Each client will earn ${loading ? "..." : pointsEachClientEarns} points${
                  shareSummary ? ` — ${shareSummary}` : " after sharing"
                }.`}
                {unclaimedShareCount > 0
                  ? ` ${unclaimedShareCount} share${unclaimedShareCount === 1 ? "" : "s"} stay in the pool for 7 days for anyone who accepts an invitation, then expire.`
                  : ""}
              </Typography>
            </Box>
        ) : null}
      </Box>
    </Box>
  );
};

const to12HourLabel = (hours: number, minutes: number) => {
  const safeH = Number.isFinite(hours) ? hours : 0;
  const safeM = Number.isFinite(minutes) ? minutes : 0;
  const suffix = safeH >= 12 ? "PM" : "AM";
  const displayH = ((safeH + 11) % 12) + 1; // 0 -> 12, 13 -> 1
  return `${displayH}:${String(safeM).padStart(2, "0")} ${suffix}`;
};

const buildTimeOptions = (stepMinutes: number) => {
  const safeStep = Math.max(1, Math.min(60, stepMinutes || 30));
  const totalSteps = Math.floor((24 * 60) / safeStep);
  return Array.from({ length: totalSteps }, (_, i) => {
    const totalMinutes = i * safeStep;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const value = `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0",
    )}`;
    return { label: to12HourLabel(hour, minute), value };
  });
};

interface AppointmentEvent {
  id: string | number;
  text: string;
  start_date: Date;
  end_date: Date;
  customer_name?: string;
  phone?: string;
  court?: string;
  status?: "CONFIRMED" | "PENDING" | "IN_PROGRESS" | "DONE" | "CANCELLED";
  duration?: number;
}

interface Court {
  id: string;
  name: string;
  type: string;
  color: string;
}

interface Client {
  id: string;
  _id?: string;
  name?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  country?: string;
  countryCode?: string;
  country_code?: string;
  active?: boolean | string;
}

interface Service {
  id: string;
  _id?: string;
  name?: string;
  title?: string;
  service_name?: string;
  label?: string;
  service_id?: string;
  duration?: number;
  price?: number;
}

interface ClientsResponse {
  users: Client[];
}

const normalizeClientRecord = (raw: any): Client => {
  if (!raw) return { id: "" };
  const id =
    raw.id ||
    raw._id ||
    raw.client_id ||
    raw.user_id ||
    raw.value ||
    raw.userId ||
    raw.clientId ||
    "";
  const name =
    raw.name ||
    raw.fullName ||
    raw.full_name ||
    raw.label ||
    raw.email ||
    (id ? `Client ${id}` : "Client");
  const countryCode = raw.country_code || raw.countryCode || "";
  const phoneCandidate =
    raw.mobile ||
    raw.phone ||
    raw.phoneNumber ||
    raw.contact ||
    raw.number ||
    raw.mobile_number ||
    raw.telephone ||
    "";
  const labelPhoneMatch =
    typeof raw.label === "string"
      ? raw.label.match(/(\+?\d[\d\s-]*)$/)?.[1] || ""
      : "";
  const basePhone = String(phoneCandidate || labelPhoneMatch || "").trim();
  const prefixedPhone =
    basePhone || !countryCode
      ? basePhone
      : `${countryCode}${basePhone.replace(/^\+?/, "")}`;
  return {
    id: String(id),
    name: String(name).trim(),
    email: raw.email || raw.mail || "",
    phone: prefixedPhone || basePhone,
    mobile: prefixedPhone || basePhone,
    country_code: countryCode,
    active: raw.active,
  };
};

const ORG_CLIENTS_INITIAL_LIMIT = 50;
const ORG_CLIENTS_SEARCH_LIMIT = 30;

const formatClientLabel = (client?: Client) => {
  if (!client) return "Client";
  const name =
    client.name ||
    client.email ||
    (client.id ? `Client ${client.id}` : "Client");
  const phone = client.mobile || client.phone || "";
  const digitsOnly = (val: string) => val.replace(/\D+/g, "");
  const phoneDigits = digitsOnly(phone);
  const nameDigits = digitsOnly(name);
  if (phone && phoneDigits && nameDigits.includes(phoneDigits)) {
    return name;
  }
  return phone ? `${name} - ${phone}` : name;
};

function Appointment() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;
  const { isEnabled } = useFeatures();
  const classesEnabled = isEnabled("CLASSES");

  const [selectedCourt, setSelectedCourt] = useState<string>("all");
  const [allAppointments, setAllAppointments] = useState<AppointmentEvent[]>(
    [],
  );
  const selectedCourtRef = useRef<string>(selectedCourt);
  const [clients, setClients] = useState<ClientsResponse | null>(null);
  const branchesRef = useRef<any[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const servicesRef = useRef<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [rewardServices, setRewardServices] = useState<Service[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [stockItems, setStockItems] = useState<any[]>([]);
  const [loyaltyRewards, setLoyaltyRewards] = useState<LoyaltyReward[]>([]);
  const [loadingLoyaltyRewards, setLoadingLoyaltyRewards] = useState(false);

  const [branches, setBranches] = useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [unitSettings, setUnitSettings] = useState<Record<string, any>>({});
  const [calendarStartHour, setCalendarStartHour] = useState<number>(0);
  const [calendarEndHour, setCalendarEndHour] = useState<number>(24);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("all");
  const [embedUrl, setEmbedUrl] = useState<string>(
    "https://calendar.google.com/calendar/embed?src=en.indian%23holiday%40group.v.calendar.google.com&ctz=UTC",
  );
  const [events, setEvents] = useState<any[]>([]);
  const invalidateSlotCacheForUnit = (_unitId: string | number) => {};
  const [activeDate, setActiveDate] = useState<Date>(new Date());
  const branchCalendarRefs = useRef<Record<string, any>>({});
  const schedulerWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadLoyaltyRewards = async () => {
      try {
        setLoadingLoyaltyRewards(true);
        const [rewards, productResponse, stockResponse, businessesResponse] = await Promise.all([
          getRewards(),
          fetchProducts().catch(() => []),
          fetchStockSummary().catch(() => null),
          getAllBusinesses().catch(() => []),
        ]);
        if (!cancelled) {
          const productList = normalizeArrayResponse(productResponse);
          const stockList = normalizeArrayResponse((stockResponse as any)?.items);
          const businesses = normalizeArrayResponse(businessesResponse);
          const serviceResponses = await Promise.allSettled(
            businesses
              .map((business: any) =>
                String(business?.id || business?._id || ""),
              )
              .filter(Boolean)
              .map((businessId) => getAllServicesByBusiness(businessId)),
          );
          const organizationServices = serviceResponses.flatMap((response) =>
            response.status === "fulfilled"
              ? normalizeArrayResponse(response.value)
              : [],
          );
          const serviceEntries = organizationServices
            .map((service: any): [string, Service] => [
              String(service?.id || service?._id || service?.service_id || ""),
              service as Service,
            ])
            .filter(([id]) => Boolean(id));
          const uniqueServices = Array.from(
            new Map<string, Service>(serviceEntries).values(),
          );
          setLoyaltyRewards(
            (Array.isArray(rewards) ? rewards : []).filter((reward) =>
              isTruthyFlag(reward?.active),
            ),
          );
          setProducts(productList);
          setStockItems(stockList);
          setRewardServices(uniqueServices);
        }
      } catch {
        if (!cancelled) {
          setLoyaltyRewards([]);
          setProducts([]);
          setStockItems([]);
          setRewardServices([]);
        }
      } finally {
        if (!cancelled) setLoadingLoyaltyRewards(false);
      }
    };

    loadLoyaltyRewards();

    return () => {
      cancelled = true;
    };
  }, []);

  const displayedBranches = React.useMemo(() => {
    const list = Array.isArray(branches) ? branches : [];
    if (selectedBranchId === "all") return list;
    return list.filter(
      (item: any) => String(item.id) === String(selectedBranchId),
    );
  }, [branches, selectedBranchId]);

  const displayedBranchIds = React.useMemo(
    () =>
      displayedBranches.map((branch: any) => String(branch.id)).filter(Boolean),
    [displayedBranches],
  );
  const isSingleBranch = displayedBranchIds.length <= 1;
  const axisBranchId = !isSingleBranch ? displayedBranchIds[0] || "" : "";

  const eventsByBranch = React.useMemo(() => {
    const grouped: Record<string, any[]> = {};
    (Array.isArray(events) ? events : []).forEach((ev: any) => {
      const branchId = ev?.extendedProps?.branchId;
      if (!branchId) return;
      const key = String(branchId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(ev);
    });
    return grouped;
  }, [events]);

  const [currentView, setCurrentView] = useState<"day" | "week" | "month">(
    "day",
  );
  const viewType =
    currentView === "month"
      ? "dayGridMonth"
      : currentView === "week"
        ? "timeGridWeek"
        : "timeGridDay";
  const isMobileInteraction = React.useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const userAgent = navigator.userAgent || "";
    const isMobileAgent = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
      userAgent,
    );
    const isIpadOs =
      /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 0;
    const uaDataMobile = Boolean((navigator as any).userAgentData?.mobile);
    const hasTouch = navigator.maxTouchPoints > 0;
    const coarsePointer =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    const smallViewport =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 900px)").matches;
    return (
      isMobileAgent ||
      uaDataMobile ||
      isIpadOs ||
      coarsePointer ||
      (hasTouch && smallViewport)
    );
  }, []);
  const displayedBranchKey = React.useMemo(
    () => displayedBranchIds.join("|"),
    [displayedBranchIds],
  );
  const shiftActiveDate = (direction: -1 | 1) => {
    setActiveDate((prev) => {
      if (currentView === "month") {
        const year = prev.getFullYear();
        const month = prev.getMonth() + direction;
        const day = prev.getDate();
        const target = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        target.setDate(Math.min(day, daysInMonth));
        return target;
      }
      const next = new Date(prev);
      if (currentView === "week") {
        next.setDate(next.getDate() + 7 * direction);
      } else {
        next.setDate(next.getDate() + direction);
      }
      return next;
    });
  };
  const gotoPrev = () => shiftActiveDate(-1);
  const gotoNext = () => shiftActiveDate(1);
  const gotoToday = () => setActiveDate(new Date());
  const gotoDate = (d: Date) => setActiveDate(d);
  const handleViewChange = (nextView: "day" | "week" | "month") => {
    if (nextView === "day") {
      setSelectedBranchId("all");
      setActiveDate(new Date());
    }
    if (
      nextView !== "day" &&
      selectedBranchId === "all" &&
      branches.length > 0
    ) {
      setSelectedBranchId(String(branches[0].id));
    }
    setCurrentView(nextView);
  };
  const handleBranchChange = (value: string) => {
    const nextValue = String(value);
    if (nextValue === "all" && currentView !== "day") {
      setCurrentView("day");
    }
    setSelectedBranchId(nextValue);
  };
  const [visibleStart, setVisibleStart] = useState<Date>(new Date());
  const [visibleEnd, setVisibleEnd] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d;
  });
  const lastAutoRefreshKeyRef = useRef<string>("");
  const latestRefreshRequestIdRef = useRef(0);
  const [calendarKey, setCalendarKey] = useState<number>(0);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);
  const [clientDetailsViewOpen, setClientDetailsViewOpen] = useState(false);
  const [clientHistoryDialogOpen, setClientHistoryDialogOpen] = useState(false);
  const [clientDetailsLoading, setClientDetailsLoading] = useState(false);
  const [clientHistoryLoading, setClientHistoryLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientHistoryInvoices, setClientHistoryInvoices] = useState<any[]>([]);
  const clientFieldHelpersRef = useRef<{
    setFieldValue?: (name: string, value: any) => void;
    values?: Record<string, any>;
  } | null>(null);
  const [formDate, setFormDate] = useState<string>(
    new Date().toISOString().split("T")[0],
  );

  const [formTimeStart, setFormTimeStart] = useState<string>("09:00");
  const [formTimeEnd, setFormTimeEnd] = useState<string>("10:00");
  const [formCustomerName, setFormCustomerName] = useState<string>("");
  const [formNotes, setFormNotes] = useState<string>("");
  const [formClientId, setFormClientId] = useState<string>("");
  const [formServiceId, setFormServiceId] = useState<string>("");
  const [formPromoId, setFormPromoId] = useState<string>("");
  const [formStatus, setFormStatus] = useState<string>("PENDING");
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingEventRaw, setEditingEventRaw] = useState<any | null>(null);
  const [editingEventStarted, setEditingEventStarted] = useState(false);
  const [invoiceEditOpen, setInvoiceEditOpen] = useState(false);
  const [invoiceEditingRow, setInvoiceEditingRow] = useState<any | null>(null);
  const [invoiceServices, setInvoiceServices] = useState<Service[]>([]);
  const [invoiceEditLoading, setInvoiceEditLoading] = useState(false);
  const [invoiceSubmitLoading, setInvoiceSubmitLoading] = useState(false);
  const [formBranchId, setFormBranchId] = useState<string>("all");
  const [promos, setPromos] = useState<
    { id: string; code: string; type?: string; value?: number }[]
  >([]);

  const [appointmentDialogTab, setAppointmentDialogTab] = useState(0);
  const [showBranchFieldInCreate, setShowBranchFieldInCreate] = useState(false);
  const [blockAction, setBlockAction] = useState<"open" | "close">("close");
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  // Appointment id of the class session whose panel is open on the calendar.
  const [classSessionId, setClassSessionId] = useState<string | null>(null);

  // Push notifications for a class carry screen: "ClassSessionDetails" and an
  // appointment_id, which the service worker turns into ?classSession=<id> on
  // this route. The id may be a projected one (`v:<class>:<date>`), so it is
  // read straight out of the query and passed through untouched — never parsed
  // or validated. Cleared from the URL once consumed so a refresh doesn't
  // reopen the panel.
  useEffect(() => {
    const deepLinkedSession = new URLSearchParams(location.search).get(
      "classSession",
    );
    if (!deepLinkedSession) return;
    setClassSessionId(deepLinkedSession);
    const nextParams = new URLSearchParams(location.search);
    nextParams.delete("classSession");
    navigate(
      { pathname: location.pathname, search: nextParams.toString() },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate]);
  // Drives the "Add Class" tab's conditional recurrence fields, same pattern
  // ClassFormDialog uses on the Classes page.
  const [classFormFrequency, setClassFormFrequency] = useState("NONE");
  const [classFormEndMode, setClassFormEndMode] = useState("COUNT");
  const [isClassSubmitting, setIsClassSubmitting] = useState(false);
  const [classConflicts, setClassConflicts] = useState<ScheduleConflict[]>([]);
  const [classConflictMessage, setClassConflictMessage] = useState("");
  const [classConflictsOpen, setClassConflictsOpen] = useState(false);
  const [blockDate, setBlockDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });
  const [blockStart, setBlockStart] = useState<string>("09:00");
  const [blockEnd, setBlockEnd] = useState<string>("10:00");
  const [slotDuration, setSlotDuration] = useState<string>("00:30:00");
  const [slotDurationLoading, setSlotDurationLoading] =
    useState<boolean>(false);
  const [isAppointmentSubmitting, setIsAppointmentSubmitting] = useState(false);
  const formatTo12h = React.useCallback((value?: string | null) => {
    if (!value) return "-";
    const raw = String(value).trim();

    // If already contains AM/PM, just normalize case/spacing
    const apMatch = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (apMatch) {
      const h = parseInt(apMatch[1], 10) || 0;
      const m = parseInt(apMatch[2] || "0", 10) || 0;
      const period = (apMatch[3] || "").toUpperCase();
      return `${h === 0 || h === 12 ? 12 : h % 12}:${String(m).padStart(
        2,
        "0",
      )} ${period}`;
    }

    // Fallback: treat as 24h and convert
    const [hStr = "0", mStr = "0"] = raw.split(":");
    let h = parseInt(hStr, 10);
    const m = parseInt(mStr, 10) || 0;
    if (!Number.isFinite(h)) h = 0;
    const period = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
  }, []);
  const pickNonEmptyString = (...values: unknown[]): string | undefined => {
    for (const value of values) {
      if (typeof value === "string") {
        const trimmed = value.trim();
        if (trimmed) return trimmed;
      }
      if (typeof value === "number") {
        const text = String(value);
        if (text) return text;
      }
    }
    return undefined;
  };
  const normalizeCountryCodeValue = (value?: unknown): string => {
    if (value === undefined || value === null) return "";
    if (typeof value === "object" && value !== null) {
      const candidate =
        (value as any).phone ??
        (value as any).dialCode ??
        (value as any).dial_code ??
        (value as any).code ??
        (value as any).value;
      if (candidate !== undefined && candidate !== null) {
        return normalizeCountryCodeValue(candidate);
      }
    }
    const stringValue = typeof value === "string" ? value : String(value);
    const match = stringValue.match(/^\s*(\+?\d+)/);
    const dialCode = match ? match[1] : stringValue.trim();
    if (!dialCode) return "";
    return dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
  };
  const normalizeActiveStatus = (
    value?: unknown,
  ): "verified" | "unverified" => {
    if (typeof value === "boolean") return value ? "verified" : "unverified";
    if (typeof value === "number")
      return value === 1 ? "verified" : "unverified";
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (normalized === "verified") return "verified";
      if (normalized === "unverified") return "unverified";
      if (
        normalized === "active" ||
        normalized === "enabled" ||
        normalized === "true"
      ) {
        return "verified";
      }
      if (
        normalized === "inactive" ||
        normalized === "disabled" ||
        normalized === "false"
      ) {
        return "unverified";
      }
    }
    return "unverified";
  };
  const resolveClientIdValue = (value?: any): string | null => {
    const extractId = (input: any): string | null => {
      if (input === undefined || input === null) return null;
      if (typeof input === "string" || typeof input === "number") {
        return String(input);
      }
      if (typeof input !== "object") return null;

      const direct =
        input.id ||
        input._id ||
        input.user_id ||
        input.userId ||
        input.user?.id ||
        input.user?._id ||
        input.client_user_id ||
        input.clientUserId ||
        input.client_id ||
        input.clientId ||
        input.client?.id ||
        input.client?._id ||
        input.client?.user_id ||
        input.client?.userId ||
        input.value;

      if (direct !== undefined && direct !== null) {
        // If nested object, try to resolve its id instead of stringifying it.
        if (typeof direct === "object") return extractId(direct);
        return String(direct);
      }
      return null;
    };

    return extractId(value);
  };
  const resolveClientHistoryRecord = (payload: any) => {
    if (!payload) return null;
    const raw = payload?.data ?? payload;
    const client =
      raw?.client ?? payload?.client ?? payload?.data?.client ?? raw;
    const user =
      raw?.user ??
      payload?.user ??
      client?.user ??
      payload?.client?.user ??
      payload?.data?.client?.user;
    return {
      ...(user || {}),
      ...(client || {}),
      ...(raw || {}),
    };
  };
  const resolveClientPhone = (raw: any): string | undefined => {
    return pickNonEmptyString(
      raw?.mobile,
      raw?.phone,
      raw?.phoneNumber,
      raw?.client_phone,
      raw?.customer_phone,
      raw?.patient?.phone,
      raw?.patient?.mobile,
      raw?.client?.phone,
      raw?.client?.mobile,
      raw?.customer?.phone,
      raw?.customer?.mobile,
      raw?.user?.phone,
      raw?.user?.mobile,
    );
  };
  const resolveClientCountryCode = (raw: any): string | undefined => {
    return pickNonEmptyString(
      raw?.country,
      raw?.country_code,
      raw?.countryCode,
      raw?.client?.country,
      raw?.client?.country_code,
      raw?.client?.countryCode,
      raw?.customer?.country,
      raw?.customer?.country_code,
      raw?.customer?.countryCode,
      raw?.user?.country,
      raw?.user?.country_code,
      raw?.user?.countryCode,
    );
  };
  const formatPhoneWithCountry = (phone?: string, country?: string) => {
    const basePhone = String(phone || "").trim();
    const baseCountry = String(country || "").trim();
    if (!basePhone) return "";
    if (!baseCountry) return basePhone;
    if (basePhone.startsWith("+") || basePhone.startsWith(baseCountry)) {
      return basePhone;
    }
    const normalizedCountry = baseCountry.startsWith("+")
      ? baseCountry
      : `+${baseCountry}`;
    return `${normalizedCountry} ${basePhone}`;
  };
  const resolveInvoiceStatusFormValue = (
    status: unknown,
    fallbackId?: unknown,
  ):
    | "PAID"
    | "PARTIALLY_PAID"
    | "UNPAID"
    | "CANCELLED"
    | "REFUNDED"
    | "PARTIALLY_REFUNDED"
    | "PENDING" => {
    const numeric = Number(status ?? fallbackId);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "PAID";
      if (numeric === 2) return "UNPAID";
      if (numeric === 3) return "CANCELLED";
    }
    const normalize = (val: any) =>
      typeof val === "string" ? val.trim().toUpperCase() : "";

    const normalizedStatus = normalize(status);
    const normalizedFallback = normalize(fallbackId);
    if (normalizedStatus === "VOID" || normalizedStatus === "VOIDED") {
      return "CANCELLED";
    }
    if (normalizedFallback === "VOID" || normalizedFallback === "VOIDED") {
      return "CANCELLED";
    }

    const allowed = new Set([
      "PAID",
      "PARTIALLY_PAID",
      "UNPAID",
      "CANCELLED",
      "REFUNDED",
      "PARTIALLY_REFUNDED",
      "PENDING",
    ]);

    if (allowed.has(normalizedStatus)) {
      return normalizedStatus as any;
    }
    if (allowed.has(normalizedFallback)) {
      return normalizedFallback as any;
    }
    return "UNPAID";
  };
  const resolveInvoicePaymentMethodValue = (value: unknown): string => {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      if (numeric === 1) return "card";
      if (numeric === 2) return "cash";
      if (numeric === 3) return "bank transfer";
    }
    if (typeof value === "string" && value.trim()) {
      const normalized = value.trim().toLowerCase();
      if (
        normalized === "cash" ||
        normalized === "card" ||
        normalized === "bank transfer"
      ) {
        return normalized;
      }
    }
    return "cash";
  };
  const formatInvoiceDateValue = (value: any): string => {
    if (!value) return "";
    const dateObj = value instanceof Date ? value : new Date(value);
    if (isNaN(dateObj.getTime())) return "";
    return dateObj.toISOString().split("T")[0];
  };
  const computeInvoiceItemTotal = (
    quantity: number,
    unitCost: number,
    discount: number,
  ) => {
    const qty = Number(quantity) || 0;
    const unit = Number(unitCost) || 0;
    const disc = Math.min(100, Math.max(0, Number(discount) || 0));
    const subtotal = qty * unit;
    const discountAmount = (subtotal * disc) / 100;
    return Math.round((subtotal - discountAmount) * 100) / 100;
  };
  const roundInvoiceMoney = (value: number) =>
    Math.round((Number(value) || 0) * 100) / 100;
  const pickFiniteNumber = (...values: any[]) => {
    for (const value of values) {
      if (value === undefined || value === null || value === "") continue;
      const numeric = Number(value);
      if (Number.isFinite(numeric)) return numeric;
    }
    return undefined;
  };
  const getInvoiceItemDiscountAmount = (item: any) => {
    const quantity = Number(item?.quantity) || 0;
    const unitCost = Number(item?.unitCost) || 0;
    const subtotal = quantity * unitCost;
    const totalPrice = Number(item?.totalPrice);

    if (Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(Math.max(subtotal - totalPrice, 0));
    }

    const discountPercent = Math.min(
      100,
      Math.max(0, Number(item?.discount) || 0),
    );
    return roundInvoiceMoney((subtotal * discountPercent) / 100);
  };
  const resolveInvoiceItemDiscountPercent = (
    detail: any,
    quantity: number,
    unitCost: number,
    totalPrice: number,
    promoType?: string,
    promoValue?: number,
  ) => {
    const explicitPercentValue =
      detail?.discountPercent ??
      detail?.discount_percent ??
      detail?.discountPercentage ??
      detail?.discount_percentage ??
      detail?.metadata?.discountPercent ??
      detail?.metadata?.discount_percent;
    const explicitPercent = Number(explicitPercentValue);
    if (
      explicitPercentValue !== undefined &&
      explicitPercentValue !== null &&
      explicitPercentValue !== "" &&
      Number.isFinite(explicitPercent) &&
      explicitPercent > 0
    ) {
      return Math.min(100, Math.max(0, explicitPercent));
    }

    const subtotal = (Number(quantity) || 0) * (Number(unitCost) || 0);
    if (subtotal > 0 && Number.isFinite(totalPrice)) {
      return roundInvoiceMoney(
        (Math.max(subtotal - totalPrice, 0) / subtotal) * 100,
      );
    }

    const normalizedPromoType = String(promoType || "")
      .trim()
      .toUpperCase();
    const numericPromoValue = Number(promoValue);
    if (
      subtotal > 0 &&
      Number.isFinite(numericPromoValue) &&
      numericPromoValue > 0
    ) {
      if (
        normalizedPromoType === "PERCENT" ||
        normalizedPromoType === "PERCENTAGE" ||
        normalizedPromoType === "DISCOUNT"
      ) {
        return Math.min(100, Math.max(0, numericPromoValue));
      }
      if (normalizedPromoType === "FLAT" || normalizedPromoType === "FIXED") {
        const lineDiscountAmount = Number(detail?.discount);
        const flatDiscountAmount =
          Number.isFinite(lineDiscountAmount) && lineDiscountAmount > 0
            ? lineDiscountAmount
            : numericPromoValue;
        return roundInvoiceMoney(
          Math.min(100, Math.max(0, (flatDiscountAmount / subtotal) * 100)),
        );
      }
    }

    return Math.min(100, Math.max(0, Number(detail?.discount) || 0));
  };
  const resolveInvoiceDetails = (row: any): any[] => {
    const candidates = [
      row?.details,
      row?.invoice?.details,
      row?.invoice?.data?.details,
      row?.invoice_details,
      row?.data?.details,
      row?.items,
      row?.invoice?.items,
      row?.data?.items,
      row?.invoice?.data?.items,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    const metadataServices = row?.metadata?.services;
    if (Array.isArray(metadataServices)) {
      return metadataServices.map((svc: any) => ({
        service_id: svc?.id || svc?.serviceId || svc?.service_id,
        title: svc?.name || svc?.title,
        quantity: 1,
        unitPrice: svc?.price ?? svc?.cost ?? 0,
        discount: 0,
        totalPrice: svc?.price ?? svc?.cost ?? 0,
      }));
    }

    const service = row?.service || row?.invoice?.service;
    if (service) {
      const fallbackAmount =
        Number(service?.price ?? service?.unit_price ?? 0) || 0;
      return [
        {
          service_id: service?.id || service?._id,
          title: service?.name || service?.title,
          quantity: 1,
          unitPrice: fallbackAmount,
          discount: 0,
          totalPrice: fallbackAmount,
        },
      ];
    }
    return [];
  };

  const resolveInvoiceProducts = (row: any): any[] => {
    const candidates = [
      row?.products,
      row?.invoice?.products,
      row?.metadata?.products,
      row?.invoice?.metadata?.products,
    ];
    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };
  const buildInvoiceItems = (
    details: any[],
    servicesList: Service[],
    products: any[] = [],
    invoiceRow?: any,
  ): InvoiceItem[] => {
    const invoicePromoType =
      invoiceRow?.promoType ||
      invoiceRow?.promo_type ||
      invoiceRow?.invoice?.promoType ||
      invoiceRow?.invoice?.promo_type ||
      "";
    const invoicePromoValue =
      pickFiniteNumber(
        invoiceRow?.promoValue,
        invoiceRow?.promo_value,
        invoiceRow?.invoice?.promoValue,
        invoiceRow?.invoice?.promo_value,
      ) ?? 0;

    const serviceItems: InvoiceItem[] = (
      Array.isArray(details) ? details : []
    ).map((detail, index) => {
      const detailServiceId =
        detail?.service_id ??
        detail?.serviceId ??
        detail?.serviceID ??
        detail?.metadata?.serviceId ??
        detail?.metadata?.productId ??
        detail?.productId ??
        detail?.product_id ??
        detail?.service?.id ??
        detail?.service?.service_id;
      const itemType =
        String(
          detail?.metadata?.type || detail?.type || "SERVICE",
        ).toUpperCase() === "PRODUCT"
          ? "PRODUCT"
          : "SERVICE";
      const normalizedServiceId =
        detailServiceId !== undefined && detailServiceId !== null
          ? String(detailServiceId)
          : undefined;
      let matchedService = (
        Array.isArray(servicesList) ? servicesList : []
      ).find(
        (srv: any) =>
          String(srv.id || srv._id || srv.service_id || "") ===
          String(normalizedServiceId || ""),
      );

      if (!matchedService && detail?.title) {
        matchedService = (Array.isArray(servicesList) ? servicesList : []).find(
          (srv: any) => {
            const serviceNumber = srv.title
              ? String(srv.title).match(/^\d+/)?.[0]
              : null;
            const detailTitle = String(detail.title).trim();
            return serviceNumber === detailTitle || srv.title === detailTitle;
          },
        );
      }

      const matchedServiceId =
        matchedService?.id || matchedService?._id || matchedService?.service_id;
      const resolvedServiceId = matchedServiceId || normalizedServiceId;
      let serviceValue: any;
      if (resolvedServiceId) {
        serviceValue = String(resolvedServiceId);
      } else if (matchedService && matchedService.title) {
        const titleMatch = String(matchedService.title).match(/^\d+/);
        serviceValue = titleMatch
          ? parseInt(titleMatch[0], 10)
          : matchedService.title;
      } else if (detail?.title) {
        const parsedTitle = parseInt(detail.title, 10);
        serviceValue = !isNaN(parsedTitle) ? parsedTitle : detail.title;
      } else {
        serviceValue = detail?.service_id || 60;
      }

      const quantity = Number(detail?.quantity ?? detail?.qty) || 1;
      const unitCost =
        Number(
          detail?.amount ??
            detail?.unit_cost ??
            detail?.unitCost ??
            detail?.unitPrice ??
            detail?.price,
        ) || 0;
      const totalPriceRaw = pickFiniteNumber(
        detail?.net_amount ??
          detail?.totalPrice ??
          detail?.total_price ??
          detail?.total_amount ??
          detail?.total,
      );
      const totalPrice =
        totalPriceRaw !== undefined
          ? totalPriceRaw
          : computeInvoiceItemTotal(
              quantity,
              unitCost,
              Number(detail?.discount) || 0,
            );
      const discount = resolveInvoiceItemDiscountPercent(
        detail,
        quantity,
        unitCost,
        totalPrice,
        invoicePromoType,
        invoicePromoValue,
      );
      const isRewardItem = Boolean(
        detail?.loyaltyReward ||
          detail?.loyaltyRewardId ||
          detail?.loyalty_reward_id ||
          detail?.rewardType ||
          detail?.reward_type ||
          detail?.metadata?.loyaltyReward ||
          detail?.metadata?.loyaltyRewardId ||
          detail?.metadata?.loyalty_reward_id ||
          detail?.metadata?.rewardType ||
          detail?.metadata?.reward_type,
      );
      const detailMetadata =
        detail?.metadata && typeof detail.metadata === "object"
          ? detail.metadata
          : {};

      return {
        id: index + 1,
        service: serviceValue,
        serviceId: resolvedServiceId ? String(resolvedServiceId) : undefined,
        serviceLabel:
          detail?.title ||
          detail?.service_name ||
          detail?.service_label ||
          detail?.description ||
          undefined,
        quantity,
        unitCost,
        discount,
        loyaltyDiscountPercent: isRewardItem
          ? 0
          : Number(
              detail?.loyaltyDiscountPercent ??
                detail?.loyalty_discount_percent ??
                detail?.metadata?.loyaltyDiscountPercent ??
                detail?.metadata?.loyalty_discount_percent ??
                0,
            ),
        promoDiscountPercent: Number(
          detail?.promoDiscountPercent ??
            detail?.promo_discount_percent ??
            detail?.metadata?.promoDiscountPercent ??
            detail?.metadata?.promo_discount_percent ??
            0,
        ),
        loyaltyRewardId:
          detail?.loyaltyRewardId ??
          detail?.loyalty_reward_id ??
          detail?.metadata?.loyaltyRewardId ??
          detail?.metadata?.loyalty_reward_id ??
          null,
        loyaltyRewardType:
          detail?.loyaltyRewardType ??
          detail?.loyalty_reward_type ??
          detail?.rewardType ??
          detail?.reward_type ??
          detail?.metadata?.loyaltyRewardType ??
          detail?.metadata?.loyalty_reward_type ??
          detail?.metadata?.rewardType ??
          detail?.metadata?.reward_type ??
          null,
        metadata: {
          ...detailMetadata,
          ...(detail?.loyaltyRewardId ?? detail?.loyalty_reward_id
            ? {
                loyaltyRewardId:
                  detail?.loyaltyRewardId ?? detail?.loyalty_reward_id,
              }
            : {}),
          ...(detail?.loyaltyRewardType ?? detail?.loyalty_reward_type
            ? {
                loyaltyRewardType:
                  detail?.loyaltyRewardType ?? detail?.loyalty_reward_type,
              }
            : {}),
          ...(detail?.rewardType ?? detail?.reward_type
            ? { rewardType: detail?.rewardType ?? detail?.reward_type }
            : {}),
        },
        loyaltyOnFullInvoice: extractInvoiceLoyaltyOnFullInvoice(detail),
        totalPrice,
        itemType: itemType as "SERVICE" | "PRODUCT",
      };
    });

    const productItems: InvoiceItem[] = (
      Array.isArray(products) ? products : []
    ).map((p: any, idx: number) => {
      const qty = Number(p?.qty ?? p?.quantity ?? 1) || 1;
      const price = Number(p?.price ?? p?.unitPrice ?? p?.unit_price ?? 0) || 0;
      const subtotal = Math.round(qty * price * 100) / 100;
      const totalPrice =
        pickFiniteNumber(
          p?.net_amount,
          p?.totalPrice,
          p?.total_price,
          p?.total_amount,
          p?.total,
        ) ?? subtotal;
      const discount = resolveInvoiceItemDiscountPercent(
        p,
        qty,
        price,
        totalPrice,
        invoicePromoType,
        invoicePromoValue,
      );
      const id =
        p?.id ?? p?._id ?? p?.productId ?? p?.product_id ?? `${idx + 1}`;
      const productMetadata =
        p?.metadata && typeof p.metadata === "object" ? p.metadata : {};
      return {
        id: idx + 1 + 1000,
        service: String(id),
        serviceId: String(id),
        serviceLabel: String(
          p?.name ?? p?.title ?? p?.label ?? p?.description ?? "Product",
        ),
        quantity: qty,
        unitCost: price,
        discount,
        totalPrice,
        metadata: productMetadata,
        itemType: "PRODUCT" as const,
      } as InvoiceItem;
    });

    return [...serviceItems, ...productItems];
  };
  const [dragConfirmState, setDragConfirmState] = useState<{
    resolve: (val: boolean) => void;
    fromBranch?: string;
    toBranch?: string;
    fromStart?: string;
    fromEnd?: string;
    toStart?: string;
    toEnd?: string;
  } | null>(null);

  const [inactiveClientConfirm, setInactiveClientConfirm] = useState<{
    resolve: (val: boolean) => void;
  } | null>(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingAppointment, setDeletingAppointment] = useState(false);
  const [pendingDeleteScope, setPendingDeleteScope] = useState<
    "ALL" | "UPCOMING" | undefined
  >(undefined);
  const [recurringScopeConfirm, setRecurringScopeConfirm] = useState<{
    action: "delete" | "cancel";
    resolve: (val: "ALL" | "UPCOMING" | null | undefined) => void;
  } | null>(null);

  const displayEvents = useMemo(() => {
    const base = Array.isArray(events) ? events : [];
    // If calendar spans overnight hours (endHour > 24), keep events intact.
    if (calendarEndHour > 24) return base;
    const result: any[] = [];
    base.forEach((ev) => {
      // If already split, keep as-is
      if (ev?.extendedProps?.segmentPart) {
        result.push(ev);
        return;
      }
      const parts = splitEventForDisplay(ev);
      if (Array.isArray(parts)) {
        parts.forEach((p) => result.push(p));
      } else {
        result.push(ev);
      }
    });
    return result;
  }, [events, calendarEndHour]);

  const requestDragConfirmation = React.useCallback(
    (details: {
      fromBranch?: string;
      toBranch?: string;
      fromStart?: string;
      fromEnd?: string;
      toStart?: string;
      toEnd?: string;
    }) =>
      new Promise<boolean>((resolve) => {
        setDragConfirmState({ resolve, ...details });
      }),
    [],
  );
  const requestRecurringScope = React.useCallback(
    (action: "delete" | "cancel") =>
      new Promise<"ALL" | "UPCOMING" | null | undefined>((resolve) => {
        setRecurringScopeConfirm({ action, resolve });
      }),
    [],
  );
  const timeStepMinutes = slotDuration === "00:15:00" ? 15 : 30;
  const timeOptions = React.useMemo(
    () => buildTimeOptions(timeStepMinutes),
    [timeStepMinutes],
  );
  const normalizeTimeValue = React.useCallback(
    (value: string) => {
      const matched = timeOptions.find((option) => option.value === value);
      if (matched) return matched.value;

      const [rawHour = "0", rawMinute = "0"] = String(value || "")
        .split(":")
        .map((part) => part.trim());
      const hour = Math.min(23, Math.max(0, parseInt(rawHour, 10) || 0));
      const parsedMinute = Math.max(0, parseInt(rawMinute, 10) || 0);
      const step = Math.max(1, timeStepMinutes || 30);
      const minute = Math.floor(Math.min(59, parsedMinute) / step) * step;

      return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
        2,
        "0",
      )}`;
    },
    [timeOptions, timeStepMinutes],
  );

  const parseDateTime = (dateStr: string, timeStr: string) => {
    if (!dateStr) return null;
    try {
      const combined = `${dateStr} ${timeStr || ""}`.trim();
      const d = new Date(combined);
      if (!isNaN(d.getTime())) return d.toISOString();
    } catch {}
    try {
      const base = new Date(dateStr);
      if (!isNaN(base.getTime())) return base.toISOString();
    } catch {}
    return null;
  };

  const formatDateForPayload = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}/${m}/${day}`;
  };

  const formatTimeForPayload = (d: Date) => {
    return toTimeInput(d);
  };

  const timeStringToMinutes = (t: string) => {
    if (!t) return 0;
    const raw = String(t).trim();
    if (!raw) return 0;
    const ampmMatch = raw.match(
      /(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM)/i,
    );
    if (ampmMatch) {
      let hh = parseInt(ampmMatch[1] || "0", 10);
      const mm = parseInt(ampmMatch[2] || "0", 10) || 0;
      const ap = (ampmMatch[4] || "").toLowerCase();
      if (ap === "pm" && hh < 12) hh += 12;
      if (ap === "am" && hh === 12) hh = 0;
      if (isNaN(hh) || isNaN(mm)) return 0;
      return hh * 60 + mm;
    }
    const digitsOnly = (val: string) => val.replace(/\D+/g, "");
    const parts = raw.split(":");
    const hh = parseInt(digitsOnly(parts[0] || "0"), 10);
    const mm = parseInt(digitsOnly(parts[1] || "0"), 10);
    if (isNaN(hh) || isNaN(mm)) return 0;
    return hh * 60 + mm;
  };

  const MAX_APPOINTMENT_MINUTES = 500;
  const APPOINTMENTS_PAGE_SIZE = 100;
  const MAX_APPOINTMENT_PAGES = 20;

  const clampEndTime = (
    startTime: string,
    desiredEndTime: string,
    maxMinutes = MAX_APPOINTMENT_MINUTES,
  ) => {
    try {
      const maxDuration = Number(maxMinutes || 120);
      const desiredRaw = String(desiredEndTime || "").trim();
      const desiredDuration = desiredRaw
        ? minutesBetweenTimes(startTime, desiredRaw)
        : maxDuration;
      const finalDuration = Math.min(
        Math.max(desiredDuration || 0, 0),
        maxDuration,
      );
      return addMinutesToTime(startTime, finalDuration);
    } catch (e) {
      return desiredEndTime;
    }
  };

  const minutesBetweenTimes = (startTime: string, endTime: string) => {
    const startMin = timeStringToMinutes(startTime);
    const endMin = timeStringToMinutes(endTime);
    const diff = endMin - startMin;
    return diff < 0 ? endMin + 24 * 60 - startMin : diff;
  };

  const isMidnightTimeValue = (value: any) => {
    if (value === null || value === undefined) return false;
    const trimmed = String(value).trim();
    if (!trimmed) return false;
    if (/^24:0{2}(:0{2})?$/.test(trimmed)) return true;
    return timeStringToMinutes(trimmed) === 0;
  };

  const formatTimeHHMM = (value: any) => {
    if (value === null || value === undefined) return "";

    let hours: number | null = null;
    let minutes: number | null = null;

    if (value instanceof Date) {
      hours = value.getHours();
      minutes = value.getMinutes();
    } else if (typeof value === "number" && Number.isFinite(value)) {
      hours = value;
      minutes = 0;
    } else if (typeof value === "string") {
      const raw = value.trim();
      const ampmMatch = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
      if (ampmMatch) {
        let h = parseInt(ampmMatch[1], 10);
        const m = parseInt(ampmMatch[2] || "0", 10);
        const period = (ampmMatch[3] || "").toLowerCase();
        if (period === "pm" && h < 12) h += 12;
        if (period === "am" && h === 12) h = 0;
        hours = h;
        minutes = m;
      } else {
        const parts = raw.split(":");
        if (parts.length >= 2) {
          hours = parseInt(parts[0], 10);
          minutes = parseInt(parts[1], 10);
        } else if (/^\d{1,2}$/.test(raw)) {
          hours = parseInt(raw, 10);
          minutes = 0;
        } else {
          const parsed = new Date(raw);
          if (!isNaN(parsed.getTime())) {
            hours = parsed.getHours();
            minutes = parsed.getMinutes();
          }
        }
      }
    }

    if (hours === null || minutes === null) return String(value);

    const safeHours = Math.max(0, Math.min(23, hours));
    const safeMinutes = Math.max(0, Math.min(59, minutes));
    return to12HourLabel(safeHours, safeMinutes);
  };

  const convertTimeLabel = (label: string) => {
    const raw = String(label || "");
    if (!raw) return raw;
    return raw.replace(
      /\b(\d{1,2})(?::(\d{2}))?\b(?!\s*(?:AM|PM))/gi,
      (match, hStr, mStr) => {
        const h = parseInt(hStr, 10);
        const m = mStr !== undefined ? parseInt(mStr, 10) : 0;
        if (!Number.isFinite(h) || h > 23) return match;
        return to12HourLabel(
          Math.max(0, Math.min(23, h)),
          Math.max(0, Math.min(59, Number.isFinite(m) ? m : 0)),
        );
      },
    );
  };

  const lastDateClickRef = React.useRef<{ time: number; iso: string | null }>({
    time: 0,
    iso: null,
  });

  const toDateInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const toTimeInput = (d: Date) => {
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const combineDateAndTime = (
    dateStr: string | undefined | null,
    timeStr: string | undefined | null,
  ) => {
    if (!dateStr) return null;
    let datePart = String(dateStr).trim();
    datePart = datePart.replace(/\//g, "-");
    const dateMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      datePart = `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    }

    let t = timeStr ? String(timeStr).trim() : "00:00";
    let addDay = 0;
    const raw24Match = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (raw24Match) {
      let hh = parseInt(raw24Match[1], 10);
      const mm = parseInt(raw24Match[2] || "0", 10) || 0;
      const ss = parseInt(raw24Match[3] || "0", 10) || 0;
      if (hh >= 24) {
        addDay = Math.floor(hh / 24);
        hh = hh % 24;
        t = `${String(hh).padStart(2, "0")}:${String(mm).padStart(
          2,
          "0",
        )}:${String(ss).padStart(2, "0")}`;
      }
    }
    const ampm = t.match(/(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(AM|PM|am|pm)/);
    if (ampm) {
      try {
        let hh = parseInt(ampm[1], 10);
        const mm = parseInt(ampm[2] || "0", 10) || 0;
        const ss = parseInt(ampm[3] || "0", 10) || 0;
        const ap = (ampm[4] || "").toLowerCase();
        if (ap === "pm" && hh < 12) hh += 12;
        if (ap === "am" && hh === 12) hh = 0;
        t = `${String(hh).padStart(2, "0")}:${String(mm).padStart(
          2,
          "0",
        )}:${String(ss).padStart(2, "0")}`;
      } catch (e) {}
    } else {
      if (t.length === 5 && /^\d{1,2}:\d{2}$/.test(t)) t = `${t}:00`;
    }

    try {
      const combined = `${datePart}T${t}`;
      const d = new Date(combined);
      if (!isNaN(d.getTime())) {
        if (addDay) d.setDate(d.getDate() + addDay);
        return d;
      }
    } catch (e) {}

    try {
      const d = new Date(`${datePart} ${timeStr || ""}`);
      if (!isNaN(d.getTime())) return d;
    } catch (e) {}
    try {
      const d = new Date(datePart);
      if (!isNaN(d.getTime())) return d;
    } catch (e) {}
    return null;
  };

  const getStatusColors = (status: string) => {
    const statusLower = String(status || "").toLowerCase();

    if (statusLower === "done") {
      return {
        backgroundColor: "#53be57",
        borderColor: "#3dad42",
        textColor: "white",
      };
    } else if (statusLower === "in_progress" || statusLower === "in progress") {
      return {
        backgroundColor: "#facc15",
        borderColor: "#eab308",
        textColor: "#1f2937",
      };
    } else if (statusLower === "pending") {
      return {
        backgroundColor: "#2196F3",
        borderColor: "#1976D2",
        textColor: "white",
      };
    } else if (statusLower === "cancelled") {
      return {
        backgroundColor: "#f44336",
        borderColor: "#da190b",
        textColor: "white",
      };
    }

    return {
      backgroundColor: "#4caf50",
      borderColor: "#45a049",
      textColor: "white",
    };
  };

  const normalizeAppointmentStatus = (value: any) =>
    typeof value === "string" ? value.trim().toUpperCase() : "";

  const deriveAppointmentStatus = (
    currentStatus: any,
    startInput: any,
    endInput: any,
  ): "PENDING" | "IN_PROGRESS" | "DONE" | "CANCELLED" => {
    const normalized = normalizeAppointmentStatus(currentStatus);
    if (normalized === "CANCELLED") return "CANCELLED";

    const start =
      startInput instanceof Date
        ? startInput
        : startInput
          ? new Date(startInput)
          : null;
    let end =
      endInput instanceof Date
        ? endInput
        : endInput
          ? new Date(endInput)
          : null;

    if (!start || isNaN(start.getTime())) return "PENDING";
    if (!end || isNaN(end.getTime()))
      end = new Date(start.getTime() + 60 * 60000);

    // Defensive: handle overnight or invalid ranges (end before start)
    let safety = 0;
    while (end.getTime() <= start.getTime() && safety < 2) {
      end = new Date(end.getTime() + 24 * 60 * 60000);
      safety += 1;
    }
    if (end.getTime() <= start.getTime()) {
      end = new Date(start.getTime() + 60 * 60000);
    }

    const now = new Date();
    const nowMs = now.getTime();
    if (end.getTime() <= nowMs) return "DONE";
    if (start.getTime() <= nowMs && nowMs < end.getTime()) return "IN_PROGRESS";
    return "PENDING";
  };

  const buildEventTooltip = (raw: any) => {
    if (!raw) return "";
    const parts: string[] = [];
    try {
      if (raw.start_date || raw.time_start) {
        const sd = raw.start_date || raw.date || "";
        const st = raw.time_start || raw.time || raw.start_time || "";
        parts.push(`Start: ${sd} ${st}`.trim());
      }
      if (raw.time_end) parts.push(`End: ${raw.time_end}`);
      if (raw.patient && raw.patient.name)
        parts.push(`Client: ${raw.patient.name}`);
      if (raw.client_name || raw.customer_name)
        parts.push(`Client: ${raw.client_name || raw.customer_name}`);
      if (raw.notes)
        parts.push(`Notes: ${String(raw.notes).replace(/\n/g, " ")}`);
      if (raw.services) {
        const svc = Array.isArray(raw.services)
          ? raw.services.join(", ")
          : String(raw.services);
        parts.push(`Services: ${svc}`);
      }
      if (raw.status) parts.push(`Status: ${raw.status}`);
    } catch (e) {}
    return parts.join(" | ");
  };

  const resolveClientName = (raw: any, fallbackTitle?: string): string => {
    const resolved =
      raw?.client_name ||
      raw?.clientName ||
      (typeof raw?.client === "string" || typeof raw?.client === "number"
        ? raw?.client
        : null) ||
      raw?.customer_name ||
      raw?.customerName ||
      (typeof raw?.customer === "string" || typeof raw?.customer === "number"
        ? raw?.customer
        : null) ||
      (raw?.customer && raw?.customer.name) ||
      (raw?.patient && raw?.patient.name) ||
      raw?.patient_name ||
      raw?.client?.full_name ||
      raw?.client?.fullName ||
      raw?.client?.name ||
      raw?.user_name ||
      raw?.user?.name ||
      raw?.name ||
      raw?.title ||
      fallbackTitle ||
      "";
    return String(resolved || "").trim();
  };

  const resolveBranchName = (raw: any): string => {
    const directName =
      raw?.unit_name ||
      raw?.unitName ||
      raw?.unit?.name ||
      raw?.branch_name ||
      raw?.branchName ||
      raw?.branch?.name ||
      raw?.business_name ||
      raw?.businessName ||
      raw?.business?.name;

    if (directName) return String(directName).trim();

    const rawUnitId =
      raw?.unit_id ||
      raw?.unitId ||
      raw?.unit?.id ||
      raw?.branch_id ||
      raw?.branchId ||
      raw?.branch?.id;

    if (rawUnitId && Array.isArray(branches)) {
      const matchedUnit = branches.find(
        (b: any) => String(b.id) === String(rawUnitId),
      );
      if (matchedUnit) {
        return String(
          matchedUnit.name ||
            matchedUnit.title ||
            matchedUnit.branch_name ||
            matchedUnit.id,
        ).trim();
      }
    }

    return "";
  };

  const resolveResourceId = (raw: any) => {
    const units = Array.isArray(branches) ? branches : [];
    const rawUnitId =
      raw?.unit_id ||
      raw?.unitId ||
      raw?.unit?.id ||
      raw?.branch_id ||
      raw?.branchId ||
      raw?.branch?.id;

    if (rawUnitId) {
      const matchedUnit = units.find(
        (unit: any) => String(unit.id) === String(rawUnitId),
      );
      return matchedUnit ? String(matchedUnit.id) : String(rawUnitId);
    }

    const legacyId = raw?.business_id || raw?.businessId || raw?.business;
    if (legacyId) {
      const matchedLegacy = units.find(
        (unit: any) => String(unit.id) === String(legacyId),
      );
      if (matchedLegacy) return String(matchedLegacy.id);
    }

    if (selectedBranchId !== "all") return String(selectedBranchId);

    return "";
  };

  const formatAppointmentMoney = (value: any): string => {
    if (value === undefined || value === null || value === "") return "";
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    return numeric % 1 === 0 ? String(numeric) : numeric.toFixed(2);
  };

  const resolveAppointmentInvoiceTotal = (raw: any): string => {
    const candidates = [
      raw?.invoice?.total,
      raw?.invoice?.total_amount,
      raw?.invoice?.totalAmount,
      raw?.invoice?.amount,
      raw?.invoiceTotal,
      raw?.total_price,
      raw?.total,
      raw?.total_amount,
      raw?.amount,
    ];

    for (const candidate of candidates) {
      const formatted = formatAppointmentMoney(candidate);
      if (formatted) return formatted;
    }

    return "";
  };

  const resolveAppointmentInvoiceStatus = (raw: any): string => {
    const candidates = [
      raw?.invoice?.status,
      raw?.invoice?.status_name,
      raw?.invoice?.status_label,
      raw?.invoice?.invoiceStatus,
      raw?.invoice?.invoice_status,
      raw?.invoiceStatus,
      raw?.invoice_status,
      raw?.invoiceStatusName,
      raw?.invoice_status_name,
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null || candidate === "")
        continue;
      if (typeof candidate === "number") {
        if (candidate === 1) return "PAID";
        if (candidate === 2) return "UNPAID";
      }
      const normalized = String(candidate).trim().toUpperCase();
      if (!normalized) continue;
      if (normalized === "APPROVED") return "PAID";
      return normalized;
    }

    return "";
  };

  const resolveAppointmentInvoiceId = (raw: any): string => {
    return (
      pickNonEmptyString(
        raw?.invoice?.id,
        raw?.invoice?.invoiceId,
        raw?.invoice?.invoice_id,
        raw?.invoiceId,
        raw?.invoice_id,
        raw?.invoice?.Invoice_number,
        raw?.invoice?.invoice_number,
        raw?.Invoice_number,
        raw?.invoice_number,
      ) || ""
    );
  };

  const hasZeroInvoiceAmount = (raw: any): boolean => {
    const candidates = [
      raw?.invoice?.amount,
      raw?.invoice?.total,
      raw?.invoice?.total_amount,
      raw?.invoice?.totalAmount,
      raw?.invoice?.subtotal,
      raw?.invoiceTotal,
      raw?.total_price,
      raw?.amount,
      raw?.total,
      raw?.total_amount,
    ];

    return candidates.some((candidate) => {
      if (candidate === undefined || candidate === null || candidate === "")
        return false;
      const numeric = Number(candidate);
      return Number.isFinite(numeric) && numeric === 0;
    });
  };

  const enrichZeroInvoiceAppointments = async (appointments: any[]) => {
    return Promise.all(
      appointments.map(async (appointment) => {
        const invoiceId = resolveAppointmentInvoiceId(appointment);
        if (!invoiceId || !hasZeroInvoiceAmount(appointment)) {
          return appointment;
        }

        try {
          const response: any = await getInvoiceById(invoiceId);
          const invoiceData = response?.data ?? response;
          const invoice = invoiceData?.invoice ?? invoiceData;

          if (!invoice || typeof invoice !== "object") return appointment;

          return {
            ...appointment,
            invoice: {
              ...(appointment?.invoice || {}),
              ...invoice,
            },
            invoice_status:
              invoice?.status ??
              invoice?.status_name ??
              appointment?.invoice_status,
            invoiceStatus:
              invoice?.status ??
              invoice?.status_name ??
              appointment?.invoiceStatus,
            invoiceTotal:
              invoice?.amount ??
              invoice?.total ??
              invoice?.total_amount ??
              appointment?.invoiceTotal,
            total_price:
              invoice?.amount ??
              invoice?.total ??
              invoice?.total_amount ??
              appointment?.total_price,
          };
        } catch (error) {
          console.warn("Failed to enrich zero invoice appointment", {
            invoiceId,
            error,
          });
          return appointment;
        }
      }),
    );
  };

  const renderEventContent = (arg: any) => {
    try {
      const raw = arg.event.extendedProps?.raw || {};
      let timeText = arg.timeText || "";
      const rawStart = raw.time_start || raw.start_time || raw.time || "";
      const rawEnd = raw.time_end || raw.end_time || raw.end || "";
      if (rawStart && rawEnd && isMidnightTimeValue(rawEnd)) {
        const startMin = timeStringToMinutes(String(rawStart));
        if (startMin > 0) {
          const startLabel = formatTimeHHMM(rawStart);
          timeText = startLabel ? `${startLabel} - 12:00` : "12:00";
        }
      }
      timeText = convertTimeLabel(timeText);
      const status = arg.event.extendedProps?.status || raw.status || "";
      const normalizedInvoiceStatus = resolveAppointmentInvoiceStatus(raw);
      const invoiceTotalText = resolveAppointmentInvoiceTotal(raw);
      const invoiceTotalNumber = Number(invoiceTotalText);
      const isZeroInvoiceTotal =
        invoiceTotalText !== "" &&
        Number.isFinite(invoiceTotalNumber) &&
        invoiceTotalNumber === 0;
      const isInvoicePaid =
        normalizedInvoiceStatus === "PAID" ||
        normalizedInvoiceStatus === "APPROVED" ||
        isZeroInvoiceTotal;
      const isClosedSlot =
        String(arg.event.title || "").toLowerCase() === "closed" ||
        arg.event.extendedProps?.slot ||
        arg.event.extendedProps?.blocked ||
        arg.event.display === "background" ||
        (arg.event as any)?._def?.display === "background" ||
        (arg.event as any)?.rendering === "background" ||
        raw?.slot ||
        raw?.blocked ||
        raw?.closed === true ||
        raw?.closed === 1;
      const name =
        arg.event.extendedProps?.clientName ||
        resolveClientName(raw, arg.event.title) ||
        "Appointment";
      const serviceName =
        raw?.service?.name ||
        raw?.service?.title ||
        raw?.service_name ||
        raw?.serviceName ||
        raw?.service_title ||
        "";
      const serviceLine =
        serviceName && invoiceTotalText
          ? `${serviceName} - ${invoiceTotalText}$`
          : serviceName
            ? String(serviceName)
            : "";
      const branchName =
        arg.event.extendedProps?.branchName || resolveBranchName(raw);
      const metaParts: string[] = [];
      if (selectedBranchId === "all" && branchName) metaParts.push(branchName);
      if (status) metaParts.push(status);

      return (
        <div className="fc-custom-event" style={{ position: "relative" }}>
          {!isClosedSlot && (
            <div
              style={{
                position: "absolute",
                top: 2,
                right: 2,
                zIndex: 2,
              }}
              title={isInvoicePaid ? "Invoice Paid" : "Invoice Unpaid"}>
              {isInvoicePaid ? (
                <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
              ) : (
                <CancelIcon sx={{ fontSize: 16, color: "#d32f2f" }} />
              )}
            </div>
          )}
          <div
            className="fc-event-time"
            style={{ fontSize: "0.82rem", opacity: 0.9 }}>
            {timeText}
          </div>
          <div
            className="fc-event-main"
            style={{ fontSize: "0.62rem", fontWeight: 600 }}>
            {name}
          </div>
          {serviceLine && (
            <div
              className="fc-event-service"
              style={{ fontSize: "0.6rem", opacity: 0.9 }}>
              {serviceLine}
            </div>
          )}
          {metaParts.length > 0 && (
            <div
              className="fc-event-status"
              style={{
                fontSize: "0.6rem",
                opacity: 0.85,
                marginTop: "0px",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}>
              <span>{metaParts.join(" • ")}</span>
            </div>
          )}
        </div>
      );
    } catch (e) {
      return null;
    }
  };

  const mapAppointmentToEvent = (appt: any) => {
    const parseAsDate = (value: any): Date | null => {
      if (!value) return null;
      const d = value instanceof Date ? value : new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    const startAtDate = parseAsDate(
      appt?.start_at ?? appt?.startAt ?? appt?.start_at_utc ?? appt?.startAtUtc,
    );
    const endAtDate = parseAsDate(
      appt?.end_at ?? appt?.endAt ?? appt?.end_at_utc ?? appt?.endAtUtc,
    );

    const toHHMM = (d: Date | null) => {
      if (!d) return "";
      return formatTimeHHMM(d);
    };

    const startTimeValue =
      appt?.time_start ??
      appt?.start_time ??
      appt?.timeStart ??
      appt?.startTime ??
      "";
    const endTimeValue =
      appt?.time_end ?? appt?.end_time ?? appt?.timeEnd ?? appt?.endTime ?? "";

    const safeStartTime = String(startTimeValue || "").trim();
    const safeEndTime = String(endTimeValue || "").trim();

    let start: any =
      startAtDate && !isNaN(startAtDate.getTime())
        ? startAtDate.toISOString()
        : null;
    let end: any =
      endAtDate && !isNaN(endAtDate.getTime()) ? endAtDate.toISOString() : null;
    let derivedEndForDisplay: Date | null = null;

    const hasLegacyStartTime = safeStartTime !== "";
    const hasLegacyEndTime = safeEndTime !== "";

    const startDateObj = hasLegacyStartTime
      ? combineDateAndTime(appt?.start_date, safeStartTime)
      : null;
    const endDateObj = hasLegacyEndTime
      ? combineDateAndTime(appt?.start_date, safeEndTime)
      : null;

    if (!start) {
      start =
        (startDateObj ? startDateObj.toISOString() : null) ||
        parseDateTime(appt?.start_date, safeStartTime) ||
        appt?.start_date ||
        null;
    }

    if (!end) {
      end =
        (endDateObj ? endDateObj.toISOString() : null) ||
        parseDateTime(appt?.start_date, safeEndTime) ||
        appt?.end_date ||
        null;
    }

    if (startDateObj) {
      const startMin = timeStringToMinutes(String(safeStartTime || ""));
      const endMin = timeStringToMinutes(String(safeEndTime || ""));
      if (isMidnightTimeValue(safeEndTime) && startMin > 0) {
        const endOfDay = combineDateAndTime(appt?.start_date, "23:59:59");
        if (endOfDay) end = endOfDay.toISOString();
      } else if (endDateObj && endMin < startMin) {
        const adjustedEnd = new Date(endDateObj.getTime() + 24 * 60 * 60000);
        end = adjustedEnd.toISOString();
        derivedEndForDisplay = adjustedEnd;
      }
    }

    // If end time isn't provided, derive it from duration to avoid 23:59 clamp.
    if (!safeEndTime) {
      const durationMinutes = getAppointmentDuration(appt);
      const baseStart =
        startAtDate && !isNaN(startAtDate.getTime())
          ? startAtDate
          : start
            ? new Date(start)
            : null;
      if (
        durationMinutes &&
        durationMinutes > 0 &&
        baseStart &&
        !isNaN(baseStart.getTime())
      ) {
        const endD = new Date(baseStart.getTime() + durationMinutes * 60000);
        end = endD.toISOString();
        derivedEndForDisplay = endD;
      }
    }

    if (start && !end) {
      try {
        const startD = start instanceof Date ? start : new Date(start);
        if (!isNaN(startD.getTime())) {
          end = new Date(startD.getTime() + 60 * 60000).toISOString();
        }
      } catch (e) {}
    }

    const crossesMidnightByDates =
      startAtDate &&
      endAtDate &&
      endAtDate.getTime() > startAtDate.getTime() &&
      (endAtDate.getFullYear() !== startAtDate.getFullYear() ||
        endAtDate.getMonth() !== startAtDate.getMonth() ||
        endAtDate.getDate() !== startAtDate.getDate());
    const endTimeLooksClamped =
      safeEndTime &&
      timeStringToMinutes(safeEndTime) >= 23 * 60 + 50 &&
      Boolean(crossesMidnightByDates);
    const displayStart = safeStartTime
      ? formatTimeHHMM(safeStartTime)
      : toHHMM(startAtDate);
    const displayEnd = endTimeLooksClamped
      ? toHHMM(endAtDate)
      : safeEndTime
        ? formatTimeHHMM(safeEndTime)
        : derivedEndForDisplay
          ? formatTimeHHMM(derivedEndForDisplay)
          : toHHMM(endAtDate);

    if (String(appt?.id) === "117cfa0c-b462-4c3f-a717-b508f4463b4a") {
      console.log("Debug appt times", {
        id: appt.id,
        start_at: appt?.start_at || appt?.startAt,
        end_at: appt?.end_at || appt?.endAt,
        safeStartTime,
        safeEndTime,
        start,
        end,
        displayStart,
        displayEnd,
        derivedEndForDisplay,
      });
    }
    const timeStart = displayStart || undefined;
    const timeEnd = displayEnd || undefined;

    const effectiveStatus = deriveAppointmentStatus(appt?.status, start, end);
    const effectiveColors = getStatusColors(effectiveStatus);
    const clientName = resolveClientName(appt);
    const branchName = resolveBranchName(appt);
    const branchId = resolveResourceId(appt);
    const invoiceNumber = pickNonEmptyString(
      appt?.invoice?.Invoice_number,
      appt?.invoice?.invoice_number,
      appt?.Invoice_number,
      appt?.invoice_number,
      appt?.invoiceId,
      appt?.invoice_id,
      appt?.invoice?.id,
    );
    // A class session is an appointment with a roster instead of one client, so
    // it gets the class name as its label and its own colour. A projected one
    // has no row behind it yet — it still holds the court, but it is drawn
    // lighter so it doesn't read as a confirmed booking.
    const isClassSession = isClassAppointment(appt);
    const isVirtualClassSession = isClassSession && isVirtualClassAppointment(appt);
    const classColors = isVirtualClassSession
      ? VIRTUAL_CLASS_EVENT_COLORS
      : CLASS_EVENT_COLORS;
    const baseTitle = isClassSession
      ? getClassEventTitle(appt)
      : clientName || appt?.notes || "Appointment";
    const title = branchName ? `${baseTitle} (${branchName})` : baseTitle;

    const idStartPart =
      safeStartTime ||
      (startAtDate
        ? `${String(startAtDate.getHours()).padStart(2, "0")}:${String(
            startAtDate.getMinutes(),
          ).padStart(2, "0")}`
        : "");

    return {
      id: String(
        appt?.id ||
          appt?._id ||
          appt?.appointment_id ||
          `${appt?.start_date || appt?.start_at || "appt"}_${idStartPart}`,
      ),
      title,
      start,
      end,
      backgroundColor: isClassSession
        ? classColors.backgroundColor
        : effectiveColors.backgroundColor,
      borderColor: isClassSession
        ? classColors.borderColor
        : effectiveColors.borderColor,
      textColor: isClassSession
        ? classColors.textColor
        : effectiveColors.textColor,
      // Nothing about a class tile is draggable, and a projected one has no
      // row to drag in the first place.
      ...(isClassSession ? { editable: false, durationEditable: false } : {}),
      extendedProps: {
        status: effectiveStatus,
        raw: appt,
        clientName: isClassSession
          ? getClassEventTitle(appt)
          : clientName || "Appointment",
        branchName,
        branchId,
        ...(isClassSession ? buildClassEventProps(appt) : {}),
        ...(invoiceNumber ? { invoiceNumber } : {}),
        ...(timeStart ? { timeStart } : {}),
        ...(timeEnd ? { timeEnd } : {}),
      },
    };
  };

  const extractAppointmentsFromResponse = (resp: any): any[] => {
    if (!resp) return [];
    if (resp.today && resp.all) {
      const allData = resp.all;
      if (allData?.appointments) return allData.appointments;
      if (allData?.data) return allData.data;
      if (Array.isArray(allData)) return allData;
    }
    if (resp.appointments) return resp.appointments;
    if (resp.data) return resp.data;
    if (Array.isArray(resp)) return resp;
    return [];
  };

  /**
   * Pages through a calendar endpoint that may append projected class tiles.
   *
   * Those tiles are generated per request, so they have no stable offset to
   * page over: the API appends them to page 0 only, and only when `viewType`
   * bounds the window — which is why every caller passes `currentView`, and
   * why omitting it would drop every class from the calendar with no error.
   *
   * Two consequences handled here. The page-size check counts only persisted
   * rows, so appended tiles can't fake another page into existence. And the
   * merged list is sorted client-side, because appended tiles arrive after the
   * ordered rows rather than in place.
   */
  const collectPagedAppointments = async (
    fetchPage: (page: number) => Promise<any>,
  ): Promise<any[]> => {
    const merged: any[] = [];
    for (let page = 0; page < MAX_APPOINTMENT_PAGES; page++) {
      const resp = await fetchPage(page);
      const rows = extractAppointmentsFromResponse(resp);
      if (!rows.length) break;
      merged.push(...rows);
      const persistedRows = rows.filter(
        (row: any) => !isVirtualClassAppointment(row),
      );
      if (persistedRows.length < APPOINTMENTS_PAGE_SIZE) break;
    }

    return merged.sort((a: any, b: any) => {
      const aTime = new Date(a?.start_at ?? a?.startAt ?? 0).getTime();
      const bTime = new Date(b?.start_at ?? b?.startAt ?? 0).getTime();
      if (Number.isNaN(aTime) || Number.isNaN(bTime)) return 0;
      return aTime - bTime;
    });
  };

  const fetchAllUnitAppointmentsForDate = async (
    unitId: string,
    dateParam: string,
  ): Promise<any[]> =>
    collectPagedAppointments((page) =>
      getAppointmentsByUnit(String(unitId), {
        viewType: currentView,
        date: dateParam,
        page,
        size: APPOINTMENTS_PAGE_SIZE,
      }),
    );

  /** The multi-court grid — same projected-tile handling as a single unit. */
  const fetchAllBusinessAppointmentsForDate = async (
    businessId: string,
    dateParam: string,
  ): Promise<any[]> =>
    collectPagedAppointments((page) =>
      getAppointmentsByBusiness(String(businessId), {
        viewType: currentView,
        date: dateParam,
        page,
        size: APPOINTMENTS_PAGE_SIZE,
      }),
    );

  const timestampsClose = (
    a: any,
    b: any,
    toleranceMs: number = 60_000,
  ): boolean => {
    const ta = a ? new Date(a).getTime() : NaN;
    const tb = b ? new Date(b).getTime() : NaN;
    if (isNaN(ta) || isNaN(tb)) return false;
    return Math.abs(ta - tb) <= toleranceMs;
  };

  const isSameAppointmentRecord = (
    appt: any,
    target: {
      start_at: string;
      end_at: string;
      service_id?: any;
      client_id?: any;
      unit_id?: any;
    },
  ) => {
    const startMatch = timestampsClose(
      appt?.start_at || appt?.startAt,
      target.start_at,
    );
    const endMatch = timestampsClose(
      appt?.end_at || appt?.endAt,
      target.end_at,
    );
    const serviceMatch = target.service_id
      ? String(appt?.service_id || appt?.serviceId || "") ===
        String(target.service_id || "")
      : true;
    const clientMatch = target.client_id
      ? String(
          appt?.client_id ||
            appt?.clientId ||
            appt?.client?.id ||
            appt?.client?._id ||
            "",
        ) === String(target.client_id || "")
      : true;
    const unitMatch = target.unit_id
      ? String(appt?.unit_id || appt?.unitId || appt?.branch_id || "") ===
        String(target.unit_id || "")
      : true;

    return startMatch && endMatch && serviceMatch && clientMatch && unitMatch;
  };

  /**
   * Split a single event into per-day segments for rendering only.
   * Keeps a reference to the original event id in extendedProps.originalId.
   * Declared as a function for hoisting (used above in useMemo).
   */
  function splitEventForDisplay(event: any) {
    try {
      // Build start/end even if the event lacks Date objects (some data only has time strings)
      const raw = event?.extendedProps?.raw || {};
      const rawDate =
        raw.start_date ||
        raw.date ||
        raw.day ||
        event?.start_date ||
        event?.date ||
        activeDate?.toISOString()?.split("T")?.[0];

      const deriveDate = (value: any, timeValue: string | undefined | null) => {
        if (value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) return d;
        }
        if (rawDate && timeValue) {
          const d = combineDateAndTime(rawDate, timeValue);
          if (d && !isNaN(d.getTime())) return d;
        }
        return null;
      };

      const start =
        (event?.start ? new Date(event.start) : null) ||
        deriveDate(null, event?.extendedProps?.timeStart) ||
        deriveDate(null, raw?.time_start || raw?.start_time || raw?.time);
      let end =
        (event?.end ? new Date(event.end) : null) ||
        deriveDate(null, event?.extendedProps?.timeEnd) ||
        deriveDate(null, raw?.time_end || raw?.end_time || raw?.end);

      if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [event];
      }

      // If the end time is on or before the start time (e.g., 23:30 -> 05:30),
      // treat it as spilling into the next day(s) so it can be split properly.
      if (end.getTime() <= start.getTime()) {
        const adjusted = new Date(end);
        adjusted.setDate(adjusted.getDate() + 1);
        while (adjusted.getTime() <= start.getTime()) {
          adjusted.setDate(adjusted.getDate() + 1);
        }
        end = adjusted;
      }

      const parts: any[] = [];
      let cursorStart = new Date(start);
      let partIdx = 1;
      const finalMs = end.getTime();

      // Safety: cap to 7 days to avoid runaway loops on corrupted data
      const maxParts = 7;

      while (cursorStart.getTime() < finalMs && partIdx <= maxParts) {
        const endOfDay = new Date(cursorStart);
        endOfDay.setHours(23, 59, 59, 999);
        const segmentEnd =
          endOfDay.getTime() >= finalMs ? new Date(finalMs) : endOfDay;

        const segmentTimeStart = formatTimeHHMM(cursorStart);
        const segmentTimeEnd = formatTimeHHMM(segmentEnd);

        const seg: any = {
          ...event,
          id: `${event.id}__seg${partIdx}`,
          start: cursorStart.toISOString(),
          end: segmentEnd.toISOString(),
          extendedProps: {
            ...event.extendedProps,
            originalId: event.id,
            segmentPart: partIdx,
            segmentTimeStart,
            segmentTimeEnd,
            timeStart: segmentTimeStart,
            timeEnd: segmentTimeEnd,
          },
        };
        parts.push(seg);

        cursorStart = new Date(cursorStart);
        cursorStart.setDate(cursorStart.getDate() + 1);
        cursorStart.setHours(0, 0, 0, 0);
        partIdx += 1;
      }

      return parts;
    } catch (e) {
      console.warn("splitEventForDisplay failed", e);
      return [event];
    }
  }

  const resolveSlotBranchId = (slot: any, fallbackBusinessId?: any) => {
    const direct = resolveResourceId(slot);
    if (direct) return direct;

    const nestedBranch = slot?.branch || slot?.business;
    if (nestedBranch) {
      const nestedResolved = resolveResourceId(nestedBranch);
      if (nestedResolved) return nestedResolved;
    }

    if (fallbackBusinessId !== undefined && fallbackBusinessId !== null) {
      const fallbackResolved = resolveResourceId({
        business_id: fallbackBusinessId,
      });
      if (fallbackResolved) return fallbackResolved;
      const fallbackStr = String(fallbackBusinessId);
      if (displayedBranchIds.some((id) => String(id) === fallbackStr)) {
        return fallbackStr;
      }
    }

    if (selectedBranchId !== "all") return String(selectedBranchId);

    return "";
  };

  const resolveBlockIdFromSlot = (slot: any): string | null => {
    if (!slot) return null;
    const candidate =
      slot.blockId ||
      slot.block_id ||
      slot.blockID ||
      slot.id ||
      slot._id ||
      slot.appointment_block_id ||
      slot.appointmentBlockId ||
      slot.block?.id ||
      slot.block?._id ||
      null;
    return candidate !== null &&
      candidate !== undefined &&
      String(candidate).trim() !== ""
      ? String(candidate)
      : null;
  };

  const buildSlotEvents = (slotsData: any[], businessId: any) => {
    const slotEvents: any[] = [];

    (Array.isArray(slotsData) ? slotsData : []).forEach(
      (slot: any, idx: number) => {
        const slotDateRaw =
          slot.date || slot.start_date || slot.slot_date || slot.day;
        const slotStartRaw =
          slot.start_time || slot.time_start || slot.from || slot.start;
        const slotEndRaw =
          slot.end_time || slot.time_end || slot.to || slot.end;

        const startAtIso = slot.start_at || slot.startAt;
        const endAtIso = slot.end_at || slot.endAt;

        let startDateObj: Date | null = null;
        let endDateObj: Date | null = null;

        if (startAtIso && endAtIso) {
          const parsedStart = new Date(startAtIso);
          const parsedEnd = new Date(endAtIso);
          if (!isNaN(parsedStart.getTime()) && !isNaN(parsedEnd.getTime())) {
            startDateObj = parsedStart;
            endDateObj = parsedEnd;
          }
        }

        let slotDate = slotDateRaw;
        let slotStart = slotStartRaw;
        let slotEnd = slotEndRaw;

        if (startDateObj && endDateObj) {
          slotDate = toDateInput(startDateObj);
          slotStart = toTimeInput(startDateObj);
          slotEnd = toTimeInput(endDateObj);
        }

        if (!slotDate || !slotStart || !slotEnd) return;

        if (!startDateObj)
          startDateObj = combineDateAndTime(slotDate, slotStart);
        if (!endDateObj) endDateObj = combineDateAndTime(slotDate, slotEnd);

        const startIso = startDateObj ? startDateObj.toISOString() : null;
        const endIso = endDateObj ? endDateObj.toISOString() : null;
        const branchId = resolveSlotBranchId(slot, businessId);

        if (!branchId) return;
        if (
          displayedBranchIds.length > 0 &&
          !displayedBranchIds.some((id) => String(id) === String(branchId))
        ) {
          return;
        }

        const blockId = resolveBlockIdFromSlot(slot);
        slotEvents.push({
          id:
            blockId !== null
              ? `slot_blocked_${blockId}`
              : `slot_blocked_${businessId}_${branchId}_${idx}_${slotDate}_${slotStart}`,
          title: "Closed",
          start: startIso || `${slotDate}T${slotStart || "00:00"}:00`,
          end: endIso || `${slotDate}T${slotEnd || "00:00"}:00`,
          backgroundColor: "rgba(75, 85, 99, 0.92)",
          borderColor: "rgba(55, 65, 81, 1)",
          textColor: "white",
          display: "background",
          editable: false,
          overlap: true,
          extendedProps: {
            raw: slot,
            slot: true,
            blocked: true,
            branchId,
            blockId: blockId || undefined,
          },
        });
      },
    );

    return slotEvents;
  };

  const lastDatesSetRef = React.useRef<{ start: number; end: number }>({
    start: 0,
    end: 0,
  });

  const handleDatesSet = (arg: any) => {
    try {
      const startMs =
        arg && arg.start instanceof Date ? arg.start.getTime() : 0;
      const endMs = arg && arg.end instanceof Date ? arg.end.getTime() : 0;
      if (
        startMs &&
        endMs &&
        (lastDatesSetRef.current.start !== startMs ||
          lastDatesSetRef.current.end !== endMs)
      ) {
        lastDatesSetRef.current = { start: startMs, end: endMs };
        setVisibleStart(new Date(startMs));
        setVisibleEnd(new Date(endMs));
      }

      if (arg && arg.view && arg.view.type) {
        const viewType = arg.view.type;
        let mappedView: "day" | "week" | "month" = "week";
        if (viewType === "dayGridMonth") mappedView = "month";
        else if (viewType === "timeGridWeek") mappedView = "week";
        else if (viewType === "timeGridDay") mappedView = "day";
        setCurrentView((prev) => (prev === mappedView ? prev : mappedView));
      }
    } catch (e) {}
  };

  const formatDateTimeShort = (d: Date | null | undefined) => {
    if (!d) return "";
    try {
      const dt = d instanceof Date ? d : new Date(d as any);
      if (isNaN(dt.getTime())) return "";
      return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(dt.getDate()).padStart(2, "0")} ${String(
        dt.getHours(),
      ).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
    } catch (e) {
      return "";
    }
  };

  const addMinutesToTime = (timeStr: string, minutesToAdd: number) => {
    if (!timeStr) return timeStr;
    const parts = timeStr.split(":");
    if (parts.length < 2) return timeStr;
    const hh = parseInt(parts[0], 10);
    const mm = parseInt(parts[1], 10);
    if (isNaN(hh) || isNaN(mm)) return timeStr;
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    d.setMinutes(d.getMinutes() + Number(minutesToAdd || 0));
    return toTimeInput(d);
  };

  const findServiceDuration = (serviceId: string) => {
    if (!serviceId) return null;

    // Debug: log what we're searching for
    const availableServices = Array.isArray(services) ? services : [];

    const svcRaw = availableServices.find(
      (s: any) =>
        String(s.id || s._id || s.service_id || "") === String(serviceId),
    );

    const svc: any = svcRaw as any;
    if (!svc) {
      console.warn(
        `Service not found in memory: ${serviceId}. Available services:`,
        availableServices.map((s: any) => ({
          id: s.id || s._id,
          name: s.name,
          title: s.title,
          duration: s.duration,
        })),
      );
      return null;
    }

    console.log(`Looking up service: ${serviceId}`, svc);

    // Strategy 1: Check for explicit duration field
    let val = svc.duration ?? svc.length ?? svc.minutes ?? null;
    if (val !== undefined && val !== null) {
      const num = Number(val);
      if (!isNaN(num) && num > 0) {
        console.log(
          `Found service duration (explicit field): ${serviceId} -> ${num} minutes`,
        );
        return num;
      }
    }

    // Get the service name/title for parsing
    const text = String(
      svc.name || svc.title || svc.label || svc.service_name || "",
    ).trim();

    if (text) {
      console.log(`Service name/title: "${text}"`);

      // Strategy 2: Try to parse the entire name as a number first (e.g., "60" or "90")
      const plainNumber = Number(text);
      if (!isNaN(plainNumber) && plainNumber > 0) {
        console.log(
          `Found service duration (plain number): ${serviceId} -> ${plainNumber} minutes`,
        );
        return plainNumber;
      }

      const textLower = text.toLowerCase();

      // Strategy 3: Try hour format (e.g., "1h", "1.5h", "2h")
      const hourMatch = textLower.match(/(\d+(?:[.,]\d+)?)\s*h(?:our)?s?/);
      if (hourMatch) {
        const asNum = Number(hourMatch[1].replace(",", "."));
        if (!isNaN(asNum) && asNum > 0) {
          const mins = Math.round(asNum * 60);
          console.log(
            `Found service duration (hour format): ${serviceId} -> ${mins} minutes (from ${asNum}h)`,
          );
          return mins;
        }
      }

      // Strategy 4: Try minute format with "min", "mins", "m" suffix (e.g., "60m", "90min")
      const minuteMatch = textLower.match(/(\d+)\s*(?:m|min|mins|minutes)?/);
      if (minuteMatch) {
        const asNum = Number(minuteMatch[1]);
        if (!isNaN(asNum) && asNum > 0) {
          // Make sure this isn't matching a small number that's part of something else
          if (asNum >= 15) {
            // Only accept if 15 minutes or more (reasonable minimum)
            console.log(
              `Found service duration (minute format): ${serviceId} -> ${asNum} minutes`,
            );
            return asNum;
          }
        }
      }

      // Strategy 5: Specific text patterns
      if (
        textLower.includes("1 hour") ||
        textLower.includes("one hour") ||
        textLower.includes("60m") ||
        textLower.includes("60")
      ) {
        console.log(
          `Found service duration (1 hour pattern): ${serviceId} -> 60 minutes`,
        );
        return 60;
      }
      if (
        textLower.includes("1.5 hour") ||
        textLower.includes("90") ||
        textLower.includes("90m") ||
        textLower.includes("1 hour 30") ||
        textLower.includes("1h30")
      ) {
        console.log(
          `Found service duration (1.5 hour pattern): ${serviceId} -> 90 minutes`,
        );
        return 90;
      }
      if (
        textLower.includes("2 hour") ||
        textLower.includes("two hour") ||
        textLower.includes("120") ||
        textLower.includes("120m") ||
        textLower.includes("2h")
      ) {
        console.log(
          `Found service duration (2 hour pattern): ${serviceId} -> 120 minutes`,
        );
        return 120;
      }
      if (textLower.includes("30") || textLower.includes("30m")) {
        console.log(
          `Found service duration (30 min pattern): ${serviceId} -> 30 minutes`,
        );
        return 30;
      }
      if (textLower.includes("45") || textLower.includes("45m")) {
        console.log(
          `Found service duration (45 min pattern): ${serviceId} -> 45 minutes`,
        );
        return 45;
      }
    }

    console.warn(`Could not determine duration for service: ${serviceId}`, svc);
    return null;
  };

  // Async version that can fetch services if needed
  const findServiceDurationAsync = async (
    serviceId: string,
    branchId?: string,
  ): Promise<number | null> => {
    if (!serviceId) return null;

    console.log(
      `findServiceDurationAsync: Looking for service ${serviceId} in branch ${branchId}`,
    );

    // First try with current services in memory
    const inMemoryResult = findServiceDuration(serviceId);
    if (inMemoryResult !== null) {
      return inMemoryResult;
    }

    // If not found and branchId provided, fetch services for that branch
    if (branchId) {
      try {
        console.log(
          `Service not in memory, fetching services for branch ${branchId}`,
        );
        const { getAllServices } = await import("../services/ServicesService");
        const fetchedServices = (await getAllServices(String(branchId))) as any;

        let serviceList: any[] = [];
        if (Array.isArray(fetchedServices)) {
          serviceList = fetchedServices;
        } else if (
          fetchedServices?.services &&
          Array.isArray(fetchedServices.services)
        ) {
          serviceList = fetchedServices.services;
        } else if (
          fetchedServices?.data &&
          Array.isArray(fetchedServices.data)
        ) {
          serviceList = fetchedServices.data;
        }

        console.log(
          `Fetched ${serviceList.length} services for branch ${branchId}`,
        );

        // Try to find the service in fetched list
        const foundService = serviceList.find(
          (s: any) =>
            String(s.id || s._id || s.service_id || "") === String(serviceId),
        );

        if (foundService) {
          console.log(`Found service in API response:`, foundService);

          // Try explicit duration first
          let val =
            foundService.duration ??
            foundService.length ??
            foundService.minutes ??
            null;
          if (val !== undefined && val !== null) {
            const num = Number(val);
            if (!isNaN(num) && num > 0) {
              console.log(
                `Service duration from API (explicit): ${num} minutes`,
              );
              return num;
            }
          }

          // Try parsing name as number
          const serviceName = String(
            foundService.name || foundService.title || "",
          ).trim();
          if (serviceName) {
            const plainNum = Number(serviceName);
            if (!isNaN(plainNum) && plainNum > 0) {
              console.log(
                `Service duration from API (name): ${plainNum} minutes`,
              );
              return plainNum;
            }
          }
        } else {
          console.warn(
            `Service ${serviceId} not found in API response for branch ${branchId}`,
          );
        }
      } catch (error) {
        console.error(`Error fetching services for branch ${branchId}:`, error);
      }
    }

    console.warn(
      `Could not determine duration for service ${serviceId} even after API fetch`,
    );
    return null;
  };

  // Helper function to extract duration from appointment data
  const getAppointmentDuration = (raw: any): number | null => {
    // Try to get duration from service inside the appointment
    if (raw.services && Array.isArray(raw.services)) {
      for (const svc of raw.services) {
        if (typeof svc === "object") {
          // First try explicit duration field
          const duration = svc.duration ?? svc.minutes ?? svc.length;
          if (duration && !isNaN(Number(duration)) && Number(duration) > 0) {
            console.log(
              "getAppointmentDuration: Found from service.duration:",
              duration,
            );
            return Number(duration);
          }

          // Then try parsing from service name/title
          const serviceName = String(svc.name || svc.title || "").trim();
          if (serviceName) {
            const plainNum = Number(serviceName);
            if (!isNaN(plainNum) && plainNum > 0) {
              console.log(
                "getAppointmentDuration: Found from service name:",
                plainNum,
              );
              return plainNum;
            }
          }
        }
      }
    }

    // Try to get duration from service object
    if (raw.service && typeof raw.service === "object") {
      const duration =
        raw.service.duration ?? raw.service.minutes ?? raw.service.length;
      if (duration && !isNaN(Number(duration)) && Number(duration) > 0) {
        console.log(
          "getAppointmentDuration: Found from service field:",
          duration,
        );
        return Number(duration);
      }

      // Try parsing service name
      const serviceName = String(
        raw.service.name || raw.service.title || "",
      ).trim();
      if (serviceName) {
        const plainNum = Number(serviceName);
        if (!isNaN(plainNum) && plainNum > 0) {
          console.log(
            "getAppointmentDuration: Found from service name:",
            plainNum,
          );
          return plainNum;
        }
      }
    }

    // Try to calculate from existing start/end time
    const rawStartTime =
      raw.time_start ||
      raw.start_time ||
      raw.timeStart ||
      raw.startTime ||
      raw.time;
    const rawEndTime =
      raw.time_end || raw.end_time || raw.timeEnd || raw.endTime || raw.end;
    if (rawStartTime && rawEndTime) {
      try {
        const [startH, startM] = String(rawStartTime).split(":").map(Number);
        const [endH, endM] = String(rawEndTime).split(":").map(Number);

        if (!isNaN(startH) && !isNaN(startM) && !isNaN(endH) && !isNaN(endM)) {
          const startMin = startH * 60 + startM;
          const endMin = endH * 60 + endM;
          const duration =
            endMin > startMin ? endMin - startMin : endMin + 1440 - startMin;

          if (duration > 0) {
            console.log(
              "getAppointmentDuration: Calculated from times:",
              duration,
            );
            return duration;
          }
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }

    return null;
  };

  const isClosedSlotAt = (date: Date, branchId?: string) => {
    const targetMs = date.getTime();
    const branchKey = branchId ? String(branchId) : "";
    return (Array.isArray(events) ? events : []).some((ev: any) => {
      const raw = ev.extendedProps?.raw || {};
      const eventBranchId = ev.extendedProps?.branchId;
      if (branchKey && (!eventBranchId || String(eventBranchId) !== branchKey))
        return false;
      const isClosed =
        ev.title === "Closed" ||
        ev.extendedProps?.blocked ||
        ev.extendedProps?.slot ||
        raw.blocked ||
        raw.slot ||
        raw.closed ||
        raw.is_blocked ||
        raw.closed === 1 ||
        raw.is_blocked === 1;
      if (!isClosed) return false;

      const startVal = ev.start;
      const endVal = ev.end;
      const startDate =
        startVal instanceof Date
          ? startVal
          : startVal
            ? new Date(startVal)
            : null;
      if (!startDate || isNaN(startDate.getTime())) return false;
      const endDate =
        endVal instanceof Date
          ? endVal
          : endVal
            ? new Date(endVal)
            : new Date(startDate.getTime() + 60 * 60000);
      const startMs = startDate.getTime();
      const endMs = endDate.getTime();
      return targetMs >= startMs && targetMs < endMs;
    });
  };

  const isClosedSlotEvent = (ev: any) => {
    if (!ev) return false;
    const raw = ev.extendedProps?.raw || {};
    return (
      ev.title === "Closed" ||
      ev.extendedProps?.blocked ||
      ev.extendedProps?.slot ||
      raw.blocked ||
      raw.slot ||
      raw.closed ||
      raw.is_blocked ||
      raw.closed === 1 ||
      raw.is_blocked === 1
    );
  };

  const openCreateDialogForDate = (clicked: Date, branchIdForForm: string) => {
    const mins = clicked.getMinutes();
    const rounded = Math.floor(mins / 15) * 15;
    const start = new Date(clicked);
    start.setMinutes(rounded);

    if (isClosedSlotAt(start, branchIdForForm)) {
      toast.error("This time is closed by admin.");
      lastDateClickRef.current = { time: 0, iso: null };
      return;
    }

    setFormDate(toDateInput(clicked));
    setFormBranchId(branchIdForForm);

    // Fetch branch-specific services
    const fetchBranchSpecificServices = async () => {
      try {
        const unitId = branchIdForForm;
        const response = (await getAllServices(unitId)) as any;

        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching branch services:", error);
      }
    };
    fetchBranchSpecificServices();

    const end = new Date(start.getTime() + 60 * 60000);
    setFormTimeStart(toTimeInput(start));
    setFormTimeEnd(toTimeInput(end));
    setBlockDate(toDateInput(clicked));
    setBlockStart(toTimeInput(start));
    setBlockEnd(toTimeInput(end));
    setBlockAction("close");
    setSelectedBlockId(null);
    try {
      if (formServiceId) {
        const dur = findServiceDuration(formServiceId);
        if (dur) {
          const desired = addMinutesToTime(toTimeInput(start), dur);
          setFormTimeEnd(
            clampEndTime(toTimeInput(start), desired, MAX_APPOINTMENT_MINUTES),
          );
        }
      }
    } catch (e) {}
    setFormNotes("");
    setFormCustomerName("");
    setFormClientId("");
    setFormServiceId("");
    setFormPromoId("");
    setFormStatus("PENDING");
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingEventStarted(false);
    setAppointmentDialogTab(0);
    setShowBranchFieldInCreate(false);
    setAddDialogOpen(true);
    lastDateClickRef.current = { time: 0, iso: null };
  };

  const openCreateDialogEmpty = () => {
    setFormDate(toDateInput(new Date()));
    setFormTimeStart("");
    setFormTimeEnd("");
    setFormNotes("");
    setFormCustomerName("");
    setFormClientId("");
    setFormServiceId("");
    setFormPromoId("");
    setFormStatus("PENDING");
    setFormBranchId("");
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingEventStarted(false);
    setAppointmentDialogTab(0);
    setShowBranchFieldInCreate(true);
    setAddDialogOpen(true);
    lastDateClickRef.current = { time: 0, iso: null };
  };
  const handleDateClick = (info: any, branchOverride?: string) => {
    const branchIdForForm =
      branchOverride && String(branchOverride).trim() !== ""
        ? String(branchOverride)
        : selectedBranchId !== "all"
          ? selectedBranchId
          : "";

    if (!branchIdForForm) {
      toast.warning(
        "Please select a specific branch before adding an appointment",
      );
      return;
    }
    const clicked: Date = info.date;
    if (isMobileInteraction) {
      openCreateDialogForDate(clicked, branchIdForForm);
      return;
    }
    const iso = clicked.toISOString();
    const now = Date.now();
    const prev = lastDateClickRef.current;
    if (prev.iso === iso && now - prev.time < 350) {
      openCreateDialogForDate(clicked, branchIdForForm);
    } else {
      lastDateClickRef.current = { time: now, iso };
    }
  };

  const lastEventClickRef = React.useRef<{ time: number; id: string | null }>({
    time: 0,
    id: null,
  });

  const openClosedSlotDialog = (ev: any) => {
    const raw = ev.extendedProps?.raw || {};
    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(ev.end || startD.getTime() + 60 * 60000);
    const safeStart = isNaN(startD.getTime()) ? new Date() : startD;
    const safeEnd = isNaN(endD.getTime())
      ? new Date(safeStart.getTime() + 60 * 60000)
      : endD;
    const rawBranchId =
      ev.extendedProps?.branchId ||
      raw.branch_id ||
      raw.branchId ||
      raw.business_id ||
      raw.businessId;
    const branchIdStr = rawBranchId ? String(rawBranchId) : "";
    if (branchIdStr) {
      setFormBranchId(branchIdStr);
      if (selectedBranchId !== "all") {
        setSelectedBranchId(branchIdStr);
      }
    } else {
      setFormBranchId(selectedBranchId || "all");
    }
    setBlockDate(toDateInput(safeStart));
    setBlockStart(toTimeInput(safeStart));
    setBlockEnd(toTimeInput(safeEnd));
    setBlockAction("open");
    setSelectedBlockId(
      (ev.extendedProps?.blockId && String(ev.extendedProps.blockId)) ||
        resolveBlockIdFromSlot(raw),
    );
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingEventStarted(false);
    setAppointmentDialogTab(1);
    setAddDialogOpen(true);
  };

  const openEditDialogForEvent = async (ev: any, id: string) => {
    const raw = ev.extendedProps?.raw || {};

    // Class sessions have a roster, a coach and their own cancel flow, so they
    // open the class session panel instead of the appointment editor.
    if (isClassEvent(ev)) {
      setClassSessionId(String(raw?.id || ev.extendedProps?.originalId || id));
      return;
    }
    const invoiceNumberFromEvent = pickNonEmptyString(
      ev.extendedProps?.invoiceNumber,
      ev.extendedProps?.invoice_number,
      raw?.invoice?.Invoice_number,
      raw?.invoice?.invoice_number,
      raw?.Invoice_number,
      raw?.invoice_number,
      raw?.invoiceNumber,
      raw?.invoice_number,
    );

    const invoiceIdFromEvent = pickNonEmptyString(
      ev.extendedProps?.invoiceId,
      ev.extendedProps?.invoice_id,
      raw?.invoiceId,
      raw?.invoice_id,
      raw?.invoice?.id,
    );

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(ev.end || new Date(startD.getTime() + 60 * 60000));
    // Allow editing past appointments; just track whether it already started
    try {
      setEditingEventStarted(startD.getTime() <= Date.now());
    } catch (e) {
      setEditingEventStarted(false);
    }
    setSelectedBlockId(null);
    const rawWithInvoice = { ...raw } as any;
    const uiBranchId = ev.extendedProps?.branchId;
    if (uiBranchId) {
      rawWithInvoice._uiBranchId = String(uiBranchId);
    }
    if (
      invoiceNumberFromEvent &&
      !rawWithInvoice?.invoice_number &&
      !rawWithInvoice?.Invoice_number
    ) {
      rawWithInvoice.invoice_number = invoiceNumberFromEvent;
    }
    if (
      invoiceIdFromEvent &&
      !rawWithInvoice?.invoice_id &&
      !rawWithInvoice?.invoiceId
    ) {
      rawWithInvoice.invoice_id = invoiceIdFromEvent;
    }
    setEditingEventRaw(rawWithInvoice);
    setFormDate(toDateInput(startD));
    setFormTimeStart(toTimeInput(startD));
    setFormTimeEnd(toTimeInput(endD));
    setFormClientId(
      String(
        raw.client_id ||
          raw.clientId ||
          raw.client?.id ||
          raw.client?._id ||
          "",
      ),
    );
    const promoVal =
      raw.promo_id ||
      raw.promoId ||
      raw.promo?.id ||
      raw.promo?.promo_id ||
      raw.promo?.promoId ||
      "";
    setFormPromoId(String(promoVal || ""));
    const svcVal =
      raw.service?.id ||
      raw.service_id ||
      raw.serviceId ||
      (raw.services && raw.services[0]
        ? typeof raw.services[0] === "object"
          ? raw.services[0].id ||
            raw.services[0]._id ||
            raw.services[0].service_id
          : raw.services[0]
        : "");
    setFormServiceId(String(svcVal || ""));
    try {
      const dur = findServiceDuration(String(svcVal || ""));
      if (dur) setFormTimeEnd(addMinutesToTime(toTimeInput(startD), dur));
    } catch (e) {}
    setFormNotes(raw.notes || raw.text || "");
    setFormCustomerName((raw.patient && raw.patient.name) || "");
    setFormStatus(
      deriveAppointmentStatus(
        raw.status || raw.state || "PENDING",
        startD,
        endD,
      ),
    );
    try {
      // Prefer explicit unit/branch id from the appointment
      const rawUnitId =
        raw.unit_id ||
        raw.unitId ||
        raw.unit?.id ||
        raw.branch_id ||
        raw.branchId ||
        raw.branch?.id;
      if (rawUnitId) {
        const branchIdStr = String(rawUnitId);
        setFormBranchId(branchIdStr);

        try {
          const response = (await getAllServices(branchIdStr)) as any;
          let servicesData: any[] = [];
          if (response?.services) servicesData = response.services;
          else if (response?.data?.services)
            servicesData = response.data.services;
          else if (response?.data && Array.isArray(response.data))
            servicesData = response.data;
          else if (Array.isArray(response)) servicesData = response;
          setServices(Array.isArray(servicesData) ? servicesData : []);
        } catch (error) {
          console.error("Error fetching branch services:", error);
        }
      } else {
        const rawBusinessId =
          raw.business_id || raw.businessId || raw.business || null;
        let foundBranch = null;
        if (Array.isArray(branches)) {
          foundBranch = branches.find(
            (b: any) =>
              String(b.business_id) === String(rawBusinessId) ||
              String(b.id) === String(rawBusinessId),
          );
        }
        if (foundBranch) {
          const branchId = String(foundBranch.id);
          setFormBranchId(branchId);

          // Fetch branch-specific services
          try {
            const response = (await getAllServices(branchId)) as any;
            let servicesData = [];
            if (response && response.services) {
              servicesData = response.services;
            } else if (response && response.data && response.data.services) {
              servicesData = response.data.services;
            } else if (
              response &&
              response.data &&
              Array.isArray(response.data)
            ) {
              servicesData = response.data;
            } else if (Array.isArray(response)) {
              servicesData = response;
            }
            setServices(Array.isArray(servicesData) ? servicesData : []);
          } catch (error) {
            console.error("Error fetching branch services:", error);
          }
        } else {
          setFormBranchId(selectedBranchId || "all");
        }
      }
    } catch (e) {
      setFormBranchId(selectedBranchId || "all");
    }
    setEditingEventId(id);
    setAppointmentDialogTab(0);
    setAddDialogOpen(true);
    lastEventClickRef.current = { time: 0, id: null };
  };

  const handleEventClick = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const originalId = ev.extendedProps?.originalId || id;
    const original =
      events.find((e) => String(e.id) === String(originalId)) || ev;
    const now = Date.now();
    const prev = lastEventClickRef.current;
    if (isClosedSlotEvent(original)) {
      if (
        !isMobileInteraction &&
        prev.id === originalId &&
        now - prev.time < 350
      ) {
        return;
      }
      if (!isMobileInteraction) {
        lastEventClickRef.current = { time: now, id: originalId };
      } else {
        lastEventClickRef.current = { time: 0, id: null };
      }
      openClosedSlotDialog(original);
      return;
    }
    // A class tile opens its panel on a single press — there is nothing to
    // double-click into, the panel is the whole interaction.
    if (isClassEvent(original)) {
      await openEditDialogForEvent(original, originalId);
      return;
    }
    // For split/overnight segments, open editor immediately on single click
    if (original.extendedProps?.segmentPart) {
      await openEditDialogForEvent(original, originalId);
      return;
    }
    if (isMobileInteraction) {
      await openEditDialogForEvent(original, originalId);
      return;
    }
    if (prev.id === originalId && now - prev.time < 350) {
      await openEditDialogForEvent(original, originalId);
    } else {
      lastEventClickRef.current = { time: now, id: originalId };
    }
  };

  const handleEventDrop = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const raw = ev.extendedProps?.raw || {};

    // Moving one session of a class would desync it from its series and its
    // roster's bookings, so rescheduling stays in the Classes page.
    if (isClassEvent(ev)) {
      toast.error(
        "Class sessions cannot be moved from the calendar. Manage them from Classes.",
      );
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(
            (ev.end || new Date(startD.getTime() + 60 * 60000)).getTime(),
          );

    try {
      const now = new Date();
      if (endD.getTime() < now.getTime()) {
        toast.error(
          "Cannot move appointments that already ended. Move reverted.",
        );
        if (info && typeof info.revert === "function") info.revert();
        return;
      }
    } catch (e) {}

    const startStr = formatTimeForPayload(startD);
    const endStr = formatTimeForPayload(endD);
    const diff = minutesBetweenTimes(startStr, endStr);
    if (diff > MAX_APPOINTMENT_MINUTES) {
      toast.error("Maximum appointment duration is 3 hours. Move reverted.");
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const businessId =
      raw.business_id ||
      raw.businessId ||
      (branches && branches[0] && (branches[0].business_id || branches[0].id));
    if (!businessId) {
      console.error("No business id for dropped event");
      info.revert();
      return;
    }

    const unitId = raw.unit_id || raw.unitId || raw.unit?.id;
    const serviceId =
      raw.service?.id ||
      raw.service_id ||
      raw.serviceId ||
      (Array.isArray(raw.services) && raw.services[0]
        ? typeof raw.services[0] === "object"
          ? raw.services[0].id ||
            raw.services[0]._id ||
            raw.services[0].service_id ||
            raw.services[0].serviceId
          : raw.services[0]
        : undefined);

    const payload: any = {
      unit_id: unitId,
      service_id: serviceId,
      start_at: startD.toISOString(),
      end_at: endD.toISOString(),
      notes: raw.notes || raw.text || "",
      status: deriveAppointmentStatus(
        raw.status || raw.state || "PENDING",
        startD,
        endD,
      ),
    };

    try {
      const confirmMove = window.confirm(
        "Do you want to move this appointment to the new time?",
      );
      if (!confirmMove) {
        if (info && typeof info.revert === "function") info.revert();
        return;
      }

      await updateAppointment(id, payload);
      try {
        const unitIdForFetch =
          ev.extendedProps?.branchId ||
          raw.unit_id ||
          raw.unitId ||
          raw.unit?.id ||
          raw.branch_id ||
          raw.branchId ||
          raw.branch?.id ||
          (selectedBranchId !== "all" ? selectedBranchId : null);

        const dateParam = formatDateForPayload(visibleStart).replace(
          /\//g,
          "-",
        );

        const appointments = unitIdForFetch
          ? await fetchAllUnitAppointmentsForDate(
              String(unitIdForFetch),
              dateParam,
            )
          : [];
        const enrichedAppointments =
          await enrichZeroInvoiceAppointments(appointments);
        const mapped = enrichedAppointments.map(mapAppointmentToEvent);

        try {
          const slotId = unitIdForFetch;
          const slotResp = await getSlots({
            unit_id: slotId,
            start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
            end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          const slotEvents = buildSlotEvents(slotsData, slotId);

          setEvents([...mapped, ...slotEvents]);
          // Force calendar re-mount after events update
          setCalendarKey((prev) => prev + 1);
        } catch (slotErr) {
          console.warn("Failed to fetch slots after drop", slotErr);
          setEvents(mapped);
          // Force calendar re-mount even if slots fail
          setCalendarKey((prev) => prev + 1);
        }
      } catch (e) {
        console.warn("Refresh after drop failed", e);
      }
    } catch (err) {
      console.error("Error updating appointment after drop", err);
      showErrorToast(
        getErrorMessage(err, "Could not move the appointment. Change reverted."),
      );
      if (info && typeof info.revert === "function") info.revert();
    }
  };

  const handleEventResize = async (info: any) => {
    const ev = info.event;
    const id = String(ev.id);
    const raw = ev.extendedProps?.raw || {};

    if (isClassEvent(ev)) {
      toast.error(
        "Class session length is set by the class. Change it from Classes.",
      );
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const startD = ev.start instanceof Date ? ev.start : new Date(ev.start);
    const endD =
      ev.end instanceof Date
        ? ev.end
        : new Date(
            (ev.end || new Date(startD.getTime() + 60 * 60000)).getTime(),
          );

    try {
      const now = new Date();
      if (endD.getTime() < now.getTime()) {
        toast.error(
          "Cannot resize appointments that already ended. Resize reverted.",
        );
        if (info && typeof info.revert === "function") info.revert();
        return;
      }
    } catch (e) {}

    const startStr = formatTimeForPayload(startD);
    const endStr = formatTimeForPayload(endD);
    const diff = minutesBetweenTimes(startStr, endStr);
    if (diff > MAX_APPOINTMENT_MINUTES) {
      toast.error("Maximum appointment duration is 3 hours. Resize reverted.");
      if (info && typeof info.revert === "function") info.revert();
      return;
    }

    const businessId =
      raw.business_id ||
      raw.businessId ||
      (branches && branches[0] && (branches[0].business_id || branches[0].id));
    if (!businessId) {
      console.error("No business id for resized event");
      info.revert();
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const unitId = raw.unit_id || raw.unitId || raw.unit?.id;
    const serviceId =
      raw.service?.id ||
      raw.service_id ||
      raw.serviceId ||
      (Array.isArray(raw.services) && raw.services[0]
        ? typeof raw.services[0] === "object"
          ? raw.services[0].id ||
            raw.services[0]._id ||
            raw.services[0].service_id ||
            raw.services[0].serviceId
          : raw.services[0]
        : undefined);

    const payload: any = {
      unit_id: unitId,
      service_id: serviceId,
      start_at: startD.toISOString(),
      end_at: endD.toISOString(),
      notes: raw.notes || raw.text || "",
      status: deriveAppointmentStatus(
        raw.status || raw.state || "PENDING",
        startD,
        endD,
      ),
    };

    try {
      await updateAppointment(id, payload);
      try {
        const unitIdForFetch =
          ev.extendedProps?.branchId ||
          raw.unit_id ||
          raw.unitId ||
          raw.unit?.id ||
          raw.branch_id ||
          raw.branchId ||
          raw.branch?.id ||
          (selectedBranchId !== "all" ? selectedBranchId : null);

        const dateParam = formatDateForPayload(visibleStart).replace(
          /\//g,
          "-",
        );

        const appointments = unitIdForFetch
          ? await fetchAllUnitAppointmentsForDate(
              String(unitIdForFetch),
              dateParam,
            )
          : [];
        const enrichedAppointments =
          await enrichZeroInvoiceAppointments(appointments);
        const mapped = enrichedAppointments.map(mapAppointmentToEvent);
        try {
          const slotId = unitIdForFetch;
          const slotResp = await getSlots({
            unit_id: slotId,
            start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
            end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          const slotEvents = buildSlotEvents(slotsData, slotId);

          setEvents([...mapped, ...slotEvents]);
          // Force calendar re-mount after events update
          setCalendarKey((prev) => prev + 1);
        } catch (slotErr) {
          console.warn("Failed to fetch slots after resize", slotErr);
          setEvents(mapped);
          // Force calendar re-mount even if slots fail
          setCalendarKey((prev) => prev + 1);
        }
      } catch (e) {
        console.warn("Refresh after resize failed", e);
      }
    } catch (err) {
      console.error("Error updating appointment after resize", err);
      showErrorToast(
        getErrorMessage(err, "Could not resize the appointment. Change reverted."),
      );
      if (info && typeof info.revert === "function") info.revert();
    }
  };

  useEffect(() => {
    // sync form branch id with selected branch, unless we're editing a specific appointment
    if (!editingEventId) setFormBranchId(selectedBranchId || "all");
  }, [selectedBranchId, editingEventId]);

  useEffect(() => {
    if (currentView === "day") return;
    if (selectedBranchId === "all" && branches.length > 0) {
      setSelectedBranchId(String(branches[0].id));
    }
  }, [currentView, selectedBranchId, branches]);

  useEffect(() => {
    const normalizeDate = (d: Date) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    let start = normalizeDate(activeDate);
    let end = normalizeDate(activeDate);

    if (currentView === "week") {
      const weekStart = normalizeDate(activeDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      start = weekStart;
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      end = weekEnd;
    } else if (currentView === "month") {
      const year = activeDate.getFullYear();
      const month = activeDate.getMonth();
      start = new Date(year, month, 1);
      end = new Date(year, month + 1, 0);
    }

    setVisibleStart(start);
    setVisibleEnd(end);
  }, [activeDate, currentView]);

  useEffect(() => {
    const calendars = Object.values(branchCalendarRefs.current);
    calendars.forEach((calendar) => {
      const api = calendar?.getApi?.();
      if (!api) return;
      if (api.view && api.view.type !== viewType) {
        api.changeView(viewType);
      }
      api.gotoDate(activeDate);
    });
  }, [activeDate, viewType, displayedBranchKey]);

  useEffect(() => {
    const root = schedulerWrapperRef.current;
    if (!root) return;
    const scrollers = Array.from(
      root.querySelectorAll(".fc-timegrid-body .fc-scroller"),
    ) as HTMLElement[];
    if (scrollers.length < 1) return;
    let isSyncing = false;
    const onScroll = (event: Event) => {
      if (isSyncing) return;
      isSyncing = true;
      const target = event.target as HTMLElement;
      scrollers.forEach((scroller) => {
        if (scroller !== target) scroller.scrollTop = target.scrollTop;
      });
      isSyncing = false;
    };
    scrollers.forEach((scroller) =>
      scroller.addEventListener("scroll", onScroll),
    );

    let wheelTargets: HTMLElement[] = [];
    let wheelHandler: ((event: WheelEvent) => void) | null = null;

    if (!isSingleBranch) {
      const axisScroller = root.querySelector(
        ".branch-col.axis-owner .fc-timegrid-body .fc-scroller",
      ) as HTMLElement | null;
      if (axisScroller) {
        wheelTargets = Array.from(
          root.querySelectorAll(
            ".branch-col:not(.axis-owner) .fc-timegrid-body .fc-scroller",
          ),
        ) as HTMLElement[];
        const handleWheel = (event: WheelEvent) => {
          if (!axisScroller) return;
          event.preventDefault();
          axisScroller.scrollTop += event.deltaY;
        };
        wheelTargets.forEach((scroller) =>
          scroller.addEventListener("wheel", handleWheel, { passive: false }),
        );
        wheelHandler = handleWheel;
      }
    }

    return () => {
      scrollers.forEach((scroller) =>
        scroller.removeEventListener("scroll", onScroll),
      );
      if (wheelHandler) {
        const handler = wheelHandler;
        wheelTargets.forEach((scroller) =>
          scroller.removeEventListener("wheel", handler),
        );
      }
    };
  }, [currentView, displayedBranchKey, calendarKey, isSingleBranch]);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        if (!venueId) {
          setBranches([]);
          return;
        }
        const response = await getAllBranches(venueId);
        let branchesData: any[] = [];
        if (response && response.units) branchesData = response.units;
        else if (response && response.data) branchesData = response.data;
        else if (Array.isArray(response)) branchesData = response;

        const validBranches = Array.isArray(branchesData) ? branchesData : [];
        setBranches(validBranches);

        // Fetch unit settings for all branches to calculate calendar start hour
        if (validBranches.length > 0) {
          const settingsPromises = validBranches.map(async (branch) => {
            try {
              const unitId = branch.id || branch._id || branch.unit_id;
              if (unitId) {
                const settings = await getUnitSettings(String(unitId));
                return { unitId: String(unitId), settings };
              }
            } catch (error) {
              console.warn(
                `Failed to fetch settings for unit ${branch.id}:`,
                error,
              );
            }
            return null;
          });

          const settingsResults = await Promise.all(settingsPromises);
          const validSettings = settingsResults.filter(
            (result) => result !== null,
          );

          // Store unit settings
          const settingsMap: Record<string, any> = {};
          validSettings.forEach((result) => {
            if (result) {
              settingsMap[result.unitId] = result.settings;
            }
          });
          setUnitSettings(settingsMap);

          // Helper function to parse time format like "08:00" or "8:00 AM"
          const parseTimeString = (time: string | undefined): number | null => {
            if (!time) return null;
            const match = time.match(/(\d{1,2}):(\d{2})/);
            if (match) {
              let hour = parseInt(match[1], 10);
              const isPM = time.toUpperCase().includes("PM") && hour !== 12;
              const isAM = time.toUpperCase().includes("AM") && hour === 12;
              if (isPM) hour = hour + 12;
              if (isAM) hour = 0;
              return hour;
            }
            return null;
          };

          // Calculate earliest opening time minus 1 hour
          if (validSettings.length > 0) {
            const openingHours = validSettings
              .map((result) => result?.settings?.open_time)
              .map(parseTimeString)
              .filter((hour) => hour !== null && !isNaN(hour)) as number[];

            const closingHours = validSettings
              .map((result) => {
                const openHour = parseTimeString(result?.settings?.open_time);
                const closeHour = parseTimeString(result?.settings?.close_time);
                if (closeHour === null) return null;
                if (openHour === null) return closeHour;
                // If close is earlier than (or equal to) open, treat as next-day close.
                return closeHour <= openHour ? closeHour + 24 : closeHour;
              })
              .filter((hour) => hour !== null && !isNaN(hour)) as number[];

            if (openingHours.length > 0) {
              const earliestHour = Math.min(...openingHours);
              setCalendarStartHour(Math.max(0, earliestHour - 1)); // Ensure not negative
            } else {
              setCalendarStartHour(0); // Default to midnight if no valid times
            }

            if (closingHours.length > 0) {
              const latestHour = Math.max(...closingHours);
              setCalendarEndHour(latestHour);
            } else {
              setCalendarEndHour(24); // Default to midnight if no closing times
            }
          } else {
            setCalendarStartHour(0); // Default to midnight if no settings
            setCalendarEndHour(24);
          }
        } else {
          setUnitSettings({});
          setCalendarStartHour(0);
          setCalendarEndHour(24);
        }
      } catch (err) {
        console.error("Error fetching branches:", err);
        setBranches([]);
        setUnitSettings({});
        setCalendarStartHour(0);
        setCalendarEndHour(24);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, [venueId]);

  // `clients` is a cache, not the whole directory: the first page of clients,
  // plus whatever the pickers searched for and the clients on the booking or
  // invoice being edited. Results are merged in, never replaced.
  const mergeOrgClients = React.useCallback((incoming: Client[]) => {
    if (incoming.length === 0) return;
    setClients((prev) => {
      const byId = new Map<string, Client>();
      (prev?.users || []).forEach((c) => byId.set(String(c.id || c._id || ""), c));
      incoming.forEach((c) => byId.set(String(c.id || c._id || ""), c));
      return { users: Array.from(byId.values()) };
    });
  }, []);

  const loadOrgClients = React.useCallback(
    async ({ search, ids }: { search?: string; ids?: string[] } = {}) => {
      if (!venueId) return;
      try {
        const response = await getClientsSelect({
          businessId: String(venueId),
          ids: ids && ids.length > 0 ? ids : undefined,
          search,
          // Only the ids are wanted when loading specific clients.
          limit: search !== undefined ? ORG_CLIENTS_SEARCH_LIMIT : ids?.length ? 1 : ORG_CLIENTS_INITIAL_LIMIT,
        });
        const list = Array.isArray(response?.content) ? response.content : [];
        mergeOrgClients(
          list
            .map((c: any) =>
              normalizeClientRecord({
                id: c?.id,
                name: c?.name,
                email: c?.email,
                phone: c?.mobile || c?.phone,
                mobile: c?.mobile || c?.phone,
                country_code: c?.country_code,
                label: c?.label,
                active:
                  c?.active ??
                  c?.is_active ??
                  c?.isActive ??
                  c?.verified ??
                  c?.status,
              }),
            )
            .filter((c: Client) => c.id),
        );
      } catch (error) {
        console.error("Error fetching organization clients:", error);
      }
    },
    [mergeOrgClients, venueId],
  );

  const fetchOrgClients = React.useCallback(
    async (preferredClient?: Client) => {
      if (preferredClient?.id) mergeOrgClients([preferredClient]);
      await loadOrgClients(
        preferredClient?.id ? { ids: [String(preferredClient.id)] } : {},
      );
    },
    [loadOrgClients, mergeOrgClients],
  );

  useEffect(() => {
    setClients({ users: [] });
    fetchOrgClients();
  }, [fetchOrgClients, venueId]);

  // Typing in a client picker fetches matches from the server; the picker
  // keeps filtering locally over the growing cache meanwhile.
  const clientSearchTimerRef = useRef<number | undefined>(undefined);
  const handleClientSearchInput = React.useCallback(
    (term: string) => {
      window.clearTimeout(clientSearchTimerRef.current);
      const trimmed = term.trim();
      if (trimmed.length < 2) return;
      clientSearchTimerRef.current = window.setTimeout(() => {
        void loadOrgClients({ search: trimmed });
      }, 300);
    },
    [loadOrgClients],
  );
  useEffect(() => () => window.clearTimeout(clientSearchTimerRef.current), []);

  // A booking or invoice can name clients outside the cached first page; load
  // those so their names show in the pickers and the loyalty preview.
  const cachedClientIds = React.useMemo(
    () => new Set((clients?.users || []).map((c) => String(c.id || c._id || ""))),
    [clients],
  );
  const neededClientIdsKey = React.useMemo(() => {
    const ids = [
      resolveClientIdValue(
        editingEventRaw?.client_id ||
          editingEventRaw?.clientId ||
          editingEventRaw?.client,
      ),
      formClientId,
      resolveClientIdValue(
        invoiceEditingRow?.client_id ||
          invoiceEditingRow?.invoice?.client_id ||
          invoiceEditingRow?.metadata?.clientOrgUserId ||
          invoiceEditingRow?.clientOrgUserId,
      ),
      ...(invoiceEditingRow
        ? extractInvoiceSharedClientIds(invoiceEditingRow)
        : []),
    ]
      .map((id) => String(id || "").trim())
      .filter(Boolean);
    return Array.from(new Set(ids)).sort().join(",");
    // resolveClientIdValue is a plain helper recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEventRaw, formClientId, invoiceEditingRow]);
  useEffect(() => {
    if (!neededClientIdsKey) return;
    const missing = neededClientIdsKey
      .split(",")
      .filter((id) => !cachedClientIds.has(id));
    if (missing.length > 0) void loadOrgClients({ ids: missing });
    // Only when the needed ids change; re-checking on every cache update
    // would retry ids the server does not know forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neededClientIdsKey, loadOrgClients]);

  useEffect(() => {
    fetchPromos()
      .then((data: any) => {
        const list: any[] = Array.isArray(data)
          ? data
          : data?.promos || data?.data || [];
        setPromos(
          list.map((p: any) => ({
            id: String(p.id || p._id || ""),
            code: String(p.code || ""),
            type: String(p.type || "").toUpperCase(),
            value: Number(p.value) || 0,
          })),
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchServices = async () => {
      setLoadingServices(true);
      try {
        if (!venueId) {
          console.error("No venue ID available for services");
          setServices([]);
          return;
        }
        let targetBranch;
        const branchList =
          Array.isArray(branches) && branches.length
            ? branches
            : branchesRef.current;
        if (selectedBranchId === "all" || !selectedBranchId) {
          targetBranch = branchList?.[0];
        } else {
          targetBranch =
            (branchList || []).find(
              (b) => String(b.id) === String(selectedBranchId),
            ) || branchList?.[0];
        }

        const branchId = targetBranch?.id;
        const businessId = targetBranch?.business_id;

        if (!businessId && !branchId) {
          console.error("No valid business ID or branch ID found for services");
          setServices([]);
          return;
        }

        // Use branch ID if available, otherwise use business ID
        const idToUse = branchId || businessId;
        const response = (await getAllServices(idToUse)) as any;
        console.log("Services fetched for branch:", idToUse, response);
        let servicesData = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }

        setServices(Array.isArray(servicesData) ? servicesData : []);

        const serviceOptions = (
          Array.isArray(servicesData) ? servicesData : []
        ).map((service) => ({
          key: service.id || service._id,
          label: service.name,
        }));
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, [venueId, selectedBranchId, branches]);

  useEffect(() => {
    if (selectedBranchId === "all") return;
    const branch = branches.find(
      (b) => String(b.id) === String(selectedBranchId),
    );
    if (branch && branch.calendar_embed) {
      setEmbedUrl(branch.calendar_embed);
    } else if (branch && branch.calendarId) {
      setEmbedUrl(
        `https://calendar.google.com/calendar/embed?src=${encodeURIComponent(
          branch.calendarId,
        )}&ctz=UTC`,
      );
    }
  }, [selectedBranchId, branches, visibleStart, visibleEnd]);

  /* const createInvoiceForAppointment = async (
    appointmentId: string,
    businessId: string,
    clientId: string | null,
    servicePrice: number,
    notes: string,
    serviceId?: string | null
  ) => {
    const invoiceNumber = Math.floor(Date.now() / 1000);
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const formattedAmount =
      typeof servicePrice === "number"
        ? servicePrice.toFixed(2)
        : String(servicePrice || "0.00");

    const now = new Date();
    const pad2 = (n: number) => String(n).padStart(2, "0");
    const formattedNow = `${now.getFullYear()}-${pad2(
      now.getMonth() + 1
    )}-${pad2(now.getDate())} ${pad2(now.getHours())}:${pad2(
      now.getMinutes()
    )}:${pad2(now.getSeconds())}`;

    const invoicePayload: any = {
      appointment_id: appointmentId,
      business_id: businessId,
      client_id: clientId,
      created_by: user.id || user._id || clientId || businessId,
      discount: 0,
      id: 1,
      invoice_number: invoiceNumber,
      net_amount: formattedAmount,
      Vat: 0,
      total_amount: formattedAmount,
      notes: notes || "",
      created_at: formattedNow,
      updated_at: formattedNow,
      payment_method_id: 2,
      status_id: 2,
      services: [
        {
          service_id: serviceId || formServiceId || null,
          quantity: 1,
          amount: formattedAmount,
          net_amount: formattedAmount,
          discount: 0,
          created_at: formattedNow,
          updated_at: formattedNow,
        },
      ],
    };
    try {
      await createInvoice(invoicePayload);
      console.log(
        "Invoice created successfully for appointment",
        appointmentId
      );
    } catch (invoiceErr) {
      console.error("Error creating invoice", invoiceErr);
    }
  };*/

  const exportSlotsToWhatsApp = async () => {
    if (!branches || branches.length === 0) {
      toast.error("No branches available to export slots from");
      return;
    }

    const exportDateRaw =
      activeDate instanceof Date ? activeDate : new Date(activeDate);
    const safeExportDate = !isNaN(exportDateRaw.getTime())
      ? exportDateRaw
      : new Date();
    const exportDateKey = toDateInput(safeExportDate);
    const today = new Date();
    const isToday = exportDateKey === toDateInput(today);
    const minStartMin = isToday
      ? today.getHours() * 60 + today.getMinutes()
      : 0;

    const start = formatDateForPayload(safeExportDate).replace(/\//g, "-");
    const end = start;

    const parseSlotsData = (slotResp: any): any[] => {
      let slotsData: any[] = [];
      if (!slotResp) slotsData = [];
      else if (slotResp.history && Array.isArray(slotResp.history))
        slotsData = slotResp.history;
      else if (slotResp.slots) slotsData = slotResp.slots;
      else if (slotResp.data && Array.isArray(slotResp.data))
        slotsData = slotResp.data;
      else if (Array.isArray(slotResp)) slotsData = slotResp;
      return slotsData;
    };

    const getBranchLabel = (branch: any) => {
      const label =
        branch?.name ||
        branch?.title ||
        branch?.branch_name ||
        branch?.business_name ||
        branch?.id ||
        "Branch";
      return String(label).trim();
    };

    const normalizeTime = (timeStr: string) => {
      if (!timeStr) return null;
      const t = String(timeStr).trim();
      const parts = t.split(":");
      if (parts.length < 2) return null;
      const hh = parseInt(parts[0], 10) || 0;
      const mm = parseInt(parts[1], 10) || 0;
      return hh * 60 + mm;
    };

    const formatTime12Hour = (minutes: number) => {
      const fullDay = 24 * 60;
      const normalized = ((minutes % fullDay) + fullDay) % fullDay;
      const hh = Math.floor(normalized / 60);
      const mm = normalized % 60;
      const period = hh >= 12 ? "pm" : "am";
      const displayHh = hh === 0 ? 12 : hh > 12 ? hh - 12 : hh;
      return `${displayHh}:${String(mm).padStart(2, "0")} ${period}`;
    };

    const generateSlots = (
      availablePeriods: Array<{ startMin: number; endMin: number }>,
      granularity = 30,
      minStart = 0,
    ) => {
      const slots: string[] = [];

      const formatRange = (s: number, e: number) => {
        const start = formatTime12Hour(s);
        const end = formatTime12Hour(e);
        return `${start} - ${end}`;
      };

      availablePeriods.forEach(({ startMin, endMin }) => {
        let cursor = startMin;
        const candidates: Array<{ s: number; e: number }> = [];

        while (cursor + granularity <= endMin) {
          const next = cursor + granularity;
          if (cursor >= minStart) candidates.push({ s: cursor, e: next });
          cursor = next;
        }

        // Merge contiguous candidates into larger ranges (e.g., two 30-min => 60-min)
        let i = 0;
        while (i < candidates.length) {
          let s = candidates[i].s;
          let e = candidates[i].e;
          i++;
          while (i < candidates.length && candidates[i].s === e) {
            e = candidates[i].e;
            i++;
          }
          // push merged range
          slots.push(formatRange(s, e));
        }
      });

      return slots;
    };

    const buildClosedSlots = (branchId?: string) => {
      const closedSlots: Array<{ startMin: number; endMin: number }> = [];
      try {
        const toDateObj = (v: any) => (v instanceof Date ? v : new Date(v));
        const targetDateKey = exportDateKey;
        (Array.isArray(events) ? events : []).forEach((ev: any) => {
          try {
            if (branchId) {
              const eventBranchId =
                ev.extendedProps?.branchId ||
                resolveResourceId(ev.extendedProps?.raw || {});
              if (!eventBranchId) return;
              if (String(eventBranchId) !== String(branchId)) return;
            }

            const s = ev.start ? toDateObj(ev.start) : null;
            const e = ev.end ? toDateObj(ev.end) : null;
            if (!s) return;

            const sLocal = toDateInput(s);
            if (sLocal !== targetDateKey) return;

            const startMin = s.getHours() * 60 + s.getMinutes();
            const endDate = e || new Date(s.getTime() + 60 * 60000);
            let endMin = endDate.getHours() * 60 + endDate.getMinutes();
            if (endMin < startMin) endMin = 24 * 60;

            if (!isNaN(startMin) && !isNaN(endMin)) {
              closedSlots.push({ startMin, endMin });
            }
          } catch (err) {}
        });
      } catch (e) {}
      return closedSlots;
    };

    const buildGroupedSlots = (
      slotsData: any[],
      occupiedIntervals: Array<{ startMin: number; endMin: number }>,
    ) => {
      const grouped: Record<string, string[]> = {};
      (Array.isArray(slotsData) ? slotsData : []).forEach((s: any) => {
        const blocked = !!(
          s.blocked ||
          s.closed ||
          s.is_blocked ||
          s.blocked === true ||
          s.blocked === 1 ||
          s.closed === 1 ||
          s.is_blocked === 1
        );

        if (blocked) {
          console.log("Skipping blocked slot:", s);
          return;
        }

        const slotDate = s.date || s.start_date || s.slot_date || s.day || "";
        const slotStart =
          s.start_time || s.time_start || s.from || s.start || "";
        const slotEnd = s.end_time || s.time_end || s.to || s.end || "";
        if (!slotDate || !slotStart || !slotEnd) return;
        const slotStartMin = normalizeTime(slotStart);
        let slotEndMin = normalizeTime(slotEnd);
        if (slotStartMin === null || slotEndMin === null) return;
        if (slotEndMin <= slotStartMin && slotStartMin > 0) {
          slotEndMin = 24 * 60;
        }

        let overlaps = false;
        for (const occ of occupiedIntervals) {
          if (slotStartMin < occ.endMin && slotEndMin > occ.startMin) {
            overlaps = true;
            break;
          }
        }
        if (overlaps) return;

        if (!grouped[slotDate]) grouped[slotDate] = [];
        const availablePeriods = [
          { startMin: slotStartMin, endMin: slotEndMin },
        ];
        // prefer 15-minute granularity but merge contiguous slots into longer ranges
        grouped[slotDate].push(
          ...generateSlots(availablePeriods, 30, minStartMin),
        );
      });

      if (
        !Object.keys(grouped).length ||
        !grouped[start] ||
        grouped[start].length === 0
      ) {
        const merged: Array<{ startMin: number; endMin: number }> = [];
        occupiedIntervals
          .sort((a, b) => a.startMin - b.startMin)
          .forEach((iv) => {
            if (!merged.length) merged.push({ ...iv });
            else {
              const last = merged[merged.length - 1];
              if (iv.startMin <= last.endMin)
                last.endMin = Math.max(last.endMin, iv.endMin);
              else merged.push({ ...iv });
            }
          });

        const DAY_START = 8 * 60; // 08:00
        const DAY_END = 24 * 60; // 24:00
        const availablePeriods: Array<{ startMin: number; endMin: number }> =
          [];
        let cursor = DAY_START;

        for (const iv of merged) {
          if (cursor < iv.startMin) {
            availablePeriods.push({ startMin: cursor, endMin: iv.startMin });
          }
          cursor = Math.max(cursor, iv.endMin);
        }
        if (cursor < DAY_END) {
          availablePeriods.push({ startMin: cursor, endMin: DAY_END });
        }

        const fallbackSlots = generateSlots(availablePeriods, 30, minStartMin);
        if (fallbackSlots.length) {
          grouped[start] = fallbackSlots;
          console.log("Using fallback available slots:", fallbackSlots);
        }
      }

      return grouped;
    };

    /**
     * Times a class is sitting on, straight from the API. The calendar's own
     * events already cover the unit currently on screen, but this export runs
     * across every branch — so without asking the server, a class on any other
     * court reads as free time and gets offered to a client.
     */
    const buildClassOccupiedIntervals = async (
      unitId: string,
    ): Promise<Array<{ startMin: number; endMin: number }>> => {
      let slots: any[] = [];
      try {
        slots = await getUnavailableSlots(String(unitId), {
          viewType: "day",
          date: exportDateKey,
          start,
          end,
        });
      } catch (error) {
        console.warn("Failed to fetch unavailable slots for", unitId, error);
        return [];
      }

      const intervals: Array<{ startMin: number; endMin: number }> = [];
      slots.forEach((slot: any) => {
        if (!isClassUnavailableSlot(slot)) return;
        const startAt = slot?.start_at ? new Date(slot.start_at) : null;
        const endAt = slot?.end_at ? new Date(slot.end_at) : null;
        if (!startAt || isNaN(startAt.getTime())) return;
        if (toDateInput(startAt) !== exportDateKey) return;

        const startMin = startAt.getHours() * 60 + startAt.getMinutes();
        let endMin =
          endAt && !isNaN(endAt.getTime())
            ? endAt.getHours() * 60 + endAt.getMinutes()
            : startMin + 60;
        if (endMin <= startMin) endMin = 24 * 60;
        intervals.push({ startMin, endMin });
      });
      return intervals;
    };

    const getBranchAvailability = async (
      branch: any,
      applyBranchFilter: boolean,
    ) => {
      console.log("Fetching availability for branch:", branch?.id);
      const unitId =
        branch?.id ||
        branch?.unit_id ||
        branch?.unitId ||
        branch?.branch_id ||
        branch?.branchId ||
        branch?.business_id;

      if (!unitId) return { grouped: {} };
      let slotsData: any[] = [];
      try {
        const slotResp = await getSlots({
          unit_id: unitId,
          start_date: start,
          end_date: end,
        });
        slotsData = parseSlotsData(slotResp);
      } catch (error) {
        console.error("Error fetching slots for branch:", branch?.id, error);
      }

      const branchId = unitId ? String(unitId) : null;
      const occupiedIntervals = buildClosedSlots(
        applyBranchFilter ? branchId || undefined : undefined,
      );
      occupiedIntervals.push(...(await buildClassOccupiedIntervals(unitId)));

      const grouped = buildGroupedSlots(slotsData, occupiedIntervals);
      return { grouped };
    };

    let message = "🎾 Available Court Slots 🎾\n\n";
    let totalSlots = 0;

    if (selectedBranchId === "all") {
      for (const branch of branches) {
        const branchLabel = getBranchLabel(branch);
        const { grouped } = await getBranchAvailability(branch, true);
        message += ` ${branchLabel}\n`;

        const entries = Object.entries(grouped).filter(
          ([, slots]) => slots.length > 0,
        );

        if (!entries.length) {
          message += "   - No available slots\n\n";
          continue;
        }

        entries.forEach(([date, slots]) => {
          message += `📅 ${date}\n`;
          slots.forEach((slot) => {
            message += `   ✓ ${slot}\n`;
            totalSlots++;
          });
          message += `\n`;
        });
      }
    } else {
      const branch = branches.find(
        (bb) => String(bb.id) === String(selectedBranchId),
      );
      if (!branch) {
        toast.error("No business selected");
        return;
      }
      const branchLabel = getBranchLabel(branch);
      const { grouped } = await getBranchAvailability(branch, false);

      message += ` ${branchLabel}\n`;

      Object.entries(grouped).forEach(([date, slots]) => {
        if (slots.length > 0) {
          message += `📅 ${date}\n`;
          slots.forEach((slot) => {
            message += `   ✓ ${slot}\n`;
            totalSlots++;
          });
          message += `\n`;
        }
      });
    }

    if (totalSlots === 0) {
      toast.error(
        `No available slots for ${exportDateKey}. All times are either booked or closed.`,
      );
      return;
    }

    message += ``;

    console.log("Exporting available slots:", message);

    const encodedMessage = encodeURIComponent(message);

    const hasNavigator = typeof navigator !== "undefined";
    const userAgent =
      hasNavigator && navigator.userAgent ? navigator.userAgent : "";
    const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(userAgent);

    const targets = isMobile
      ? [
          `https://wa.me/?text=${encodedMessage}`,
          `https://api.whatsapp.com/send?text=${encodedMessage}`,
        ]
      : [
          `https://web.whatsapp.com/send?text=${encodedMessage}`,
          `https://api.whatsapp.com/send?text=${encodedMessage}`,
        ];

    // Skip native share dialog and go directly to WhatsApp
    let opened = false;
    targets.some((url) => {
      const newWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (newWindow) {
        opened = true;
        return true;
      }
      return false;
    });

    if (!opened) {
      const canWriteClipboard =
        hasNavigator &&
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function";

      if (canWriteClipboard) {
        try {
          await navigator.clipboard.writeText(message);
          toast.success("Slots copied to clipboard. Paste them into WhatsApp.");
          return;
        } catch (err) {}
      }

      toast.error(
        `Unable to open WhatsApp automatically. Copy and share this message:\n\n${message}`,
      );
    }
  };
  const refreshAppointments = async () => {
    const requestId = ++latestRefreshRequestIdRef.current;
    let businessId: any = null;
    if (!branches || branches.length === 0) return;
    if (selectedBranchId === "all") {
      businessId = branches[0]?.business_id || branches[0]?.id;
    } else {
      const b = branches.find(
        (bb) => String(bb.id) === String(selectedBranchId),
      );
      businessId = b?.business_id || b?.id;
    }

    if (!businessId) {
      if (requestId === latestRefreshRequestIdRef.current) {
        setEvents([]);
      }
      return;
    }

    setLoadingAppointments(true);
    try {
      let appointments: any[] = [];

      const dateParam = formatDateForPayload(visibleStart).replace(/\//g, "-");
      // Only day view needs previous-day fetch to keep overnight spillover.
      const shouldFetchPrevDay = currentView === "day";
      const prevDay = new Date(visibleStart);
      prevDay.setDate(prevDay.getDate() - 1);
      const prevDateParam = formatDateForPayload(prevDay).replace(/\//g, "-");

      if (selectedBranchId === "all") {
        // Venue mode — fetch all units under the business
        const [respToday, respPrev] = await Promise.all([
          fetchAllBusinessAppointmentsForDate(String(venueId), dateParam),
          shouldFetchPrevDay
            ? fetchAllBusinessAppointmentsForDate(
                String(venueId),
                prevDateParam,
              )
            : Promise.resolve([]),
        ]);
        appointments = [...respToday, ...respPrev];
        console.log("view: ", currentView);
      } else {
        // Branch mode — fetch one unit
        const [respToday, respPrev] = await Promise.all([
          fetchAllUnitAppointmentsForDate(selectedBranchId, dateParam),
          shouldFetchPrevDay
            ? fetchAllUnitAppointmentsForDate(selectedBranchId, prevDateParam)
            : Promise.resolve([]),
        ]);
        appointments = [...respToday, ...respPrev];
        console.log("view: ", currentView);
      }

      // Deduplicate by id to avoid doubles from the extra day fetch
      const uniqueAppointments = Array.from(
        new Map(
          appointments.map((a: any) => [
            String(a?.id || a?._id || Math.random()),
            a,
          ]),
        ).values(),
      );
      const enrichedAppointments =
        await enrichZeroInvoiceAppointments(uniqueAppointments);
      const mapped = enrichedAppointments.map(mapAppointmentToEvent);

      try {
        const startDateParam = formatDateForPayload(visibleStart).replace(
          /\//g,
          "-",
        );
        const endDateParam = formatDateForPayload(visibleEnd).replace(
          /\//g,
          "-",
        );
        let slotEvents: any[] = [];

        if (selectedBranchId === "all") {
          const slotEventsByBranch = await Promise.all(
            branches.map(async (branch) => {
              try {
                const branchUnitId =
                  branch?.id ||
                  branch?.unit_id ||
                  branch?.unitId ||
                  branch?.branch_id ||
                  branch?.branchId ||
                  branch?.business_id;

                if (!branchUnitId) return [];
                const slotResp = await getSlots({
                  unit_id: branchUnitId,
                  start_date: startDateParam,
                  end_date: endDateParam,
                });
                let slotsData: any[] = [];
                if (!slotResp) slotsData = [];
                else if (slotResp.history && Array.isArray(slotResp.history))
                  slotsData = slotResp.history;
                else if (slotResp.slots) slotsData = slotResp.slots;
                else if (slotResp.data && Array.isArray(slotResp.data))
                  slotsData = slotResp.data;
                else if (Array.isArray(slotResp)) slotsData = slotResp;

                return buildSlotEvents(slotsData, branchUnitId);
              } catch (error) {
                console.error(
                  "Error fetching slots for branch:",
                  branch?.id,
                  error,
                );
                return [];
              }
            }),
          );
          slotEvents = slotEventsByBranch.flat();
        } else {
          const unitIdForSlots =
            selectedBranchId ||
            businessId ||
            branches.find((bb) => String(bb.id) === String(selectedBranchId))
              ?.id;
          const slotResp = await getSlots({
            unit_id: unitIdForSlots,
            start_date: startDateParam,
            end_date: endDateParam,
          });
          let slotsData: any[] = [];
          if (!slotResp) slotsData = [];
          else if (slotResp.history && Array.isArray(slotResp.history))
            slotsData = slotResp.history;
          else if (slotResp.slots) slotsData = slotResp.slots;
          else if (slotResp.data && Array.isArray(slotResp.data))
            slotsData = slotResp.data;
          else if (Array.isArray(slotResp)) slotsData = slotResp;

          slotEvents = buildSlotEvents(slotsData, unitIdForSlots);
        }

        const allEvents = [...mapped, ...slotEvents];
        if (requestId === latestRefreshRequestIdRef.current) {
          setEvents(allEvents);
        }
      } catch (e) {
        console.warn("Failed to fetch/merge slots", e);
        if (requestId === latestRefreshRequestIdRef.current) {
          setEvents(mapped);
        }
      }
    } catch (err) {
      console.error("Error fetching appointments for branch", businessId, err);
      if (requestId === latestRefreshRequestIdRef.current) {
        setEvents([]);
      }
    } finally {
      setLoadingAppointments(false);
    }
  };

  const refreshUnitAppointmentsForDate = async (
    unitId: string,
    anchorDate: Date,
  ) => {
    if (!unitId) return;
    const requestId = ++latestRefreshRequestIdRef.current;
    try {
      const dateParam = formatDateForPayload(anchorDate).replace(/\//g, "-");
      const shouldFetchPrevDay = currentView === "day";
      const prevDay = new Date(anchorDate);
      prevDay.setDate(prevDay.getDate() - 1);
      const prevDateParam = formatDateForPayload(prevDay).replace(/\//g, "-");

      const [respToday, respPrev] = await Promise.all([
        fetchAllUnitAppointmentsForDate(String(unitId), dateParam),
        shouldFetchPrevDay
          ? fetchAllUnitAppointmentsForDate(String(unitId), prevDateParam)
          : Promise.resolve([]),
      ]);

      const appointments = [...respToday, ...respPrev];
      const uniqueAppointments = Array.from(
        new Map(
          appointments.map((a: any) => [
            String(a?.id || a?._id || a?.appointment_id || Math.random()),
            a,
          ]),
        ).values(),
      );
      const enrichedAppointments =
        await enrichZeroInvoiceAppointments(uniqueAppointments);
      const mappedAppointments = enrichedAppointments.map(
        mapAppointmentToEvent,
      );

      let mappedSlots: any[] = [];
      try {
        const slotResp = await getSlots({
          unit_id: unitId,
          start_date: formatDateForPayload(visibleStart).replace(/\//g, "-"),
          end_date: formatDateForPayload(visibleEnd).replace(/\//g, "-"),
        });
        let slotsData: any[] = [];
        if (!slotResp) slotsData = [];
        else if (slotResp.history && Array.isArray(slotResp.history))
          slotsData = slotResp.history;
        else if (slotResp.slots) slotsData = slotResp.slots;
        else if (slotResp.data && Array.isArray(slotResp.data))
          slotsData = slotResp.data;
        else if (Array.isArray(slotResp)) slotsData = slotResp;
        mappedSlots = buildSlotEvents(slotsData, unitId);
      } catch (slotErr) {
        console.warn("Failed targeted slot refresh after drag", slotErr);
      }

      if (requestId === latestRefreshRequestIdRef.current) {
        setEvents((prev) => {
          const base = Array.isArray(prev) ? prev : [];
          const target = String(unitId);
          const untouched = base.filter((ev: any) => {
            const eventUnitId = ev?.extendedProps?.branchId;
            if (!eventUnitId) return true;
            return String(eventUnitId) !== target;
          });
          return [...untouched, ...mappedAppointments, ...mappedSlots];
        });
      }
    } catch (err) {
      console.warn(
        "Targeted refresh failed, falling back to full refresh",
        err,
      );
      await refreshAppointments();
    }
  };

  const resolveUnitIdFromRaw = (raw: any): string | null => {
    const unitId =
      raw?.unit_id ||
      raw?.unitId ||
      raw?.unit?.id ||
      raw?.branch_id ||
      raw?.branchId ||
      raw?.branch?.id;
    return unitId ? String(unitId) : null;
  };

  const resolveAnchorDateFromRaw = (raw: any): Date | null => {
    const fromIso =
      raw?.start_at || raw?.startAt || raw?.start_at_utc || raw?.startAtUtc;
    if (fromIso) {
      const parsed = new Date(fromIso);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    const fromLegacy = parseDateTime(
      raw?.start_date || raw?.date,
      raw?.time_start || raw?.start_time || raw?.time,
    );
    if (fromLegacy) {
      const parsed = new Date(fromLegacy);
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
  };

  useEffect(() => {
    const handleLiveAppointmentUpdate = (event: Event) => {
      const payload = (event as CustomEvent<AdminAppointmentSocketPayload>)
        .detail;
      const appointment = payload?.appointment || {};
      const previousAppointment = payload?.previousAppointment || {};

      if (payload?.type === "APPOINTMENT_CANCELLED" && appointment?.id) {
        setEvents((prev) =>
          (Array.isArray(prev) ? prev : []).filter((ev: any) => {
            const eventId = String(
              ev?.extendedProps?.originalId || ev?.id || "",
            );
            return eventId !== String(appointment.id);
          }),
        );
      }

      const refreshSources =
        payload?.type === "APPOINTMENT_UPDATED"
          ? [previousAppointment, appointment]
          : [appointment];
      const refreshTargets = refreshSources
        .map((source) => {
          const unitId = resolveUnitIdFromRaw(source);
          const anchorDate = resolveAnchorDateFromRaw(source);
          return unitId && anchorDate ? { unitId, anchorDate } : null;
        })
        .filter((target): target is { unitId: string; anchorDate: Date } =>
          Boolean(target),
        )
        .filter((target, index, targets) => {
          const key = `${target.unitId}::${target.anchorDate.toDateString()}`;
          return (
            targets.findIndex(
              (item) =>
                `${item.unitId}::${item.anchorDate.toDateString()}` === key,
            ) === index
          );
        });

      if (refreshTargets.length > 0) {
        void Promise.all(
          refreshTargets.map((target) =>
            refreshUnitAppointmentsForDate(target.unitId, target.anchorDate),
          ),
        );
      } else {
        void refreshAppointments();
      }
    };

    window.addEventListener(
      ADMIN_APPOINTMENT_SOCKET_EVENT,
      handleLiveAppointmentUpdate,
    );

    return () => {
      window.removeEventListener(
        ADMIN_APPOINTMENT_SOCKET_EVENT,
        handleLiveAppointmentUpdate,
      );
    };
  }, [
    selectedBranchId,
    branches,
    visibleStart,
    visibleEnd,
    currentView,
    venueId,
  ]);

  useEffect(() => {
    if (!Array.isArray(branches) || branches.length === 0) return;
    const branchIdsKey = branches
      .map((b: any) =>
        String(
          b?.id || b?.unit_id || b?.unitId || b?.branch_id || b?.branchId || "",
        ),
      )
      .join("|");
    const startKey = visibleStart ? formatDateForPayload(visibleStart) : "";
    const endKey = visibleEnd ? formatDateForPayload(visibleEnd) : "";
    const key = [
      String(venueId || ""),
      String(selectedBranchId || ""),
      currentView,
      startKey,
      endKey,
      branchIdsKey,
    ].join("::");

    if (lastAutoRefreshKeyRef.current === key) return;
    lastAutoRefreshKeyRef.current = key;
    void refreshAppointments();
  }, [
    selectedBranchId,
    branches,
    visibleStart,
    visibleEnd,
    currentView,
    venueId,
  ]);

  const handleBlockSlotSubmit = async (values: Record<string, any>) => {
    const branchIdForBlock =
      formBranchId && formBranchId !== "all" ? formBranchId : selectedBranchId;
    if (!branchIdForBlock || branchIdForBlock === "all") {
      toast.error("Select a branch before opening or closing slots.");
      return;
    }
    const branch = branches.find(
      (b) => String(b.id) === String(branchIdForBlock),
    );
    const unitId = branch?.id || null;
    if (!unitId) {
      toast.error("Select a branch before opening or closing slots.");
      return;
    }

    const action = (values.block_action || blockAction || "close") as
      | "open"
      | "close";
    const date = String(values.block_date || blockDate || "").trim();
    const startRaw = String(values.block_start || blockStart || "").trim();
    const endRaw = String(values.block_end || blockEnd || "").trim();

    if (!date || !startRaw || !endRaw) {
      toast.error("Please select a date and time range.");
      return;
    }

    const startTime = normalizeTimeValue(startRaw);
    const endTime = normalizeTimeValue(endRaw);

    try {
      const diff = minutesBetweenTimes(startTime, endTime);
      if (isNaN(diff) || diff <= 0) {
        toast.error("End time must be after start time");
        return;
      }
    } catch (e) {
      toast.error("Invalid time values");
      return;
    }

    const slotData: any = {
      action,
      date,
      start_time: startTime,
      end_time: endTime,
      unit_id: String(unitId),
    };

    if (action === "open") {
      const blockId = selectedBlockId;
      if (!blockId) {
        toast.error("No blocked slot selected to open.");
        return;
      }
      slotData.blockId = blockId;
    }

    try {
      await manageSlots(slotData);
      console.log("Slot management successful:", slotData);

      toast.success(
        `Slot ${action === "open" ? "opened" : "closed"} successfully.`,
      );

      const anchorDate =
        combineDateAndTime(date, startTime) ||
        new Date(`${String(date).replace(/\//g, "-")}T00:00:00`);
      if (anchorDate && !isNaN(anchorDate.getTime())) {
        void refreshUnitAppointmentsForDate(String(unitId), anchorDate);
      } else {
        void refreshAppointments();
      }
    } catch (error) {
      console.error("Error managing slots:", error);
      showErrorToast(getErrorMessage(error, "Failed to update slots"));
    } finally {
      setAddDialogOpen(false);
    }
  };

  // Same create flow as the Classes page's "Add Class" dialog — a recurring
  // class stores a rule and writes no session rows, answering with the dates
  // it projects instead. A clash inside the next 90 days comes back as a 409
  // listing which dates and why.
  const handleAddClassSubmit = async (values: Record<string, any>) => {
    const payload = buildCreateClassPayloadFromValues(values);
    if (!payload) return;

    setIsClassSubmitting(true);
    try {
      const result = await createClass(payload);
      const projected = result.preview_sessions.length;
      const openEnded =
        Boolean(payload.recurrence_frequency) &&
        !payload.recurrence_end &&
        !payload.recurrence_count;

      toast.success(
        !payload.recurrence_frequency
          ? "Class created successfully!"
          : openEnded
            ? `Class created — it runs until further notice${
                projected ? `, next ${projected} dates scheduled` : ""
              }.`
            : `Class created — ${projected} session${
                projected === 1 ? "" : "s"
              } scheduled.`,
      );
      setAddDialogOpen(false);
      void refreshAppointments();
    } catch (error) {
      const itemised = readScheduleConflicts(error);
      if (isScheduleConflictError(error)) {
        setClassConflicts(itemised);
        setClassConflictMessage(getErrorMessage(error, ""));
        setClassConflictsOpen(true);
        return;
      }
      showErrorToast(getErrorMessage(error, "Failed to create the class."));
    } finally {
      setIsClassSubmitting(false);
    }
  };

  const handleAppointmentSubmit = async (values: Record<string, any>) => {
    setIsAppointmentSubmitting(true);
    try {
      return await handleAppointmentSubmitInternal(values);
    } finally {
      setIsAppointmentSubmitting(false);
    }
  };

  const handleAppointmentSubmitInternal = async (
    values: Record<string, any>,
  ) => {
    let businessId: any = null;
    let unitId: any = null;
    if (!branches || branches.length === 0) return;
    const branchToUseId = values.branch_id || formBranchId || selectedBranchId;
    if (!branchToUseId || branchToUseId === "all") {
      const firstUnit = branches[0] || null;
      unitId = firstUnit?.id || null;
      businessId = firstUnit?.business_id || venueId || null;
    } else {
      const branchIdStr = String(branchToUseId);
      const b = branches.find(
        (bb) =>
          String(bb.id) === branchIdStr ||
          String(bb.business_id || "") === branchIdStr,
      );
      unitId = b?.id || branchToUseId;
      businessId = b?.business_id || venueId || null;
    }
    if (!unitId || !businessId) return;

    // Warn if selected client is inactive/unverified (skip for status-only updates)
    const isStatusOnlyUpdate =
      editingEventId &&
      values.status &&
      values.status !== (editingEventRaw?.status || "PENDING");

    if (!isStatusOnlyUpdate) {
      try {
        const clientId =
          values.client_id ||
          values.clientId ||
          formClientId ||
          values?.client?.id ||
          values?.client?._id;

        // Ensure clientId is a string and not an object
        const safeClientId =
          clientId && typeof clientId === "object"
            ? String(clientId.id || clientId._id || "")
            : String(clientId || "");
        const clientList = Array.isArray(clients?.users) ? clients!.users : [];
        let clientRecord =
          safeClientId && clientList.length
            ? clientList.find(
                (c: any) => String(c.id || c._id || "") === safeClientId,
              )
            : null;

        // Fallback: when select endpoint omits active flag, fetch full client profile.
        if (
          safeClientId &&
          (!clientRecord || clientRecord.active === undefined)
        ) {
          try {
            const fullClient = await getClientById(safeClientId);
            if (fullClient) {
              clientRecord = {
                ...(clientRecord || {}),
                ...(fullClient || {}),
                active:
                  fullClient?.active ??
                  fullClient?.is_active ??
                  fullClient?.isActive ??
                  fullClient?.verified ??
                  fullClient?.status ??
                  clientRecord?.active,
              };
            }
          } catch {
            // Keep current fallback behavior: do not block on lookup failure.
          }
        }
        const isActive = (() => {
          if (!clientRecord || clientRecord.active === undefined) return true;
          if (typeof clientRecord.active === "boolean")
            return clientRecord.active;
          const s = String(clientRecord.active).toLowerCase();
          return ["true", "1", "active", "verified"].includes(s);
        })();
        if (!isActive) {
          const proceed = await new Promise<boolean>((resolve) => {
            setInactiveClientConfirm({ resolve });
          });
          if (!proceed) return;
        }
      } catch (e) {
        // if any lookup fails, continue without blocking
      }
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const d = new Date(values.date);
    const startDateStr = `${d.getFullYear()}-${(d.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${d.getDate().toString().padStart(2, "0")}`;

    try {
      if (
        editingEventId &&
        (!values.service_id || String(values.service_id || "").trim() === "")
      ) {
        toast.error("Please select a service before updating.");
        return;
      }

      const svcIdForSubmit =
        values.service_id !== undefined && values.service_id !== null
          ? values.service_id
          : formServiceId || "";
      const durForSubmit = findServiceDuration(String(svcIdForSubmit || ""));
      if (durForSubmit && values.time_start) {
        const desired = addMinutesToTime(
          String(values.time_start),
          durForSubmit,
        );
        values.time_end = clampEndTime(
          String(values.time_start),
          desired,
          MAX_APPOINTMENT_MINUTES,
        );
      }
    } catch (e) {}

    try {
      const diff = minutesBetweenTimes(
        String(values.time_start),
        String(values.time_end),
      );
      if (isNaN(diff) || diff <= 0) {
        toast.error("End time must be after start time");
        return;
      }
      if (diff > MAX_APPOINTMENT_MINUTES) {
        toast.error("Maximum appointment duration is 3 hours");
        return;
      }
    } catch (e) {
      console.error("Time validation error", e);
      toast.error("Invalid time values");
      return;
    }

    let servicesForSelectedUnit = Array.isArray(services) ? services : [];
    let selectedService = servicesForSelectedUnit.find(
      (s) =>
        String(s.id || s._id || s.service_id || "") ===
        String(values.service_id || ""),
    );
    const selectedServiceUnitId = selectedService
      ? String(
          (selectedService as any).unit_id ||
            (selectedService as any).unitId ||
            (selectedService as any).unit?.id ||
            "",
        )
      : "";
    const selectedServiceBelongsToUnit =
      !selectedServiceUnitId || selectedServiceUnitId === String(unitId);

    if (
      unitId &&
      values.service_id &&
      (!selectedService || !selectedServiceBelongsToUnit)
    ) {
      try {
        const response = (await getAllServices(String(unitId))) as any;
        const servicesData = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.services)
              ? response.services
              : Array.isArray(response?.data?.services)
                ? response.data.services
                : [];
        servicesForSelectedUnit = servicesData;
        setServices(servicesData);
        selectedService = servicesForSelectedUnit.find(
          (s) =>
            String(s.id || s._id || s.service_id || "") ===
            String(values.service_id || ""),
        );
      } catch {
        // Keep the existing services list fallback and let the validation below handle it.
      }
    }

    const selectedServiceAfterFetchUnitId = selectedService
      ? String(
          (selectedService as any).unit_id ||
            (selectedService as any).unitId ||
            (selectedService as any).unit?.id ||
            "",
        )
      : "";
    if (
      values.service_id &&
      (!selectedService ||
        (selectedServiceAfterFetchUnitId &&
          selectedServiceAfterFetchUnitId !== String(unitId)))
    ) {
      toast.error("Selected service does not belong to this branch.");
      return;
    }

    const servicePrice = selectedService ? selectedService.price || 0 : 0;

    const startDateObj = combineDateAndTime(startDateStr, values.time_start);
    let endDateObj = combineDateAndTime(startDateStr, values.time_end);

    if (!startDateObj || !endDateObj) {
      toast.error(
        "Invalid date or time value. Please pick both start and end times.",
      );
      return;
    }

    // If end time is on/after midnight but earlier than the start, roll it to next day
    if (endDateObj.getTime() <= startDateObj.getTime()) {
      endDateObj = new Date(endDateObj.getTime());
      endDateObj.setDate(endDateObj.getDate() + 1);
    }

    const start_at = startDateObj.toISOString();
    const end_at = endDateObj.toISOString();

    try {
      const isRecurring =
        !editingEventId &&
        String(values.recurrence_mode || "").toUpperCase() === "RECURRING";
      let recurrencePayload: Record<string, any> = {};
      if (isRecurring) {
        const frequency = String(
          values.recurrence_frequency || "",
        ).toUpperCase();
        const intervalRaw = Number(values.recurrence_interval);
        const interval =
          Number.isFinite(intervalRaw) && intervalRaw > 0 ? intervalRaw : 1;
        const recurrenceCountRaw = Number(values.recurrence_count);
        const recurrenceCount =
          Number.isFinite(recurrenceCountRaw) && recurrenceCountRaw > 0
            ? Math.max(1, Math.round(recurrenceCountRaw))
            : undefined;
        const weekDays = Array.isArray(values.recurrence_week_days)
          ? values.recurrence_week_days
              .map((v: any) => Number(v))
              .filter((v: number) => Number.isFinite(v))
          : [];
        const recurrenceStart = startDateStr;

        if (!frequency) {
          toast.error("Please select a recurrence frequency.");
          return;
        }
        if (frequency === "WEEKLY" && weekDays.length === 0) {
          toast.error("Please select at least one weekday.");
          return;
        }
        if (frequency === "MONTHLY") {
          const monthDays = Array.isArray(values.recurrence_month_days)
            ? values.recurrence_month_days
                .map((v: any) => Number(v))
                .filter((v: number) => Number.isFinite(v) && v >= 1 && v <= 31)
            : [];
          if (monthDays.length === 0) {
            toast.error("Please select at least one day in the month.");
            return;
          }
        }

        const startDate = new Date(`${recurrenceStart}T00:00:00`);
        if (isNaN(startDate.getTime())) {
          toast.error("Invalid recurrence start date.");
          return;
        }

        // Send local date-time (no Z) to avoid UTC day-shift on the backend.
        const startIso = `${recurrenceStart}T00:00:00`;

        recurrencePayload = {
          recurrence_frequency: frequency,
          recurrence_interval: interval,
          recurrence_week_days: weekDays,
          recurrence_count: recurrenceCount,
          recurrence_month_days: Array.isArray(values.recurrence_month_days)
            ? values.recurrence_month_days
                .map((v: any) => Number(v))
                .filter((v: number) => Number.isFinite(v) && v >= 1 && v <= 31)
            : undefined,
          recurrence_start: startIso,
        };
      }

      let appointmentId: string | null = null;
      if (editingEventId) {
        // For update: use UpdateAppointmentDto schema
        const originalStart =
          editingEventRaw?.start_at || editingEventRaw?.startAt;
        const originalEnd = editingEventRaw?.end_at || editingEventRaw?.endAt;
        const timeChanged =
          !timestampsClose(originalStart, start_at) ||
          !timestampsClose(originalEnd, end_at);

        const updatePayload = {
          unit_id: unitId,
          branch_id: unitId,
          service_id: values.service_id,
          start_at,
          end_at,
          notes: values.notes || "",
          status: timeChanged
            ? deriveAppointmentStatus(
                values.status || formStatus || "PENDING",
                startDateObj,
                endDateObj,
              )
            : values.status || formStatus || "PENDING",
        };
        const requestedStatus = String(
          updatePayload.status || "",
        ).toUpperCase();
        let cancelScope: "ALL" | undefined = undefined;
        const isRecurring = isRecurringAppointment(editingEventRaw);
        if (requestedStatus === "CANCELLED" && isRecurring) {
          const scopeChoice = await requestRecurringScope("cancel");
          if (scopeChoice === undefined) return;
          cancelScope = scopeChoice === "ALL" ? "ALL" : undefined;
        }
        await updateAppointment(
          editingEventId.toString(),
          updatePayload,
          cancelScope,
        );
        // Optimistic local update so branch change shows immediately
        try {
          const normalizedClientId = resolveClientIdValue(
            values.client_id ??
              editingEventRaw?.client_id ??
              editingEventRaw?.client,
          );
          const updatedRaw = {
            ...(editingEventRaw || {}),
            id: editingEventId,
            unit_id: unitId,
            branch_id: unitId,
            service_id: values.service_id,
            service: selectedService || editingEventRaw?.service,
            client_id: normalizedClientId ?? editingEventRaw?.client_id,
            start_at,
            end_at,
            notes: values.notes || "",
            status: updatePayload.status,
          };
          const updatedEvent = mapAppointmentToEvent(updatedRaw);
          setEvents((prev) =>
            (Array.isArray(prev) ? prev : []).map((ev) => {
              const matchId = String(ev?.extendedProps?.originalId || ev?.id);
              if (matchId !== String(editingEventId)) return ev;
              return updatedEvent;
            }),
          );
          setEditingEventRaw(updatedRaw);
        } catch (e) {
          console.warn("Failed optimistic update after edit", e);
        }
        toast.success("Appointment updated successfully!");
      } else {
        const selectedRewardId = String(values.loyaltyRewardId || "");
        const selectedReward = selectedRewardId
          ? loyaltyRewards.find(
              (reward) => String(reward.id) === selectedRewardId,
            )
          : null;

        if (selectedReward) {
          if (!values.client_id) {
            toast.error("Select a client before applying a reward.");
            return;
          }

          const selectedRewardType = getRewardTypeValue(selectedReward);
          // A shorter reward leaves a remainder, and a promo or redeemed
          // points may discount that — only a full cover still blocks them.
          const bookedServiceDuration = getServiceDurationMinutes(
            services.find(
              (service: any) =>
                String(service?.id || service?._id || service?.service_id || "") ===
                String(values.service_id || ""),
            ),
          );
          const serviceRewardCoversBooked =
            selectedRewardType === "SERVICE" &&
            serviceRewardCoversBooking(selectedReward, bookedServiceDuration);
          const redeemPoints = Math.max(
            0,
            Math.floor(Number(values.loyaltyRedeemPoints) || 0),
          );
          const onFullInvoice = parseBooleanSetting(
            values.loyaltyOnFullInvoice,
            getCachedFullInvoiceEnabled(),
          );

          if (serviceRewardCoversBooked && values.promo_id) {
            toast.error("This reward already covers the whole booking.");
            return;
          }

          if (serviceRewardCoversBooked && redeemPoints > 0 && !onFullInvoice) {
            toast.error("This reward already covers the whole booking.");
            return;
          }

          const selectedBranch = branches.find(
            (branch) => String(branch?.id) === String(unitId || ""),
          );
          const rewardStockError = getProductRewardStockError(
            selectedReward,
            stockItems,
            selectedBranch?.business_id ?? null,
          );

          if (rewardStockError) {
            toast.error(rewardStockError);
            return;
          }

          const organizationId = getStoredOrganizationId();
          if (organizationId) {
            try {
              const loyaltyPreview = await getLoyaltyPreview({
                organizationId,
                userId: String(values.client_id),
                amount: 0,
                redeemPoints: 0,
              });
              const clientBalance = Number(loyaltyPreview?.balance ?? 0);

              if (clientBalance < Number(selectedReward.redeem_points || 0)) {
                toast.error(
                  "Client does not have enough loyalty points for this reward.",
                );
                return;
              }
            } catch (error: any) {
              toast.error(
                getErrorMessage(
                  error,
                  "Failed to check client loyalty balance.",
                ),
              );
              return;
            }
          }
        }

        // For create: include type and promo_id
        const createPayload = {
          unit_id: unitId,
          service_id: values.service_id,
          client_id: values.client_id || undefined,
          // Not capped here: the picker already limits selection to the
          // business's Players per match using the live preview, and the API
          // rejects an over-full roster. Re-capping from the cache only risks
          // silently dropping guests when this browser has a stale number.
          sharedClientIds: Array.isArray(values.sharedClientIds)
            ? values.sharedClientIds.map((id: any) => String(id))
            : [],
          start_at,
          end_at,
          notes: values.notes || "",
          type: "NORMAL",
          promo_id: values.promo_id || undefined,
          loyaltyRedeemPoints: Math.max(
            0,
            Math.floor(Number(values.loyaltyRedeemPoints) || 0),
          ),
          loyaltyDiscountPercent: Math.min(
            100,
            Math.max(0, Number(values.loyaltyDiscountPercent) || 0),
          ),
          loyaltyRedeemDiscountType:
            String(values.loyaltyRedeemDiscountType || "").toUpperCase() ===
            "FIXED"
              ? "FIXED"
              : "PERCENT",
          loyaltyRedeemDiscountAmount: Math.max(
            0,
            Number(values.loyaltyRedeemDiscountAmount) || 0,
          ),
          loyaltyOnFullInvoice: parseBooleanSetting(
            values.loyaltyOnFullInvoice,
            getCachedFullInvoiceEnabled(),
          ),
          ...(values.loyaltyRewardId
            ? { loyaltyRewardId: Number(values.loyaltyRewardId) }
            : {}),
          ...(recurrencePayload as Record<string, any>),
          ...(isRecurring
            ? { send_notifications: Boolean(values.send_notifications) }
            : {}),
        };
        const resp = await createAppointment(createPayload);
        appointmentId = isRecurring
          ? resp?.recurrence_parent_id ||
            resp?.appointments?.[0]?.id ||
            resp?._id
          : resp?.appointment?.id || resp?._id;
        if (
          isRecurring &&
          Array.isArray(resp?.skipped_summary) &&
          resp.skipped_summary.length > 0
        ) {
          toast.warn(resp.skipped_summary.join(" | "));
        }
        /*if (appointmentId) {
          await createInvoiceForAppointment(
            appointmentId,
            businessId,
            values.client_id || null,
            resp.servicePrice || servicePrice,
            values.notes || "",
            values.service_id || formServiceId || null
          );
        }*/
        toast.success("Appointment created successfully!");
      }

      if (isRecurring) {
        void refreshAppointments();
      } else if (unitId && startDateObj && !isNaN(startDateObj.getTime())) {
        void refreshUnitAppointmentsForDate(String(unitId), startDateObj);
        if (editingEventId) {
          const prevRaw = editingEventRaw || {};
          const prevUnitId =
            prevRaw?.unit_id ||
            prevRaw?.unitId ||
            prevRaw?.unit?.id ||
            prevRaw?.branch_id ||
            prevRaw?.branchId ||
            prevRaw?.branch?.id ||
            prevRaw?._uiBranchId;
          const prevStart =
            prevRaw?.start_at ||
            prevRaw?.startAt ||
            prevRaw?.start_at_utc ||
            prevRaw?.startAtUtc;
          const prevAnchor = prevStart ? new Date(prevStart) : startDateObj;
          const prevDateKey =
            prevAnchor && !isNaN(prevAnchor.getTime())
              ? toDateInput(prevAnchor)
              : startDateStr;
          const nextDateKey = startDateStr;
          const shouldRefreshPrev =
            prevUnitId &&
            prevAnchor &&
            !isNaN(prevAnchor.getTime()) &&
            (String(prevUnitId) !== String(unitId) ||
              String(prevDateKey) !== String(nextDateKey));
          if (shouldRefreshPrev) {
            void refreshUnitAppointmentsForDate(String(prevUnitId), prevAnchor);
          }
        }
      } else {
        void refreshAppointments();
      }

      setAddDialogOpen(false);
      setEditingEventId(null);
      setFormClientId("");
      setFormServiceId("");
    } catch (err: any) {
      console.error("Error creating appointment", err);

      if (!editingEventId) {
        try {
          const dateParam = startDateStr.replace(/\//g, "-");
          const appointments = await fetchAllUnitAppointmentsForDate(
            String(unitId),
            dateParam,
          );
          const created = appointments.find((appt) =>
            isSameAppointmentRecord(appt, {
              start_at,
              end_at,
              service_id: values.service_id,
              client_id: values.client_id,
              unit_id: unitId,
            }),
          );

          if (created) {
            if (unitId && startDateObj && !isNaN(startDateObj.getTime())) {
              void refreshUnitAppointmentsForDate(String(unitId), startDateObj);
            } else {
              void refreshAppointments();
            }
            setAddDialogOpen(false);
            setEditingEventId(null);
            setFormClientId("");
            setFormServiceId("");
            toast.warn(
              "Appointment saved, but sending the notification failed. Please notify the client manually.",
            );
            return;
          }
        } catch (recoveryErr) {
          console.warn(
            "Could not verify appointment creation after error",
            recoveryErr,
          );
        }
      }

      // The server's own message matters here — a 409 names the class or
      // booking sitting on the slot, and a 400 explains the 366-day horizon.
      const errorMessage =
        err?.response?.data?.error ||
        err?.error ||
        getErrorMessage(
          err,
          editingEventId
            ? "Failed to update appointment. Please try again."
            : "Failed to create appointment. Please try again.",
        );
      toast.error(errorMessage);
    }
  };

  const handleDeleteAppointment = async () => {
    if (!editingEventId) return;

    if (deletingAppointment) return;
    setDeletingAppointment(true);
    try {
      const deleteScope = pendingDeleteScope;
      const deleteFromDate =
        deleteScope === "UPCOMING" ? toDateInput(activeDate) : undefined;
      await deleteAppointment(editingEventId, deleteScope, deleteFromDate);
      const deletedId = String(editingEventId);
      const raw = editingEventRaw || {};
      const unitId = resolveUnitIdFromRaw(raw);
      const anchorDate = resolveAnchorDateFromRaw(raw);

      setEvents((prev) =>
        (Array.isArray(prev) ? prev : []).filter((ev: any) => {
          const matchId = String(ev?.extendedProps?.originalId || ev?.id);
          return matchId !== deletedId;
        }),
      );

      if (deleteScope) {
        void refreshAppointments();
      } else if (unitId && anchorDate) {
        void refreshUnitAppointmentsForDate(unitId, anchorDate);
      } else {
        void refreshAppointments();
      }
      setAddDialogOpen(false);
      setEditingEventId(null);
      setEditingEventRaw(null);
      toast.success("Appointment deleted successfully!");
      setDeleteConfirmOpen(false);
    } catch (error) {
      console.error("Error deleting appointment:", error);
      showErrorToast(
        getErrorMessage(
          error,
          "Failed to delete appointment. Please try again.",
        ),
      );
    } finally {
      setDeletingAppointment(false);
      setPendingDeleteScope(undefined);
    }
  };

  const handleRequestDeleteAppointment = async () => {
    if (!editingEventId) return;
    const isRecurring = isRecurringAppointment(editingEventRaw);
    if (isRecurring) {
      const scopeChoice = await requestRecurringScope("delete");
      if (scopeChoice === undefined) return;
      setPendingDeleteScope(scopeChoice || undefined);
    } else {
      setPendingDeleteScope(undefined);
    }
    setDeleteConfirmOpen(true);
  };

  const normalizeId = (value: any): string | null => {
    if (value === null || value === undefined) return null;
    const str = String(value).trim();
    if (
      !str ||
      str.toLowerCase() === "all" ||
      str.toLowerCase() === "undefined"
    )
      return null;
    return str;
  };
  const isRecurringAppointment = (raw: any): boolean => {
    if (!raw) return false;
    const truthyFlag = (value: any) => {
      if (value === true) return true;
      if (typeof value === "number") return value === 1;
      if (typeof value === "string") {
        const v = value.trim().toLowerCase();
        return ["true", "1", "yes", "y", "recurring"].includes(v);
      }
      return false;
    };
    const parentMarkers = [
      raw.recurrence_parent_id,
      raw.recurrenceParentId,
      raw.recurrence_id,
      raw.recurrenceId,
      raw.recurrence_series_id,
      raw.recurrenceSeriesId,
      raw.series_id,
      raw.seriesId,
      raw.parent_appointment_id,
      raw.parentAppointmentId,
    ];
    if (parentMarkers.some((v) => v)) return true;
    if (
      truthyFlag(raw.is_recurring) ||
      truthyFlag(raw.isRecurring) ||
      truthyFlag(raw.recurring)
    )
      return true;
    const mode = String(
      raw.recurrence_mode || raw.recurrenceMode || "",
    ).toUpperCase();
    if (mode === "RECURRING") return true;
    if (raw.recurrence_frequency || raw.recurrenceFrequency) return true;
    const count = Number(raw.recurrence_count ?? raw.recurrenceCount);
    if (Number.isFinite(count) && count > 1) return true;
    if (raw.recurrence_start || raw.recurrenceStart) return true;
    return false;
  };

  const resolveInvoiceScope = (invoiceData: any) => {
    const unitId =
      normalizeId(invoiceData?.metadata?.unitId) ||
      normalizeId(invoiceData?.metadata?.unit_id) ||
      normalizeId(invoiceData?.metadata?.branchId) ||
      normalizeId(invoiceData?.metadata?.branch_id) ||
      normalizeId(invoiceData?.unitId) ||
      normalizeId(invoiceData?.unit_id) ||
      normalizeId(invoiceData?.branchId) ||
      normalizeId(invoiceData?.branch_id) ||
      normalizeId(invoiceData?.unit?.id) ||
      normalizeId(invoiceData?.invoice?.unitId) ||
      normalizeId(invoiceData?.invoice?.unit_id) ||
      normalizeId(invoiceData?.invoice?.branchId) ||
      normalizeId(invoiceData?.invoice?.branch_id);

    const businessId =
      normalizeId(invoiceData?.business_id) ||
      normalizeId(invoiceData?.businessId) ||
      normalizeId(invoiceData?.invoice?.business_id) ||
      normalizeId(invoiceData?.invoice?.businessId) ||
      normalizeId(invoiceData?.metadata?.business_id) ||
      normalizeId(invoiceData?.metadata?.businessId) ||
      normalizeId(venueId);

    return { unitId, businessId };
  };

  const fetchInvoiceServices = async (unitId?: string | null) => {
    const normalizedUnit = normalizeId(unitId);
    if (!normalizedUnit) {
      setInvoiceServices([]);
      return;
    }
    try {
      let response: any;
      if (normalizedUnit) {
        response = (await getAllServices(String(normalizedUnit))) as any;
      } else {
        setInvoiceServices([]);
        return;
      }
      let servicesData = [];
      if (response && response.services) {
        servicesData = response.services;
      } else if (response && response.data && response.data.services) {
        servicesData = response.data.services;
      } else if (response && response.data && Array.isArray(response.data)) {
        servicesData = response.data;
      } else if (Array.isArray(response)) {
        servicesData = response;
      }
      setInvoiceServices(Array.isArray(servicesData) ? servicesData : []);
    } catch (error) {
      console.error("Error fetching invoice services:", error);
      setInvoiceServices([]);
    }
  };

  const openInvoiceEditDialog = async () => {
    if (!editingEventId) {
      toast.error("No appointment selected.");
      return;
    }
    setInvoiceEditLoading(true);
    try {
      const appointmentInvoiceId =
        editingEventRaw?.invoiceId ||
        editingEventRaw?.invoice_id ||
        editingEventRaw?.invoice?.id ||
        editingEventRaw?.invoice?.invoiceId ||
        editingEventRaw?.invoice?.invoice_id;
      if (!appointmentInvoiceId) {
        toast.error("Invoice ID not found for this appointment.");
        return;
      }

      const response: any = await getInvoiceById(String(appointmentInvoiceId));
      const invoiceData = response?.data ?? response;
      if (!invoiceData) {
        toast.error("No invoice found for this appointment.");
        return;
      }

      const { unitId: invoiceUnitId } = resolveInvoiceScope(invoiceData);

      const eventBranchUnit =
        normalizeId(editingEventRaw?.branch_id) ||
        normalizeId(editingEventRaw?.branchId) ||
        normalizeId(editingEventRaw?.unit_id) ||
        normalizeId(editingEventRaw?.unitId) ||
        normalizeId(editingEventRaw?.unit?.id);

      const formBranchUnit = normalizeId(
        formBranchId !== "all" ? formBranchId : null,
      );
      const selectedBranchUnit = normalizeId(
        selectedBranchId !== "all" ? selectedBranchId : null,
      );

      const branchCandidateId =
        invoiceUnitId ||
        eventBranchUnit ||
        formBranchUnit ||
        selectedBranchUnit;

      // Attempt to map candidate to actual unit id in loaded branches
      const branchesList = Array.isArray(branches) ? branches : [];
      const matchedBranch = branchCandidateId
        ? branchesList.find((b: any) =>
            [b?.id, b?._id, b?.unitId, b?.unit_id, b?.business_id].some(
              (val) => normalizeId(val) === normalizeId(branchCandidateId),
            ),
          )
        : undefined;
      const branchFallback =
        normalizeId(matchedBranch?.unitId) ||
        normalizeId(matchedBranch?.unit_id) ||
        normalizeId(matchedBranch?.id) ||
        normalizeId(matchedBranch?.business_id);

      const targetUnitId =
        normalizeId(branchCandidateId) || normalizeId(branchFallback);

      if (targetUnitId) {
        await fetchInvoiceServices(targetUnitId);
      } else {
        setInvoiceServices([]);
        toast.warn(
          "Branch could not be detected for this invoice. No services loaded.",
          { position: "top-right" },
        );
      }

      setInvoiceEditingRow(invoiceData);
      setInvoiceEditOpen(true);
    } catch (error) {
      console.error("Error fetching invoice:", error);
      showErrorToast(getErrorMessage(error, "Failed to load invoice."));
    } finally {
      setInvoiceEditLoading(false);
    }
  };

  const openClientDetailsViewForId = React.useCallback(
    async (clientId?: string | number | null | Record<string, any>) => {
      const resolvedId = resolveClientIdValue(clientId);
      const id = resolvedId ? String(resolvedId) : "";
      if (!id) {
        toast.error("Client ID not found.");
        return;
      }

      setSelectedClientId(id);
      setClientDetailsViewOpen(true);
      setClientDetailsLoading(true);
      setSelectedClient(null);

      try {
        const client = await getClientById(id);
        setSelectedClient(client ?? null);
      } catch (error) {
        console.error("Error fetching client:", error);
        toast.error("Failed to load client.");
      } finally {
        setClientDetailsLoading(false);
      }
    },
    [],
  );

  const handleClientDetailsViewClose = React.useCallback(() => {
    setClientDetailsViewOpen(false);
    setSelectedClient(null);
  }, []);

  const fetchClientHistoryInvoices = React.useCallback(
    async (clientId?: string | null) => {
      if (!clientId) return;
      setClientHistoryLoading(true);
      try {
        const response = await getAllInvoicesForUser(String(clientId));
        let invoicesData: any[] = [];
        if (response && response.invoices) {
          invoicesData = response.invoices;
        } else if (response && response.data) {
          invoicesData = response.data;
        } else if (Array.isArray(response)) {
          invoicesData = response;
        }
        setClientHistoryInvoices(
          Array.isArray(invoicesData) ? invoicesData : [],
        );
      } catch (error) {
        console.error("Error fetching invoices:", error);
        setClientHistoryInvoices([]);
      } finally {
        setClientHistoryLoading(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (clientHistoryDialogOpen && selectedClientId) {
      fetchClientHistoryInvoices(selectedClientId);
    }
  }, [clientHistoryDialogOpen, selectedClientId, fetchClientHistoryInvoices]);

  const helperBranchId = clientFieldHelpersRef.current?.values?.branch_id;
  const normalizedHelperBranchId =
    helperBranchId && String(helperBranchId) !== "all"
      ? String(helperBranchId)
      : "";
  const addClientBranchId =
    normalizedHelperBranchId ||
    (formBranchId && formBranchId !== "all"
      ? String(formBranchId)
      : selectedBranchId !== "all"
        ? String(selectedBranchId)
        : venueId
          ? String(venueId)
          : null);

  const buildAppointmentFields = (extraFields: Field[] = []): Field[] => {
    const clientOptions =
      clients && Array.isArray(clients.users)
        ? clients.users.map((c) => ({
            label: formatClientLabel(c),
            value: String(c.id || c._id || ""),
          }))
        : [];
    const serviceOptions =
      services && Array.isArray(services)
        ? services.map((s) => {
            const svc: any = s as any;
            const name =
              svc?.name ||
              svc?.title ||
              svc?.service_name ||
              svc?.label ||
              `Service ${svc?.id || svc?._id || ""}`;
            const duration = svc?.duration ?? svc?.length ?? svc?.minutes;
            const price = svc?.price ?? svc?.cost;
            let label = String(name);
            if (typeof duration === "number" && !isNaN(duration)) {
              label += ` (${duration}m)`;
            }
            if (price !== undefined && price !== null && price !== "") {
              label += ` - ${price}$`;
            }
            return {
              label,
              value: String(svc.id || svc._id || svc.service_id || ""),
            };
          })
        : [];
    const branchOptions = Array.isArray(branches)
      ? branches.map((branch: any) => ({
          label: String(
            branch?.name || branch?.title || branch?.id || "Branch",
          ),
          value: String(branch?.id || ""),
        }))
      : [];
    const loadBranchServices = async (branchId: string) => {
      try {
        const response = (await getAllServices(branchId)) as any;
        let servicesData: any[] = [];
        if (response && response.services) {
          servicesData = response.services;
        } else if (response && response.data && response.data.services) {
          servicesData = response.data.services;
        } else if (response && response.data && Array.isArray(response.data)) {
          servicesData = response.data;
        } else if (Array.isArray(response)) {
          servicesData = response;
        }
        setServices(Array.isArray(servicesData) ? servicesData : []);
      } catch (error) {
        console.error("Error fetching branch services:", error);
      }
    };

    const branchField: Field = {
      name: "branch_id",
      label: "Branch",
      type: "dropdown" as const,
      colSpan: 1 as const,
      customRender: (value: any, _onChange: any, helpers: any) => {
        const currentValue = value ? String(value) : "";
        const hasCurrent = currentValue
          ? branchOptions.some(
              (option) => String(option.value) === currentValue,
            )
          : false;
        const fallbackLabel =
          currentValue === "all"
            ? "All Branches"
            : currentValue
              ? `Branch ${currentValue}`
              : "Branch";
        const options =
          hasCurrent || !currentValue
            ? branchOptions
            : [{ label: fallbackLabel, value: currentValue }, ...branchOptions];
        return (
          <>
            <CustomLabel text="Branch" fontWeight={500} fontSize={14} />
            <CustomSelect
              label="Branch"
              value={currentValue}
              options={options}
              onChange={(v: any) => {
                const nextVal = String(v ?? "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("branch_id", nextVal);
                  if (nextVal !== currentValue) {
                    helpers.setFieldValue("service_id", "");
                  }
                }
                if (nextVal && nextVal !== "all") {
                  void loadBranchServices(nextVal);
                }
              }}
              fullWidth
            />
          </>
        );
      },
    };
    const clientDisplayField: Field = {
      name: "client_summary",
      label: "Client",
      type: "text",
      colSpan: 2,
      customRender: () => {
        const raw = editingEventRaw || {};
        const rawClientRef =
          raw?.client_id || raw?.clientId || raw?.client || formClientId;
        const normalizedClientId = resolveClientIdValue(rawClientRef);
        const matchedClient = (clients?.users || []).find(
          (c) =>
            String(c.id || c._id || "") === String(normalizedClientId || ""),
        );
        const displayName =
          pickNonEmptyString(
            matchedClient?.name,
            matchedClient?.email,
            resolveClientName(raw),
            formCustomerName,
          ) || "Client";
        const rawPhone = pickNonEmptyString(
          matchedClient?.mobile,
          matchedClient?.phone,
          resolveClientPhone(raw),
        );
        const rawCountry = pickNonEmptyString(
          matchedClient?.country,
          matchedClient?.country_code,
          matchedClient?.countryCode,
          resolveClientCountryCode(raw),
        );
        const displayPhone =
          formatPhoneWithCountry(rawPhone, rawCountry) || "-";
        const canOpenHistory = Boolean(normalizedClientId);
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                cursor: canOpenHistory ? "pointer" : "default",
                color: "text.primary",
                textDecoration: "none",
                width: "fit-content",
              }}
              onClick={() => {
                if (canOpenHistory) {
                  void openClientDetailsViewForId(normalizedClientId);
                }
              }}>
              {displayName}
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "text.secondary" }}>
              {displayPhone}
            </Typography>
          </Box>
        );
      },
    };
    const clientField: Field = {
      name: "client_id",
      label: "Client",
      type: "dropdown",
      colSpan: 1 as const,
      options: clientOptions,
      searchable: true,
      disabled: Boolean(editingEventId),
      customRender: (value, onChange, helpers, error) => {
        if (helpers) {
          clientFieldHelpersRef.current = helpers;
        }
        const resolvedBranchId =
          helpers?.values?.branch_id &&
          String(helpers.values.branch_id) !== "all"
            ? String(helpers.values.branch_id)
            : addClientBranchId;
        return (
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomLabel text="Client" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Client"
                value={value || ""}
                options={clientOptions}
                searchable={true}
                onSearchInput={handleClientSearchInput}
                error={Boolean(error)}
                helperText={error}
                disabled={Boolean(editingEventId)}
                onChange={(v: any) => {
                  const str = String(v ?? "");
                  if (helpers && helpers.setFieldValue) {
                    helpers.setFieldValue("client_id", str);
                  } else {
                    onChange(str);
                  }
                }}
                fullWidth
              />
            </Box>
            {!editingEventId && (
              <Button
                variant="contained"
                onClick={() => {
                  if (!resolvedBranchId) {
                    toast.warning(
                      "Please select a branch before adding a client",
                    );
                    return;
                  }
                  setAddClientDialogOpen(true);
                }}
                aria-label="Add client"
                sx={{
                  minWidth: 36,
                  width: 36,
                  height: 36,
                  minHeight: 36,
                  borderRadius: "10px",
                  p: 0,
                  lineHeight: 1,
                  mb: 0.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "primary.main",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                }}>
                <AddRounded sx={{ fontSize: 18 }} />
              </Button>
            )}
          </Box>
        );
      },
    };
    const serviceField: Field = {
      name: "service_id",
      label: "Service",
      type: "dropdown",
      required: Boolean(editingEventId),
      customRender: (value, onChange, helpers, error) => {
        const opts = serviceOptions.map((s) => ({
          label: s.label,
          value: s.value,
        }));
        const currentValue = value ? String(value) : "";
        const hasCurrent = currentValue
          ? opts.some((o) => String(o.value) === currentValue)
          : false;
        const fallbackLabel =
          editingEventRaw?.service?.name ||
          (currentValue ? `Service ${currentValue}` : "Service");
        const mergedOpts =
          hasCurrent || !currentValue
            ? opts
            : [{ label: String(fallbackLabel), value: currentValue }, ...opts];
        return (
          <>
            <CustomLabel text="Service" fontWeight={500} fontSize={14} />

            <CustomSelect
              label="Service"
              value={currentValue}
              options={mergedOpts}
              error={Boolean(error)}
              helperText={error}
              onChange={(v: any) => {
                const str = String(v || "");
                if (helpers && helpers.setFieldValue) {
                  helpers.setFieldValue("service_id", str);
                  const currentStart =
                    helpers.values && helpers.values["time_start"]
                      ? helpers.values["time_start"]
                      : formTimeStart;
                  try {
                    const dur = findServiceDuration(str);
                    if (dur && currentStart) {
                      const newEndDesired = addMinutesToTime(
                        String(currentStart),
                        dur,
                      );
                      const clamped = clampEndTime(
                        String(currentStart),
                        newEndDesired,
                        MAX_APPOINTMENT_MINUTES,
                      );
                      helpers.setFieldValue("time_end", clamped);
                    }
                  } catch (e) {}
                }
              }}
              fullWidth
            />
          </>
        );
      },
    };

    const fields: Field[] = [];
    if (editingEventId) {
      fields.push(clientDisplayField, branchField);
    } else {
      fields.push(clientField);
      if (showBranchFieldInCreate) {
        fields.push(branchField);
      }
    }
    fields.push(serviceField);
    return [
      ...fields,
      { name: "date", label: "Date", type: "date", required: true },
      {
        name: "time_start",
        label: "Start time",
        type: "text",
        required: true,
        disabled: false,
        customRender: (value, _onChange, helpers) => {
          const currentVal = value ? String(value) : "";
          const hasCurrent = currentVal
            ? timeOptions.some((option) => option.value === currentVal)
            : false;
          const startOptions =
            hasCurrent || !currentVal
              ? timeOptions
              : [{ label: currentVal, value: currentVal }, ...timeOptions];
          return (
            <>
              <CustomLabel text="Start time" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Start time"
                value={currentVal}
                options={startOptions}
                onChange={(v: any) => {
                  const nextVal = String(v ?? "");
                  if (helpers && helpers.setFieldValue) {
                    helpers.setFieldValue("time_start", nextVal);
                    const helperValues = helpers.values || {};
                    const serviceValue = Object.prototype.hasOwnProperty.call(
                      helperValues,
                      "service_id",
                    )
                      ? helperValues["service_id"]
                      : helperValues["service"];
                    const svcId =
                      serviceValue !== undefined && serviceValue !== null
                        ? String(serviceValue)
                        : formServiceId;
                    try {
                      const dur = findServiceDuration(String(svcId || ""));
                      if (dur && nextVal) {
                        const desired = addMinutesToTime(nextVal, dur);
                        const clamped = clampEndTime(
                          nextVal,
                          desired,
                          MAX_APPOINTMENT_MINUTES,
                        );
                        helpers.setFieldValue("time_end", clamped);
                      }
                    } catch (e) {}
                  }
                }}
                fullWidth
              />
            </>
          );
        },
      },
      {
        name: "time_end",
        label: "End time",
        type: "text",
        required: true,
        disabled: true,
        customRender: (value) => {
          const display = formatTimeHHMM(value ?? "");
          return (
            <>
              <CustomLabel text="End time" fontWeight={500} fontSize={14} />
              <CustomTextField value={display} fullWidth disabled />
            </>
          );
        },
      },
      {
        name: "promo_id",
        label: "Promo",
        colSpan: 1 as const,
        disabled: Boolean(editingEventId),
        customRender: (value: any, _onChange: any, helpers: any) => {
          const hasLockedPromo = Boolean(editingEventId);
          const selectedRewardId = String(helpers?.values?.loyaltyRewardId || "");
          const selectedServiceId = String(helpers?.values?.service_id || "");
          const selectedReward = loyaltyRewards.find(
            (reward: any) => String(reward?.id) === selectedRewardId,
          );
          const selectedRewardType = String(
            (selectedReward as any)?.reward_type ??
              (selectedReward as any)?.rewardType ??
              "",
          ).toUpperCase();
          const rewardServiceId = String(
            (selectedReward as any)?.reward_item_id ??
              (selectedReward as any)?.rewardItemId ??
              "",
          );
          const allRewardServices = [
            ...(Array.isArray(services) ? services : []),
            ...(Array.isArray(rewardServices) ? rewardServices : []),
          ];
          const getServiceId = (service: any) =>
            String(service?.id || service?._id || service?.service_id || "");
          const getServiceName = (service: any) =>
            String(
              service?.name ||
                service?.title ||
                service?.service_name ||
                service?.label ||
                "",
            )
              .trim()
              .toLowerCase();
          const getServicePrice = (service: any) => {
            const numeric = Number(
              service?.price ?? service?.cost ?? service?.unit_price,
            );
            return Number.isFinite(numeric) ? Math.round(numeric * 100) / 100 : null;
          };
          const selectedService = allRewardServices.find(
            (service) => getServiceId(service) === selectedServiceId,
          );
          const rewardService = allRewardServices.find(
            (service) => getServiceId(service) === rewardServiceId,
          );
          // Blocked only when the reward's free time covers the whole
          // booking; a shorter one leaves a remainder the promo can discount.
          const rewardCoversSelectedService =
            selectedRewardType === "SERVICE" &&
            Boolean(selectedServiceId) &&
            Boolean(rewardService) &&
            getServiceDurationMinutes(rewardService) > 0 &&
            getServiceDurationMinutes(rewardService) >=
              getServiceDurationMinutes(selectedService);
          const showServiceRewardPromoNotice =
            Boolean(value) && rewardCoversSelectedService;
          return (
            <>
              <CustomLabel text="Promo Code" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Promo Code"
                value={value || ""}
                options={[
                  { label: "None", value: "" },
                  ...promos.map((p) => ({
                    label: p.code,
                    value: p.id,
                  })),
                ]}
                disabled={hasLockedPromo}
                error={showServiceRewardPromoNotice}
                onChange={(v: any) => {
                  if (hasLockedPromo) return;
                  helpers?.setFieldValue("promo_id", v || undefined);
                }}
                fullWidth
                sx={appointmentSelectInputSx}
              />
              <Box sx={appointmentHelperSlotSx}>
                {showServiceRewardPromoNotice && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ display: "block", lineHeight: 1.2 }}>
                    This reward already covers the whole booking.
                  </Typography>
                )}
              </Box>
            </>
          );
        },
      },
      ...(editingEventId
        ? [
            {
              name: "status_and_delete",
              label: "Actions",
              type: "text" as const,
              colSpan: 2 as const,
              customRender: (value: any, onChange: any, helpers: any) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-end",
                      width: "100%",
                    }}>
                    <Box sx={{ flex: 1 }}>
                      <CustomLabel
                        text="Status"
                        fontWeight={500}
                        fontSize={14}
                      />
                      <CustomSelect
                        label="Status"
                        value={helpers?.values?.status || "PENDING"}
                        options={[
                          { label: "Cancelled", value: "CANCELLED" },
                          { label: "In Progress", value: "IN_PROGRESS" },
                          { label: "Pending", value: "PENDING" },
                          { label: "Done", value: "DONE" },
                        ]}
                        onChange={(v: any) => {
                          if (helpers && helpers.setFieldValue) {
                            helpers.setFieldValue("status", v);
                          }
                        }}
                        fullWidth
                      />
                    </Box>
                  </Box>
                );
              },
            },
          ]
        : []),
      ...extraFields,
      {
        name: "notes",
        label: "Notes",
        colSpan: 2 as const,
        customRender: (value, _onChange, helpers) => (
          <Box>
            <CustomLabel text="Notes" fontWeight={500} fontSize={14} />
            <CustomTextField
              label=""
              value={value || ""}
              onChange={(e: any) =>
                helpers?.setFieldValue("notes", e.target.value)
              }
              fullWidth
              multiline
              minRows={3}
              maxRows={6}
            />
          </Box>
        ),
      },
    ];
  };

  const RecurrenceMonthDaysPicker: React.FC<{
    value: number[];
    onChange: (next: number[]) => void;
    referenceDate?: string;
  }> = ({ value, onChange, referenceDate }) => {
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement | null>(null);
    const selectedDays = Array.isArray(value)
      ? value.filter((v) => Number.isFinite(v))
      : [];
    const anchorDate = referenceDate
      ? new Date(`${referenceDate}T00:00:00`)
      : new Date();

    const toggleDay = (dayNumber: number) => {
      const next = new Set<number>(selectedDays);
      if (next.has(dayNumber)) {
        next.delete(dayNumber);
      } else {
        next.add(dayNumber);
      }
      onChange(Array.from(next).sort((a, b) => a - b));
    };

    const CustomDay = (dayProps: any) => {
      const { day, outsideCurrentMonth, ...other } = dayProps;
      const dayNumber = day?.getDate?.() ?? 0;
      const isSelected =
        selectedDays.includes(dayNumber) && !outsideCurrentMonth;
      return (
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          selected={isSelected}
          sx={
            isSelected
              ? {
                  backgroundColor: "#1d4ed8",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1e40af" },
                }
              : undefined
          }
        />
      );
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton
          ref={anchorRef}
          aria-label="Pick month days"
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            border: "1px solid #e5e7eb",
            backgroundColor: "#fff",
          }}>
          <CalendarMonthRounded fontSize="small" />
        </IconButton>
        <CustomTextField
          label=""
          value={selectedDays.join(", ")}
          fullWidth
          size="small"
          minWidth="250px"
          onClick={() => setOpen(true)}
          inputProps={{ readOnly: true }}
        />
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-end"
          style={{ zIndex: 1500 }}>
          <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Paper sx={{ p: 1.5, borderRadius: 2, boxShadow: 3 }}>
              <DateCalendar
                value={null}
                referenceDate={anchorDate}
                onChange={(date) => {
                  if (!date) return;
                  toggleDay(date.getDate());
                }}
                slots={{ day: CustomDay }}
              />
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    );
  };

  const RecurrenceCountField: React.FC<{
    value: any;
    onChange: (next: any) => void;
  }> = ({ value, onChange }) => {
    const [editMode, setEditMode] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const options = React.useMemo(
      () =>
        Array.from({ length: 25 }, (_, i) => {
          const v = (i + 1).toString();
          return { label: v, value: v };
        }),
      [],
    );
    const current =
      value === undefined || value === null || value === ""
        ? "1"
        : String(value);
    const displayValue =
      editMode && (value === undefined || value === null)
        ? ""
        : editMode
          ? String(value ?? "")
          : current;

    React.useEffect(() => {
      if (editMode && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [editMode]);

    if (editMode) {
      return (
        <Box>
          <CustomLabel text="Recurrence Count" fontWeight={500} fontSize={14} />
          <CustomTextField
            label=""
            type="number"
            value={displayValue}
            fullWidth
            onChange={(e: any) => {
              const v = e.target.value;
              // Limit input to max 25
              if (v && Number(v) > 25) {
                onChange("25");
              } else {
                onChange(v);
              }
            }}
            onBlur={() => {
              const next = String(inputRef.current?.value ?? "").trim();
              if (!next) {
                onChange("1");
              } else {
                const num = Math.min(25, Math.max(1, Number(next)));
                onChange(String(num));
              }
              setEditMode(false);
            }}
            autoFocus
            inputRef={inputRef}
            inputProps={{ min: 1, max: 25 }}
          />
        </Box>
      );
    }

    return (
      <Box onDoubleClick={() => setEditMode(true)}>
        <CustomLabel text="Recurrence Count" fontWeight={500} fontSize={14} />
        <CustomSelect
          label=""
          value={current}
          options={options}
          fullWidth
          onChange={onChange}
        />
      </Box>
    );
  };

  const blockFields: Field[] = [
    {
      name: "block_action",
      label: "Action",
      type: "dropdown",
      options: [
        { label: "Open Slot", value: "open" },
        { label: "Close Slot", value: "close" },
      ],
      required: true,
      colSpan: 2,
    },
    {
      name: "block_date",
      label: "Date",
      type: "date",
      required: true,
      colSpan: 2,
    },
    {
      name: "block_start",
      label: "Start time",
      type: "dropdown",
      options: timeOptions,
      required: true,
      colSpan: 2,
    },
    {
      name: "block_end",
      label: "End time",
      type: "dropdown",
      options: timeOptions,
      required: true,
      colSpan: 2,
    },
    {
      name: "block_branch_info",
      label: "Branch",
      type: "text",
      colSpan: 2,
      customRender: () => {
        const bId = formBranchId || selectedBranchId;
        const branch = branches.find((b) => String(b.id) === String(bId));
        return (
          <Box
            sx={{
              padding: 2,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              fontSize: "0.9rem",
            }}>
            <strong>Selected Branch:</strong> {branch?.name || "All"}
          </Box>
        );
      },
    },
  ];

  const getRewardItemLabel = React.useCallback(
    (reward: LoyaltyReward) => {
      const rewardType = String(
        (reward as any).reward_type ?? (reward as any).rewardType ?? "",
      ).toUpperCase();
      const rewardItemId = String(
        (reward as any).reward_item_id ?? (reward as any).rewardItemId ?? "",
      );
      if (rewardType === "SERVICE") {
        const service = [...services, ...rewardServices].find(
          (item: any) =>
            String(item?.id || item?._id || item?.service_id || "") ===
            rewardItemId,
        );
        return (
          service?.name ||
          service?.title ||
          service?.service_name ||
          service?.label ||
          rewardItemId
        );
      }

      if (rewardType === "PRODUCT") {
        const productList = Array.isArray(products)
          ? products
          : Array.isArray((products as any)?.data)
            ? (products as any).data
            : [];
        const product = productList.find(
          (item: any) =>
            String(item?.id || item?._id || item?.product_id || "") ===
            rewardItemId,
        );
        return (
          product?.name ||
          product?.title ||
          product?.product_name ||
          product?.label ||
          rewardItemId
        );
      }

      return rewardItemId;
    },
    [products, rewardServices, services],
  );

  const rewardOptions = React.useMemo(
    () => [
      { label: "None", value: "" },
      ...loyaltyRewards.map((reward) => {
        const rewardType = String(
          (reward as any).reward_type ?? (reward as any).rewardType ?? "",
        ).toUpperCase();
        const rewardItemId = String(
          (reward as any).reward_item_id ?? (reward as any).rewardItemId ?? "",
        );
        const redeemPoints =
          (reward as any).redeem_points ?? (reward as any).redeemPoints ?? 0;
        const quantity = getRewardQuantityValue(reward);
        const itemLabel = getRewardItemLabel(reward);
        const hasResolvedName =
          itemLabel && String(itemLabel) !== rewardItemId;
        const label = hasResolvedName
          ? `${itemLabel} (${getRewardTypeLabel(rewardType)}) - ${redeemPoints} pts - qty ${quantity}`
          : `${rewardType} - ${rewardItemId} - ${redeemPoints} pts - qty ${quantity}`;

        return {
          label,
          value: String(reward.id),
        };
      }),
    ],
    [getRewardItemLabel, loyaltyRewards],
  );

  const recurringExtraFields: Field[] = [
    {
      name: "recurrence_mode",
      label: "Recurring",
      colSpan: 1,
      type: "dropdown",
      options: [
        { label: "Not Recurring", value: "NONE" },
        { label: "Recurring", value: "RECURRING" },
      ],
    },
    {
      name: "loyaltyRedeemPoints",
      label: "Loyalty",
      colSpan: 2,
      customRender: (value: any, onChange: (v: any) => void, helpers) => (
        <AppointmentLoyaltyPreview
          value={value}
          onChange={onChange}
          values={helpers?.values || {}}
          services={services}
          rewardServices={rewardServices}
          promos={promos}
          clients={clients?.users || []}
          onClientSearch={handleClientSearchInput}
          sharedClientIds={
            Array.isArray(helpers?.values?.sharedClientIds)
              ? (helpers?.values?.sharedClientIds as (string | number)[])
              : []
          }
          onSharedClientsChange={(selectedClients) =>
            helpers?.setFieldValue("sharedClientIds", selectedClients)
          }
          onDiscountChange={(discount) =>
            helpers?.setFieldValue("loyaltyDiscountPercent", discount)
          }
          onFullInvoiceChange={(onFullInvoice) =>
            helpers?.setFieldValue("loyaltyOnFullInvoice", onFullInvoice)
          }
          rewardOptions={rewardOptions}
          loyaltyRewards={loyaltyRewards}
          stockItems={stockItems}
          rewardBusinessId={
            branches.find(
              (branch) =>
                String(branch?.id) ===
                String(helpers?.values?.branch_id || selectedBranchId || ""),
            )?.business_id ?? null
          }
          loadingLoyaltyRewards={loadingLoyaltyRewards}
          onRewardChange={(rewardId) =>
            helpers?.setFieldValue("loyaltyRewardId", String(rewardId || ""))
          }
          onRedeemRuleChange={(rule) => {
            helpers?.setFieldValue("loyaltyRedeemDiscountType", rule.type);
            helpers?.setFieldValue("loyaltyRedeemDiscountAmount", rule.discount);
          }}
        />
      ),
    },
    /*
      Temporary: Reward Package dropdown is rendered inside
      AppointmentLoyaltyPreview where the Redeem Points input was.
      Keep this old standalone field shape for future layout changes.
      {
        name: "loyaltyRewardId",
        label: "Reward Package",
        colSpan: 2,
        customRender: (value: any, _onChange: (v: any) => void, helpers) => (
          <Box>
            <CustomLabel text="Reward Package" fontWeight={500} fontSize={14} />
            <CustomSelect
              label=""
              value={value || ""}
              options={rewardOptions}
              searchable
              disabled={loadingLoyaltyRewards}
              placeholder={
                loadingLoyaltyRewards ? "Loading rewards..." : "Select reward"
              }
              onChange={(nextValue) =>
                helpers?.setFieldValue("loyaltyRewardId", String(nextValue || ""))
              }
              fullWidth
            />
          </Box>
        ),
      },
    */
    {
      name: "recurrence_frequency",
      label: "Recurring",
      colSpan: 1,
      customRender: (value, _onChange, helpers) => {
        const mode = String(
          helpers?.values?.recurrence_mode || "",
        ).toUpperCase();
        if (mode !== "RECURRING") return null;
        return (
          <Box>
            <CustomLabel text="Recurring" fontWeight={500} fontSize={14} />
            <CustomSelect
              label=""
              value={value || "DAILY"}
              options={[
                { label: "Daily", value: "DAILY" },
                { label: "Weekly", value: "WEEKLY" },
                { label: "Monthly", value: "MONTHLY" },
              ]}
              fullWidth
              onChange={(v: any) =>
                helpers?.setFieldValue("recurrence_frequency", v)
              }
            />
          </Box>
        );
      },
    },
    {
      name: "recurrence_count",
      label: "Recurrence Count",
      colSpan: 1,
      customRender: (value, _onChange, helpers) =>
        (() => {
          const mode = String(
            helpers?.values?.recurrence_mode || "",
          ).toUpperCase();
          if (mode !== "RECURRING") return null;
          return (
            <RecurrenceCountField
              value={value}
              onChange={(v: any) =>
                helpers?.setFieldValue("recurrence_count", v)
              }
            />
          );
        })(),
    },
    {
      name: "recurrence_interval",
      label: "Recurrence Interval",
      colSpan: 1,
      customRender: (value, _onChange, helpers) => {
        const mode = String(
          helpers?.values?.recurrence_mode || "",
        ).toUpperCase();
        if (mode !== "RECURRING") return null;
        const frequency = String(
          helpers?.values?.recurrence_frequency || "",
        ).toUpperCase();
        const intervalValue =
          value === undefined || value === null || value === ""
            ? "1"
            : String(value);

        const buildLabel = (n: number) => {
          if (frequency === "DAILY") return `Every ${n} day${n > 1 ? "s" : ""}`;
          if (frequency === "WEEKLY")
            return `Every ${n} week${n > 1 ? "s" : ""}`;
          if (frequency === "MONTHLY")
            return `Every ${n} month${n > 1 ? "s" : ""}`;
          return `Every ${n}`;
        };

        const options = Array.from({ length: 50 }, (_, i) => {
          const val = (i + 1).toString();
          return { label: buildLabel(i + 1), value: val };
        });

        return (
          <Box>
            <CustomLabel
              text="Recurrence Interval"
              fontWeight={500}
              fontSize={14}
            />
            <CustomSelect
              label="Recurrence Interval"
              value={intervalValue}
              options={options}
              fullWidth
              onChange={(v: any) => {
                helpers?.setFieldValue("recurrence_interval", v);
              }}
            />
          </Box>
        );
      },
    },
    {
      name: "recurrence_month_days",
      label: "Month Days",
      type: "text",
      colSpan: 1,
      customRender: (value, _onChange, helpers) => {
        const mode = String(
          helpers?.values?.recurrence_mode || "",
        ).toUpperCase();
        if (mode !== "RECURRING") return null;
        const frequency = String(
          helpers?.values?.recurrence_frequency || "",
        ).toUpperCase();
        if (frequency && frequency !== "MONTHLY") {
          return null;
        }
        return (
          <Box>
            <CustomLabel text="Month Days" fontWeight={500} fontSize={14} />
            <RecurrenceMonthDaysPicker
              value={
                Array.isArray(value)
                  ? value
                      .map((v: any) => Number(v))
                      .filter((v: number) => Number.isFinite(v))
                  : []
              }
              onChange={(next) =>
                helpers?.setFieldValue("recurrence_month_days", next)
              }
              referenceDate={helpers?.values?.date}
            />
          </Box>
        );
      },
    },
    {
      name: "recurrence_week_days",
      label: "Weekdays",
      type: "text",
      colSpan: 2,
      customRender: (value, _onChange, helpers) => {
        const mode = String(
          helpers?.values?.recurrence_mode || "",
        ).toUpperCase();
        if (mode !== "RECURRING") return null;
        const frequency = String(
          helpers?.values?.recurrence_frequency || "",
        ).toUpperCase();
        if (frequency && frequency !== "WEEKLY") {
          return null;
        }
        const selected = Array.isArray(value) ? value : [];
        const toggleDay = (day: number, checked: boolean) => {
          const next = new Set<number>(selected);
          if (checked) {
            next.add(day);
          } else {
            next.delete(day);
          }
          helpers?.setFieldValue("recurrence_week_days", Array.from(next));
        };
        return (
          <Box>
            <CustomLabel text="Weekdays" fontWeight={500} fontSize={14} />
            <FormGroup row>
              {[
                { label: "Sun", value: 0 },
                { label: "Mon", value: 1 },
                { label: "Tue", value: 2 },
                { label: "Wed", value: 3 },
                { label: "Thu", value: 4 },
                { label: "Fri", value: 5 },
                { label: "Sat", value: 6 },
              ].map((day) => (
                <FormControlLabel
                  key={day.value}
                  control={
                    <Checkbox
                      checked={selected.includes(day.value)}
                      onChange={(e) => toggleDay(day.value, e.target.checked)}
                    />
                  }
                  label={day.label}
                />
              ))}
            </FormGroup>
          </Box>
        );
      },
    },
    {
      name: "send_notifications",
      label: "Send notifications",
      type: "text",
      colSpan: 1,
      customRender: (value, _onChange, helpers) => {
        const mode = String(
          helpers?.values?.recurrence_mode || "",
        ).toUpperCase();
        if (mode !== "RECURRING") return null;
        const checked = Boolean(value);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "130%",
            }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={(e) =>
                    helpers?.setFieldValue(
                      "send_notifications",
                      e.target.checked,
                    )
                  }
                />
              }
              label="Send notifications"
            />
          </Box>
        );
      },
    },
  ];

  const appointmentFields: Field[] = buildAppointmentFields(
    editingEventId ? [] : recurringExtraFields,
  );

  // Same units list the appointment/block tabs already use for their branch
  // picker, just remapped to {label, value} for the class form's dropdown.
  const classUnitOptions = React.useMemo(
    () =>
      Array.isArray(branches)
        ? branches.map((branch: any) => ({
            label: String(branch?.name || branch?.title || branch?.id || "Branch"),
            value: String(branch?.id || ""),
          }))
        : [],
    [branches],
  );
  const { options: classCoachOptions } = useCoachOptions(
    addDialogOpen && !editingEventId,
  );
  const { options: classCategoryOptions } = useCategoryOptions(
    addDialogOpen && !editingEventId,
  );
  const classFields: Field[] = React.useMemo(
    () =>
      buildClassFormFields({
        isEdit: false,
        unitOptions: classUnitOptions,
        coachOptions: classCoachOptions,
        categoryOptions: classCategoryOptions,
        frequency: classFormFrequency,
        endMode: classFormEndMode,
      }),
    [
      classUnitOptions,
      classCoachOptions,
      classCategoryOptions,
      classFormFrequency,
      classFormEndMode,
    ],
  );

  const dialogTabs = editingEventId
    ? undefined
    : [
        { label: "Add Appointment", fields: appointmentFields },
        { label: "Block Slots", fields: blockFields },
        // With CLASSES off there is nothing to create, so the tab goes away
        // rather than opening a form that the API would reject.
        ...(classesEnabled
          ? [{ label: "Add Class", fields: classFields }]
          : []),
      ];

  const dialogSubmitLabel = editingEventId
    ? "Update"
    : appointmentDialogTab === 1
      ? "Apply"
      : appointmentDialogTab === 2
        ? "Create Class"
        : "Create";
  const handleAppointmentDialogClose = () => {
    setAddDialogOpen(false);
    setEditingEventId(null);
    setEditingEventRaw(null);
    setEditingEventStarted(false);
    setSelectedBlockId(null);
    setDeleteConfirmOpen(false);
    setClassFormFrequency("NONE");
    setClassFormEndMode("COUNT");
  };
  const handleInvoiceDialogClose = () => {
    setInvoiceEditOpen(false);
    setInvoiceEditingRow(null);
    setInvoiceServices([]);
    setInvoiceSubmitLoading(false);
  };

  const appointmentInvoiceNumber = React.useMemo(() => {
    if (!editingEventId) return undefined;
    const raw: any = editingEventRaw || {};
    return pickNonEmptyString(
      raw?.invoice?.Invoice_number,
      raw?.invoice?.invoice_number,
      raw?.Invoice_number,
      raw?.invoice_number,
      raw?.invoiceId,
      raw?.invoice_id,
      raw?.invoice?.id,
      raw?.invoice?.invoiceId,
      raw?.invoice?.invoice_id,
    );
  }, [editingEventId, editingEventRaw]);

  const invoiceDialogInvoiceNumber = React.useMemo(() => {
    const raw: any = invoiceEditingRow || {};
    return pickNonEmptyString(
      raw?.invoice?.Invoice_number,
      raw?.invoice?.invoice_number,
      raw?.Invoice_number,
      raw?.invoice_number,
      raw?.invoiceId,
      raw?.invoice_id,
      raw?.invoice?.id,
      raw?.id,
    );
  }, [invoiceEditingRow]);

  const effectiveAppointmentInvoiceNumber = appointmentInvoiceNumber;
  const dialogTitleText = editingEventId
    ? "Edit Appointment"
    : "Add Appointment";
  const dialogTitle = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "primary.main" }}>
          {dialogTitleText}
        </Typography>
        <IconButton aria-label="Close" onClick={handleAppointmentDialogClose}>
          <CloseRounded />
        </IconButton>
      </Box>
      {effectiveAppointmentInvoiceNumber && (
        <Typography sx={{ fontSize: "1rem", color: COLORS.placeHolderColor }}>
          Invoice #: {effectiveAppointmentInvoiceNumber}
        </Typography>
      )}
      <Divider />
    </Box>
  );
  const appointmentDialogActions = editingEventId
    ? [
        <CustomButton
          key="invoice"
          label={invoiceEditLoading ? "Loading..." : "Invoice"}
          variant="outlined"
          onClick={openInvoiceEditDialog}
          disabled={invoiceEditLoading}
        />,
        <CustomButton
          key="delete"
          label="Delete Appointment"
          bgColor={COLORS.RED}
          textColor={COLORS.WhiteText}
          onClick={handleRequestDeleteAppointment}
        />,
      ]
    : [];

  const dialogInitialValues = React.useMemo(
    () => ({
      client_id: formClientId || "",
      service_id: formServiceId || "",
      promo_id: formPromoId || "",
      branch_id: formBranchId || selectedBranchId || "all",
      date: formDate,
      time_start: formTimeStart,
      time_end: formTimeEnd,
      notes: formNotes,
      status: formStatus || "PENDING",
      loyaltyRedeemPoints: "",
      loyaltyDiscountPercent: 0,
      loyaltyOnFullInvoice: getCachedFullInvoiceEnabled(),
      loyaltyRedeemDiscountType: "PERCENT",
      loyaltyRedeemDiscountAmount: 0,
      loyaltyRewardId: "",
      sharedClientIds: [],
      send_notifications: false,
      recurrence_mode: "NONE",
      recurrence_frequency: "WEEKLY",
      recurrence_interval: 1,
      recurrence_count: 1,
      recurrence_week_days: [],
      recurrence_month_days: [],
      block_action: blockAction,
      block_date: blockDate,
      block_start: blockStart,
      block_end: blockEnd,
      ...buildClassFormInitialValues({
        classItem: null,
        defaultUnitId:
          formBranchId && formBranchId !== "all"
            ? formBranchId
            : selectedBranchId && selectedBranchId !== "all"
              ? selectedBranchId
              : undefined,
        defaultStartDate: formDate,
        defaultStartTime: formTimeStart,
      }),
    }),
    [
      formClientId,
      formServiceId,
      formPromoId,
      formBranchId,
      selectedBranchId,
      formDate,
      formTimeStart,
      formTimeEnd,
      formNotes,
      formStatus,
      blockAction,
      blockDate,
      blockStart,
      blockEnd,
    ],
  );

  const ALL_INVOICE_STATUSES = [
    "PENDING",
    "PAID",
    "PARTIALLY_PAID",
    "UNPAID",
    "REFUNDED",
    "PARTIALLY_REFUNDED",
    "CANCELLED",
  ];

  // For appointment view, we only allow simple actions: full pay, full unpaid (full refund), or cancel.
  // Other status changes should be handled in the Invoices page.
  const APPOINTMENT_ALLOWED_TARGETS = new Set(["PAID", "UNPAID", "CANCELLED"]);

  const ALLOWED_STATUS_TRANSITIONS = new Set<string>([
    "PENDING->PENDING",
    "PENDING->PAID",
    "PENDING->CANCELLED",
    "UNPAID->UNPAID",
    "UNPAID->PAID",
    "UNPAID->CANCELLED",
    "PARTIALLY_PAID->PARTIALLY_PAID",
    "PARTIALLY_PAID->PAID",
    "PARTIALLY_PAID->UNPAID",
    "PARTIALLY_PAID->CANCELLED",
    "PAID->PAID",
    "PAID->UNPAID",
    "PAID->REFUNDED",
    "PAID->CANCELLED",
    "REFUNDED->REFUNDED",
    "REFUNDED->PAID",
    "REFUNDED->CANCELLED",
    "PARTIALLY_REFUNDED->PARTIALLY_REFUNDED",
    "PARTIALLY_REFUNDED->PAID",
    "PARTIALLY_REFUNDED->REFUNDED",
    "PARTIALLY_REFUNDED->UNPAID",
    "PARTIALLY_REFUNDED->CANCELLED",
    "CANCELLED->CANCELLED",
    "CANCELLED->PAID",
  ]);

  const invoiceFormFields: Field[] = React.useMemo(() => {
    const clientOptions =
      clients && Array.isArray(clients.users)
        ? clients.users.map((c) => ({
            label: formatClientLabel(c),
            value: String(c.id || c._id || ""),
          }))
        : [];

    const normalizeInvoiceStatus = (status: any) =>
      typeof status === "string" ? status.trim().toUpperCase() : "";

    const currentInvoiceStatus = normalizeInvoiceStatus(
      invoiceEditingRow?.invoice?.status ||
        invoiceEditingRow?.status ||
        invoiceEditingRow?.invoice?.status_name ||
        invoiceEditingRow?.status_name ||
        "PENDING",
    );

    const filteredStatusOptions =
      currentInvoiceStatus === ""
        ? ALL_INVOICE_STATUSES
        : ALL_INVOICE_STATUSES.filter((status) =>
            ALLOWED_STATUS_TRANSITIONS.has(
              `${currentInvoiceStatus}->${normalizeInvoiceStatus(status)}`,
            ),
          );

    const safeStatusOptions =
      filteredStatusOptions.length > 0
        ? filteredStatusOptions
        : ALL_INVOICE_STATUSES;

    // Limit appointment view to full pay / full refund-to-unpaid / cancel (plus keep current status for display)
    let trimmedStatusOptions = safeStatusOptions.filter((status) => {
      const normalized = normalizeInvoiceStatus(status);
      return (
        APPOINTMENT_ALLOWED_TARGETS.has(normalized) ||
        normalized === currentInvoiceStatus
      );
    });

    // Special rule: when current status is PENDING, only allow move to PAID (or stay PENDING)
    if (currentInvoiceStatus === "PENDING") {
      trimmedStatusOptions = trimmedStatusOptions.filter((status) => {
        const normalized = normalizeInvoiceStatus(status);
        return normalized === "PENDING" || normalized === "PAID";
      });
    }

    const rewardEditingLocked = Boolean(
      invoiceEditingRow && hasInvoiceSuccessfulPayment(invoiceEditingRow),
    );

    return [
      {
        name: "clientName",
        label: "Client",
        type: "dropdown",
        options: clientOptions,
        searchable: true,
        customRender: (value, onChange, helpers, error) => {
          return (
            <>
              <CustomLabel text="Client" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Client"
                value={value || ""}
                options={clientOptions}
                searchable={true}
                onSearchInput={handleClientSearchInput}
                error={Boolean(error)}
                helperText={error}
                onChange={(v: any) => {
                  const str = String(v ?? "");
                  if (helpers && helpers.setFieldValue) {
                    helpers.setFieldValue("clientName", str);
                  } else {
                    onChange(str);
                  }
                  if (!str) return;
                  const selected = (clients?.users || []).find(
                    (c) => String(c.id || c._id || "") === str,
                  );
                  if (selected) {
                    helpers?.setFieldValue("email", selected.email || "");
                    helpers?.setFieldValue(
                      "phoneNumber",
                      selected.mobile || selected.phone || "",
                    );
                    return;
                  }
                  getClientById(str)
                    .then((client) => {
                      if (client && client.user) {
                        helpers?.setFieldValue(
                          "email",
                          client.user.email || "",
                        );
                        helpers?.setFieldValue(
                          "phoneNumber",
                          client.user.mobile || "",
                        );
                      }
                    })
                    .catch(console.error);
                }}
                fullWidth
                placeholder="Select..."
              />
            </>
          );
        },
      },
      { name: "email", label: "Email" },
      { name: "phoneNumber", label: "Phone Number" },
      { name: "invoice_date", label: "Date", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "dropdown",
        options: trimmedStatusOptions,
      },
      {
        name: "paymentMethod",
        label: "Payment method",
        type: "dropdown",
        options: ["cash"],
        disabled: true,
      },
      {
        name: "loyaltyRedeemPoints",
        label: "Loyalty",
        colSpan: 2,
        customRender: (value: any, _onChange: (v: any) => void, helpers) => (
          <LoyaltyInvoicePreview
            value={value}
            values={{
              ...(helpers?.values || {}),
              sourceInvoice: invoiceEditingRow,
            }}
            clients={clients?.users || []}
            onDiscountChange={(discount) =>
              helpers?.setFieldValue("discount", discount)
            }
          />
        ),
      },
      {
        name: "loyaltyRewardId",
        label: "Reward Package",
        colSpan: 2,
        customRender: (value: string, onChange: (v: string) => void, helpers) => {
          return (
            <InvoiceRewardPackageField
              value={value}
              onChange={onChange}
              helpers={helpers}
              invoiceEditingRow={invoiceEditingRow}
              loyaltyRewards={loyaltyRewards}
              products={products}
              services={invoiceServices}
              rewardServices={rewardServices}
              stockItems={stockItems}
              venueId={venueId}
              loadingLoyaltyRewards={loadingLoyaltyRewards}
              rewardEditingLocked={rewardEditingLocked}
            />
          );

          const existingRewards = extractInvoiceLoyaltyRewardDetailsList(
            invoiceEditingRow,
          );
          const existingReward = existingRewards[0] || null;
          const hasSavedServiceReward = Boolean(
            existingRewards.some(
              (reward) =>
                reward?.rewardId &&
                String(reward?.rewardType || "").toUpperCase() === "SERVICE",
            ),
          );
          const currentItemsForOptions: any[] = Array.isArray(
            helpers?.values?.items,
          )
            ? (helpers?.values?.items as any[])
            : [];
          const invoiceServiceItems = currentItemsForOptions.filter(
            (item: any) => getInvoiceLineType(item) !== "PRODUCT",
          );
          const allKnownServices = [
            ...(Array.isArray(services) ? services : []),
            ...(Array.isArray(rewardServices) ? rewardServices : []),
          ];
          const normalizeServiceName = (value: any) =>
            String(value || "").trim().toLowerCase();
          const normalizeServicePrice = (value: any) => {
            const numeric = Number(value);
            return Number.isFinite(numeric)
              ? Math.round(numeric * 100) / 100
              : null;
          };
          const getServiceId = (service: any) =>
            String(service?.id || service?._id || service?.service_id || "");
          const findKnownServiceById = (serviceId: string) =>
            allKnownServices.find(
              (service: any) => getServiceId(service) === serviceId,
            ) || null;
          const getServiceNameForMatch = (source: any) =>
            source?.name ||
            source?.title ||
            source?.service_name ||
            source?.label ||
            source?.description ||
            "";
          const getServicePriceForMatch = (source: any) =>
            source?.price ??
            source?.cost ??
            source?.unit_price ??
            source?.unitPrice ??
            source?.unitCost ??
            source?.amount;
          const serviceRewardMatchesInvoice = (reward: any) => {
            const rewardItemId = getRewardItemIdValue(reward);
            if (!rewardItemId) return false;

            const rewardService = findKnownServiceById(rewardItemId);
            const rewardName = normalizeServiceName(
              getServiceNameForMatch(rewardService),
            );
            const rewardPrice = normalizeServicePrice(
              getServicePriceForMatch(rewardService),
            );

            return invoiceServiceItems.some((item: any) => {
              const itemServiceId = getInvoiceLineItemId(item);
              if (itemServiceId && itemServiceId === rewardItemId) return true;

              const invoiceService = findKnownServiceById(itemServiceId);
              const invoiceName = normalizeServiceName(
                getServiceNameForMatch(invoiceService || item),
              );
              const invoicePrice = normalizeServicePrice(
                getServicePriceForMatch(invoiceService || item),
              );

              return (
                Boolean(rewardName && invoiceName) &&
                rewardName === invoiceName &&
                rewardPrice !== null &&
                invoicePrice !== null &&
                rewardPrice === invoicePrice
              );
            });
          };
          const productRewardOptions = rewardOptions.filter((option) => {
            if (!option.value) return true;
            const reward = loyaltyRewards.find(
              (item: any) => String(item.id) === String(option.value),
            );
            if (!reward) return false;
            return getRewardTypeValue(reward) === "PRODUCT";
          });
          const serviceRewardOptions = rewardOptions.filter((option) => {
            if (!option.value) return true;
            const reward = loyaltyRewards.find(
              (item: any) => String(item.id) === String(option.value),
            );
            if (!reward) return false;
            if (hasSavedServiceReward) return false;
            if (getRewardTypeValue(reward) !== "SERVICE") return false;
            return serviceRewardMatchesInvoice(reward);
          });
          const editInvoiceRewardOptions = [
            ...productRewardOptions,
            ...serviceRewardOptions.filter((option) => option.value),
          ];
          const lockedMessage =
            "Rewards cannot be changed after a payment has been recorded. Refund the payment or mark the invoice unpaid first.";

          const getRewardById = (rewardId: any) =>
            loyaltyRewards.find(
              (item: any) => String(item.id) === String(rewardId || ""),
            );

          const currentProductRewardIds = Array.isArray(
            helpers?.values?.loyaltyProductRewardIds,
          )
            ? (helpers?.values?.loyaltyProductRewardIds as any[]).map(String)
            : currentItemsForOptions
                .filter(
                  (item: any) =>
                    getInvoiceLineType(item) === "PRODUCT" &&
                    (getInvoiceLineMetadata(item)?.loyaltyRewardId ||
                      item?.loyaltyRewardId),
                )
                .map((item: any) =>
                  String(
                    getInvoiceLineMetadata(item)?.loyaltyRewardId ||
                      item?.loyaltyRewardId,
                  ),
                );
          const currentServiceRewardId =
            hasSavedServiceReward
              ? ""
              : String(helpers?.values?.loyaltyServiceRewardId || "");

          const buildRewardMetadata = (reward: any) => {
            const rewardType = getRewardTypeValue(reward);
            const rewardItemId = getRewardItemIdValue(reward);
            const rewardPoints = getRewardRedeemPointsValue(reward);
            return {
              loyaltyRewardId: Number(reward.id),
              loyaltyRewardType: rewardType,
              loyaltyRewardItemId: rewardItemId,
              loyaltyRewardRedeemPoints: rewardPoints,
              rewardType,
              rewardItemId,
              redeemPoints: rewardPoints,
            };
          };

          const updateRewardMetadata = (rewards: any[]) => {
            const firstReward = rewards[0] || null;
            const rewardIds = rewards
              .map((reward) => Number(reward?.id))
              .filter((id) => Number.isFinite(id) && id > 0);
            helpers?.setFieldValue("loyaltyRewardIds", rewardIds);
            helpers?.setFieldValue(
              "loyaltyRewardId",
              firstReward ? String(firstReward.id) : "",
            );
            helpers?.setFieldValue(
              "loyaltyRewardRedeemPoints",
              rewards.reduce(
                (sum, reward) => sum + getRewardRedeemPointsValue(reward),
                0,
              ),
            );
            helpers?.setFieldValue(
              "loyaltyRewardType",
              firstReward ? getRewardTypeValue(firstReward) : "",
            );
            helpers?.setFieldValue(
              "loyaltyRewardItemId",
              firstReward ? getRewardItemIdValue(firstReward) : "",
            );
            helpers?.setFieldValue(
              "loyaltyRewardQuantity",
              firstReward ? getRewardQuantityValue(firstReward) : 0,
            );
          };

          const applyRewardsToItems = async (
            productRewardIds: string[],
            serviceRewardId: string,
          ) => {
            if (rewardEditingLocked) {
              toast.info(lockedMessage);
              return;
            }

            const currentItems: any[] = Array.isArray(helpers?.values?.items)
              ? (helpers?.values?.items as any[])
              : [];
            const selectedProductIds = Array.from(
              new Set(productRewardIds.map(String).filter(Boolean)),
            );
            const selectedProductRewards = selectedProductIds
              .map(getRewardById)
              .filter((reward): reward is LoyaltyReward => Boolean(reward));
            const selectedServiceReward = serviceRewardId
              ? getRewardById(serviceRewardId)
              : null;
            const selectedRewards = [
              ...selectedProductRewards,
              ...(selectedServiceReward ? [selectedServiceReward] : []),
            ];
            const clearRewardItems = (
              items: any[],
              preserveServiceRewards = false,
            ) =>
              items
                .filter((item: any) => {
                  const metadata = getInvoiceLineMetadata(item);
                  return !(
                    (metadata?.loyaltyRewardId || item?.loyaltyRewardId) &&
                    getInvoiceLineType(item) === "PRODUCT"
                  );
                })
                .map((item: any) => {
                  const metadata = getInvoiceLineMetadata(item);
                  if (!metadata?.loyaltyRewardId && !item?.loyaltyRewardId) {
                    return item;
                  }
                  if (
                    preserveServiceRewards &&
                    getInvoiceLineType(item) !== "PRODUCT"
                  ) {
                    return item;
                  }
                  const quantity = Number(item.quantity || 1) || 1;
                  const unitCost = Number(item.unitCost || 0) || 0;
                  const cleanedMetadata = { ...metadata };
                  delete cleanedMetadata.loyaltyRewardId;
                  delete cleanedMetadata.loyaltyRewardType;
                  delete cleanedMetadata.loyaltyRewardItemId;
                  delete cleanedMetadata.loyaltyRewardRedeemPoints;
                  delete cleanedMetadata.rewardType;
                  delete cleanedMetadata.rewardItemId;
                  delete cleanedMetadata.redeemPoints;
                  return {
                    ...item,
                    loyaltyRewardId: null,
                    loyaltyRewardType: null,
                    discount: 0,
                    totalPrice: Math.round(quantity * unitCost * 100) / 100,
                    metadata: cleanedMetadata,
                  };
                });

            if (helpers?.values?.clientName) {
              try {
                const balance = await getClientLoyaltyBalance(
                  String(helpers.values.clientName),
                );
                const available = Number((balance as any)?.balance || 0);
                const manualRedeem = Math.max(
                  0,
                  Math.floor(Number(helpers?.values?.loyaltyRedeemPoints) || 0),
                );
                const rewardPointsTotal = selectedRewards.reduce(
                  (sum, reward) => sum + getRewardRedeemPointsValue(reward),
                  0,
                );
                if (available < manualRedeem + rewardPointsTotal) {
                  toast.error(
                    "Client does not have enough loyalty points for this reward.",
                  );
                  return;
                }
              } catch (error) {
                console.error("Could not validate loyalty balance", error);
              }
            }

            let rewardBaseItems = clearRewardItems(
              currentItems,
              hasSavedServiceReward,
            );
            const newProductItems: any[] = [];
            for (const reward of selectedProductRewards) {
              const rewardItemId = getRewardItemIdValue(reward);
              const rewardQuantity = getRewardQuantityValue(reward);
              const rewardMetadata = buildRewardMetadata(reward);
              const productList = Array.isArray(products)
                ? products
                : Array.isArray((products as any)?.data)
                  ? (products as any).data
                  : [];
              const product = productList.find(
                (item: any) =>
                  String(item?.id || item?._id || item?.product_id || "") ===
                  rewardItemId,
              );
              const businessId =
                invoiceEditingRow?.business?.id ||
                invoiceEditingRow?.invoice?.metadata?.businessId ||
                invoiceEditingRow?.metadata?.businessId ||
                invoiceEditingRow?.businessId ||
                venueId;
              const stockError = getProductRewardStockError(
                reward,
                stockItems,
                businessId ? String(businessId) : null,
              );
              if (stockError) {
                toast.error(stockError);
                return;
              }

              const productName =
                product?.name ||
                product?.title ||
                product?.product_name ||
                getRewardItemLabel(reward);
              const unitCost =
                Number(
                  product?.salePrice ??
                    product?.sale_price ??
                    product?.price ??
                    product?.unitPrice ??
                    0,
                ) || 0;
              newProductItems.push({
                id: `reward-product-${reward.id}`,
                service: rewardItemId,
                serviceId: rewardItemId,
                serviceLabel: productName,
                quantity: rewardQuantity,
                unitCost,
                discount: 100,
                totalPrice: 0,
                itemType: "PRODUCT",
                loyaltyRewardId: Number(reward.id),
                loyaltyRewardType: "PRODUCT",
                metadata: rewardMetadata,
              });
            }

            const serviceReward = selectedServiceReward;
            if (serviceReward && getRewardTypeValue(serviceReward) === "SERVICE") {
              const rewardItemId = getRewardItemIdValue(serviceReward);
              const rewardMetadata = buildRewardMetadata(serviceReward);
              const rewardService = findKnownServiceById(rewardItemId);
              const rewardName = normalizeServiceName(
                getServiceNameForMatch(rewardService),
              );
              const rewardPrice = normalizeServicePrice(
                getServicePriceForMatch(rewardService),
              );
              const serviceIndex = rewardBaseItems.findIndex((item: any) => {
                if (getInvoiceLineType(item) === "PRODUCT") return false;
                const itemId = getInvoiceLineItemId(item);
                if (itemId && itemId === rewardItemId) return true;

                const invoiceService = findKnownServiceById(itemId);
                const invoiceName = normalizeServiceName(
                  getServiceNameForMatch(invoiceService || item),
                );
                const invoicePrice = normalizeServicePrice(
                  getServicePriceForMatch(invoiceService || item),
                );

                return (
                  Boolean(rewardName && invoiceName) &&
                  rewardName === invoiceName &&
                  rewardPrice !== null &&
                  invoicePrice !== null &&
                  rewardPrice === invoicePrice
                );
              });

              if (serviceIndex < 0) {
                toast.error("This reward service is not in this invoice.");
                return;
              }

              const serviceItem = rewardBaseItems[serviceIndex];
              const serviceMetadata = getInvoiceLineMetadata(serviceItem);
              const existingServiceRewardId =
                serviceMetadata?.loyaltyRewardId || serviceItem?.loyaltyRewardId;
              const isCurrentEditableServiceReward = Boolean(
                existingServiceRewardId &&
                  String(existingServiceRewardId) ===
                    String(currentServiceRewardId || ""),
              );
              const hasDifferentServiceReward = Boolean(
                existingServiceRewardId &&
                  String(existingServiceRewardId) !==
                    String(currentServiceRewardId || ""),
              );
              const hasPromoDiscount =
                Number(helpers?.values?.promoValue || 0) > 0 ||
                Number(helpers?.values?.promoDiscount || 0) > 0;
              const serviceQuantity = Number(serviceItem?.quantity || 1) || 1;
              const serviceUnitCost = Number(serviceItem?.unitCost || 0) || 0;
              const serviceTotal = Number(serviceItem?.totalPrice);
              const serviceSubtotal = serviceQuantity * serviceUnitCost;
              const hasExistingLineDiscount =
                !isCurrentEditableServiceReward &&
                (Number(serviceItem?.discount || 0) > 0 ||
                  (Number.isFinite(serviceTotal) &&
                    serviceSubtotal > 0 &&
                    serviceTotal < serviceSubtotal));
              const onFullInvoice =
                helpers?.values?.loyaltyOnFullInvoice ??
                invoiceEditingRow?.metadata?.loyaltyOnFullInvoice ??
                invoiceEditingRow?.invoice?.metadata?.loyaltyOnFullInvoice;
              const manualRedeemPoints = Math.max(
                0,
                Math.floor(Number(helpers?.values?.loyaltyRedeemPoints) || 0),
              );
              const hasServiceRedeemDiscount =
                manualRedeemPoints > 0 &&
                parseBooleanSetting(onFullInvoice, true) === false;

              if (
                hasDifferentServiceReward ||
                hasPromoDiscount ||
                hasServiceRedeemDiscount ||
                hasExistingLineDiscount
              ) {
                toast.error("This service already has a discount.");
                return;
              }

              rewardBaseItems = rewardBaseItems.map((item: any, index: number) =>
                index === serviceIndex
                  ? {
                      ...item,
                      discount: 100,
                      totalPrice: 0,
                      loyaltyRewardId: Number(serviceReward.id),
                      loyaltyRewardType: "SERVICE",
                      metadata: {
                        ...serviceMetadata,
                        ...rewardMetadata,
                      },
                    }
                  : item,
              );
            }

            const allSelectedRewards = [
              ...selectedProductIds.map(getRewardById).filter(Boolean),
              ...(serviceReward ? [serviceReward] : []),
            ];
            helpers?.setFieldValue("items", [
              ...rewardBaseItems,
              ...newProductItems,
            ]);
            helpers?.setFieldValue("loyaltyProductRewardIds", selectedProductIds);
            helpers?.setFieldValue(
              "loyaltyServiceRewardId",
              serviceReward ? String(serviceReward.id) : "",
            );
            updateRewardMetadata(allSelectedRewards);
            onChange(allSelectedRewards[0] ? String(allSelectedRewards[0].id) : "");
          };

          const selectedRewardsForList = [
            ...currentProductRewardIds.map(getRewardById).filter(Boolean),
            ...(currentServiceRewardId ? [getRewardById(currentServiceRewardId)].filter(Boolean) : []),
            ...existingRewards
              .filter((reward) => hasSavedServiceReward && reward.rewardType === "SERVICE")
              .map((reward) =>
                getRewardById(reward.rewardId) || {
                  id: reward.rewardId,
                  reward_type: reward.rewardType,
                  reward_item_id: reward.rewardItemId,
                  redeem_points: reward.redeemPoints,
                  label: reward.label,
                },
              ),
          ];
          const selectedRewardIds = [
            ...currentProductRewardIds,
            ...(currentServiceRewardId ? [currentServiceRewardId] : []),
          ];

          return (
            <>
              <CustomLabel text="Reward Package" fontWeight={500} fontSize={14} />
              <CustomSelect
                label="Reward Package"
                value={selectedRewardIds}
                options={editInvoiceRewardOptions}
                disabled={rewardEditingLocked || loadingLoyaltyRewards}
                onChange={(nextValue: any) => {
                  const rewardIds = Array.isArray(nextValue)
                    ? nextValue.map(String).filter(Boolean)
                    : [];
                  const productIds: string[] = [];
                  const serviceIds: string[] = [];

                  rewardIds.forEach((rewardId) => {
                    const reward = getRewardById(rewardId);
                    const rewardType = getRewardTypeValue(reward);
                    if (rewardType === "PRODUCT") {
                      productIds.push(rewardId);
                      return;
                    }
                    if (rewardType === "SERVICE") {
                      serviceIds.push(rewardId);
                    }
                  });

                  if (serviceIds.length > 1) {
                    toast.error("Only one service reward can be selected.", {
                      toastId: "edit-invoice-one-service-reward",
                    });
                    return;
                  }

                  void applyRewardsToItems(productIds, serviceIds[0] || "");
                }}
                multiSelect
                sx={{ width: { xs: "100%", sm: "375px" } }}
                selectedSummary={
                  selectedRewardIds.length
                    ? `${selectedRewardIds.length} rewards selected`
                    : undefined
                }
                placeholder={
                  hasSavedServiceReward
                    ? "Select product rewards"
                    : loadingLoyaltyRewards
                      ? "Loading rewards..."
                      : "Select rewards"
                }
              />
              {selectedRewardsForList.length > 0 ? (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.75,
                    width: { xs: "100%", sm: "375px" },
                  }}
                >
                  <CustomLabel
                    text="Rewards to add"
                    fontSize={12}
                    color={COLORS.typographyColor}
                  />
                  {selectedRewardsForList.map((reward: any) => {
                    const id = String(reward?.id || reward?.rewardId || "");
                    const rewardType = getRewardTypeValue(reward);
                    const isSavedService =
                      hasSavedServiceReward && rewardType === "SERVICE";
                    return (
                      <Box
                        key={`${rewardType}-${id}`}
                        sx={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "8px",
                          px: 1.25,
                          py: 0.75,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <CustomLabel
                          text={
                            reward?.label ||
                            `${getRewardItemLabel(reward)} (${
                              rewardType === "PRODUCT" ? "Product" : "Service"
                            }) - ${getRewardRedeemPointsValue(
                              reward,
                            )} pts - qty ${getRewardQuantityValue(reward)}`
                          }
                          fontSize={13}
                          color={COLORS.typographyColor}
                        />
                        {!rewardEditingLocked && !isSavedService && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (rewardType === "PRODUCT") {
                                void applyRewardsToItems(
                                  currentProductRewardIds.filter(
                                    (rewardId) => rewardId !== id,
                                  ),
                                  currentServiceRewardId,
                                );
                                return;
                              }
                              void applyRewardsToItems(
                                currentProductRewardIds,
                                "",
                              );
                            }}
                          >
                            <CloseRounded fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              ) : existingReward?.rewardId ? (
                <CustomLabel
                  text="This invoice already has a reward applied."
                  fontSize={12}
                  color={COLORS.typographyColor}
                  sx={{ mt: 0.5 }}
                />
              ) : null}
              {rewardEditingLocked && (
                <CustomLabel
                  text={lockedMessage}
                  fontSize={12}
                  color="#d32f2f"
                  sx={{ mt: 0.5 }}
                />
              )}
            </>
          );
        },
      },
      { name: "notes", label: "Notes", colSpan: 2 },
      {
        name: "items",
        label: "Items",
        colSpan: 2,
        customRender: (_value: any, onChange: (v: any) => void, helpers) => (
          <InvoiceItemsEditor
            value={_value}
            onChange={onChange}
            vatValue={helpers?.values?.vat || 0}
            onVatChange={(v) => helpers?.setFieldValue("vat", v)}
            discountValue={helpers?.values?.discount || 0}
            onDiscountChange={(v) => helpers?.setFieldValue("discount", v)}
            promoDiscountValue={helpers?.values?.promoDiscount || 0}
            onPromoDiscountChange={(v) =>
              helpers?.setFieldValue("promoDiscount", v)
            }
            promoValue={helpers?.values?.promoValue || 0}
            promoType={helpers?.values?.promoType || ""}
            onPromoValueChange={(v) => helpers?.setFieldValue("promoValue", v)}
            isEditMode={Boolean(invoiceEditingRow)}
            servicesList={invoiceServices}
          />
        ),
      },
    ];
  }, [
    clients,
    getRewardItemLabel,
    invoiceEditingRow,
    invoiceServices,
    loadingLoyaltyRewards,
    loyaltyRewards,
    products,
    rewardOptions,
    stockItems,
    venueId,
  ]);

  const invoiceDialogInitialValues = React.useMemo(() => {
    if (!invoiceEditingRow) {
      return {
        vat: "0",
        discount: "0",
        paymentMethod: "cash",
        loyaltyRedeemPoints: "",
        loyaltyRedeemRemoved: false,
      };
    }

    const clientId =
      invoiceEditingRow.invoice?.client_id ||
      invoiceEditingRow.client_id ||
      invoiceEditingRow.client?.id ||
      invoiceEditingRow.metadata?.clientOrgUserId ||
      invoiceEditingRow.clientOrgUserId ||
      "";
    const matchedClient = (clients?.users || []).find(
      (c) => String(c.id || c._id || "") === String(clientId || ""),
    );
    const invoiceDate =
      invoiceEditingRow.invoice?.created_at ||
      invoiceEditingRow.created_at ||
      invoiceEditingRow.invoice?.invoice_date ||
      invoiceEditingRow.invoice_date ||
      invoiceEditingRow.invoice?.issuedAt ||
      invoiceEditingRow.invoice?.createdAt ||
      invoiceEditingRow.invoice?.updatedAt ||
      invoiceEditingRow.invoice?.dueAt ||
      invoiceEditingRow.issuedAt ||
      invoiceEditingRow.createdAt ||
      invoiceEditingRow.updatedAt ||
      invoiceEditingRow.dueAt ||
      invoiceEditingRow.appointment?.start_at;
    const details = resolveInvoiceDetails(invoiceEditingRow);
    const detailsContainProducts = details.some(
      (detail: any) =>
        String(detail?.metadata?.type || detail?.type || "").toUpperCase() ===
        "PRODUCT",
    );
    const products = detailsContainProducts
      ? []
      : resolveInvoiceProducts(invoiceEditingRow);
    const items = buildInvoiceItems(
      details,
      invoiceServices,
      products,
      invoiceEditingRow,
    );
    const savedLoyaltyRedeemPoints =
      extractInvoiceLoyaltyRedeemPoints(invoiceEditingRow);
    const savedLoyaltyOnFullInvoice =
      extractInvoiceLoyaltyOnFullInvoice(invoiceEditingRow) ??
      getCachedFullInvoiceEnabled();
    const savedLoyaltyDiscountPercent =
      extractInvoiceLoyaltyDiscountPercent(invoiceEditingRow);
    const savedRewardDetails =
      extractInvoiceLoyaltyRewardDetails(invoiceEditingRow);
    const savedRewardDetailsList =
      extractInvoiceLoyaltyRewardDetailsList(invoiceEditingRow);
    const savedProductRewardIds = savedRewardDetailsList
      .filter((reward) => String(reward?.rewardType || "").toUpperCase() === "PRODUCT")
      .map((reward) => String(reward.rewardId || ""))
      .filter(Boolean);
    const savedServiceRewardId =
      savedRewardDetailsList.find(
        (reward) => String(reward?.rewardType || "").toUpperCase() === "SERVICE",
      )?.rewardId || "";
    const invoiceDiscount = Number(
      invoiceEditingRow.invoice?.discount ?? invoiceEditingRow.discount ?? 0,
    );
    const initialInvoiceDiscount =
      invoiceDiscount > 0
        ? invoiceDiscount
        : savedLoyaltyRedeemPoints > 0 &&
            savedLoyaltyOnFullInvoice === true &&
            savedLoyaltyDiscountPercent > 0
          ? savedLoyaltyDiscountPercent
          : 0;

    return {
      invoice_id: invoiceEditingRow.invoice?.id || invoiceEditingRow.id || "",
      clientName: String(clientId || ""),
      email:
        invoiceEditingRow.email ||
        invoiceEditingRow.invoice?.email ||
        invoiceEditingRow.metadata?.email ||
        invoiceEditingRow.client?.email ||
        invoiceEditingRow.client?.user?.email ||
        matchedClient?.email ||
        "",
      phoneNumber:
        invoiceEditingRow.client?.mobile ||
        invoiceEditingRow.client?.phone ||
        matchedClient?.mobile ||
        matchedClient?.phone ||
        "",
      invoice_date: formatInvoiceDateValue(invoiceDate),
      status: resolveInvoiceStatusFormValue(
        pickNonEmptyString(
          invoiceEditingRow.status,
          invoiceEditingRow.invoice?.status,
          invoiceEditingRow.invoice?.status_name,
          invoiceEditingRow.invoice?.status_label,
        ),
        invoiceEditingRow.invoice?.status_id ??
          invoiceEditingRow.status_id ??
          invoiceEditingRow.invoice?.status?.id ??
          invoiceEditingRow.status?.id,
      ),
      paymentMethod: "cash",
      loyaltyRedeemPoints: String(savedLoyaltyRedeemPoints),
      loyaltyRedeemRemoved: false,
      loyaltyRedeemDiscountType:
        extractInvoiceLoyaltyRedeemDiscountType(invoiceEditingRow) ||
        "PERCENT",
      loyaltyRedeemDiscountAmount:
        extractInvoiceLoyaltyRedeemDiscountAmount(invoiceEditingRow),
      loyaltyOnFullInvoice:
        savedLoyaltyOnFullInvoice,
      loyaltyRewardId: savedRewardDetails?.rewardId
        ? String(savedRewardDetails.rewardId)
        : "",
      loyaltyRewardIds: savedRewardDetailsList
        .map((reward) => Number(reward.rewardId))
        .filter((id) => Number.isFinite(id) && id > 0),
      loyaltyProductRewardIds: savedProductRewardIds,
      loyaltyServiceRewardId: savedServiceRewardId
        ? String(savedServiceRewardId)
        : "",
      loyaltyRewardRedeemPoints: String(
        savedRewardDetailsList.reduce(
          (sum, reward) => sum + (Number(reward?.redeemPoints) || 0),
          0,
        ) || Number(savedRewardDetails?.redeemPoints || 0) || 0,
      ),
      loyaltyRewardType: savedRewardDetails?.rewardType || "",
      loyaltyRewardItemId: savedRewardDetails?.rewardItemId || "",
      loyaltyRewardQuantity: "0",
      sharedClientIds: extractInvoiceSharedClientIds(invoiceEditingRow),
      notes:
        invoiceEditingRow.invoice?.notes ||
        invoiceEditingRow.notes ||
        invoiceEditingRow.metadata?.notes ||
        "",
      items,
      invoice_number:
        invoiceEditingRow.invoice?.Invoice_number ||
        invoiceEditingRow.invoice?.invoice_number ||
        invoiceEditingRow.Invoice_number ||
        invoiceEditingRow.invoice_number ||
        invoiceEditingRow.sourceReferenceId,
      appointment_id:
        invoiceEditingRow.invoice?.appointment_id ||
        invoiceEditingRow.appointment_id ||
        invoiceEditingRow.metadata?.appointmentId ||
        invoiceEditingRow.metadata?.appointment_id ||
        invoiceEditingRow.invoice?.metadata?.appointmentId ||
        invoiceEditingRow.invoice?.metadata?.appointment_id ||
        invoiceEditingRow.appointment?.id ||
        invoiceEditingRow.appointmentId ||
        invoiceEditingRow.sourceReferenceId ||
        editingEventId ||
        "1",
      net_amount:
        invoiceEditingRow.invoice?.net_amount ||
        invoiceEditingRow.net_amount ||
        invoiceEditingRow.subtotal ||
        invoiceEditingRow.total,
      total_amount:
        invoiceEditingRow.invoice?.total_amount ||
        invoiceEditingRow.total_amount ||
        invoiceEditingRow.total,
      vat: String(
        invoiceEditingRow.invoice?.tax ??
          invoiceEditingRow.tax ??
          invoiceEditingRow.invoice?.Vat ??
          invoiceEditingRow.Vat ??
          0,
      ),
      discount: String(initialInvoiceDiscount),
      promoDiscount: String(
        invoiceEditingRow.promoDiscount ??
          invoiceEditingRow.promo_discount ??
          invoiceEditingRow.invoice?.promoDiscount ??
          invoiceEditingRow.invoice?.promo_discount ??
          0,
      ),
      promoValue: String(
        invoiceEditingRow.promoValue ??
          invoiceEditingRow.promo_value ??
          invoiceEditingRow.invoice?.promoValue ??
          invoiceEditingRow.invoice?.promo_value ??
          0,
      ),
      promoType:
        invoiceEditingRow.promoType ||
        invoiceEditingRow.promo_type ||
        invoiceEditingRow.invoice?.promoType ||
        invoiceEditingRow.invoice?.promo_type ||
        "",
    };
  }, [invoiceEditingRow, invoiceServices, clients, editingEventId]);

  const normalizeStatus = (status: any) =>
    typeof status === "string" ? status.trim().toUpperCase() : "";

  const handleInvoiceSubmit = async (values: Record<string, any>) => {
    if (!invoiceEditingRow) {
      toast.error("No invoice data available.");
      return;
    }

    const pickNumber = (...vals: any[]) => {
      for (const v of vals) {
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      return 0;
    };
    // pickNumber reads null as 0, which would make "no balance field" look
    // like "nothing left to pay" — this one only answers when a value is there.
    const readStatedBalance = (...vals: any[]): number | null => {
      for (const v of vals) {
        if (v === null || v === undefined || v === "") continue;
        const n = Number(v);
        if (Number.isFinite(n)) return n;
      }
      return null;
    };

    const invoiceId = invoiceEditingRow.invoice?.id || invoiceEditingRow.id;
    if (!invoiceId) {
      toast.error("Invoice ID not found.");
      return;
    }

    // Build items payload and money totals once so we can reuse for payment/refund.
    const normalizedItems = Array.isArray(values.items) ? values.items : [];

    if (normalizedItems.some(isInvoiceProductMissingSelection)) {
      toast.error(
        "Choose a product or delete the product row before saving the invoice.",
      );
      return;
    }

    const lineItems = normalizedItems.map((item: any) => ({
      quantity: Number(item.quantity),
      amount: Number(item.unitCost),
      discount: getInvoiceItemDiscountAmount(item),
      discountPercent: Number(item.discount || 0),
      net_amount: Number(item.totalPrice),
      itemType: item.itemType || "SERVICE",
      serviceId: item.serviceId || String(item.service),
      serviceLabel: item.serviceLabel || "",
      loyaltyDiscountPercent: Number(item.loyaltyDiscountPercent) || 0,
      promoDiscountPercent: Number(item.promoDiscountPercent) || 0,
      loyaltyOnFullInvoice: item.loyaltyOnFullInvoice,
      loyaltyRewardId:
        item.loyaltyRewardId ?? item.metadata?.loyaltyRewardId ?? null,
      loyaltyRewardType:
        item.loyaltyRewardType ?? item.metadata?.loyaltyRewardType ?? null,
      loyaltyRewardItemId: item.metadata?.loyaltyRewardItemId ?? null,
      loyaltyRewardRedeemPoints:
        Number(item.metadata?.loyaltyRewardRedeemPoints || 0) || 0,
      metadata: item.metadata || {},
      discountManuallyEdited: Boolean(item.discountManuallyEdited),
    }));

    const previewAmount = lineItems.reduce(
      (sum: number, item: any) => sum + Number(item.net_amount || 0),
      0,
    );
    let loyaltyRedeemPoints = Math.max(
      0,
      Math.floor(Number(values.loyaltyRedeemPoints) || 0),
    );
    const manualRedeemRemoved = values?.loyaltyRedeemRemoved === true;
    const savedRedeemPointsForInvoice =
      manualRedeemRemoved
        ? 0
        : extractInvoiceLoyaltyRedeemPoints(invoiceEditingRow) ||
          extractInvoiceLoyaltyRedeemPoints(values);
    const requestedInvoiceStatusForLoyalty = normalizeStatus(
      values.status ||
        invoiceEditingRow.invoice?.status ||
        invoiceEditingRow.status ||
        "PENDING",
    );
    const savedLoyaltyEnabledAtCreation =
      extractInvoiceLoyaltyEnabledAtCreation(invoiceEditingRow) ??
      extractInvoiceLoyaltyEnabledAtCreation(values);
    const savedInvoiceHasLoyaltyData =
      extractInvoiceLoyaltyRedeemPoints(invoiceEditingRow) > 0 ||
      extractInvoiceSharedClientIds(invoiceEditingRow).length > 0 ||
      extractInvoiceLoyaltyOnFullInvoice(invoiceEditingRow) !== null ||
      extractInvoiceLoyaltyRedeemPoints(values) > 0 ||
      extractInvoiceSharedClientIds(values).length > 0 ||
      extractInvoiceLoyaltyOnFullInvoice(values) !== null;
    const allowPreviewWhenDisabled =
      savedLoyaltyEnabledAtCreation === true ||
      (savedLoyaltyEnabledAtCreation !== false && savedInvoiceHasLoyaltyData);
    let loyaltyDiscountPercent = 0;
    const savedServiceLoyaltyDiscountPercent = Math.max(
      0,
      Number(
        lineItems.find((item: any) => item.itemType !== "PRODUCT")
          ?.loyaltyDiscountPercent,
      ) || 0,
    );
    const savedInvoiceLoyaltyDiscountPercent = Math.max(
      0,
      extractInvoiceLoyaltyDiscountPercent(invoiceEditingRow) ||
        extractInvoiceLoyaltyDiscountPercent(values) ||
        0,
    );
    let loyaltyRedeemDiscountType =
      (manualRedeemRemoved
        ? ""
        : extractInvoiceLoyaltyRedeemDiscountType(invoiceEditingRow) ||
          extractInvoiceLoyaltyRedeemDiscountType(values)) ||
      (String(values.loyaltyRedeemDiscountType || "").toUpperCase() ===
      "FIXED"
        ? "FIXED"
        : "PERCENT");
    let loyaltyRedeemDiscountAmount =
      (manualRedeemRemoved
        ? 0
        : extractInvoiceLoyaltyRedeemDiscountAmount(invoiceEditingRow) ||
          extractInvoiceLoyaltyRedeemDiscountAmount(values)) ||
      Math.max(0, Number(values.loyaltyRedeemDiscountAmount) || 0);
    const savedItemLoyaltyOnFullInvoice =
      lineItems
        .map((item: any) => item.loyaltyOnFullInvoice)
        .find((value: any) => value !== undefined && value !== null) ?? null;
    const savedLoyaltyOnFullInvoice =
      (savedItemLoyaltyOnFullInvoice !== null
        ? parseBooleanSetting(savedItemLoyaltyOnFullInvoice, true)
        : null) ??
      extractInvoiceLoyaltyOnFullInvoice(invoiceEditingRow) ??
      extractInvoiceLoyaltyOnFullInvoice(values);
    let loyaltyOnFullInvoice =
      savedLoyaltyOnFullInvoice ??
      parseBooleanSetting(values.loyaltyOnFullInvoice, getCachedFullInvoiceEnabled());

    if (manualRedeemRemoved) {
      loyaltyRedeemPoints = 0;
      loyaltyDiscountPercent = 0;
      loyaltyRedeemDiscountType = "PERCENT";
      loyaltyRedeemDiscountAmount = 0;
    }

    if (
      loyaltyRedeemPoints > 0 &&
      requestedInvoiceStatusForLoyalty === "PAID" &&
      values.clientName
    ) {
      const organizationId = getStoredOrganizationId();

      if (organizationId) {
        try {
          const settings = await getLoyaltySettings(organizationId);
          const onFullInvoiceValue: any =
            settings?.on_full_invoice ?? settings?.onFullInvoice;
          const settingsRedeemType =
            String(settings?.redeem?.type ?? settings?.type ?? "").toUpperCase() ===
            "FIXED"
              ? "FIXED"
              : "PERCENT";
          const settingsRedeemDiscount = Number(
            settings?.redeem?.discount ??
              settings?.redeem_discount ??
              settings?.redeemDiscount,
          );
          if (savedLoyaltyOnFullInvoice === null) {
            loyaltyOnFullInvoice =
              parseBooleanSetting(onFullInvoiceValue, true);
          }
          if (!loyaltyRedeemDiscountAmount) {
            loyaltyRedeemDiscountType = settingsRedeemType;
            loyaltyRedeemDiscountAmount =
              Number.isFinite(settingsRedeemDiscount) &&
              settingsRedeemDiscount > 0
                ? settingsRedeemDiscount
                : 0;
          }
        } catch {
          if (savedLoyaltyOnFullInvoice === null) {
            loyaltyOnFullInvoice = true;
          }
        }

        const redeemPointsUnchanged =
          savedRedeemPointsForInvoice > 0 &&
          loyaltyRedeemPoints === savedRedeemPointsForInvoice;

        if (redeemPointsUnchanged) {
          loyaltyDiscountPercent =
            savedInvoiceLoyaltyDiscountPercent ||
            savedServiceLoyaltyDiscountPercent;
        } else {
          const loyaltyPreview = await getLoyaltyPreview({
            organizationId,
            userId: String(values.clientName),
            amount: previewAmount,
            redeemPoints: loyaltyRedeemPoints,
            allowWhenDisabled: allowPreviewWhenDisabled,
          });
          loyaltyRedeemPoints = Math.max(
            0,
            Math.floor(Number(loyaltyPreview?.pointsToRedeem) || 0),
          );
          loyaltyDiscountPercent = Number(loyaltyPreview?.discountPercent) || 0;
        }
      }
    }

    if (loyaltyDiscountPercent > 0 && !loyaltyOnFullInvoice) {
      const firstServiceIndex = lineItems.findIndex(
        (item: any) => item.itemType !== "PRODUCT",
      );
      if (firstServiceIndex >= 0) {
        const serviceItem = lineItems[firstServiceIndex];
        if (!serviceItem.discountManuallyEdited) {
          const combinedDiscount = calculateCombinedServiceDiscount({
            item: serviceItem,
            promoType: values.promoType,
            promoValue: values.promoValue,
            loyaltyDiscountPercent,
            onFullInvoice: loyaltyOnFullInvoice,
          });

          lineItems[firstServiceIndex] = {
            ...serviceItem,
            discount: combinedDiscount.discountAmount,
            discountPercent: combinedDiscount.discountPercent,
            loyaltyDiscountPercent:
              combinedDiscount.loyaltyDiscountPercent,
            promoDiscountPercent: combinedDiscount.promoDiscountPercent,
            net_amount: combinedDiscount.totalPrice,
          };
        }
      }
    }

    const totalAmount = lineItems.reduce(
      (sum: number, item: any) => sum + Number(item.net_amount || 0),
      0,
    );
    const discountPercent = Math.min(
      100,
      Math.max(0, Number(values.discount) || 0),
    );
    const discountAmount =
      Math.round(((totalAmount * discountPercent) / 100) * 100) / 100;
    const amountAfterDiscount = Math.max(0, totalAmount - discountAmount);
    const vatPercent = Number(values.vat) || 0;
    const vatAmount =
      Math.round(((amountAfterDiscount * vatPercent) / 100) * 100) / 100;
    const netAmount = Math.round((amountAfterDiscount + vatAmount) * 100) / 100;

    const totalFromApi = pickNumber(
      invoiceEditingRow.invoice?.net_amount,
      invoiceEditingRow.net_amount,
      invoiceEditingRow.invoice?.total_amount,
      invoiceEditingRow.total_amount,
      invoiceEditingRow.invoice?.total,
      invoiceEditingRow.total,
      netAmount,
    );
    const rawRemaining = pickNumber(
      invoiceEditingRow.invoice?.remaining_amount,
      invoiceEditingRow.invoice?.remainingAmount,
      invoiceEditingRow.invoice?.balance,
      invoiceEditingRow.invoice?.outstanding,
      invoiceEditingRow.remaining_amount,
      invoiceEditingRow.balance,
      invoiceEditingRow.outstanding,
    );
    const loadedBalance = readStatedBalance(
      invoiceEditingRow.invoice?.remaining_amount,
      invoiceEditingRow.invoice?.remainingAmount,
      invoiceEditingRow.invoice?.balance,
      invoiceEditingRow.invoice?.outstanding,
      invoiceEditingRow.remaining_amount,
      invoiceEditingRow.balance,
      invoiceEditingRow.outstanding,
    );
    let paidAmount = pickNumber(
      invoiceEditingRow.invoice?.paid_amount,
      invoiceEditingRow.invoice?.paidAmount,
      invoiceEditingRow.invoice?.total_paid,
      invoiceEditingRow.paid_amount,
      invoiceEditingRow.paid,
      invoiceEditingRow.total_paid,
    );
    const refundedAmount = pickNumber(
      invoiceEditingRow.invoice?.refunded_amount,
      invoiceEditingRow.invoice?.refundedAmount,
      invoiceEditingRow.invoice?.total_refunded,
      invoiceEditingRow.invoice?.refund_amount,
      invoiceEditingRow.refunded_amount,
      invoiceEditingRow.total_refunded,
      invoiceEditingRow.refund_amount,
    );
    if (paidAmount === 0 && totalFromApi > 0 && rawRemaining >= 0) {
      const inferredPaid = totalFromApi - rawRemaining;
      if (Number.isFinite(inferredPaid) && inferredPaid > 0) {
        paidAmount = inferredPaid;
      }
    }

    const refundableAmount = Math.max(paidAmount - refundedAmount, 0);

    const computedRemaining = Math.max(
      (totalFromApi || netAmount) - paidAmount + refundedAmount,
      0,
    );

    const payCandidates = [rawRemaining, computedRemaining, netAmount].filter(
      (v) => Number.isFinite(v) && v > 0,
    );
    const minPositive = payCandidates.length ? Math.min(...payCandidates) : 0;
    const amountToPay = rawRemaining > 0 ? rawRemaining : minPositive;

    const amountToRefund = paidAmount > 0 ? paidAmount : Math.max(netAmount, 0);

    const currentStatus = normalizeStatus(
      invoiceEditingRow.invoice?.status ||
        invoiceEditingRow.status ||
        invoiceEditingRow.invoice?.status_name ||
        invoiceEditingRow.status_name ||
        "PENDING",
    );
    const requestedStatus = normalizeStatus(
      values.status || currentStatus || "PENDING",
    );

    if (
      requestedStatus !== currentStatus &&
      !APPOINTMENT_ALLOWED_TARGETS.has(requestedStatus)
    ) {
      toast.info(
        "Cannot make this transaction here. Please go to the Invoices page to complete it.",
      );
      return;
    }

    if (currentStatus === "CANCELLED" && requestedStatus !== "CANCELLED") {
      toast.info("Cancelled invoices cannot be changed here.");
      return;
    }

    if (requestedStatus === "CANCELLED" && currentStatus !== "CANCELLED") {
      toast.info(
        "Cancellation is not supported here. Please use the Invoices page.",
      );
      return;
    }

    // When status is PENDING, only allow moving to PAID
    if (
      currentStatus === "PENDING" &&
      requestedStatus !== "PENDING" &&
      requestedStatus !== "PAID"
    ) {
      toast.info(
        "Pending invoices can only be marked as Paid here. For other actions, go to the Invoices page.",
      );
      return;
    }

    const transition = `${currentStatus}->${requestedStatus}`;
    if (!ALLOWED_STATUS_TRANSITIONS.has(transition)) {
      toast.error(
        `Cannot change invoice status from ${currentStatus} to ${requestedStatus}.`,
      );
      return;
    }

    const needsPayment = requestedStatus === "PAID" && currentStatus !== "PAID";
    const needsRefundToUnpaid =
      (currentStatus === "PAID" ||
        currentStatus === "PARTIALLY_PAID" ||
        currentStatus === "PARTIALLY_REFUNDED") &&
      requestedStatus === "UNPAID";
    const needsRefund = needsRefundToUnpaid;

    if (needsPayment && amountToPay <= 0) {
      toast.error("Nothing to pay. Invoice already settled or total is zero.");
      return;
    }

    const refundAmountToUse =
      refundableAmount > 0 ? refundableAmount : amountToRefund;

    if (needsRefund && refundAmountToUse <= 0) {
      toast.error("Nothing to refund for this invoice.");
      return;
    }

    // Enforce full-payment/full-refund only in appointment view
    if (needsPayment) {
      // pay full remaining
    } else if (needsRefund) {
      // refund full refundable amount (already captured)
    }

    let businessIdToUse =
      invoiceEditingRow.business_id ||
      invoiceEditingRow.invoice?.business_id ||
      invoiceEditingRow.businessId ||
      invoiceEditingRow.invoice?.businessId;
    if (!businessIdToUse) {
      const branchId =
        formBranchId && formBranchId !== "all"
          ? formBranchId
          : selectedBranchId && selectedBranchId !== "all"
            ? selectedBranchId
            : null;
      if (branchId) {
        const branch = branches.find((b) => String(b.id) === String(branchId));
        businessIdToUse = branch?.business_id || branch?.id;
      }
    }

    const itemsPayload = lineItems.map((item: any) => {
      const isProduct = item.itemType === "PRODUCT";
      return {
        description: item.serviceLabel || (isProduct ? "Product" : "Service"),
        qty: Number(item.quantity) || 1,
        unitPrice: Number(item.amount) || 0,
        discount: Number(item.discount || 0),
        metadata: {
          ...(item.metadata || {}),
          type: isProduct ? "PRODUCT" : "SERVICE",
          discountPercent: Number(item.discountPercent || 0),
          ...(item.loyaltyRewardId
            ? {
                loyaltyRewardId: Number(item.loyaltyRewardId),
                loyaltyRewardType: item.loyaltyRewardType,
                loyaltyRewardItemId: item.loyaltyRewardItemId,
                loyaltyRewardRedeemPoints:
                  Number(item.loyaltyRewardRedeemPoints || 0) || 0,
                rewardType: item.loyaltyRewardType,
                rewardItemId: item.loyaltyRewardItemId,
                redeemPoints: Number(item.loyaltyRewardRedeemPoints || 0) || 0,
              }
            : {}),
          ...(isProduct
            ? {
                productId: item.serviceId,
                businessId: businessIdToUse,
                stockDirection: "OUT",
              }
            : {
                serviceId: item.serviceId,
                loyaltyOnFullInvoice: parseBooleanSetting(
                  item.loyaltyOnFullInvoice,
                  loyaltyOnFullInvoice,
                ),
                loyaltyRedeemPoints,
                loyaltyDiscountPercent:
                  Number(item.loyaltyDiscountPercent) || 0,
                loyaltyRedeemDiscountType,
                loyaltyRedeemDiscountAmount,
                promoDiscountPercent:
                  Number(item.promoDiscountPercent) || 0,
              }),
        },
      };
    });
    const selectedRewardIds = Array.from(
      new Set(
        [
          ...(Array.isArray(values.loyaltyRewardIds)
            ? values.loyaltyRewardIds
            : []),
          ...lineItems
            .map((item: any) => item.loyaltyRewardId)
            .filter((id: any) => id !== undefined && id !== null && id !== ""),
        ]
          .map((id: any) => Number(id))
          .filter((id: number) => Number.isFinite(id) && id > 0),
      ),
    );
    const firstRewardId = selectedRewardIds[0] || null;

    const payload = {
      userId: values.clientName, // Billing user ID
      currency: "USD", // or from your app settings

      discount: discountPercent, // %
      tax: vatPercent, // %

      issuedAt: values.invoice_date,
      status: requestedStatus,

      sourceReferenceId:
        values.appointment_id ||
        editingEventId ||
        invoiceEditingRow.sourceReferenceId ||
        null,

      metadata: {
        clientOrgUserId: values.clientName, // CORE client UUID
        businessId: businessIdToUse, // BACKEND business (venueId in FE)
        unitId: formBranchId || selectedBranchId, // BACKEND unit (branch in FE)
        notes: values.notes,
        loyaltyRedeemPoints,
        loyaltyDiscountPercent,
        loyaltyRedeemDiscountType,
        loyaltyRedeemDiscountAmount,
        loyaltyOnFullInvoice,
        sharedClientIds:
          invoiceEditingRow?.metadata?.sharedClientIds ||
          invoiceEditingRow?.metadata?.shared_client_ids ||
          invoiceEditingRow?.invoice?.metadata?.sharedClientIds ||
          invoiceEditingRow?.invoice?.metadata?.shared_client_ids ||
          [],
        loyaltyRewardId: firstRewardId,
        loyaltyRewardIds: selectedRewardIds,
        loyaltyRewardRedeemPoints:
          Number(values.loyaltyRewardRedeemPoints || 0) || 0,
        loyaltyRewardType: values.loyaltyRewardType || null,
        loyaltyRewardItemId: values.loyaltyRewardItemId || null,
        loyaltyRewardQuantity: Number(values.loyaltyRewardQuantity || 0) || 0,
      },

      items: itemsPayload,
    };

    setInvoiceSubmitLoading(true);

    try {
      const skipUpdate = needsRefund;
      let updateResponse: any = null;

      if (!skipUpdate) {
        updateResponse = await updateQuotation(Number(invoiceId), payload);
      }

      let alreadyPaid = false;

      if (needsPayment) {
        // A loaded PAID status never gets here (needsPayment excludes it). For
        // the balance, prefer what the update just returned: an edit can add
        // lines to a settled invoice, so the loaded 0 may no longer hold.
        const balanceToSettle =
          readStatedBalance(
            updateResponse?.updated?.balance,
            updateResponse?.balance,
          ) ?? loadedBalance;

        if (balanceToSettle !== null && balanceToSettle <= 0) {
          alreadyPaid = true;
        } else {
          const updatedAmountToPay = pickNumber(
            updateResponse?.updated?.balance,
            updateResponse?.balance,
            updateResponse?.updated?.total,
            updateResponse?.total,
            amountToPay,
          );
          const payResponse = await payInvoice(String(invoiceId), {
            amount: updatedAmountToPay,
            method: "CASH",
            provider: "MANUAL",
            status: "SUCCESS",
          });
          alreadyPaid = payResponse?.alreadyPaid === true;
        }
      } else if (needsRefund) {
        await refundInvoice(String(invoiceId), {
          amount: refundAmountToUse,
          status: "SUCCESS",
        });
      }

      const actionMessage = needsPayment
        ? "Invoice fully paid."
        : needsRefundToUnpaid
          ? "Invoice marked unpaid."
          : "Invoice updated successfully!";

      // Refresh only the current event's invoice status so the icon updates immediately.
      if (editingEventId) {
        const updatedStatus = requestedStatus || currentStatus;
        const updatedInvoiceTotal = pickNumber(
          updateResponse?.updated?.amount,
          updateResponse?.updated?.total,
          updateResponse?.updated?.total_amount,
          updateResponse?.updated?.totalAmount,
          updateResponse?.updated?.net_amount,
          updateResponse?.amount,
          updateResponse?.total,
          updateResponse?.total_amount,
          updateResponse?.totalAmount,
          updateResponse?.net_amount,
          netAmount,
        );
        const updatedInvoiceSnapshot = {
          total: updatedInvoiceTotal,
          total_amount: updatedInvoiceTotal,
          totalAmount: updatedInvoiceTotal,
          amount: updatedInvoiceTotal,
          net_amount: updatedInvoiceTotal,
          subtotal: totalAmount,
          items: itemsPayload,
        };
        setEvents((prev: any[]) =>
          (Array.isArray(prev) ? prev : []).map((ev: any) => {
            const matchId = String(ev.extendedProps?.originalId || ev.id);
            if (matchId !== String(editingEventId)) return ev;
            const raw = ev.extendedProps?.raw || {};
            const updatedRaw = {
              ...raw,
              invoiceTotal: updatedInvoiceTotal,
              total_price: updatedInvoiceTotal,
              total: updatedInvoiceTotal,
              total_amount: updatedInvoiceTotal,
              amount: updatedInvoiceTotal,
              net_amount: updatedInvoiceTotal,
              invoice_status: updatedStatus,
              invoiceStatus: updatedStatus,
              invoice_status_name: updatedStatus,
              invoiceStatusName: updatedStatus,
              invoice: {
                ...(raw.invoice || {}),
                ...updatedInvoiceSnapshot,
                status: updatedStatus,
                status_name: updatedStatus,
              },
            };
            return mapAppointmentToEvent(updatedRaw);
          }),
        );
        setEditingEventRaw((prev: any) => {
          const base = prev || {};
          return {
            ...base,
            invoiceTotal: updatedInvoiceTotal,
            total_price: updatedInvoiceTotal,
            total: updatedInvoiceTotal,
            total_amount: updatedInvoiceTotal,
            amount: updatedInvoiceTotal,
            net_amount: updatedInvoiceTotal,
            invoice_status: updatedStatus,
            invoiceStatus: updatedStatus,
            invoice_status_name: updatedStatus,
            invoiceStatusName: updatedStatus,
            invoice: {
              ...(base.invoice || {}),
              ...updatedInvoiceSnapshot,
              status: updatedStatus,
              status_name: updatedStatus,
            },
          };
        });
        const eventSource = editingEventRaw || invoiceEditingRow;
        const eventUnitId = resolveUnitIdFromRaw(eventSource);
        const eventDate = resolveAnchorDateFromRaw(eventSource);
        if (eventUnitId && eventDate) {
          await refreshUnitAppointmentsForDate(eventUnitId, eventDate);
        }
      }

      if (alreadyPaid) {
        toast.info("Invoice is already paid");
      } else {
        toast.success(actionMessage);
      }
      handleInvoiceDialogClose();
    } catch (error) {
      console.error("Error saving invoice:", error);
      showErrorToast(getErrorMessage(error, "Failed to update invoice."));
      setInvoiceSubmitLoading(false);
    }
  };

  const courts: Court[] = [
    { id: "all", name: "All Courts", type: "filter", color: "#2196F3" },
    ...((Array.isArray(branches) ? branches : []).map((b: any) => ({
      id: String(b.id),
      name: String(b.name || b.title || b.id || "Court"),
      type: "branch",
      color: "#4CAF50",
    })) as Court[]),
  ];
  const clientHistoryRecord = resolveClientHistoryRecord(selectedClient);
  const clientHistoryInitialValues = clientHistoryRecord
    ? {
        fullName:
          pickNonEmptyString(
            clientHistoryRecord?.full_name,
            clientHistoryRecord?.fullName,
            clientHistoryRecord?.name,
            clientHistoryRecord?.client_name,
          ) || "",
        email: clientHistoryRecord?.email || "",
        countryCode: normalizeCountryCodeValue(
          clientHistoryRecord?.country ||
            clientHistoryRecord?.country_code ||
            clientHistoryRecord?.countryCode ||
            clientHistoryRecord?.country_code_value ||
            clientHistoryRecord?.countryCodeValue ||
            "",
        ),
        phoneNumber:
          clientHistoryRecord?.mobile ||
          clientHistoryRecord?.phone ||
          clientHistoryRecord?.phoneNumber ||
          clientHistoryRecord?.phone_number ||
          clientHistoryRecord?.client_phone ||
          "",
        status: normalizeActiveStatus(
          clientHistoryRecord?.active ?? clientHistoryRecord?.status,
        ),
        gender: clientHistoryRecord?.gender
          ? String(clientHistoryRecord.gender).toUpperCase()
          : "",
        dob:
          clientHistoryRecord?.dob ||
          clientHistoryRecord?.DOB ||
          clientHistoryRecord?.date_of_birth ||
          clientHistoryRecord?.birth_date ||
          "",
      }
    : undefined;
  const clientHistoryFields: Field[] = [
    { name: "fullName", label: "Full Name", required: true, disabled: true },
    { name: "email", label: "Email", disabled: true },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
      required: true,
      disabled: true,
    },
    {
      name: "phoneNumber",
      label: "Phone Number",
      required: true,
      disabled: true,
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Verified", value: "verified" },
        { label: "Unverified", value: "unverified" },
      ],
      disabled: true,
    },
    {
      name: "gender",
      label: "Gender",
      type: "dropdown",
      options: [
        { label: "Male", value: "MALE" },
        { label: "Female", value: "FEMALE" },
      ],
      disabled: true,
    },
    { name: "dob", label: "Date of Birth", type: "date", disabled: true },
  ];
  const clientHistoryTitle = clientDetailsLoading
    ? "View Client (Loading...)"
    : "View Client";
  const clientHistoryTotals = React.useMemo(() => {
    const totals = clientHistoryInvoices.reduce(
      (acc, inv) => {
        const total = Number(inv?.total) || 0;
        const balance = Number(inv?.balance) || 0;
        const paid = total - balance;
        acc.total += total;
        acc.paid += paid;
        acc.remaining += balance;
        return acc;
      },
      { total: 0, paid: 0, remaining: 0 },
    );
    return {
      total: totals.total.toFixed(2),
      paid: totals.paid.toFixed(2),
      remaining: totals.remaining.toFixed(2),
    };
  }, [clientHistoryInvoices]);
  const clientHistoryDisplayName =
    pickNonEmptyString(
      clientHistoryRecord?.full_name,
      clientHistoryRecord?.fullName,
      clientHistoryRecord?.name,
      clientHistoryRecord?.client_name,
    ) || "Client";

  return (
    <div className="appointment-page">
      <CustomDialog
        open={Boolean(inactiveClientConfirm)}
        onClose={() => {
          if (inactiveClientConfirm) {
            inactiveClientConfirm.resolve(false);
            setInactiveClientConfirm(null);
          }
          setAddDialogOpen(false);
          setEditingEventId(null);
          setEditingEventRaw(null);
          setEditingEventStarted(false);
        }}
        title="Inactive Client Warning"
        actions={
          <>
            <CustomButton
              label="Cancel"
              variant="outlined"
              onClick={() => {
                if (inactiveClientConfirm) {
                  inactiveClientConfirm.resolve(false);
                  setInactiveClientConfirm(null);
                }
                setAddDialogOpen(false);
                setEditingEventId(null);
                setEditingEventRaw(null);
                setEditingEventStarted(false);
              }}
            />
            <CustomButton
              label="Proceed"
              variant="contained"
              bgColor={COLORS.MainColor}
              textColor="#fff"
              onClick={() => {
                if (inactiveClientConfirm) {
                  inactiveClientConfirm.resolve(true);
                  setInactiveClientConfirm(null);
                }
              }}
            />
          </>
        }>
        <CustomLabel
          text="This client is inactive/unverified. Are you sure you want to continue with this booking?"
          fontSize={20}
        />
      </CustomDialog>

      <CustomDialog
        open={Boolean(recurringScopeConfirm)}
        onClose={() => {
          if (recurringScopeConfirm) {
            recurringScopeConfirm.resolve(undefined);
            setRecurringScopeConfirm(null);
          }
        }}
        title="Recurring Appointment"
        actions={
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              gap: 1,
              width: "100%",
              "& .MuiButton-root": {
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: { xs: "100%", sm: 128 },
                whiteSpace: "nowrap",
              },
            }}>
            <CustomButton
              label="Cancel"
              variant="text"
              sx={{ minWidth: { xs: "100%", sm: 96 } }}
              onClick={() => {
                if (recurringScopeConfirm) {
                  recurringScopeConfirm.resolve(undefined);
                  setRecurringScopeConfirm(null);
                }
              }}
            />
            <CustomButton
              label={
                recurringScopeConfirm?.action === "delete"
                  ? "Selected only"
                  : "No"
              }
              variant="outlined"
              sx={{ minWidth: { xs: "100%", sm: 128 } }}
              onClick={() => {
                if (recurringScopeConfirm) {
                  recurringScopeConfirm.resolve(null);
                  setRecurringScopeConfirm(null);
                }
              }}
            />
            {recurringScopeConfirm?.action === "delete" && (
              <CustomButton
                label="Delete upcoming"
                variant="contained"
                bgColor={COLORS.MainColor}
                textColor="#fff"
                sx={{ minWidth: { xs: "100%", sm: 152 } }}
                onClick={() => {
                  if (recurringScopeConfirm) {
                    recurringScopeConfirm.resolve("UPCOMING");
                    setRecurringScopeConfirm(null);
                  }
                }}
              />
            )}
            <CustomButton
              label={
                recurringScopeConfirm?.action === "delete"
                  ? "Delete all"
                  : "Yes"
              }
              variant="contained"
              bgColor={
                recurringScopeConfirm?.action === "delete"
                  ? COLORS.RED
                  : COLORS.MainColor
              }
              textColor="#fff"
              sx={{ minWidth: { xs: "100%", sm: 128 } }}
              onClick={() => {
                if (recurringScopeConfirm) {
                  recurringScopeConfirm.resolve("ALL");
                  setRecurringScopeConfirm(null);
                }
              }}
            />
          </Box>
        }>
        {recurringScopeConfirm?.action === "delete" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <CustomLabel
              text="This is a recurring appointment. What do you want to delete?"
              fontSize={20}
            />
            <Typography sx={{ color: COLORS.placeHolderColor }}>
              Delete all removes the full recurring series, but done
              appointments and appointments with paid invoices will be ignored.
              Delete upcoming removes this appointment and the upcoming
              appointments in the series with the same exceptions, leaving past
              appointments untouched.
            </Typography>
          </Box>
        ) : (
          <CustomLabel
            text="This is a recurring appointment. Do you want to cancel all appointments?"
            fontSize={20}
          />
        )}
      </CustomDialog>

      <CustomDialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setPendingDeleteScope(undefined);
        }}
        title="Delete Appointment"
        actions={
          <>
            <CustomButton
              label="Cancel"
              variant="outlined"
              onClick={() => {
                setDeleteConfirmOpen(false);
                setPendingDeleteScope(undefined);
              }}
              disabled={deletingAppointment}
            />
            <CustomButton
              label={deletingAppointment ? "Deleting..." : "Delete"}
              variant="contained"
              bgColor={COLORS.RED}
              textColor={COLORS.WhiteText}
              onClick={handleDeleteAppointment}
              disabled={deletingAppointment}
            />
          </>
        }>
        <CustomLabel
          text={
            pendingDeleteScope === "UPCOMING"
              ? "Are you sure you want to delete upcoming appointments in this recurring series?"
              : pendingDeleteScope === "ALL"
                ? "Are you sure you want to delete all appointments in this recurring series?"
                : "Are you sure you want to delete this appointment?"
          }
          fontSize={20}
        />
      </CustomDialog>

      <Dialog
        open={Boolean(dragConfirmState)}
        onClose={() => {
          if (dragConfirmState) {
            dragConfirmState.resolve(false);
            setDragConfirmState(null);
          }
        }}
        sx={{
          // Force this confirmation dialog above all calendar content
          zIndex: 99999,
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            zIndex: 100000,
          },
        }}
        BackdropProps={{
          sx: {
            zIndex: 99998,
          },
        }}
        TransitionProps={{ appear: false, timeout: 0 }}>
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>Confirm Move</DialogTitle>
        <DialogContent sx={{ minWidth: 320, pt: 1 }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            You are about to move this appointment:
          </Typography>
          <Typography variant="body2">
            <strong>From branch:</strong> {dragConfirmState?.fromBranch || "-"}
          </Typography>
          <Typography variant="body2">
            <strong>To branch:</strong> {dragConfirmState?.toBranch || "-"}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Current time
            </Typography>
            <Typography variant="body2">
              Start: {formatTo12h(dragConfirmState?.fromStart) || "-"}
            </Typography>
            <Typography variant="body2">
              End: {formatTo12h(dragConfirmState?.fromEnd) || "-"}
            </Typography>
          </Box>
          <Box sx={{ mt: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              New time
            </Typography>
            <Typography variant="body2">
              Start: {formatTo12h(dragConfirmState?.toStart)}
            </Typography>
            <Typography variant="body2">
              End: {formatTo12h(dragConfirmState?.toEnd)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              if (dragConfirmState) {
                dragConfirmState.resolve(false);
                setDragConfirmState(null);
              }
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (dragConfirmState) {
                dragConfirmState.resolve(true);
                setDragConfirmState(null);
              }
            }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <GenericFormDialog
        open={clientDetailsViewOpen}
        onClose={handleClientDetailsViewClose}
        title={clientHistoryTitle}
        fields={clientHistoryFields}
        initialValues={clientHistoryInitialValues}
        showSubmit={false}
        disabled={true}
        additionalActions={[
          <CustomButton
            key="info"
            label="View Info"
            variant="outlined"
            startIcon={<InfoIcon />}
            onClick={() => {
              const safeClientId = resolveClientIdValue(selectedClientId);
              if (!safeClientId) {
                toast.error("Client ID not found.");
                return;
              }
              navigate(`/dashboard/${venueId}/clients/details/${safeClientId}`);
            }}
            sx={{
              color: COLORS.MainColor,
            }}
          />,
          <CustomButton
            key="history"
            label="History"
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => {
              const safeClientId = resolveClientIdValue(selectedClientId);
              if (!safeClientId) {
                toast.error("Client ID not found.");
                return;
              }
              setClientHistoryDialogOpen(true);
            }}
            sx={{ mr: 1 }}
          />,
        ]}
      />

      <GenericFormDialog
        open={clientHistoryDialogOpen}
        onClose={() => setClientHistoryDialogOpen(false)}
        title={`Invoice History for ${clientHistoryDisplayName}`}
        customContent={
          <Box sx={{ width: "100%", maxHeight: 400, overflow: "auto" }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                gap: 1.5,
                mb: 2,
              }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                  border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                }}>
                <CustomLabel text="Total" fontWeight="600" fontSize="0.9rem" />
                <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                  {clientHistoryTotals.total}
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                  border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                }}>
                <CustomLabel text="Paid" fontWeight="600" fontSize="0.9rem" />
                <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                  {clientHistoryTotals.paid}
                </Box>
              </Box>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "10px",
                  backgroundColor: "#f5f5f5",
                  border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                }}>
                <CustomLabel
                  text="Remaining"
                  fontWeight="600"
                  fontSize="0.9rem"
                />
                <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                  {clientHistoryTotals.remaining}
                </Box>
              </Box>
            </Box>
            <CustomTable<any>
              filters={{ page: 0, size: 10 }}
              clientSidePagination={true}
              loading={clientHistoryLoading}
              fields={[
                { key: "type", header: "Type" },
                { key: "total", header: "Total" },
                {
                  key: "paid",
                  header: "Paid",
                  transform: async (row: any) => {
                    const total = Number(row?.total) || 0;
                    const balance = Number(row?.balance) || 0;
                    const paid = total - balance;
                    return paid >= 0 ? paid.toFixed(2) : "0.00";
                  },
                },
                { key: "balance", header: "Remaining" },
                {
                  key: "status",
                  header: "Status",
                  transform: async (row: any) => {
                    const status = row?.status || "";
                    const formatted = status
                      .toLowerCase()
                      .split("_")
                      .map(
                        (word: string) =>
                          word.charAt(0).toUpperCase() + word.slice(1),
                      )
                      .join(" ");

                    return (
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "10px",
                          backgroundColor: COLORS.MainColor,
                          color: COLORS.WhiteText,
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          textTransform: "none",
                        }}>
                        {formatted || "-"}
                      </Box>
                    );
                  },
                },
                {
                  key: "date",
                  header: "Date",
                  transform: async (row: any) => {
                    const dateStr = row?.issuedAt || row?.createdAt;
                    if (!dateStr) return "-";
                    return formatTableDate(dateStr);
                  },
                },
              ]}
              fetchFunc={async (params?: any) => {
                const content = clientHistoryInvoices;
                const totalElements = content.length;
                const pageSize = (params && params.size) || 10;
                const totalPages = Math.max(
                  1,
                  Math.ceil(totalElements / pageSize),
                );

                return {
                  content,
                  number: 0,
                  size: pageSize,
                  totalElements,
                  totalPages,
                };
              }}
            />
          </Box>
        }
        showSubmit={false}
      />

      <div className="appointment-header-section">
        <Box
          sx={{
            display: { xs: "flex", lg: "grid" },
            gridTemplateColumns: {
              lg: "minmax(0, 1fr) auto minmax(0, 1fr)",
            },
            columnGap: { lg: 2 },
            rowGap: 1,
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center", lg: "center" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            gap: { xs: 1, sm: 1 },
            width: "100%",
            flexWrap: { xs: "wrap", md: "wrap" },
            paddingY: 0,
          }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: 1,
              flexWrap: { xs: "wrap", md: "wrap" },
              flex: { xs: "0 0 auto", md: 1 },
              width: { xs: "100%", md: "auto", lg: "auto" },
              minWidth: 0,
              justifySelf: { lg: "start" },
            }}>
            <CustomSelect
              value={selectedBranchId}
              onChange={(value) => handleBranchChange(String(value))}
              options={[
                { value: "all", label: "All Branches" },
                ...branches.map((branch) => ({
                  value: String(branch.id),
                  label: String(
                    branch?.name || branch?.title || branch?.id || "Branch",
                  ),
                })),
              ]}
              sx={{
                minWidth: { xs: "100%", sm: 180 },
                maxWidth: { xs: "100%", sm: 240 },
                width: { xs: "100%", sm: 190 },
                flexShrink: 0,
                "& .MuiOutlinedInput-root": {
                  height: 28,
                  minHeight: 28,
                  borderRadius: "10px",
                },
                "& .MuiSelect-select": {
                  padding: "4px 8px",
                  fontSize: "0.78rem",
                },
              }}
            />
            <Box
              sx={{
                display: "flex",
                gap: { xs: 0.5, md: 1 },
                flexWrap: { xs: "wrap", md: "nowrap" },
                alignItems: { xs: "stretch", md: "center" },
                width: { xs: "100%", md: "auto" },
              }}>
              <CustomButton
                variant="contained"
                onClick={async () => {
                  try {
                    await exportSlotsToWhatsApp();
                  } catch (e) {
                    console.error("Export slots failed", e);
                    alert("Failed to export slots");
                  }
                }}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  backgroundColor: "green",
                  height: { xs: "auto", sm: 28 },
                  minHeight: { xs: 26, sm: 28 },
                  px: { xs: 0.6, sm: 1 },
                  fontSize: { xs: "0.66rem", sm: "0.74rem" },
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  lineHeight: 1.1,
                  minWidth: 0,
                  flex: { xs: "1 1 0", sm: "0 0 auto" },
                  textAlign: "center",
                }}>
                Export Available Slots
              </CustomButton>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              flex: { xs: "0 0 auto", md: 1 },
              flexWrap: "nowrap",
              minWidth: { xs: "100%", md: "auto" },
              width: { xs: "100%", md: "auto", lg: "fit-content" },
              justifySelf: { lg: "center" },
            }}>
            <Button
              variant="outlined"
              size="small"
              onClick={gotoPrev}
              sx={{
                minWidth: { xs: 26, sm: 28 },
                width: { xs: 26, sm: 28 },
                height: { xs: 26, sm: 28 },
                px: 0,
              }}>
              <ChevronLeftRounded fontSize="small" />
            </Button>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={activeDate}
                onChange={(date) => {
                  if (!date || isNaN(date.getTime())) return;
                  gotoDate(date);
                }}
                format="EEEE, d-M-yyyy"
                slotProps={{
                  textField: {
                    size: "small",
                    hiddenLabel: true,
                    inputProps: {
                      "aria-label": "Date",
                      style: { textAlign: "center" },
                    },
                    sx: {
                      fontSize: "8px",
                      minWidth: { xs: 0, md: 180 },
                      width: { xs: "100%", md: "auto" },
                      flex: { xs: 1, md: "0 0 auto" },
                      "& .MuiInputBase-root": {
                        height: 15,
                        minHeight: 15,
                        borderRadius: "10px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "8px",
                        fontWeight: 100,
                        textAlign: "center",
                      },
                      "& .MuiInputAdornment-root": {
                        marginRight: 0,
                      },
                      "& .MuiIconButton-root": {
                        padding: "3px",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: "17px",
                      },
                    },
                  },
                  // Style the popup calendar day numbers
                  popper: {
                    sx: {
                      "& .MuiPickersDay-root": {
                        fontSize: "8px",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <Button
              variant="outlined"
              size="small"
              onClick={gotoNext}
              sx={{
                minWidth: { xs: 26, sm: 28 },
                width: { xs: 26, sm: 28 },
                height: { xs: 26, sm: 28 },
                px: 0,
              }}>
              <ChevronRightRounded fontSize="small" />
            </Button>
          </Box>

          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              gap: 0.75,
              justifyContent: "flex-start",
              flexWrap: "wrap",
              alignItems: "center",
              width: "100%",
            }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                height: 26,
                minHeight: 26,
                px: 0.75,
                fontSize: "0.7rem",
              }}
              onClick={openCreateDialogEmpty}
              aria-label="Add appointment">
              <AddRounded fontSize="small" />
            </Button>

            <Button
              variant={currentView === "week" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 26,
                minHeight: 26,
                px: 0.75,
                fontSize: "0.7rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("week")}>
              Week
            </Button>
            <Button
              variant={currentView === "day" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 26,
                minHeight: 26,
                px: 0.75,
                fontSize: "0.7rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("day")}>
              Day
            </Button>
            <Button
              variant={currentView === "month" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 26,
                minHeight: 26,
                px: 0.75,
                fontSize: "0.7rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("month")}>
              Month
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={gotoToday}
              sx={{
                height: 26,
                minHeight: 26,
                px: 0.75,
                fontSize: "0.7rem",
                whiteSpace: "nowrap",
              }}></Button>
          </Box>

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              gap: 1,
              justifyContent: "flex-end",
              flexWrap: "wrap",
              alignItems: "center",
              minWidth: { xs: "100%", md: "auto", lg: 0 },
              minHeight: 32,
              justifySelf: { lg: "end" },
            }}>
            <Button
              variant="contained"
              size="small"
              sx={{
                height: 28,
                minHeight: 28,
                px: 0.8,
                fontSize: "0.74rem",
                whiteSpace: "nowrap",
              }}
              onClick={openCreateDialogEmpty}
              aria-label="Add appointment">
              <AddRounded fontSize="small" />
            </Button>
            <Button
              variant={currentView === "week" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 28,
                minHeight: 28,
                px: 1,
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("week")}>
              Week
            </Button>
            <Button
              variant={currentView === "day" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 28,
                minHeight: 28,
                px: 1,
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("day")}>
              Day
            </Button>
            <Button
              variant={currentView === "month" ? "contained" : "outlined"}
              size="small"
              sx={{
                height: 28,
                minHeight: 28,
                px: 1,
                fontSize: "0.72rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
              }}
              onClick={() => handleViewChange("month")}>
              Month
            </Button>
            {/* <Button
              variant="text"
              size="small"
              onClick={gotoToday}
              sx={{
                height: 32,
                minHeight: 32,
                px: 1.25,
                fontSize: "0.78rem",
                whiteSpace: "nowrap",
              }}
            >
              Today
            </Button> */}
          </Box>
        </Box>

        <AddClientDialog
          open={addClientDialogOpen}
          onClose={() => setAddClientDialogOpen(false)}
          branches={branches}
          initialBranch={addClientBranchId}
          onCreated={(client) => {
            try {
              if (!client) return;
              const normalizedClient = normalizeClientRecord(client);
              const newClientId = normalizedClient.id
                ? String(normalizedClient.id)
                : "";
              setClients((prev) => {
                const existing =
                  prev && Array.isArray(prev.users) ? prev.users : [];
                const deduped = existing.filter(
                  (c) => String(c.id) !== String(normalizedClient.id),
                );
                return { users: [normalizedClient, ...deduped] };
              });

              if (newClientId && clientFieldHelpersRef.current?.setFieldValue) {
                clientFieldHelpersRef.current.setFieldValue(
                  "client_id",
                  newClientId,
                );
              } else if (newClientId && !addDialogOpen) {
                setFormClientId(newClientId);
              }
              const name = normalizedClient.name || normalizedClient.email;
              if (name) setFormCustomerName(String(name));
              void fetchOrgClients(normalizedClient);
            } catch (e) {
              console.error("Error while adding client to appointment: ", e);
            }
          }}
        />
      </div>

      <ScheduleConflictsDialog
        open={classConflictsOpen}
        message={classConflictMessage}
        conflicts={classConflicts}
        onClose={() => setClassConflictsOpen(false)}
      />

      <SessionRosterDialog
        open={Boolean(classSessionId)}
        businessId={venueId}
        sessionId={classSessionId}
        allowSessionCancel
        onClose={() => setClassSessionId(null)}
        onRosterChanged={() => {
          void refreshAppointments();
        }}
      />

      <Box
        className="scheduler-wrapper"
        ref={schedulerWrapperRef}
        sx={{
          marginTop: 0,
        }}>
        {slotDurationLoading ? (
          <Box
            sx={{
              height: "100%",
              minHeight: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontWeight: 500,
            }}>
            Loading calendar...
          </Box>
        ) : (
          <GridCalendar
            key={calendarKey}
            events={displayEvents}
            branches={displayedBranches}
            activeDate={activeDate}
            view={currentView}
            disableDrag={isMobileInteraction}
            displayedBranchIds={displayedBranchIds}
            onDateClick={(dateObj, branchId) => {
              handleDateClick(
                {
                  date: dateObj,
                  dayEl: null,
                  dateStr: dateObj.toISOString().split("T")[0],
                },
                branchId,
              );
            }}
            onEventClick={(eventId) => {
              const rendered = displayEvents.find(
                (e) => String(e.id) === String(eventId),
              );
              const targetId = rendered?.extendedProps?.originalId || eventId;
              const event =
                events.find((e) => String(e.id) === String(targetId)) ||
                rendered;
              if (event) {
                handleEventClick({
                  event: {
                    id: event.id,
                    start: event.start,
                    end: event.end,
                    title: event.title,
                    extendedProps: event.extendedProps,
                  },
                });
              }
            }}
            onEventDoubleClick={(eventId) => {
              const rendered = displayEvents.find(
                (e) => String(e.id) === String(eventId),
              );
              const targetId = rendered?.extendedProps?.originalId || eventId;
              const event =
                events.find((e) => String(e.id) === String(targetId)) ||
                rendered;
              if (!event) return;
              void openEditDialogForEvent(
                {
                  id: event.id,
                  start: event.start,
                  end: event.end,
                  title: event.title,
                  extendedProps: event.extendedProps,
                },
                targetId,
              );
            }}
            onDragValidationFailed={(message) => {
              toast.error(message);
            }}
            onEventDragEnd={async (
              eventId,
              newStartTime,
              newEndTime,
              branchId,
            ) => {
              let rollbackTargetId: any = eventId;
              let rollbackRawBase: any = {};
              let rollbackOriginalBranchId: any = null;
              let rollbackApptUnitId: any = null;
              let rollbackApptServiceId: any = null;
              let rollbackOrigStart: Date | null = null;
              let rollbackOrigEnd: Date | null = null;
              let rollbackNewStartDt: Date | null = null;
              try {
                const rendered = displayEvents.find(
                  (e) => String(e.id) === String(eventId),
                );
                const targetId = rendered?.extendedProps?.originalId || eventId;
                rollbackTargetId = targetId;
                const original = events.find(
                  (e) => String(e.id) === String(targetId),
                );
                if (!original) return;
                if (isClosedSlotEvent(original)) {
                  toast.error("Closed by admin.");
                  return;
                }
                if (isClassEvent(original)) {
                  toast.error(
                    "Class sessions cannot be moved from the calendar. Manage them from Classes.",
                  );
                  return;
                }

                const raw = original.extendedProps?.raw || {};
                rollbackRawBase = raw;
                const targetBranch = branches.find(
                  (branch) => String(branch.id) === String(branchId),
                );
                const legacyBranchCandidate =
                  raw.business_id || raw.businessId || raw.business;
                const legacyAsUnitId =
                  legacyBranchCandidate &&
                  branches.some(
                    (b) => String(b.id) === String(legacyBranchCandidate),
                  )
                    ? legacyBranchCandidate
                    : null;
                const originalBranchId =
                  original.extendedProps?.branchId ||
                  raw.unit_id ||
                  raw.unitId ||
                  raw.unit?.id ||
                  raw.branch_id ||
                  raw.branchId ||
                  raw.branch?.id ||
                  legacyAsUnitId;
                rollbackOriginalBranchId = originalBranchId;

                const origStart = original.start
                  ? new Date(original.start)
                  : null;
                let origEnd = original.end ? new Date(original.end) : null;
                if (
                  !origStart ||
                  !origEnd ||
                  isNaN(origStart.getTime()) ||
                  isNaN(origEnd.getTime())
                ) {
                  toast.error("Cannot move: invalid appointment time");
                  return;
                }
                if (origEnd.getTime() <= origStart.getTime()) {
                  while (origEnd.getTime() <= origStart.getTime()) {
                    origEnd = new Date(origEnd.getTime() + 24 * 60 * 60000);
                  }
                }
                rollbackOrigStart = origStart;
                rollbackOrigEnd = origEnd;
                const durationMs = origEnd.getTime() - origStart.getTime();

                let segmentStartMinutes = 0;
                if (rendered?.start) {
                  const segStart = new Date(rendered.start);
                  segmentStartMinutes =
                    segStart.getHours() * 60 + segStart.getMinutes();
                } else if (rendered?.extendedProps?.segmentTimeStart) {
                  const [sh, sm] = String(
                    rendered.extendedProps.segmentTimeStart,
                  )
                    .split(":")
                    .map(Number);
                  segmentStartMinutes = (sh || 0) * 60 + (sm || 0);
                }
                const [newH, newM] = String(newStartTime)
                  .split(":")
                  .map(Number);
                const newStartMinutes = (newH || 0) * 60 + (newM || 0);
                const deltaMinutes = newStartMinutes - segmentStartMinutes;

                const newStartDt = new Date(
                  origStart.getTime() + deltaMinutes * 60000,
                );
                const newEndDt = new Date(newStartDt.getTime() + durationMs);
                rollbackNewStartDt = newStartDt;

                const confirmMove = await requestDragConfirmation({
                  fromBranch:
                    branches.find(
                      (b) => String(b.id) === String(originalBranchId),
                    )?.name || String(originalBranchId || "Current"),
                  toBranch:
                    targetBranch?.name ||
                    targetBranch?.title ||
                    String(branchId || "Target"),
                  fromStart:
                    original.extendedProps?.timeStart ||
                    raw.time_start ||
                    raw.start_time ||
                    formatTimeHHMM(origStart),
                  fromEnd:
                    original.extendedProps?.timeEnd ||
                    raw.time_end ||
                    raw.end_time ||
                    formatTimeHHMM(origEnd),
                  toStart: formatTimeHHMM(newStartDt),
                  toEnd: formatTimeHHMM(newEndDt),
                });
                if (!confirmMove) return false;

                const apptUnitId = raw.unit_id || raw.unitId || raw.unit?.id;
                const apptServiceId =
                  raw.service_id ||
                  raw.service?.id ||
                  (raw.services &&
                    Array.isArray(raw.services) &&
                    raw.services[0]);
                rollbackApptUnitId = apptUnitId;
                rollbackApptServiceId = apptServiceId;
                const targetUnit = branchId || originalBranchId || apptUnitId;

                const payload = {
                  unit_id: targetUnit,
                  branch_id: targetUnit,
                  service_id: apptServiceId,
                  start_at: newStartDt.toISOString(),
                  end_at: newEndDt.toISOString(),
                  notes: raw.notes || raw.text || "",
                  status: deriveAppointmentStatus(
                    raw.status || raw.state || "PENDING",
                    newStartDt,
                    newEndDt,
                  ),
                };

                await updateAppointment(targetId.toString(), payload);

                // Optimistic local update so status/color change is visible immediately
                setEvents((prev) => {
                  const updatedRaw = {
                    ...(raw || {}),
                    start_at: payload.start_at,
                    end_at: payload.end_at,
                    status: payload.status ?? raw.status,
                    unit_id: payload.unit_id ?? raw.unit_id,
                    service_id: payload.service_id ?? raw.service_id,
                  };
                  const updatedEvent = mapAppointmentToEvent(updatedRaw);
                  return prev.map((ev) => {
                    const matchId = String(
                      ev.extendedProps?.originalId || ev.id,
                    );
                    if (matchId !== String(targetId)) return ev;
                    return updatedEvent;
                  });
                });

                if (targetUnit) {
                  void refreshUnitAppointmentsForDate(
                    String(targetUnit),
                    newStartDt,
                  );
                } else {
                  void refreshAppointments();
                }
                return true;
              } catch (error) {
                console.error("Error updating appointment:", error);
                const rollbackUnit =
                  rollbackOriginalBranchId ||
                  rollbackApptUnitId ||
                  rollbackRawBase?.unit_id;
                const safeOrigStart = rollbackOrigStart || new Date();
                const safeOrigEnd =
                  rollbackOrigEnd ||
                  new Date(safeOrigStart.getTime() + 60 * 60000);
                const rollbackRaw = {
                  ...(rollbackRawBase || {}),
                  start_at: safeOrigStart.toISOString(),
                  end_at: safeOrigEnd.toISOString(),
                  unit_id: rollbackUnit,
                  branch_id: rollbackUnit,
                  service_id:
                    rollbackApptServiceId ?? rollbackRawBase?.service_id,
                  status:
                    rollbackRawBase?.status ||
                    rollbackRawBase?.state ||
                    "PENDING",
                };
                const rollbackEvent = mapAppointmentToEvent(rollbackRaw);
                setEvents((prev) =>
                  (Array.isArray(prev) ? prev : []).map((ev) => {
                    const matchId = String(
                      ev.extendedProps?.originalId || ev.id,
                    );
                    if (matchId !== String(rollbackTargetId)) return ev;
                    return rollbackEvent;
                  }),
                );

                if (rollbackUnit) {
                  invalidateSlotCacheForUnit(String(rollbackUnit));
                  void refreshUnitAppointmentsForDate(
                    String(rollbackUnit),
                    safeOrigStart,
                  );
                }
                if (
                  branchId &&
                  rollbackUnit &&
                  String(branchId) !== String(rollbackUnit) &&
                  rollbackNewStartDt
                ) {
                  invalidateSlotCacheForUnit(String(branchId));
                  void refreshUnitAppointmentsForDate(
                    String(branchId),
                    rollbackNewStartDt,
                  );
                }
                // Some drag libs keep internal position state after failed updates.
                // Force a remount + full sync only on error path.
                setCalendarKey((prev) => prev + 1);
                void refreshAppointments();
                showErrorToast(
                  getErrorMessage(
                    error,
                    "Failed to update appointment. Changes reverted.",
                  ),
                );
                return false;
              }
            }}
            startHour={calendarStartHour}
            endHour={calendarEndHour}
          />
        )}
      </Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <GenericFormDialog
          open={addDialogOpen}
          onClose={handleAppointmentDialogClose}
          title={dialogTitle}
          submitLabel={dialogSubmitLabel}
          showSubmit
          showCloseIcon={false}
          initialValues={dialogInitialValues}
          disableSubmitWhenUnchanged={Boolean(editingEventId)}
          additionalActions={appointmentDialogActions}
          fields={appointmentFields}
          tabs={dialogTabs}
          initialTab={appointmentDialogTab}
          onTabChange={setAppointmentDialogTab}
          isSubmitting={
            !editingEventId && appointmentDialogTab === 2
              ? isClassSubmitting
              : isAppointmentSubmitting
          }
          onValuesChange={(values) => {
            const { frequency, endMode } = readClassFormControlState(values);
            setClassFormFrequency((prev) => (prev === frequency ? prev : frequency));
            setClassFormEndMode((prev) => (prev === endMode ? prev : endMode));
          }}
          onSubmit={async (values) => {
            if (!editingEventId && appointmentDialogTab === 2) {
              await handleAddClassSubmit(values);
              return;
            }
            if (!editingEventId && appointmentDialogTab === 1) {
              await handleBlockSlotSubmit(values);
              return;
            }
            await handleAppointmentSubmit(values);
          }}
        />
      </LocalizationProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <GenericFormDialog
          open={invoiceEditOpen}
          onClose={handleInvoiceDialogClose}
          title={
            invoiceDialogInvoiceNumber
              ? `Edit Invoice #${invoiceDialogInvoiceNumber}`
              : "Edit Invoice"
          }
          submitLabel="Update"
          initialValues={invoiceDialogInitialValues}
          fields={invoiceFormFields}
          onSubmit={handleInvoiceSubmit}
          isSubmitting={invoiceSubmitLoading}
        />
      </LocalizationProvider>
    </div>
  );
}

export default Appointment;
